import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQrMenuTranslation } from "../../services/menu/useQrMenuTranslation";
import { buildLinkedRuleKey, buildLinkedRuleSourceKey, getRuleField, getLinkedOnlyProductIds, indexLinkedRulesByProduct, resolveLinkedRuleProducts, isIngredientLinkedRule } from "../../services/menu/linkedRules";
import { buildBeverageConsumptions, resolveBeverageSelection, withoutBeverageVarietyIngredients } from "../../services/menu/beverageRelations";
import { isProductVisibleInMenu } from "../../services/menu/productVisibility";
import { compactPromotionPaymentFields, promotionPaymentNotice } from "../../services/supabase/edge-functions/_shared/promotionPayments";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import BarMenuCatalog from "../common/BarMenuCatalog";
import CatalogImage from "../common/CatalogImage";
import { QR_SESSION_CHANGED, markQrCustomerEntryExpired, getQrCustomerSessionToken, validateQrCustomerSession } from "../../services/supabase/qrCustomerSessionService";
import MenuPromotionCard from "../common/MenuPromotionCard";
import { normalizeQrMenuStyle } from "../../services/local/qrMenuStyle";
import { orderQrCategories, orderQrSubcategories } from "../../services/local/qrCategoryOrder";
import { repairTextEncoding } from "../../services/local/textEncoding";
import PizzaSaleModeSelector from "../menu/PizzaSaleModeSelector";
import { getDefaultPizzaSaleMode, getPizzaStartingPrice, getPizzaSizePriceSummary, getPizzaPortionScale, isPizzaSaleModeEnabled, validatePizzaCartItems } from "../../services/local/pizzaHalfAndHalf";
import mozzoAppKioskLogoUrl from "../../assets/branding/mozzoapp-kiosk-logo.png";
import {
  analyzeImageLuminance,
  buildAdaptiveHeroPalette,
  getCssColorLuminance,
} from "../common/heroContrast";
import useModalBackNavigation from "../common/useModalBackNavigation";
import {
  buildPromotionPricing,
  isDailyMenuPromotion,
  isPromotionAvailableNow,
  isPromotionSelectableNow,
} from "../../services/promotions/promotionEngine";
import {
  finalizeApprovedPublicMenuPayment,
  getPublicBranchPaymentSetup,
  getPublicKioskMenuByBranchId,
  getPublicMenuByQrToken,
  getPublicQrPendingAccounts,
} from "../../services/supabase/qrService";
import { isSupabaseConfigured } from "../../services/supabase/config";
import {
  cancelPublicQrOrderFromSharedState,
  hydrateSharedOperationalStateToLocal,
  pushSharedPaymentOperationsState,
  submitPublicKioskOrderToSharedState,
  submitPublicQrOrderToSharedState,
  submitPublicWaiterServiceRequestToSharedState,
} from "../../services/supabase/operationalStateService";
import { getBranchMenuSnapshot } from "../../services/supabase/menuService";
import {
  DIAS_SEMANA,
  getRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  buildRestaurantBrandTheme,
} from "../../services/local/brandTheme";
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
} from "../../services/local/institutionalBranding";
import { setDocumentTitle } from "../../services/local/pageTitle";
import {
  loadLocalMenuSnapshot,
  saveLocalMenuSnapshot,
} from "../../services/local/menuStorage";
import {
  buildOperationalConsumptions,
  createOperationalOrderItem,
  orderRequiresKitchen,
} from "../../services/local/orderModel";
import {
  buildPizzaHalfAndHalfOptions,
  buildPizzaHalfAndHalfSelectionDetails,
  createPizzaHalfAndHalfSelection,
  createPizzaSizeSelectionDetail,
  findPizzaSizeOption,
  getPizzaHalfAndHalfSelection,
  getPizzaHalfAndHalfPricing,
  getPizzaProductName,
  getPizzaSizeOptions,
  isPizzaCategoryProduct,
  isPizzaHalfAndHalfSelection,
  normalizePizzaSizeOptions,
  productHasPizzaSizeOptions,
  resolvePizzaProductPrice,
} from "../../services/local/pizzaHalfAndHalf";
import {
  createEmpanadaCookingSelectionDetail,
  createEmpanadaSaleSelectionDetail,
  findEmpanadaCookingOption,
  findEmpanadaSaleOption,
  getEmpanadaCookingOptions,
  getEmpanadaSaleOptions,
  isEmpanadaCategoryProduct,
  normalizeEmpanadaCookingOptions,
  normalizeEmpanadaSaleOptions,
  productHasEmpanadaSaleOptions,
  resolveEmpanadaProductPrice,
} from "../../services/local/empanadaSales";
import {
  formatPreparationNumber,
  printKitchenTicket,
  printCustomerPickupTicket,
} from "../../services/local/kitchenPrinting";
import {
  buildMozzoAndroidCustomerTicketPayload,
  buildMozzoAndroidKitchenTicketPayload,
  canRequestMozzoAndroidMicrophonePermission,
  isMozzoAndroidBridgeAvailable,
  printTicketWithMozzoAndroid,
  requestMozzoAndroidMicrophonePermission,
} from "../../services/local/androidKioskBridge";
import {
  buildMozzoWindowsCustomerTicketPayload,
  buildMozzoWindowsKitchenTicketPayload,
  isMozzoWindowsBridgeAvailable,
  printTicketWithMozzoWindows,
} from "../../services/local/windowsKioskBridge";
import {
  getIngredientConsumptionQuantity,
  getIngredientMenuBaseQuantity,
} from "../../services/local/menuIngredientModel";
import {
  buildOperationalOrdersStorageKey,
  getOperationalOrdersState,
  OPERATIONAL_ORDERS_UPDATED_EVENT,
  saveOperationalOrdersState,
} from "../../services/local/operationalOrders";
import {
  buildPaymentOperationsStorageKey,
  getPaymentOperations,
  PAYMENT_OPERATIONS_UPDATED_EVENT,
  savePaymentOperationCommissionSettlement,
  savePaymentOperations,
} from "../../services/local/paymentOperations";
import {
  buildBranchPaymentSetupSnapshot,
  KIOSK_DIGITAL_PAYMENT_METHOD,
  resolveBranchCommissionRateByChannel,
} from "../../services/local/paymentSettings";
import {
  PROMOTIONS_UPDATED_EVENT,
  buildPromotionsStorageKey,
  getPromotions,
  normalizePromotionsPayload,
} from "../../services/local/promotions";
import {
  buildIngredientStockStorageKey,
  getIngredientStockValidationMessage,
  getIngredientStock,
  getOperationalProductStockState,
  hydrateIngredientStockFromRemote,
  INGREDIENT_STOCK_UPDATED_EVENT,
  validateIngredientStock,
} from "../../services/local/ingredientStock";
import { hydrateBranchPromotionsToLocal } from "../../services/supabase/promotionsService";
import { finalizeApprovedDigitalCommission } from "../../services/supabase/commissionLedgerService";
import {
  checkMzRuntimeUsageAvailability,
  recordMzRuntimeUsage,
} from "../../services/supabase/mzUsageRuntimeService";
import { applyOperationalOrderStock } from "../../services/supabase/orderStockService";
import {
  createBranchDigitalPaymentIntent,
  normalizePaymentIntentSnapshot,
} from "../../services/payments/paymentIntents";
import { getPublicKioskDigitalPaymentIntent } from "../../services/supabase/kioskDigitalPaymentsService";
import { buildPublicKioskPaymentLandingUrl } from "../../services/supabase/kioskDigitalPaymentsService";
import { MozzoFiscalMenuFooter } from "../common/MozzoFiscalBadge";
import { buildPaymentRailSnapshot } from "../../services/payments/paymentRails";
import {
  findMenuQrWalletProvider,
  getCompatibleMenuQrWalletProviders,
  getOperationalMenuQrWalletProviders,
  resolveMenuQrWalletLaunchUrl,
} from "../../services/payments/menuQrWalletProviders";
import {
  buildWaiterAssistanceStorageKey,
  getWaiterAssistanceConfig,
  normalizeWaiterAssistanceConfig,
  WAITER_ASSISTANCE_UPDATED_EVENT,
} from "../../services/local/waiterAssistance";
import {
  buildPublicMenuAssistantIngredientSelectionPlan,
  buildPublicMenuAssistantContextualQuestion,
  buildPublicMenuAssistantOrderDraft,
  buildPublicMenuAssistantPromptSuggestions,
  createPublicMenuAssistantReply,
} from "../../services/supabase/publicMenuAssistantService";
import {
  assignWaiterServiceRequest,
  assignWaiterToTable,
  createDeliveryAlert,
  createWaiterServiceRequest,
  getBranchActiveWaiters,
  getWaiterAssignmentForTable,
  pickLeastBusyWaiterForServiceRequest,
} from "../../services/local/waiterOperations";
import { useAuth } from "../context/AuthContext";

const currencyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 2,
});

const DEFAULT_MAX_EXTRA_QUANTITY = 2;
const CART_STORAGE_PREFIX = "mozzoapp:qr-cart:";
const SUBMITTED_ORDER_STORAGE_PREFIX = "mozzoapp:qr-submitted-order:";
const CUSTOMER_IDENTITY_STORAGE_PREFIX = "mozzoapp:qr-customer:";
const KIOSK_SERVICE_TYPE_STORAGE_PREFIX = "mozzoapp:kiosk-service-type:";
const KIOSK_INACTIVITY_TIMEOUT_MS = 2 * 60 * 1000;
const KIOSK_INACTIVITY_CONFIRMATION_SECONDS = 7;
const KIOSK_PRINT_RESET_DELAY_MS = 5000;
const KIOSK_PENDING_PAYMENT_RECOVERY_MS = 45 * 1000;
const CHROME_ANDROID_PACKAGE = "com.android.chrome";
const DEFAULT_QR_ACCESS_EXPIRED_MESSAGE =
  "Pasaron mas de 2 horas desde que abriste este menu. Para volver a pedir, escanea otra vez el QR desde la mesa.";
const COUNTER_CHECKOUT_METHOD = "counter";
const KIOSK_QR_CHECKOUT_METHOD = KIOSK_DIGITAL_PAYMENT_METHOD;
const mzUnitsFormatter = new Intl.NumberFormat("es-AR", {
  maximumFractionDigits: 0,
});

const isMzRuntimeBlocked = (result = {}) =>
  result?.blocked === true ||
  result?.allowed === false ||
  result?.reason === "insufficient_balance";

const buildMzRuntimeBlockedMessage = (result = {}) => {
  const balance = Number.isFinite(Number(result?.balanceBeforeUnits))
    ? Number(result.balanceBeforeUnits)
    : null;
  const required = Number.isFinite(Number(result?.unitsRequired))
    ? Number(result.unitsRequired)
    : Number.isFinite(Number(result?.unitsConsumed))
      ? Number(result.unitsConsumed)
      : null;

  if (balance !== null && required !== null && required > 0) {
    return `Cuenta bloqueada por creditos MZ insuficientes. Saldo: ${mzUnitsFormatter.format(
      Math.max(0, Math.round(balance))
    )} MZ. Este pedido requiere ${mzUnitsFormatter.format(
      Math.max(0, Math.round(required))
    )} MZ. Compra un pack MZ para volver a operar.`;
  }

  return "Cuenta bloqueada por falta de creditos MZ. Compra un pack MZ para volver a operar.";
};

const resolveMzRuntimeEventKeyForCheckout = ({
  isKioskExperience,
  checkoutMethod,
} = {}) => {
  const normalizedMethod = normalizeCheckoutMethod(checkoutMethod);

  if (!isKioskExperience && normalizedMethod === "deep_link") {
    return "menu_qr_order_paid";
  }

  if (isKioskExperience && normalizedMethod === KIOSK_QR_CHECKOUT_METHOD) {
    return "kiosk_order_paid";
  }

  return "counter_order_confirmed";
};

const resolveBrowserUserAgent = () =>
  typeof navigator === "undefined" ? "" : String(navigator.userAgent || "");

const isSamsungInternetBrowser = (userAgent = resolveBrowserUserAgent()) =>
  /SamsungBrowser/i.test(userAgent);

const isAndroidChromeBrowser = (userAgent = resolveBrowserUserAgent()) =>
  /Android/i.test(userAgent) &&
  /Chrome\/\d+/i.test(userAgent) &&
  !/SamsungBrowser/i.test(userAgent) &&
  !/EdgA\/|OPR\/|Firefox\//i.test(userAgent);

const isIosChromeBrowser = (userAgent = resolveBrowserUserAgent()) =>
  isAppleTouchDevice(userAgent) && /CriOS\/\d+/i.test(userAgent);

const buildAndroidChromeIntentUrl = (rawUrl) => {
  if (!rawUrl) {
    return "";
  }

  try {
    const currentUrl = new URL(rawUrl);
    const scheme = String(currentUrl.protocol || "https:").replace(":", "") || "https";
    const host = currentUrl.host || "";
    const path = `${currentUrl.pathname || "/"}${currentUrl.search || ""}${
      currentUrl.hash || ""
    }`;

    if (!host) {
      return "";
    }

    return `intent://${host}${path}#Intent;scheme=${scheme};package=${CHROME_ANDROID_PACKAGE};end`;
  } catch (error) {
    return "";
  }
};

const buildIosChromeUrl = (rawUrl) => {
  if (!rawUrl) {
    return "";
  }

  try {
    return `googlechrome-x-callback://x-callback-url/open/?url=${encodeURIComponent(
      String(rawUrl)
    )}`;
  } catch (error) {
    return "";
  }
};

const buildIosChromeLegacyUrl = (rawUrl) => {
  if (!rawUrl) {
    return "";
  }

  try {
    const currentUrl = new URL(rawUrl);
    const isHttps = String(currentUrl.protocol || "").toLowerCase() === "https:";
    const iosChromeScheme = isHttps ? "googlechromes://" : "googlechrome://";
    const host = currentUrl.host || "";
    const path = `${currentUrl.pathname || "/"}${currentUrl.search || ""}${
      currentUrl.hash || ""
    }`;

    if (!host) {
      return "";
    }

    return `${iosChromeScheme}${host}${path}`;
  } catch (error) {
    return "";
  }
};

const navigateToExternalUrl = (targetUrl) => {
  if (typeof document === "undefined" || !targetUrl) {
    return false;
  }

  try {
    const link = document.createElement("a");
    link.href = targetUrl;
    link.target = "_self";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch (error) {
    return false;
  }
};

const isSettledPaymentStatus = (value) => {
  const normalizedStatus = String(value || "")
    .trim()
    .toLowerCase();

  return normalizedStatus === "paid" || normalizedStatus === "closed";
};

const isApprovedOrSettledPaymentStatus = (value) => {
  const normalizedStatus = String(value || "")
    .trim()
    .toLowerCase();

  return (
    normalizedStatus === "approved" ||
    normalizedStatus === "paid" ||
    normalizedStatus === "closed"
  );
};

const isFailedPaymentStatus = (value) => {
  const normalizedStatus = String(value || "")
    .trim()
    .toLowerCase();

  return (
    normalizedStatus === "failed" ||
    normalizedStatus === "rejected" ||
    normalizedStatus === "expired" ||
    normalizedStatus === "cancelled"
  );
};

const normalizeDigitalPaymentReturnStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const resolveKioskTicketPaymentMethodLabel = (
  checkoutMethod,
  walletProviderLabel = ""
) => {
  const normalizedMethod = String(checkoutMethod || "")
    .trim()
    .toLowerCase();

  if (normalizedMethod === COUNTER_CHECKOUT_METHOD) {
    return "Caja";
  }

  if (normalizedMethod === KIOSK_QR_CHECKOUT_METHOD) {
    return "QR";
  }

  if (normalizedMethod === "deep_link") {
    return String(walletProviderLabel || "").trim() || "Pago digital";
  }

  return "MozzoApp";
};

const resolveAiSpeechRecognitionConstructor = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const hasBrowserSpeechSynthesis = () =>
  typeof window !== "undefined" &&
  "speechSynthesis" in window &&
  typeof window.SpeechSynthesisUtterance !== "undefined";

const AI_ASSISTANT_FIXED_SPEECH_LOCALE = "es-AR";
const AI_ASSISTANT_SPEECH_LOCALE_CANDIDATES = [
  "es-AR",
  "es_AR",
  "es-MX",
  "es_MX",
  "es-ES",
  "es_ES",
  "es-US",
  "es_US",
  "es",
];

const isAppleTouchDevice = (userAgent = resolveBrowserUserAgent()) => {
  if (!userAgent) {
    return false;
  }

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return true;
  }

  return (
    /Macintosh/i.test(userAgent) &&
    typeof navigator !== "undefined" &&
    Number(navigator.maxTouchPoints || 0) > 1
  );
};

const looksLikeEnglishAssistantReply = (value = "") => {
  const normalized = normalizeAssistantProductKey(value);

  if (!normalized || normalized.length < 12) {
    return false;
  }

  const englishSignals = [
    "what",
    "would",
    "with",
    "available",
    "choose",
    "drink",
    "food",
    "here",
    "there",
    "your",
    "you can",
    "i can",
    "price",
    "hello",
    "please",
  ];
  const spanishSignals = [
    "tenes",
    "tienes",
    "puedo",
    "quieres",
    "te",
    "para",
    "con",
    "sin",
    "pesos",
    "menu",
    "plato",
    "bebida",
    "opciones",
  ];
  const englishHits = englishSignals.reduce(
    (total, token) => total + (normalized.includes(token) ? 1 : 0),
    0
  );
  const spanishHits = spanishSignals.reduce(
    (total, token) => total + (normalized.includes(token) ? 1 : 0),
    0
  );

  return englishHits >= 2 && englishHits > spanishHits;
};

const isLikelyMeasurementToken = (value = "") =>
  /^\d+(?:ml|cc|g|gr|kg|l|lt|lts)$/i.test(String(value || "").trim());

const stripLeadingAssistantCodeFragments = (value = "") => {
  const safeValue = sanitizeDisplayText(value)
    .replace(/\s+/g, " ")
    .trim();

  if (!safeValue) {
    return "";
  }

  const tokens = safeValue.split(" ");
  let currentIndex = 0;

  while (currentIndex < tokens.length && currentIndex < 3) {
    const token = String(tokens[currentIndex] || "").replace(/^[-:|,.;]+|[-:|,.;]+$/g, "");
    const looksLikeCode =
      token.length >= 4 &&
      token.length <= 12 &&
      /[a-z]/i.test(token) &&
      /\d/.test(token) &&
      !isLikelyMeasurementToken(token) &&
      /^[a-z0-9-]+$/i.test(token);

    if (!looksLikeCode) {
      break;
    }

    currentIndex += 1;
  }

  return tokens.slice(currentIndex).join(" ").trim() || safeValue;
};

const getAssistantVisibleProductName = (value, fallback = "Producto") =>
  stripLeadingAssistantCodeFragments(toRenderableText(value, fallback)) || fallback;

const scoreAiVoiceTranscriptQuality = (value = "") => {
  const normalized = sanitizeDisplayText(value)
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) {
    return Number.NEGATIVE_INFINITY;
  }

  const normalizedKey = normalizeAssistantProductKey(normalized);
  const comparableTokens = tokenizeAssistantComparableText(normalizedKey);
  const uniqueTokens = new Set(comparableTokens);
  let score = 0;

  if (looksLikeLowSignalVoiceTranscript(normalized)) {
    score -= 28;
  } else {
    score += 18;
  }

  score += Math.min(uniqueTokens.size, 8) * 1.9;
  score += Math.min(normalized.length, 80) * 0.05;

  if (
    /\b(que|tenes|tienes|hay|para|quiero|dame|poneme|sumame|agregame|sin|con|precio|sale|cuanto|recomienda|recomendame|sugerime|bebida|comer|tomar|postre|milanesa|pasta|hamburguesa)\b/.test(
      normalizedKey
    )
  ) {
    score += 7;
  }

  if (/^(hola|buenas|a ver|mmm|eh|este|ok|dale)$/i.test(normalized)) {
    score -= 12;
  }

  return score;
};

const looksLikeLowSignalVoiceTranscript = (value = "") => {
  const normalized = sanitizeDisplayText(value)
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  if (!normalized) {
    return true;
  }

  const tokens = normalized.match(/[a-z0-9áéíóúüñ]+/gi) || [];

  if (!tokens.length) {
    return true;
  }

  const meaningfulTokens = tokens.filter(
    (token) => token.replace(/[0-9]/g, "").length >= 3
  );
  const totalLetters = (normalized.match(/[a-záéíóúüñ]/gi) || []).length;

  if (totalLetters < 3) {
    return true;
  }

  if (meaningfulTokens.length === 0) {
    return true;
  }

  if (/^(?:[a-z0-9]{1,2}\s+){2,10}[a-z0-9]{1,2}$/i.test(normalized)) {
    return true;
  }

  return false;
};

const resolveAiSpeechErrorMessage = (errorCode = "") => {
  switch (String(errorCode || "").trim().toLowerCase()) {
    case "not-allowed":
    case "service-not-allowed":
      return "El navegador no tiene permiso para usar el micrófono.";
    case "audio-capture":
      return "No encontramos un micrófono disponible en este dispositivo.";
    case "network":
      return "Se cortó la conexión del reconocimiento de voz.";
    case "no-speech":
      return "No escuché una consulta clara. Puedes volver a intentarlo.";
    case "aborted":
      return "Se canceló la escucha por voz.";
    default:
      return "No pudimos iniciar la interacción por voz en este navegador.";
  }
};

const resolveAiSpeechRuntimeMessage = (
  errorCode = "",
  {
    microphonePermissionGranted = false,
    isAppleTouch = false,
    isIosChrome = false,
  } = {}
) => {
  const normalizedCode = String(errorCode || "").trim().toLowerCase();

  if (
    microphonePermissionGranted &&
    (normalizedCode === "not-allowed" ||
      normalizedCode === "service-not-allowed")
  ) {
    if (isIosChrome) {
      return "Chrome en iPhone ya tiene permiso de microfono, pero no habilito el dictado por voz del navegador. Usa Safari para hablarle a la IA o escribe tu consulta.";
    }

    if (isAppleTouch) {
      return "Este navegador ya tiene permiso de microfono, pero no pudo iniciar el dictado por voz. Vuelve a intentarlo o escribe tu consulta.";
    }

    return "El microfono esta permitido, pero el navegador no pudo iniciar el dictado por voz.";
  }

  return resolveAiSpeechErrorMessage(errorCode);
};

const resolveAiMicrophonePermissionMessage = (errorCode = "") => {
  const normalizedCode = String(errorCode || "").trim().toLowerCase();

  if (
    normalizedCode === "not-allowed" ||
    normalizedCode === "notallowederror" ||
    normalizedCode === "service-not-allowed" ||
    normalizedCode === "permissiondeniederror" ||
    normalizedCode === "denied"
  ) {
    return "El microfono esta bloqueado para este sitio. Habilitalo en los ajustes del navegador y vuelve a tocar IA.";
  }

  return resolveAiSpeechErrorMessage(errorCode);
};

const readDigitalPaymentReturnPayload = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search || "");
  const status = normalizeDigitalPaymentReturnStatus(
    params.get("status") || params.get("collection_status")
  );

  if (!status) {
    return null;
  }

  return {
    status,
    paymentId: sanitizeDisplayText(params.get("payment_id") || "").slice(0, 120),
    merchantOrderId: sanitizeDisplayText(
      params.get("merchant_order_id") || ""
    ).slice(0, 120),
    preferenceId: sanitizeDisplayText(params.get("preference_id") || "").slice(
      0,
      120
    ),
    externalReference: sanitizeDisplayText(
      params.get("external_reference") || ""
    ).slice(0, 160),
  };
};

const clearDigitalPaymentReturnPayload = () => {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  const keys = [
    "status",
    "collection_status",
    "payment_id",
    "merchant_order_id",
    "preference_id",
    "external_reference",
    "collection_id",
    "collection_status",
  ];

  let changed = false;

  keys.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (!changed) {
    return;
  }

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, nextUrl);
};

const normalizeCheckoutMethod = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (
    normalized === "counter" ||
    normalized === "caja" ||
    normalized === "efectivo" ||
    normalized === "cash"
  ) {
    return COUNTER_CHECKOUT_METHOD;
  }

  if (
    normalized === "deep_link" ||
    normalized === KIOSK_QR_CHECKOUT_METHOD
  ) {
    return normalized;
  }

  return COUNTER_CHECKOUT_METHOD;
};

const mergeBranchPaymentSetupIntoPublicBranch = (
  currentBranch = {},
  paymentSetup = {}
) => {
  const nextPaymentSetup = buildBranchPaymentSetupSnapshot(paymentSetup);

  return {
    ...currentBranch,
    paymentProvider: nextPaymentSetup.paymentProvider,
    paymentProviderStatus: nextPaymentSetup.paymentProviderStatus,
    paymentAccountReference: nextPaymentSetup.paymentAccountReference,
    paymentAccountCbu: nextPaymentSetup.paymentAccountCbu,
    paymentAccountAlias: nextPaymentSetup.paymentAccountAlias,
    paymentLocationCity: nextPaymentSetup.paymentLocationCity,
    paymentLocationState: nextPaymentSetup.paymentLocationState,
    paymentLocationLatitude: nextPaymentSetup.paymentLocationLatitude,
    paymentLocationLongitude: nextPaymentSetup.paymentLocationLongitude,
    paymentLocationReference: nextPaymentSetup.paymentLocationReference,
    commissionRate: nextPaymentSetup.commissionRate,
    manualOrderCommissionRate: nextPaymentSetup.manualOrderCommissionRate,
    menuQrCommissionRate: nextPaymentSetup.menuQrCommissionRate,
    kioskCommissionRate: nextPaymentSetup.kioskCommissionRate,
    iaCommissionRate: nextPaymentSetup.iaCommissionRate,
    paymentSplitMode: nextPaymentSetup.paymentSplitMode,
    kioskPaymentMethods: nextPaymentSetup.kioskPaymentMethods,
    qrPaymentMethods: nextPaymentSetup.qrPaymentMethods,
    manualOperationsRuleEnabled: nextPaymentSetup.manualOperationsRuleEnabled,
    manualOperationUnitsCost: nextPaymentSetup.manualOperationUnitsCost,
    manualOperationsAvailable: nextPaymentSetup.manualOperationsAvailable,
    manualOperationsLowBalance: nextPaymentSetup.manualOperationsLowBalance,
    manualOperationsBlockingReason:
      nextPaymentSetup.manualOperationsBlockingReason,
    operationalAccessAllowed: nextPaymentSetup.operationalAccessAllowed,
    operationalAccessMode: nextPaymentSetup.operationalAccessMode,
    operationalBlockingReason: nextPaymentSetup.operationalBlockingReason,
  };
};

const KIOSK_KEYBOARD_LAYOUT = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L", "\u00d1"],
  ["SHIFT", "Z", "X", "C", "V", "B", "N", "M", "BORRAR"],
  ["LIMPIAR", "ESPACIO", "LISTO"],
];
const KIOSK_LANGUAGE_STORAGE_PREFIX = "mozzoapp:kiosk-language:";
const PRODUCT_DETAIL_HISTORY_STATE_KEY = "mozzoappProductDetail";
const AI_ASSISTANT_HISTORY_STATE_KEY = "mozzoappAiAssistant";
const KIOSK_LANGUAGE_OPTIONS = [
  { id: "es", short: "ES", label: "Espanol" },
  { id: "pt", short: "PT", label: "Portugues" },
  { id: "en", short: "EN", label: "English" },
];
const KIOSK_TRANSLATIONS = {
  es: {
    language: "Idioma",
    autoservice: "Autoservicio",
    chooseWhere: "Elegi donde disfrutar tu pedido",
    chooseWhereText: "Selecciona una opcion para continuar con tu pedido en este totem.",
    dineIn: "Comer aqui",
    dineInSubtitle: "Retiras por numero y disfrutas en el local",
    dineInFallback: "Comer",
    takeaway: "Para llevar",
    takeawaySubtitle: "Pides, pagas y retiras listo para salir",
    takeawayFallback: "Llevar",
    chooseMode: "Elegir modalidad",
    ownMenu: "Carta propia",
    activeMenu: "Carta activa",
    sharedMenu: "Carta compartida",
    digitalMenu: "Menu digital",
    heroTitle: "Pide, paga y retira por numero",
    heroText: "Explora familias grandes, arma combos y personaliza cada producto desde este totem.",
    qrHeroText: "Escanea, revisa la carta y elegi tranquilo desde tu mesa.",
    changeMode: "Cambiar modalidad",
    currentOrder: "Pedido actual",
    stillEmpty: "Todavia vacio",
    item: "item",
    items: "items",
    lastNumber: "Ultimo numero: #{number}",
    prepHint: "Al confirmar te damos numero de preparacion",
    viewOrder: "Ver mi pedido",
    menu: "Menu",
    sections: "secciones",
    promotionsAndCombos: "Promos y combos",
    option: "opcion",
    options: "opciones",
    chooseSuffix: "para elegir",
    continueBrowsing: "Seguir viendo",
    payOrder: "Enviar pedido",
    payOrderTitle: "Confirmar pedido",
    backToOrder: "Volver a mi pedido",
    myOrder: "Mi pedido",
    noImage: "Sin imagen",
    noChanges: "Sin cambios",
    product: "producto",
    products: "productos",
    listPrice: "Precio de lista",
    appliedDiscounts: "Descuentos aplicados",
    comboApplied: "Combo aplicado",
    promotionApplied: "Promocion aplicada",
    howToPay: "Como vas a pagar",
    hallHandlesPayments:
      "El pedido se enviara ahora. Puedes pagar despues desde la mesa o con un mozo.",
    kioskCounterHint:
      "Caja confirmara el cobro antes de que el pedido pase a preparacion.",
    openCheckoutHint:
      "Primero abrimos el cobro en tu telefono. El pedido solo se enviara a la sucursal cuando el pago sea aprobado.",
    pickupName: "Ingresa tu nombre para retirar",
    pickupNamePlaceholder: "Toca aqui y escribe tu nombre",
    pickupNameHint:
      "Es obligatorio: con este nombre caja y pantalla llaman tu pedido.",
    required: "Obligatorio",
    missingPickupNameTitle: "Primero ingresa tu nombre",
    missingPickupNameBody:
      "Para enviar el pedido necesitamos un nombre o alias. Toca el campo de nombre y escribelo antes de continuar.",
    missingPickupNameAction: "Ingresar nombre",
    emptyOrder: "Vaciar pedido",
    keepChoosing: "Seguir eligiendo",
    close: "Cerrar",
    sending: "Enviando...",
    sendOrder: "Enviar pedido",
    confirmOrder: "Confirmar pedido",
    keyboard: "Teclado",
    typePickupName: "Escribe el nombre para entrega",
    payAtCounter: "Pagar en caja",
    orderNowPayLater: "Pagar despues",
    payWithPhone: "Pagar con QR",
    deepLink: "Pagar ahora",
    chooseDigitalWallet: "Elegi tu medio de pago",
    chooseDigitalWalletHint:
      "MozzoApp abrira la billetera que elijas y enviara el pedido solo cuando el pago sea aprobado.",
    chooseDigitalWalletError:
      "Antes de continuar, elige con que billetera quieres pagar este pedido.",
    menuWalletsMissing:
      "Backoffice todavia no cargo billeteras operativas para este Menu QR.",
    walletSoon:
      "Proximamente",
    menuWalletsIncompatible:
      "Este Menu QR hoy cobra con #{provider}. Las demas billeteras quedaran visibles cuando tengan un checkout propio conectado en MozzoApp.",
    scanToPayTitle: "Escanea este QR para pagar",
    scanToPayBody:
      "Abre una billetera virtual compatible en tu telefono, escanea el codigo y completa el pago de este pedido.",
    scanToOpenLinkTitle: "ESCANEA EL QR CON TU CELULAR",
    scanToOpenLinkBody:
      "Abre una billetera virtual compatible y escanea el codigo para pagar.",
    openCheckoutTitle: "Estamos abriendo tu billetera",
    openCheckoutBody:
      "Si tu telefono no la abre automaticamente, toca continuar y completa el pago. Tu pedido ya queda registrado como pendiente de pago.",
    paymentQrReady:
      "El pedido ya esta enviado. Ya puedes abrir el cobro desde tu celular y despues seguir el numero de preparacion.",
    paymentQrRetry:
      "El pedido entro a la sucursal, pero no pudimos generar el QR todavia. Puedes reintentarlo desde aqui.",
    paymentLinkReady:
      "Tu cobro esta listo. Estamos intentando abrir tu billetera automaticamente.",
    paymentLinkRetry:
      "No pudimos dejar listo el cobro digital. Puedes reintentarlo desde aqui sin perder el pedido.",
    regeneratePaymentQr: "Generar QR otra vez",
    cancelPendingKioskPayment: "Cancelar cobro",
    pendingKioskPaymentRecovered:
      "Cerramos este cobro pendiente y recargamos el pedido para que puedas intentarlo otra vez.",
    failedKioskPaymentHint:
      "El cobro no se pudo completar. Puedes cancelar este intento o generar un QR nuevo.",
    openPaymentLink: "Continuar pago",
    copyPaymentLink: "Copiar link",
    paymentLinkCopied: "Copiamos el link de cobro.",
    checkoutCameraOnlyBadge: "PAGA CON TU BILLETERA VIRTUAL",
    checkoutCameraOnlyHint:
      "Abre una billetera virtual compatible y escanea el codigo para completar el pago.",
    checkoutLinkHint: "Abri la camara del celular y apunta al QR.",
    kioskPaymentLockHint:
      "No cierres esta pantalla del totem. Cuando se confirme el cobro, MozzoApp imprimira el ticket y volvera sola al inicio.",
    completionEyebrow: "PEDIDO CONFIRMADO",
    completionSubmittingTitle: "Estamos enviando tu pedido a cocina",
    completionKitchenTitle: "Espere mientras imprimimos la comanda",
    completionTicketTitle: "Espere mientras imprimimos su ticket",
    completionHint:
      "No cierre esta pantalla. Volveremos al inicio automaticamente cuando finalice la impresion.",
    space: "Espacio",
    delete: "Borrar",
    clear: "Limpiar",
    done: "Listo",
    lower: "Min",
    upper: "May",
    inactivityPromptTitle: "Quieres seguir con este pedido?",
    inactivityPromptText:
      "Si no respondes en #{seconds} segundos, MozzoApp limpiara el carrito y volvera a la pantalla inicial del totem.",
    inactivityPromptContinue: "Si, continuar",
    inactivityPromptReset: "No, limpiar",
  },
  pt: {
    language: "Idioma",
    autoservice: "Autosservico",
    chooseWhere: "Escolha onde aproveitar seu pedido",
    chooseWhereText: "Selecione uma opcao para continuar com seu pedido neste totem.",
    dineIn: "Consumir aqui",
    dineInSubtitle: "Retire pelo numero e aproveite no local",
    dineInFallback: "Aqui",
    takeaway: "Para levar",
    takeawaySubtitle: "Peca, pague e retire pronto para sair",
    takeawayFallback: "Levar",
    chooseMode: "Escolher modalidade",
    ownMenu: "Cardapio proprio",
    activeMenu: "Cardapio ativo",
    sharedMenu: "Cardapio compartilhado",
    digitalMenu: "Cardapio digital",
    heroTitle: "Peca, pague e retire pelo numero",
    heroText: "Explore categorias, monte combos e personalize cada produto neste totem.",
    qrHeroText: "Escaneie, confira o cardapio e escolha com calma da sua mesa.",
    changeMode: "Mudar modalidade",
    currentOrder: "Pedido atual",
    stillEmpty: "Ainda vazio",
    item: "item",
    items: "itens",
    lastNumber: "Ultimo numero: #{number}",
    prepHint: "Ao confirmar, damos o numero de preparo",
    viewOrder: "Ver meu pedido",
    menu: "Cardapio",
    sections: "secoes",
    promotionsAndCombos: "Promocoes e combos",
    option: "opcao",
    options: "opcoes",
    chooseSuffix: "para escolher",
    continueBrowsing: "Continuar vendo",
    payOrder: "Enviar pedido",
    payOrderTitle: "Confirmar pedido",
    backToOrder: "Voltar ao pedido",
    myOrder: "Meu pedido",
    noImage: "Sem imagem",
    noChanges: "Sem alteracoes",
    product: "produto",
    products: "produtos",
    listPrice: "Preco de tabela",
    appliedDiscounts: "Descontos aplicados",
    comboApplied: "Combo aplicado",
    promotionApplied: "Promocao aplicada",
    howToPay: "Como vai pagar",
    hallHandlesPayments:
      "O pedido sera enviado agora. Voce pode pagar depois na mesa ou com um garcom.",
    kioskCounterHint:
      "O caixa confirmara o pagamento antes de o pedido seguir para preparo.",
    openCheckoutHint:
      "Primeiro abrimos a cobranca no telefone. O pedido so sera enviado para a filial quando o pagamento for aprovado.",
    pickupName: "Digite seu nome para retirar",
    pickupNamePlaceholder: "Toque aqui e digite seu nome",
    pickupNameHint:
      "E obrigatorio: com este nome, caixa e tela chamam seu pedido.",
    required: "Obrigatorio",
    missingPickupNameTitle: "Primeiro digite seu nome",
    missingPickupNameBody:
      "Para enviar o pedido precisamos de um nome ou apelido. Toque no campo de nome e digite antes de continuar.",
    missingPickupNameAction: "Digitar nome",
    emptyOrder: "Esvaziar pedido",
    keepChoosing: "Continuar escolhendo",
    close: "Fechar",
    sending: "Enviando...",
    sendOrder: "Enviar pedido",
    confirmOrder: "Confirmar pedido",
    keyboard: "Teclado",
    typePickupName: "Digite o nome para entrega",
    payAtCounter: "Pagar no caixa",
    orderNowPayLater: "Pedir agora e pagar depois",
    payWithPhone: "Pagar com QR",
    deepLink: "Pagar agora",
    chooseDigitalWallet: "Escolha sua carteira",
    chooseDigitalWalletHint:
      "A MozzoApp abrira a carteira que voce escolher e o pedido so sera enviado quando o pagamento voltar aprovado.",
    chooseDigitalWalletError:
      "Antes de continuar, escolha com qual carteira voce quer pagar este pedido.",
    menuWalletsMissing:
      "O Backoffice ainda nao carregou carteiras operativas para este Menu QR.",
    walletSoon:
      "Em breve",
    menuWalletsIncompatible:
      "Este Menu QR hoje cobra com #{provider}. As outras carteiras aparecerao quando tiverem um checkout proprio conectado na MozzoApp.",
    scanToPayTitle: "Escaneie este QR para pagar",
    scanToPayBody:
      "Abra uma carteira digital compativel no telefone, escaneie o codigo e conclua o pagamento deste pedido.",
    scanToOpenLinkTitle: "ESCANEIE O QR COM O CELULAR",
    scanToOpenLinkBody:
      "Abra uma carteira digital compativel e escaneie o codigo para pagar.",
    openCheckoutTitle: "Estamos abrindo sua carteira",
    openCheckoutBody:
      "Se o telefone nao abrir automaticamente, toque em continuar e conclua o pagamento. Quando voltar aprovado, a MozzoApp enviara o pedido automaticamente.",
    paymentQrReady:
      "O pedido ja foi enviado. Agora voce pode abrir a cobranca no celular e depois acompanhar o numero de preparo.",
    paymentQrRetry:
      "O pedido entrou na sucursal, mas ainda nao conseguimos gerar o QR. Voce pode tentar novamente daqui.",
    paymentLinkReady:
      "A cobranca esta pronta. Estamos tentando abrir sua carteira automaticamente.",
    paymentLinkRetry:
      "Ainda nao enviamos o pedido. Nao conseguimos preparar a cobranca digital; voce pode tentar novamente daqui.",
    regeneratePaymentQr: "Gerar QR novamente",
    cancelPendingKioskPayment: "Cancelar cobranca",
    pendingKioskPaymentRecovered:
      "Fechamos esta cobranca pendente e recarregamos o pedido para voce tentar de novo.",
    failedKioskPaymentHint:
      "A cobranca nao pode ser concluida. Voce pode cancelar esta tentativa ou gerar um novo QR.",
    openPaymentLink: "Continuar pagamento",
    copyPaymentLink: "Copiar link",
    paymentLinkCopied: "O link de cobranca foi copiado.",
    checkoutCameraOnlyBadge: "PAGUE COM SUA CARTEIRA DIGITAL",
    checkoutCameraOnlyHint:
      "Abra uma carteira digital compativel e escaneie o codigo para concluir o pagamento.",
    checkoutLinkHint: "Abra a camera do celular e aponte para o QR.",
    kioskPaymentLockHint:
      "Nao feche esta tela do totem. Quando o pagamento for confirmado, o MozzoApp vai imprimir o ticket e voltar sozinho ao inicio.",
    completionEyebrow: "PEDIDO CONFIRMADO",
    completionSubmittingTitle: "Estamos enviando seu pedido para a cozinha",
    completionKitchenTitle: "Aguarde enquanto imprimimos a comanda",
    completionTicketTitle: "Aguarde enquanto imprimimos seu ticket",
    completionHint:
      "Nao feche esta tela. Voltaremos ao inicio automaticamente quando a impressao terminar.",
    space: "Espaco",
    delete: "Apagar",
    clear: "Limpar",
    done: "Pronto",
    lower: "Min",
    upper: "Mai",
    inactivityPromptTitle: "Deseja continuar com este pedido?",
    inactivityPromptText:
      "Se voce nao responder em #{seconds} segundos, a MozzoApp vai limpar o carrinho e voltar para a tela inicial do totem.",
    inactivityPromptContinue: "Sim, continuar",
    inactivityPromptReset: "Nao, limpar",
  },
  en: {
    language: "Language",
    autoservice: "Self-service",
    chooseWhere: "Choose where to enjoy your order",
    chooseWhereText: "Select an option to continue your order at this kiosk.",
    dineIn: "Dine in",
    dineInSubtitle: "Pick up by number and enjoy it in the venue",
    dineInFallback: "Dine in",
    takeaway: "Takeaway",
    takeawaySubtitle: "Order, pay and pick up ready to go",
    takeawayFallback: "Takeaway",
    chooseMode: "Choose mode",
    ownMenu: "Own menu",
    activeMenu: "Active menu",
    sharedMenu: "Shared menu",
    digitalMenu: "Digital menu",
    heroTitle: "Order, pay and pick up by number",
    heroText: "Browse categories, build combos and customize each product from this kiosk.",
    qrHeroText: "Scan, browse the menu and order calmly from your table.",
    changeMode: "Change mode",
    currentOrder: "Current order",
    stillEmpty: "Still empty",
    item: "item",
    items: "items",
    lastNumber: "Last number: #{number}",
    prepHint: "We will give you a preparation number when you confirm",
    viewOrder: "View my order",
    menu: "Menu",
    sections: "sections",
    promotionsAndCombos: "Promotions and combos",
    option: "option",
    options: "options",
    chooseSuffix: "to choose from",
    continueBrowsing: "Keep browsing",
    payOrder: "Send order",
    payOrderTitle: "Confirm order",
    backToOrder: "Back to order",
    myOrder: "My order",
    noImage: "No image",
    noChanges: "No changes",
    product: "product",
    products: "products",
    listPrice: "List price",
    appliedDiscounts: "Applied discounts",
    comboApplied: "Combo applied",
    promotionApplied: "Promotion applied",
    howToPay: "How would you like to pay",
    hallHandlesPayments:
      "The order will be sent now. You can pay later at the table or with a waiter.",
    kioskCounterHint:
      "The cashier will confirm payment before the order moves to preparation.",
    openCheckoutHint:
      "We open checkout on your phone first. The order will be sent to the branch only after the payment is approved.",
    pickupName: "Enter your name for pickup",
    pickupNamePlaceholder: "Tap here and type your name",
    pickupNameHint:
      "Required: cashier and pickup screen will call the order with this name.",
    required: "Required",
    missingPickupNameTitle: "Enter your name first",
    missingPickupNameBody:
      "To send the order we need a name or alias. Tap the name field and type it before continuing.",
    missingPickupNameAction: "Enter name",
    emptyOrder: "Clear order",
    keepChoosing: "Keep choosing",
    close: "Close",
    sending: "Sending...",
    sendOrder: "Send order",
    confirmOrder: "Confirm order",
    keyboard: "Keyboard",
    typePickupName: "Type the pickup name",
    payAtCounter: "Pay at counter",
    orderNowPayLater: "Order now and pay later",
    payWithPhone: "Pay with QR",
    deepLink: "Pay now",
    chooseDigitalWallet: "Choose your wallet",
    chooseDigitalWalletHint:
      "MozzoApp will open the wallet you choose, and the order will only be sent once the payment returns approved.",
    chooseDigitalWalletError:
      "Before continuing, choose which wallet should pay for this order.",
    menuWalletsMissing:
      "Backoffice has not loaded any operational wallets for this Menu QR yet.",
    walletSoon:
      "Soon",
    menuWalletsIncompatible:
      "This Menu QR currently charges through #{provider}. The other wallets will appear once they have their own checkout connected in MozzoApp.",
    scanToPayTitle: "Scan this QR to pay",
    scanToPayBody:
      "Open a compatible digital wallet on your phone, scan the code, and complete the payment for this order.",
    scanToOpenLinkTitle: "SCAN THE QR WITH YOUR PHONE",
    scanToOpenLinkBody:
      "Open a compatible digital wallet and scan the code to pay.",
    openCheckoutTitle: "We are opening your wallet",
    openCheckoutBody:
      "If your phone does not open it automatically, tap continue and complete the payment. Once it returns approved, MozzoApp will send the order automatically.",
    paymentQrReady:
      "The order has already been sent. You can now open the checkout on your phone and then follow the preparation number.",
    paymentQrRetry:
      "The order reached the branch, but we could not generate the payment QR yet. You can try again from here.",
    paymentLinkReady:
      "Your checkout is ready. We are trying to open your wallet automatically.",
    paymentLinkRetry:
      "We have not sent the order yet. We could not prepare the digital checkout; you can retry it from here.",
    regeneratePaymentQr: "Generate QR again",
    cancelPendingKioskPayment: "Cancel payment",
    pendingKioskPaymentRecovered:
      "We closed this pending payment and reloaded the order so you can try again.",
    failedKioskPaymentHint:
      "The payment could not be completed. You can cancel this attempt or generate a new QR.",
    openPaymentLink: "Continue payment",
    copyPaymentLink: "Copy link",
    paymentLinkCopied: "The payment link was copied.",
    checkoutCameraOnlyBadge: "PAY WITH YOUR DIGITAL WALLET",
    checkoutCameraOnlyHint:
      "Open a compatible digital wallet and scan the code to complete the payment.",
    checkoutLinkHint: "Open the phone camera and point it at the QR.",
    kioskPaymentLockHint:
      "Do not close this kiosk screen. Once the payment is confirmed, MozzoApp will print the ticket and return to the start automatically.",
    completionEyebrow: "ORDER CONFIRMED",
    completionSubmittingTitle: "We are sending your order to the kitchen",
    completionKitchenTitle: "Please wait while we print the kitchen ticket",
    completionTicketTitle: "Please wait while we print your receipt",
    completionHint:
      "Do not close this screen. We will return to the start automatically when printing finishes.",
    space: "Space",
    delete: "Delete",
    clear: "Clear",
    done: "Done",
    lower: "Lower",
    upper: "Upper",
    inactivityPromptTitle: "Do you want to continue with this order?",
    inactivityPromptText:
      "If you do not respond within #{seconds} seconds, MozzoApp will clear the cart and return to the kiosk home screen.",
    inactivityPromptContinue: "Yes, continue",
    inactivityPromptReset: "No, clear it",
  },
};
const MOZZOAPP_KIOSK_APP_NAME = "MozzoApp";
const MOZZOAPP_KIOSK_APP_LOGO_URL = mozzoAppKioskLogoUrl;
const KIOSK_PROMOTIONS_SECTION_ID = "__kiosk_promotions__";
const NON_TRANSLATABLE_LANGUAGE = "zxx";
const DEFAULT_PROMOTION_FONT_FAMILY =
  'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const QR_COMPACT_VIEWPORT_BREAKPOINT = 760;
const KIOSK_COMPACT_VIEWPORT_BREAKPOINT = 1024;
// Kiosk branch assets can be uploaded as data URLs from the admin panel.
// If this cap is too low, the public kiosk silently falls back to stale or
// empty visuals even though the branch already has the right images saved.
const MAX_QR_HERO_DATA_IMAGE_LENGTH = 6000000;
// Product photos can come from the editor as full data URLs. If this cap is too
// low, the kiosk silently drops the image and shows "Sin imagen" even though the
// product is otherwise published correctly.
const MAX_KIOSK_PRODUCT_DATA_IMAGE_LENGTH = 6000000;
const MAX_KIOSK_CATEGORY_DATA_IMAGE_LENGTH = 6000000;
const MAX_KIOSK_PROMOTION_DATA_IMAGE_LENGTH = 420000;

const isRenderableRecord = (value) => Boolean(value) && typeof value === "object";

const toRenderableText = (value, fallback = "") => {
  if (typeof value === "string") {
    return repairTextEncoding(value.trim());
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Si" : "No";
  }

  return fallback;
};

const sanitizeUiImageUrl = (
  value,
  maxDataImageLength = Number.POSITIVE_INFINITY
) => {
  if (typeof value !== "string") {
    return "";
  }

  const normalized = value.trim();
  if (!normalized) {
    return "";
  }

  if (
    normalized.startsWith("data:") &&
    Number.isFinite(maxDataImageLength) &&
    normalized.length > maxDataImageLength
  ) {
    return "";
  }

  return normalized;
};

const normalizeAssistantProductKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const tokenizeAssistantComparableText = (value = "") =>
  normalizeAssistantProductKey(value)
    .split(/[^a-z0-9]+/g)
    .filter((token) => token.length >= 2);

const buildAssistantComparableBigrams = (value = "") => {
  const normalized = normalizeAssistantProductKey(value).replace(/\s+/g, "");

  if (!normalized) {
    return [];
  }

  if (normalized.length === 1) {
    return [normalized];
  }

  const bigrams = [];

  for (let index = 0; index < normalized.length - 1; index += 1) {
    bigrams.push(normalized.slice(index, index + 2));
  }

  return bigrams;
};

const scoreAssistantComparablePhrases = (left = "", right = "") => {
  const normalizedLeft = normalizeAssistantProductKey(left);
  const normalizedRight = normalizeAssistantProductKey(right);

  if (!normalizedLeft || !normalizedRight) {
    return 0;
  }

  if (normalizedLeft === normalizedRight) {
    return 1;
  }

  if (
    normalizedLeft.length >= 4 &&
    normalizedRight.length >= 4 &&
    (normalizedLeft.includes(normalizedRight) || normalizedRight.includes(normalizedLeft))
  ) {
    return 0.94;
  }

  const leftTokens = new Set(tokenizeAssistantComparableText(normalizedLeft));
  const rightTokens = new Set(tokenizeAssistantComparableText(normalizedRight));
  const sharedTokens = Array.from(leftTokens).filter((token) => rightTokens.has(token)).length;
  const tokenScore =
    leftTokens.size || rightTokens.size
      ? (2 * sharedTokens) / Math.max(1, leftTokens.size + rightTokens.size)
      : 0;

  const leftBigrams = buildAssistantComparableBigrams(normalizedLeft);
  const rightBigrams = buildAssistantComparableBigrams(normalizedRight);
  const rightBigramMap = new Map();
  rightBigrams.forEach((bigram) => {
    rightBigramMap.set(bigram, (rightBigramMap.get(bigram) || 0) + 1);
  });

  let sharedBigrams = 0;
  leftBigrams.forEach((bigram) => {
    const remaining = rightBigramMap.get(bigram) || 0;

    if (remaining > 0) {
      sharedBigrams += 1;
      rightBigramMap.set(bigram, remaining - 1);
    }
  });

  const bigramScore =
    leftBigrams.length || rightBigrams.length
      ? (2 * sharedBigrams) / Math.max(1, leftBigrams.length + rightBigrams.length)
      : 0;

  return Math.max(tokenScore, bigramScore * 0.9 + tokenScore * 0.1);
};

const isAiAssistantExplicitOrderRequest = (question = "") => {
  const normalizedQuestion = normalizeAssistantProductKey(question);

  if (!normalizedQuestion) {
    return false;
  }

  if (
    /\b(agrega|agregame|agregas|agregues|agregar|sumame|sumas|sumes|suma|sumar|poneme|pone|pones|pongas|poner|cargame|cargas|cargues|carga|cargar|pedime|pedi|pedir|pido|anadi|anadime|anadis|anadas|dame|me das|mandame|mandas|mandes|llevo)\b/.test(
      normalizedQuestion
    )
  ) {
    return true;
  }

  if (/\bquiero que me (agregues|sumes|pongas|cargues|mandes|des)\b/.test(normalizedQuestion)) {
    return true;
  }

  return /\bquiero\b/.test(normalizedQuestion) &&
    !/\b(algo|recom|suger|opcion|opciones|cual|cuales|ver|mirar|mostrar|mostrame|saber|conocer|info|precio|sale|cuanto|conviene)\b/.test(
      normalizedQuestion
    );
};

const resolveKioskSectionIcon = (section = {}) => {
  const icon = String(section?.icon || section?.emoji || "").trim();

  if (!icon) {
    return "";
  }

  if (/^[\p{L}\p{N}]{1,3}$/u.test(icon.replace(/\s+/g, ""))) {
    return "";
  }

  return icon;
};

const isQrAccessTerminationMessage = (message) => {
  const normalizedMessage = String(message || "").trim().toLowerCase();

  if (!normalizedMessage) {
    return false;
  }

  return (
    normalizedMessage.includes("vuelve a escanear el qr") ||
    normalizedMessage.includes("vuelve a escanear el qr desde la mesa") ||
    normalizedMessage.includes("esta mesa ya no tiene el qr habilitado") ||
    normalizedMessage.includes("esta mesa todavia no esta habilitada para pedidos qr") ||
    normalizedMessage.includes("reactive el acceso antes de seguir") ||
    normalizedMessage.includes("esta mesa ya no esta disponible") ||
    normalizedMessage.includes("la sesion del menu ya no coincide con esta mesa")
  );
};

class MenuCatalogErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("El catalogo de MenuComensalQr fallo al renderizar:", error);
  }

  componentDidUpdate(prevProps) {
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <section
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #dbe4f0",
          borderRadius: 20,
          padding: 24,
          display: "grid",
          gap: 14,
          boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
        }}
      >
        <strong style={{ color: "#0f172a", fontSize: 18 }}>
          Esta seccion del menu tuvo un problema al abrirse.
        </strong>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.5 }}>
          La pantalla no deberia quedar en blanco. Volvamos a cargar esta parte del catalogo y
          seguimos.
        </p>
        <button
          type="button"
          onClick={() => {
            this.setState({ hasError: false });
            this.props.onRecover?.();
          }}
          style={{
            justifySelf: "start",
            border: "none",
            borderRadius: 999,
            padding: "12px 18px",
            backgroundColor: this.props.primaryColor || "#f97316",
            color: this.props.secondaryColor || "#ffffff",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Reintentar
        </button>
      </section>
    );
  }
}

const resolveCompactViewport = (isKioskExperience) =>
  typeof window !== "undefined"
    ? window.innerWidth <
      (isKioskExperience
        ? KIOSK_COMPACT_VIEWPORT_BREAKPOINT
        : QR_COMPACT_VIEWPORT_BREAKPOINT)
    : false;

const normalizeKioskStationNumber = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 1;
  }

  return Math.min(12, Math.max(1, Math.round(parsed)));
};

const buildKioskSessionKey = (branchId, stationNumber) =>
  `kiosk:${String(branchId || "default").trim()}:${normalizeKioskStationNumber(
    stationNumber
  )}`;

const getKioskLanguageStorageKey = (sessionKey) =>
  `${KIOSK_LANGUAGE_STORAGE_PREFIX}${String(sessionKey || "default").trim()}`;

const loadKioskLanguageFromStorage = (sessionKey) => {
  if (typeof window === "undefined") {
    return "es";
  }

  try {
    const stored = window.localStorage.getItem(getKioskLanguageStorageKey(sessionKey));
    return KIOSK_TRANSLATIONS[stored] ? stored : "es";
  } catch (error) {
    console.error("No pudimos leer el idioma del totem:", error);
    return "es";
  }
};

const persistKioskLanguageToStorage = (sessionKey, language) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      getKioskLanguageStorageKey(sessionKey),
      KIOSK_TRANSLATIONS[language] ? language : "es"
    );
  } catch (error) {
    console.error("No pudimos guardar el idioma del totem:", error);
  }
};

const getKioskTranslation = (language, key, variables = {}) => {
  const dictionary = KIOSK_TRANSLATIONS[language] || KIOSK_TRANSLATIONS.es;
  const template = dictionary[key] || KIOSK_TRANSLATIONS.es[key] || key;

  return Object.entries(variables).reduce(
    (message, [name, value]) => message.replaceAll(`#{${name}}`, String(value ?? "")),
    template
  );
};

const formatMenuQrCheckoutProviderLabel = (value) =>
  value === "modo"
    ? "MODO"
    : value === "mercado_pago"
      ? "Mercado Pago"
      : value === "uala"
        ? "Uala"
        : value === "naranja_x"
          ? "Naranja X"
          : "este proveedor";

const isVisibleMenuProduct = (product) => product?.active !== false;

const buildKioskPayload = ({
  branch,
  branchProfile,
  menuSnapshot,
  kioskStationNumber,
}) => {
  const normalizedStationNumber = normalizeKioskStationNumber(kioskStationNumber);
  const branchName = branch?.name || branchProfile?.nombre || "Sucursal";

  return {
    branch: {
      id: branch?.id || "",
      name: branchName,
      phone: branch?.phone || branchProfile?.telefono || "",
      address: branch?.address || branchProfile?.direccion || "",
      description: branch?.description || branchProfile?.descripcion || "",
      logoUrl:
        branch?.logoUrl ||
        branch?.logo_url ||
        branchProfile?.branding?.logoPrimaryUrl ||
        branchProfile?.branding?.logoCompactUrl ||
        "",
      logoCompactUrl:
        branch?.logoCompactUrl ||
        branch?.logo_compact_url ||
        branch?.logoUrl ||
        branch?.logo_url ||
        branchProfile?.branding?.logoCompactUrl ||
        branchProfile?.branding?.logoPrimaryUrl ||
        "",
      iconLogoUrl:
        branch?.iconLogoUrl ||
        branch?.icon_logo_url ||
        branch?.logoCompactUrl ||
        branch?.logo_compact_url ||
        branchProfile?.branding?.iconLogoUrl ||
        branchProfile?.branding?.logoCompactUrl ||
        "",
      loadingLogoUrl:
        branch?.loadingLogoUrl ||
        branch?.loading_logo_url ||
        branch?.iconLogoUrl ||
        branch?.icon_logo_url ||
        branch?.logoCompactUrl ||
        branch?.logo_compact_url ||
        branchProfile?.branding?.loadingLogoUrl ||
        branchProfile?.branding?.iconLogoUrl ||
        branchProfile?.branding?.logoCompactUrl ||
        "",
      coverUrl: branch?.coverUrl || branchProfile?.imagenPortada || "",
      kioskDineInImageUrl:
        branch?.kioskDineInImageUrl ||
        branch?.kiosk_dine_in_image_url ||
        branchProfile?.kioskDineInImageUrl ||
        "",
      kioskTakeawayImageUrl:
        branch?.kioskTakeawayImageUrl ||
        branch?.kiosk_takeaway_image_url ||
        branchProfile?.kioskTakeawayImageUrl ||
        "",
      primaryColor:
        branch?.primaryColor ||
        branch?.primary_color ||
        branchProfile?.branding?.primaryColor ||
        "",
      secondaryColor:
        branch?.secondaryColor ||
        branch?.secondary_color ||
        branchProfile?.branding?.secondaryColor ||
        "",
      fontFamily:
        branch?.fontFamily ||
        branch?.font_family ||
        branchProfile?.branding?.fontFamily ||
        "",
      loadingAnimation:
        branch?.loadingAnimation ||
        branch?.loading_animation ||
        branchProfile?.branding?.loadingAnimation ||
        "spin",
      welcomeMessage:
        branch?.welcomeMessage ||
        branch?.welcome_message ||
        branchProfile?.mensajeBienvenida ||
        "Toca la carta, personaliza tu pedido y envialo desde este totem.",
      showPrices:
        typeof branch?.showPrices === "boolean"
          ? branch.showPrices
          : branchProfile?.showPrices !== false,
      kitchenMode:
        branch?.kitchenMode ||
        branch?.kitchen_mode ||
        branchProfile?.kitchenMode ||
        "manual",
      serviceMode:
        branch?.serviceMode ||
        branch?.service_mode ||
        branchProfile?.serviceMode ||
        "kiosk",
      kioskMenuMode:
        branch?.kioskMenuMode ||
        branch?.kiosk_menu_mode ||
        branchProfile?.kioskMenuMode ||
        "current",
      kioskStationCount: normalizeKioskStationNumber(
        branch?.kioskStationCount ||
          branch?.kiosk_station_count ||
          branchProfile?.kioskStationCount ||
          kioskStationNumber
      ),
    },
    table: {
      id: `kiosk-${branch?.id || "default"}-${normalizedStationNumber}`,
      label: `Totem ${normalizedStationNumber}`,
      tableNumber: normalizedStationNumber,
    },
    menu: {
      id: menuSnapshot?.menuId || `kiosk-menu-${branch?.id || "default"}`,
      name: `${branchName} \u00b7 Kiosk`,
      mode: menuSnapshot?.mode || "shared",
    },
    categories: Array.isArray(menuSnapshot?.categories) ? menuSnapshot.categories : [],
    linkedRules: Array.isArray(menuSnapshot?.linkedSubcategoryRules)
      ? menuSnapshot.linkedSubcategoryRules
      : [],
    products: Array.isArray(menuSnapshot?.products)
      ? menuSnapshot.products.filter(isVisibleMenuProduct)
      : [],
  };
};

const normalizeCustomerFacingIngredient = (ingredient) => {
  const menuBaseQuantity = getIngredientMenuBaseQuantity(ingredient);
  const consumptionQuantity = getIngredientConsumptionQuantity(ingredient);
  const ingredientName = toRenderableText(ingredient?.name || ingredient?.nombre, "Ingrediente");

  return {
    ...ingredient,
    id: ingredient?.id || ingredient?.ingredientId || ingredient?.ingredient_id || "",
    ingredientId:
      ingredient?.ingredientId || ingredient?.ingredient_id || ingredient?.id || "",
    name: ingredientName,
    nombre: ingredientName,
    imageUrl: sanitizeUiImageUrl(
      ingredient?.imageUrl || ingredient?.fotoUrl || "",
      MAX_KIOSK_PRODUCT_DATA_IMAGE_LENGTH
    ),
    baseQuantity: menuBaseQuantity,
    cantidad: menuBaseQuantity,
    quantity: menuBaseQuantity,
    menuBaseQuantity,
    menuQuantity: menuBaseQuantity,
    consumptionQuantity,
    stockQuantity: consumptionQuantity,
    consumptionBaseQuantity: consumptionQuantity,
    canRemove: ingredient?.canRemove === true || ingredient?.can_remove === true,
    canAddMore: ingredient?.canAddMore === true || ingredient?.can_add_more === true,
    maxExtraQuantity: Number(
      ingredient?.maxExtraQuantity ?? ingredient?.max_extra_quantity ?? DEFAULT_MAX_EXTRA_QUANTITY
    ),
  };
};

const normalizeCustomerFacingProduct = (product) => {
  const sourceIngredients = Array.isArray(product?.ingredients)
    ? product.ingredients
    : Array.isArray(product?.ingredientes)
      ? product.ingredientes
      : [];
  const normalizedIngredients = sourceIngredients.map(normalizeCustomerFacingIngredient);
  const productName = toRenderableText(product?.name || product?.nombre, "Producto");
  const productDescription = toRenderableText(
    product?.description || product?.descripcion,
    ""
  );
  const productSubcategoryName = toRenderableText(
    product?.subcategoryName || product?.subcategoria || product?.subcategoriaName,
    ""
  );
  const pizzaSizeOptions = normalizePizzaSizeOptions(
    product?.pizzaSizeOptions ||
      product?.pizza_size_options ||
      product?.pizzaSizes ||
      product?.pizza_sizes ||
      []
  );
  const empanadaSaleOptions = normalizeEmpanadaSaleOptions(
    product?.empanadaSaleOptions ||
      product?.empanada_sale_options ||
      product?.empanadaOptions ||
      product?.empanada_options ||
      []
  );
  const empanadaCookingOptions = normalizeEmpanadaCookingOptions(
    product?.empanadaCookingOptions ||
      product?.empanada_cooking_options ||
      product?.empanadaCookOptions ||
      product?.empanada_cook_options ||
      []
  );

  return {
    ...product,
    name: productName,
    nombre: productName,
    description: productDescription,
    descripcion: productDescription,
    subcategoryName: productSubcategoryName,
    subcategoria: productSubcategoryName,
    imageUrl: sanitizeUiImageUrl(
      product?.imageUrl || product?.fotoUrl || "",
      MAX_KIOSK_PRODUCT_DATA_IMAGE_LENGTH
    ),
    ingredients: normalizedIngredients,
    ingredientes: normalizedIngredients.map((ingredient) => ({ ...ingredient })),
    pizzaSizeOptions,
    pizza_size_options: pizzaSizeOptions,
    empanadaSaleOptions,
    empanada_sale_options: empanadaSaleOptions,
    empanadaCookingOptions,
    empanada_cooking_options: empanadaCookingOptions,
  };
};

const normalizeMenuPayload = (menuPayload) => {
  const categories = Array.isArray(menuPayload?.categories)
    ? menuPayload.categories
        .filter(isRenderableRecord)
        .map((category) => ({
          ...category,
          name:
            String(category?.name || category?.nombre || "").trim() || "Categoria",
          nombre:
            String(category?.nombre || category?.name || "").trim() || "Categoria",
        }))
    : [];
  const categoryNameById = new Map(
    categories.map((category) => [
      normalizeEntityId(category?.id),
      String(category?.name || category?.nombre || "").trim(),
    ])
  );

  return {
    ...menuPayload,
    categories,
    products: Array.isArray(menuPayload?.products)
    ? menuPayload.products
        .filter(isRenderableRecord)
        .filter(isVisibleMenuProduct)
        .map((product) => {
          const normalizedProduct = normalizeCustomerFacingProduct(product);
          const categoryId = normalizeEntityId(
            normalizedProduct?.categoryId ||
              normalizedProduct?.categoria ||
              normalizedProduct?.category_id
          );

          return {
            ...normalizedProduct,
            categoryName:
              normalizedProduct?.categoryName || categoryNameById.get(categoryId) || "",
          };
        })
      : [],
  };
};

const resolveNextPreparationNumber = (ordersState) => {
  const allOrders = [
    ...((ordersState?.mesasActivas || [])),
    ...((ordersState?.pedidosHistoricos || [])),
  ];

  const maxNumber = allOrders.reduce((currentMax, order) => {
    const parsed = Number(order?.preparationNumber || 0);

    if (!Number.isFinite(parsed)) {
      return currentMax;
    }

    return Math.max(currentMax, Math.floor(parsed));
  }, 0);

  return maxNumber + 1;
};

const isOperationalOrderFinished = (order) => {
  const phase = String(order?.phase || "")
    .trim()
    .toLowerCase();

  return phase === "closed" || phase === "cancelled";
};

const findActiveQrOrderForCustomer = ({
  ordersState,
  orderId = "",
  tableId = "",
  qrToken = "",
  sessionKey = "",
  customerSessionId = "",
}) =>
  (ordersState?.mesasActivas || []).find((order) => {
    if (!order || isOperationalOrderFinished(order)) {
      return false;
    }

    if (orderId && order.id === orderId) {
      return true;
    }

    return (
      order.tableId === tableId &&
      (String(order.sessionKey || "").trim() === String(sessionKey || "").trim() ||
        order.qrToken === qrToken) &&
      String(order.customerSessionId || "").trim() ===
        String(customerSessionId || "").trim()
    );
  }) || null;

const getQrOrderEditLockState = (order) => {
  if (!order) {
    return {
      locked: false,
      reason: "",
    };
  }

  const phase = String(order.phase || "")
    .trim()
    .toLowerCase();
  const kitchenMode = String(order.kitchenModeSnapshot || "manual")
    .trim()
    .toLowerCase();
  const kitchenStatus = String(order.kitchenDispatchStatus || "")
    .trim()
    .toLowerCase();

  if (phase === "closed" || phase === "cancelled") {
    return {
      locked: true,
      reason: "Este pedido ya no esta activo, asi que no se puede volver a modificar.",
    };
  }

  if (phase === "ready" || kitchenStatus === "screen_ready") {
    return {
      locked: true,
      reason: "Cocina ya dejo este pedido listo, asi que no se puede modificar desde la mesa.",
    };
  }

  if (kitchenStatus === "screen_confirmed") {
    return {
      locked: true,
      reason: "Cocina ya tomo este pedido y por eso ya no se puede modificar desde la mesa.",
    };
  }

  if (phase === "preparation") {
    if (kitchenMode === "screen" && kitchenStatus === "screen_pending") {
      return {
        locked: false,
        reason: "",
      };
    }

    return {
      locked: true,
      reason:
        "Este pedido ya entro en preparacion en cocina y por eso ya no se puede modificar desde la mesa.",
    };
  }

  return {
    locked: false,
    reason: "",
  };
};

const loadingLogoKeyframes = `
  @keyframes mozzoapp-loading-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes mozzoapp-loading-pulse {
    0%, 100% { transform: scale(0.92); opacity: 0.82; }
    50% { transform: scale(1.04); opacity: 1; }
  }

  @keyframes mozzoapp-loading-float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }
`;

const persistPublicMenuSnapshot = (payload) => {
  const branchId = payload?.branch?.id;

  if (!branchId) {
    return;
  }

  saveLocalMenuSnapshot(branchId, {
    menuId: payload?.menu?.id || `public-menu-${branchId}`,
    mode: payload?.menu?.mode || "shared",
    categories: Array.isArray(payload?.categories) ? payload.categories : [],
    subcategoriasPorCategoria: {},
    linkedSubcategoryRules: Array.isArray(payload?.linkedRules)
      ? payload.linkedRules
      : [],
    products: Array.isArray(payload?.products)
      ? payload.products.filter(isVisibleMenuProduct)
      : [],
  });
};

const canFallbackToLocalKioskSnapshot = (error) => {
  const message = String(error?.message || "")
    .trim()
    .toLowerCase();

  if (!message) {
    return true;
  }

  if (message.includes("falta aplicar")) {
    return false;
  }

  if (
    message.includes("column ") ||
    message.includes("branch_settings") ||
    message.includes("relation ") ||
    message.includes("function ")
  ) {
    return false;
  }

  return true;
};

const BellDeskIcon = ({ color = "currentColor" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 14a6 6 0 1 1 12 0"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 14h16"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.5 14c0 3.038 2.462 5.5 5.5 5.5h2c3.038 0 5.5-2.462 5.5-5.5"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 5.5v-1"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const RobotAssistantIcon = ({ color = "currentColor" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="5"
      y="7"
      width="14"
      height="11"
      rx="4"
      stroke={color}
      strokeWidth="1.8"
    />
    <path
      d="M12 4v3"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="9.5" cy="12" r="1.1" fill={color} />
    <circle cx="14.5" cy="12" r="1.1" fill={color} />
    <path
      d="M10 15h4"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const GlobeLanguageIcon = ({ color = "currentColor" }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.25" stroke={color} strokeWidth="1.8" />
    <path
      d="M4.5 12h15"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M12 3.75c2.1 2.25 3.3 5.2 3.3 8.25 0 3.05-1.2 6-3.3 8.25"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 3.75c-2.1 2.25-3.3 5.2-3.3 8.25 0 3.05 1.2 6 3.3 8.25"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = ({ color = "currentColor", open = false }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }}
  >
    <path
      d="M6 9.5 12 15l6-5.5"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const resolvePromotionFontFamily = (promotion) =>
  toRenderableText(promotion?.fontFamily, DEFAULT_PROMOTION_FONT_FAMILY) ||
  DEFAULT_PROMOTION_FONT_FAMILY;

const normalizePromotionSchedule = (schedule) =>
  DIAS_SEMANA.map((day) => {
    const found = Array.isArray(schedule)
      ? schedule.find((item) => item?.id === day.id)
      : null;

    return {
      id: day.id,
      label: day.label,
      active: Boolean(found?.active),
      allDay: Boolean(found?.allDay),
      apertura: found?.apertura || "08:00",
      cierre: found?.cierre || "12:00",
    };
  });

const buildPromotionScheduleSummary = (schedule) => {
  const normalized = normalizePromotionSchedule(schedule);
  const activeDays = normalized.filter((day) => day.active);

  if (activeDays.length === 0) {
    return "Disponible todos los dias";
  }

  return activeDays
    .map((day) =>
      day.allDay ? `${day.label} todo el dia` : `${day.label} ${day.apertura} a ${day.cierre}`
    )
    .join(" \u00b7 ");
};

const getPromotionHeadline = (promotion) =>
  isDailyMenuPromotion(promotion)
    ? toRenderableText(promotion?.hook) ||
      toRenderableText(promotion?.pricingSummary) ||
      "Menu del dia"
    :
  toRenderableText(promotion?.hook) ||
  toRenderableText(promotion?.pricingSummary) ||
  (promotion?.type === "promotion" ? "Promocion" : "Combo");

const getPromotionScopeLabel = (promotion) =>
  toRenderableText(promotion?.targetLabel) ||
  toRenderableText(promotion?.subcategoryName) ||
  toRenderableText(promotion?.productName) ||
  toRenderableText(promotion?.categoryName) ||
  "";

const getPromotionDescription = (promotion) => {
  if (promotion?.detail) {
    return toRenderableText(promotion.detail, "");
  }

  if (promotion?.type === "promotion") {
    const target = getPromotionScopeLabel(promotion) || "esta seccion";
    return `${toRenderableText(promotion?.pricingSummary, "Promo activa")} en ${target}.`;
  }

  if (isDailyMenuPromotion(promotion)) {
    return (
      toRenderableText(promotion?.detail) ||
      "Seleccion especial del dia armada con productos de esta sucursal."
    );
  }

  return "Combo listo para pedir desde esta sucursal.";
};

const getPromotionDisplayPriority = (promotion) =>
  isDailyMenuPromotion(promotion) ? 1 : 0;

const resolvePromotionUiImageUrl = (
  promotion,
  maxDataImageLength = MAX_KIOSK_PROMOTION_DATA_IMAGE_LENGTH
) => sanitizeUiImageUrl(promotion?.imageUrl, maxDataImageLength);

const buildPromotionBackdropPatternStyle = (logoUrl) => ({
  position: "absolute",
  inset: 0,
  backgroundImage: `url("${logoUrl}")`,
  backgroundRepeat: "repeat",
  backgroundPosition: "center",
  backgroundSize: "72px auto",
  opacity: 0.12,
  filter: "grayscale(1) blur(1.8px)",
  transform: "scale(1.04)",
  pointerEvents: "none",
});

const resolveDisplayPrice = (product) => {
  if (!product) {
    return 0;
  }

  if (productHasPizzaSizeOptions(product)) {
    const prices = getPizzaSizeOptions(product)
      .map(getPizzaStartingPrice)
      .filter((price) => price > 0);

    if (prices.length > 0) {
      return Math.min(...prices);
    }
  }

  if (productHasEmpanadaSaleOptions(product)) {
    const prices = getEmpanadaSaleOptions(product)
      .map((option) => Number(option.price || 0))
      .filter((price) => price > 0);

    if (prices.length > 0) {
      return Math.min(...prices);
    }
  }

  if (product.priceMode === "suggested") {
    return Number(product.suggestedPrice || 0);
  }

  return Number(product.price || 0);
};

const normalizeEntityId = (value) => String(value || "").trim();
const normalizeEntityLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeLinkedRuleSelectionMode = () => "single_optional";

const getLinkedRuleSourceSubcategoryId = (rule) =>
  normalizeEntityId(rule?.sourceSubcategoryId || rule?.source_subcategory_id);

const getLinkedRuleTargetCategoryId = (rule) =>
  normalizeEntityId(rule?.targetCategoryId || rule?.target_category_id);

const getLinkedRuleTargetSubcategoryId = (rule) =>
  normalizeEntityId(rule?.targetSubcategoryId || rule?.target_subcategory_id);

const getLinkedRuleTargetCategoryName = (rule) =>
  String(rule?.targetCategoryName || rule?.target_category_name || "").trim();

const getLinkedRuleTargetSubcategoryName = (rule) =>
  String(rule?.targetSubcategoryName || rule?.target_subcategory_name || "").trim();

const getLinkedRuleTitle = (rule) => String(rule?.title || "").trim();

const buildLinkedRuleChoiceGroupKey = (rule) =>
  `${buildLinkedRuleSourceKey(rule)}${isIngredientLinkedRule(rule) ? "::beverage-variety" : ""}`;

const getLinkedRuleGroupLabel = (rule) =>
  (isIngredientLinkedRule(rule) ? "Variedad de bebida" : "") ||
  getLinkedRuleTargetSubcategoryName(rule) ||
  getLinkedRuleTargetCategoryName(rule) ||
  "Opcion";

const getLinkedChoiceGroupLabel = (group) => {
  const customTitle = String(group?.customTitle || "").trim();

  if (customTitle) {
    return customTitle;
  }

  const uniqueLabels = Array.isArray(group?.targetLabels)
    ? Array.from(new Set(group.targetLabels.filter(Boolean)))
    : [];

  if (uniqueLabels.length === 1) {
    return uniqueLabels[0];
  }

  return uniqueLabels.length > 1 ? "Acompanamiento" : "Opcion";
};

const getLinkedChoicePromptTitle = (group) => {
  const customTitle = String(group?.customTitle || "").trim();

  if (customTitle) {
    return customTitle;
  }

  const uniqueLabels = Array.isArray(group?.targetLabels)
    ? Array.from(new Set(group.targetLabels.filter(Boolean)))
    : [];

  if (uniqueLabels.length === 1) {
    return `Elegi una opcion de ${uniqueLabels[0]}`;
  }

  return uniqueLabels.length > 1
    ? "Elegi un acompanamiento"
    : "Elegi una opcion asociada";
};

const buildLinkedRuleChoiceGroups = (rules = [], products = [], sourceProductId = "") => {
  const groupsByKey = new Map();

  (Array.isArray(rules) ? rules : []).forEach((rule) => {
    const groupKey = buildLinkedRuleChoiceGroupKey(rule);

    if (!groupsByKey.has(groupKey)) {
      groupsByKey.set(groupKey, {
        groupKey,
        required: isIngredientLinkedRule(rule),
        customTitle: "",
        customTitles: [],
        selectionMode: normalizeLinkedRuleSelectionMode(
          rule?.selectionMode || rule?.selection_mode
        ),
        rules: [],
        targetLabels: [],
      });
    }

    const group = groupsByKey.get(groupKey);
    group.rules.push(rule);

    const nextCustomTitle = getLinkedRuleTitle(rule);
    if (nextCustomTitle) {
      group.customTitles.push(nextCustomTitle);
    }

    const nextLabel = getLinkedRuleGroupLabel(rule);
    if (nextLabel) {
      group.targetLabels.push(nextLabel);
    }
  });

  return Array.from(groupsByKey.values())
    .map((group) => {
      const optionsById = new Map();

      group.rules.forEach((rule) => {
        resolveLinkedRuleProducts(rule, products, sourceProductId).forEach((product) => {
          if (optionsById.has(product.id)) {
            return;
          }

          optionsById.set(product.id, {
            ...product,
            linkedRuleKey: buildLinkedRuleKey(rule),
            targetCategoryId: getLinkedRuleTargetCategoryId(rule),
            targetCategoryName: getLinkedRuleTargetCategoryName(rule),
            targetSubcategoryId: getLinkedRuleTargetSubcategoryId(rule),
            targetSubcategoryName: getLinkedRuleTargetSubcategoryName(rule),
          });
        });
      });

      const customTitles = Array.from(new Set((group.customTitles || []).filter(Boolean)));
      const resolvedGroup = { ...group, customTitle: customTitles.length === 1 ? customTitles[0] : "" };
      return {
        ...resolvedGroup,
        groupLabel: getLinkedChoiceGroupLabel(resolvedGroup),
        promptTitle: getLinkedChoicePromptTitle(resolvedGroup),
        options: Array.from(optionsById.values()).sort((left, right) =>
          String(left?.name || "").localeCompare(String(right?.name || ""), "es")
        ),
      };
    })
    .sort((left, right) =>
      left.groupLabel.localeCompare(right.groupLabel, "es")
    );
};

const normalizeLinkedSelectionEntries = (linkedSelections = []) =>
  (Array.isArray(linkedSelections) ? linkedSelections : [])
    .map((selection) => ({
      ruleKey: normalizeEntityId(selection?.ruleKey),
      ruleTitle: String(selection?.ruleTitle || "").trim(),
      groupLabel: String(selection?.groupLabel || "").trim(),
      targetCategoryId: normalizeEntityId(selection?.targetCategoryId),
      targetCategoryName: String(selection?.targetCategoryName || "").trim(),
      targetSubcategoryId: normalizeEntityId(selection?.targetSubcategoryId),
      targetSubcategoryName: String(selection?.targetSubcategoryName || "").trim(),
      productId: normalizeEntityId(selection?.productId || selection?.linkedProductId),
      targetIngredientId: normalizeEntityId(selection?.targetIngredientId),
      consumptionMl: Number(selection?.consumptionMl || 0),
      productName: getAssistantVisibleProductName(
        selection?.productName || selection?.linkedProductName || "",
        ""
      ),
      unitPrice: Number(selection?.unitPrice || selection?.linkedUnitPrice || 0),
      baseUnitPrice: Number(selection?.baseUnitPrice || 0),
      baseHalfUnitPrice: Number(selection?.baseHalfUnitPrice || 0),
      linkedHalfUnitPrice: Number(selection?.linkedHalfUnitPrice || 0),
      totalUnitPrice: Number(selection?.totalUnitPrice || 0),
      splitMode: String(selection?.splitMode || "").trim(),
      priceStrategy: String(selection?.priceStrategy || "").trim(),
      pizzaSizeOptionId: normalizeEntityId(selection?.pizzaSizeOptionId),
      pizzaSizeLabel: String(selection?.pizzaSizeLabel || "").trim(),
      baseHalfProductId: normalizeEntityId(selection?.baseHalfProductId),
      baseHalfProductName: getAssistantVisibleProductName(
        selection?.baseHalfProductName || "",
        ""
      ),
      linkedHalfProductId: normalizeEntityId(selection?.linkedHalfProductId),
      linkedHalfProductName: getAssistantVisibleProductName(
        selection?.linkedHalfProductName || "",
        ""
      ),
    }))
    .filter((selection) => selection.productId)
    .sort((left, right) => left.ruleKey.localeCompare(right.ruleKey, "es"));

const getCartItemLinkedSelections = (cartItem, productsById = null) => {
  const normalizedSelections = normalizeLinkedSelectionEntries(cartItem?.linkedSelections);

  if (normalizedSelections.length > 0) {
    return normalizedSelections;
  }

  const legacyLinkedProductId = normalizeEntityId(cartItem?.linkedProductId);
  if (!legacyLinkedProductId) {
    return [];
  }

  const linkedProduct = productsById?.get(legacyLinkedProductId) || null;

  return [
    {
      ruleKey: "legacy-linked",
      ruleTitle: "",
      groupLabel:
        linkedProduct?.subcategoryName ||
        String(cartItem?.linkedProductName || "").trim() ||
        "Opcion",
      targetCategoryId: normalizeEntityId(linkedProduct?.categoryId || linkedProduct?.category_id),
      targetCategoryName: "",
      targetSubcategoryId: normalizeEntityId(
        linkedProduct?.subcategoryId || linkedProduct?.subcategory_id
      ),
      targetSubcategoryName: String(linkedProduct?.subcategoryName || "").trim(),
      productId: legacyLinkedProductId,
      productName: getAssistantVisibleProductName(
        linkedProduct?.name || cartItem?.linkedProductName || "",
        "Opcion"
      ),
      unitPrice: Number(
        linkedProduct ? resolveDisplayPrice(linkedProduct) : cartItem?.linkedUnitPrice || 0
      ),
    },
  ];
};

const buildDraftLinkedSelectionsForGroups = (
  groups = [],
  previousSelectionsByKey = {},
  previousSelectionEntries = []
) => {
  const previousEntries = normalizeLinkedSelectionEntries(previousSelectionEntries);
  const previousProductIds = Array.from(
    new Set([
      ...previousEntries.map((entry) => normalizeEntityId(entry?.productId)),
      ...Object.values(previousSelectionsByKey || {}).map((value) => normalizeEntityId(value)),
    ].filter(Boolean))
  );

  return (Array.isArray(groups) ? groups : []).reduce((acc, group) => {
    const directSelectionId = normalizeEntityId(previousSelectionsByKey?.[group.groupKey]);

    if (group.options.some((option) => option.id === directSelectionId)) {
      acc[group.groupKey] = directSelectionId;
      return acc;
    }

    const previousEntryForGroup = previousEntries.find(
      (entry) =>
        normalizeEntityId(entry?.ruleKey) === group.groupKey &&
        group.options.some((option) => option.id === normalizeEntityId(entry?.productId))
    );

    if (previousEntryForGroup?.productId) {
      acc[group.groupKey] = normalizeEntityId(previousEntryForGroup.productId);
      return acc;
    }

    const matchingPreviousProductId = previousProductIds.find((productId) =>
      group.options.some((option) => option.id === productId)
    );

    if (matchingPreviousProductId) {
      acc[group.groupKey] = matchingPreviousProductId;
      return acc;
    }

    return acc;
  }, {});
};

const getProductCategoryId = (product) =>
  normalizeEntityId(product?.categoryId || product?.category_id || product?.categoria);

const getProductSubcategoryId = (product) =>
  normalizeEntityId(
    product?.subcategoryId ||
      product?.subcategory_id ||
      product?.subcategoriaId ||
      product?.subcategoria_id
  );

const getProductDisplayName = (product) =>
  String(product?.name || product?.nombre || "").trim();

const resolvePromotionItemProduct = (item, productsById = new Map(), productsByName = new Map()) => {
  const candidateIds = [
    item?.productId,
    item?.product_id,
    item?.id,
    item?.linkedProductId,
    item?.linked_product_id,
  ]
    .map(normalizeEntityId)
    .filter(Boolean);

  for (const candidateId of candidateIds) {
    const matchedById = productsById.get(candidateId);

    if (matchedById) {
      return matchedById;
    }
  }

  const candidateNames = [
    item?.name,
    item?.productName,
    item?.product_name,
    item?.nombre,
  ]
    .map(normalizeEntityLabel)
    .filter(Boolean);

  for (const candidateName of candidateNames) {
    const matchedByName = productsByName.get(candidateName);

    if (matchedByName) {
      return matchedByName;
    }
  }

  return null;
};

const isLinkedOnlyProduct = (product, hiddenStandaloneProductIds = new Set()) =>
  !isProductVisibleInMenu(product) ||
  product?.linkedOnly === true ||
  product?.linked_only === true ||
  product?.isLinkedOnly === true ||
  hiddenStandaloneProductIds.has(normalizeEntityId(product?.id));

const buildCategorySections = (
  categories = [],
  products = [],
  hiddenStandaloneProductIds = new Set()
) =>
  categories
    .map((category) => {
      const categoryId = normalizeEntityId(category?.id);
      const categoryProducts = products.filter(
        (product) =>
          isRenderableRecord(product) &&
          getProductCategoryId(product) === categoryId &&
          !isLinkedOnlyProduct(product, hiddenStandaloneProductIds)
      );

      const subcategories = categoryProducts.reduce((acc, product) => {
        const key = getProductSubcategoryId(product) || "__general__";
        const label = product.subcategoryName || "Especialidades";

        if (!acc[key]) {
          acc[key] = {
            id: key,
            name: label,
            products: [],
          };
        }

        acc[key].products.push(product);
        return acc;
      }, {});
      const categoryImageUrl = sanitizeUiImageUrl(
        category?.imageUrl ||
          category?.image_url ||
          category?.fotoUrl ||
          category?.foto_url ||
          category?.imagenUrl ||
          category?.imagen ||
          "",
        MAX_KIOSK_CATEGORY_DATA_IMAGE_LENGTH
      );
      const fallbackProductImageUrl =
        categoryProducts
          .map((product) =>
            sanitizeUiImageUrl(
              product?.imageUrl || product?.fotoUrl || product?.image_url || "",
              MAX_KIOSK_CATEGORY_DATA_IMAGE_LENGTH
            )
          )
          .find(Boolean) || "";
      const categoryName =
        String(category?.name || category?.nombre || "").trim() || "Categoria";

      return {
        ...category,
        name: categoryName,
        nombre: categoryName,
        imageUrl: categoryImageUrl || fallbackProductImageUrl,
        itemsCount: categoryProducts.length,
        description:
          String(category?.description || category?.descripcion || "").trim() ||
          `${categoryProducts.length} opcion${
            categoryProducts.length === 1 ? "" : "es"
          } para elegir`,
        products: categoryProducts,
        subcategories: Object.values(subcategories),
      };
    })
    .filter((category) => category.products.length > 0);

const getCategoryDisplayName = (category) =>
  String(category?.name || category?.nombre || "").trim() || "Categoria";

const createDraftSelection = (product, existingSelection = null) =>
  (product?.ingredients || []).reduce((acc, ingredient) => {
    acc[ingredient.id] = Number(
      existingSelection?.[ingredient.id] ?? ingredient.baseQuantity ?? 0
    );
    return acc;
  }, {});

const getCustomizationSummary = (product, selection = null) => {
  if (!product?.ingredients?.length || !selection) {
    return null;
  }

  let removedUnits = 0;
  let extraUnits = 0;
  let extraCost = 0;

  product.ingredients.forEach((ingredient) => {
    const baseQuantity = Number(ingredient.baseQuantity || 0);
    const selectedQuantity = Number(selection?.[ingredient.id] ?? baseQuantity);

    if (selectedQuantity < baseQuantity) {
      removedUnits += baseQuantity - selectedQuantity;
    }

    if (selectedQuantity > baseQuantity) {
      const extra = selectedQuantity - baseQuantity;
      extraUnits += extra;
      extraCost += extra * Number(ingredient.price || 0);
    }
  });

  if (!removedUnits && !extraUnits) {
    return null;
  }

  return {
    removedUnits,
    extraUnits,
    extraCost,
    changes: removedUnits + extraUnits,
  };
};

const getIngredientStateLabel = (ingredient, quantity) => {
  const baseQuantity = Number(ingredient.baseQuantity || 0);

  if (baseQuantity <= 0 && quantity <= 0) {
    return "Sin agregar";
  }

  if (quantity <= 0) {
    return "Quitado";
  }

  if (quantity === baseQuantity) {
    return "Normal";
  }

  if (quantity > baseQuantity) {
    return `+${quantity - baseQuantity} extra`;
  }

  return "Ajustado";
};

const getCartStorageKey = (qrToken) =>
  `${CART_STORAGE_PREFIX}${(qrToken || "default").trim()}`;

const getSubmittedOrderStorageKey = (qrToken) =>
  `${SUBMITTED_ORDER_STORAGE_PREFIX}${(qrToken || "default").trim()}`;

const getCustomerIdentityStorageKey = (qrToken) =>
  `${CUSTOMER_IDENTITY_STORAGE_PREFIX}${(qrToken || "default").trim()}`;

const getKioskServiceTypeStorageKey = (sessionKey) =>
  `${KIOSK_SERVICE_TYPE_STORAGE_PREFIX}${(sessionKey || "default").trim()}`;

const resolveGuestSessionStorage = (isKioskExperience) => {
  if (typeof window === "undefined") {
    return null;
  }

  if (isKioskExperience) {
    return window.localStorage;
  }

  try {
    return window.sessionStorage || window.localStorage;
  } catch (error) {
    return window.localStorage;
  }
};

const removeStorageKey = (storage, key) => {
  if (!storage || !key) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch (error) {
    // No frenamos el flujo por limpieza local.
  }
};

const clearGuestSessionArtifacts = (sessionKey, isKioskExperience = false) => {
  if (typeof window === "undefined" || !sessionKey) {
    return;
  }

  const keysToClear = [
    getCartStorageKey(sessionKey),
    getSubmittedOrderStorageKey(sessionKey),
    getCustomerIdentityStorageKey(sessionKey),
  ];

  const storages = [];

  if (isKioskExperience) {
    storages.push(window.localStorage);
  } else {
    try {
      storages.push(window.sessionStorage);
    } catch (error) {
      // Seguimos con localStorage legado.
    }
    storages.push(window.localStorage);
  }

  storages.forEach((storage) => {
    keysToClear.forEach((key) => removeStorageKey(storage, key));
  });
};

const sanitizeCustomerName = (value) =>
  sanitizeDisplayText(value)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);

const sanitizeDisplayText = (value) =>
  repairTextEncoding(value || "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/(^|\n)\s*[*-]\s+/g, "$1\u2022 ")
    .replace(/\*/g, "")
    .trim();

const sanitizeAiAssistantReplyText = (value) =>
  sanitizeDisplayText(value)
    .replace(
      /(?:^|\n)\s*["']?(?:answer|respuesta|reply)["']?\s*[:=-]\s*/i,
      ""
    )
    .replace(/\b1\s*x(?=\s+[a-z0-9áéíóúüñ])/gi, "una unidad de")
    .replace(
      /\b(\d+)\s*x(?=\s+[a-z0-9áéíóúüñ])/gi,
      (_, quantity) => `${quantity} unidades de`
    )
    .replace(/\s+/g, " ")
    .trim();

const buildCustomerSessionId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `customer-session-${window.crypto.randomUUID()}`;
  }

  return `customer-session-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const loadKioskServiceTypeFromStorage = (sessionKey) => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const rawValue = String(
      window.localStorage.getItem(getKioskServiceTypeStorageKey(sessionKey)) || ""
    )
      .trim()
      .toLowerCase();

    return rawValue === "takeaway" || rawValue === "dine_in" ? rawValue : "";
  } catch (error) {
    return "";
  }
};

const persistKioskServiceTypeToStorage = (sessionKey, serviceType) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const safeValue = String(serviceType || "").trim().toLowerCase();

    if (!safeValue) {
      window.localStorage.removeItem(getKioskServiceTypeStorageKey(sessionKey));
      return;
    }

    window.localStorage.setItem(
      getKioskServiceTypeStorageKey(sessionKey),
      safeValue
    );
  } catch (error) {
    // No frenamos el flujo del totem por persistencia local.
  }
};

const hasKioskCartInStorage = (sessionKey) => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const rawValue = window.localStorage.getItem(getCartStorageKey(sessionKey));

    if (!rawValue) {
      return false;
    }

    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue) && parsedValue.length > 0;
  } catch (error) {
    return false;
  }
};

const loadCartFromStorage = (qrToken, storage = null) => {
  const activeStorage =
    storage || (typeof window !== "undefined" ? window.localStorage : null);

  if (!activeStorage) {
    return [];
  }

  try {
    const rawValue = activeStorage.getItem(getCartStorageKey(qrToken));
    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
};

const persistCartToStorage = (qrToken, cartItems, storage = null) => {
  const activeStorage =
    storage || (typeof window !== "undefined" ? window.localStorage : null);

  if (!activeStorage) {
    return;
  }

  try {
    activeStorage.setItem(
      getCartStorageKey(qrToken),
      JSON.stringify(cartItems)
    );
  } catch (error) {
    // Ignoramos errores de persistencia local para no romper el flujo del comensal.
  }
};

const loadSubmittedOrderFromStorage = (qrToken, storage = null) => {
  const activeStorage =
    storage || (typeof window !== "undefined" ? window.localStorage : null);

  if (!activeStorage) {
    return null;
  }

  try {
    const rawValue = activeStorage.getItem(getSubmittedOrderStorageKey(qrToken));
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    return parsedValue && Array.isArray(parsedValue.items) ? parsedValue : null;
  } catch (error) {
    return null;
  }
};

const persistSubmittedOrderToStorage = (qrToken, submittedOrder, storage = null) => {
  const activeStorage =
    storage || (typeof window !== "undefined" ? window.localStorage : null);

  if (!activeStorage) {
    return;
  }

  try {
    const hasSubmittedItems = Boolean(submittedOrder?.items?.length);
    const hasTrackedDigitalPayment = Boolean(
      submittedOrder?.paymentIntent?.id &&
        normalizeCheckoutMethod(submittedOrder?.checkoutMethod) === "deep_link"
    );

    if (!hasSubmittedItems && !hasTrackedDigitalPayment) {
      activeStorage.removeItem(getSubmittedOrderStorageKey(qrToken));
      return;
    }

    activeStorage.setItem(
      getSubmittedOrderStorageKey(qrToken),
      JSON.stringify(submittedOrder)
    );
  } catch (error) {
    // Ignoramos errores locales para no romper el flujo del comensal.
  }
};

const loadCustomerIdentityFromStorage = (qrToken, storage = null) => {
  const activeStorage =
    storage || (typeof window !== "undefined" ? window.localStorage : null);

  if (!activeStorage) {
    return null;
  }

  try {
    const rawValue = activeStorage.getItem(getCustomerIdentityStorageKey(qrToken));

    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    const customerName = sanitizeCustomerName(parsedValue?.customerName);
    const customerSessionId =
      String(parsedValue?.customerSessionId || "").trim() || buildCustomerSessionId();

    if (!customerName && !customerSessionId) {
      return null;
    }

    return {
      customerName,
      customerSessionId,
      createdAt: parsedValue?.createdAt || new Date().toISOString(),
    };
  } catch (error) {
    return null;
  }
};

const persistCustomerIdentityToStorage = (
  qrToken,
  customerIdentity,
  storage = null
) => {
  const activeStorage =
    storage || (typeof window !== "undefined" ? window.localStorage : null);

  if (!activeStorage) {
    return;
  }

  try {
    if (!customerIdentity?.customerSessionId) {
      activeStorage.removeItem(getCustomerIdentityStorageKey(qrToken));
      return;
    }

    activeStorage.setItem(
      getCustomerIdentityStorageKey(qrToken),
      JSON.stringify({
        customerName: sanitizeCustomerName(customerIdentity.customerName),
        customerSessionId: customerIdentity.customerSessionId || buildCustomerSessionId(),
        createdAt: customerIdentity.createdAt || new Date().toISOString(),
      })
    );
  } catch (error) {
    // Si falla el guardado local, dejamos seguir al comensal igual.
  }
};

const createSelectionSignature = (
  product,
  selection = {},
  linkedSelections = [],
  {
    pizzaSizeOption = null,
    pizzaPortionMode = "full",
    empanadaSaleOption = null,
    empanadaCookingOption = null,
  } = {}
) =>
  [
    pizzaSizeOption?.id ? `pizza-size:${pizzaSizeOption.id}` : "",
    pizzaPortionMode && pizzaPortionMode !== "full"
      ? `pizza-portion:${pizzaPortionMode}`
      : "",
    empanadaSaleOption?.id ? `empanada-sale:${empanadaSaleOption.id}` : "",
    empanadaCookingOption?.id
      ? `empanada-cooking:${empanadaCookingOption.id}`
      : "",
    ...(product?.ingredients || [])
    .map((ingredient) => {
      const baseQuantity = Number(ingredient.baseQuantity || 0);
      const selectedQuantity = Number(
        selection?.[ingredient.id] ?? ingredient.baseQuantity ?? 0
      );

      return `${ingredient.id}:${baseQuantity}:${selectedQuantity}`;
    }),
    ...normalizeLinkedSelectionEntries(linkedSelections).map(
      (linkedSelection) =>
        `linked:${linkedSelection.ruleKey}:${linkedSelection.productId || "none"}${linkedSelection.targetIngredientId ? `:${linkedSelection.consumptionMl}ml` : ""}`
    ),
  ].filter(Boolean).join("|");

const buildLinkedSelectionDetail = (linkedSelection) => {
  if (!linkedSelection?.productName) {
    return "";
  }

  const groupLabel = linkedSelection.groupLabel || "Opcion";
  return `${groupLabel}: ${linkedSelection.productName}${linkedSelection.targetIngredientId ? ` (${linkedSelection.consumptionMl} ml)` : ""}`;
};

const buildSelectionDetails = (
  product,
  selection = {},
  linkedSelections = [],
  {
    pizzaSizeOption = null,
    pizzaPortionMode = "full",
    empanadaSaleOption = null,
    empanadaCookingOption = null,
  } = {}
) => {
  const normalizedLinkedSelections = normalizeLinkedSelectionEntries(linkedSelections);
  const linkedDetails = normalizedLinkedSelections
    .flatMap((linkedSelection) =>
      isPizzaHalfAndHalfSelection(linkedSelection)
        ? buildPizzaHalfAndHalfSelectionDetails(product, linkedSelection)
        : [buildLinkedSelectionDetail(linkedSelection)]
    )
    .filter(Boolean);
  const sizeDetail = normalizedLinkedSelections.some(isPizzaHalfAndHalfSelection)
    ? ""
    : createPizzaSizeSelectionDetail(pizzaSizeOption);
  const portionDetail =
    pizzaPortionMode === "half" ? "Formato: Media pizza" : pizzaPortionMode === "slice" ? "Formato: Porcion" : "";
  const empanadaSaleDetail = createEmpanadaSaleSelectionDetail(empanadaSaleOption);
  const empanadaCookingDetail =
    createEmpanadaCookingSelectionDetail(empanadaCookingOption);

  if (!product?.ingredients?.length) {
    return [
      sizeDetail,
      portionDetail,
      empanadaSaleDetail,
      empanadaCookingDetail,
      ...linkedDetails,
    ].filter(Boolean);
  }

  const details = product.ingredients.reduce((acc, ingredient) => {
    const baseQuantity = Number(ingredient.baseQuantity || 0);
    const selectedQuantity = Number(
      selection?.[ingredient.id] ?? ingredient.baseQuantity ?? 0
    );

    if (selectedQuantity === baseQuantity) {
      return acc;
    }

    if (selectedQuantity <= 0 && baseQuantity > 0) {
      acc.push(`Sin ${ingredient.name}`);
      return acc;
    }

    if (selectedQuantity < baseQuantity) {
      acc.push(`-${baseQuantity - selectedQuantity} ${ingredient.name}`);
      return acc;
    }

    if (selectedQuantity > baseQuantity) {
      acc.push(`+${selectedQuantity - baseQuantity} ${ingredient.name}`);
    }

    return acc;
  }, []);

  details.push(
    ...[
      sizeDetail,
      portionDetail,
      empanadaSaleDetail,
      empanadaCookingDetail,
      ...linkedDetails,
    ].filter(Boolean)
  );

  return details;
};

const getAiAssistantProductSearchText = (product = {}) =>
  normalizeAssistantProductKey(
    [
      product?.name,
      product?.nombre,
      product?.description,
      product?.descripcion,
      product?.categoryName,
      product?.categoria,
      product?.subcategoryName,
      product?.subcategoria,
    ]
      .filter(Boolean)
      .join(" ")
  );

const scoreAiAssistantLinkedOption = (question = "", product = {}) => {
  const normalizedQuestion = normalizeAssistantProductKey(question);
  const haystack = getAiAssistantProductSearchText(product);

  if (!normalizedQuestion || !haystack) {
    return 0;
  }

  let score = 0;
  const name = normalizeAssistantProductKey(product?.name || product?.nombre);

  if (name && normalizedQuestion.includes(name)) {
    score += 24;
  }

  normalizedQuestion
    .split(/[^a-z0-9]+/g)
    .filter((token) => token.length >= 3)
    .forEach((token) => {
      if (name.includes(token)) {
        score += 10;
      } else if (haystack.includes(token)) {
        score += 4;
      }
    });

  return score;
};

const buildAiLinkedSelectionPlan = ({
  groups = [],
  question = "",
  getLinkedOptionStockState = () => ({ available: true }),
} = {}) => {
  const selectionIdsByGroup = {};
  const selectionEntries = [];
  const missingRequiredGroups = [];
  const asksForNoCombination = /\b(nada|solo|sola|sin acompanamiento|sin guarnicion|sin salsa|sin combinacion)\b/i.test(
    normalizeAssistantProductKey(question)
  );

  (Array.isArray(groups) ? groups : []).forEach((group) => {
    const availableOptions = (Array.isArray(group?.options) ? group.options : []).filter(
      (option) => getLinkedOptionStockState(option).available !== false
    );

    if (availableOptions.length === 0 || asksForNoCombination) {
      if (group.required) missingRequiredGroups.push(group);
      return;
    }

    const scoredOptions = availableOptions
      .map((option) => ({
        option,
        score: scoreAiAssistantLinkedOption(question, option),
      }))
      .sort((left, right) => right.score - left.score);
    const bestMatch = scoredOptions[0]?.score > 0 ? scoredOptions[0].option : null;
    const selectedOption = bestMatch;

    if (!selectedOption) {
      if (group.required) missingRequiredGroups.push(group);
      return;
    }

    selectionIdsByGroup[group.groupKey] = selectedOption.id;
    selectionEntries.push({
      ruleKey: group.groupKey,
      ruleTitle: group.promptTitle,
      groupLabel: group.groupLabel,
      targetCategoryId: selectedOption.targetCategoryId || "",
      targetCategoryName: selectedOption.targetCategoryName || "",
      targetSubcategoryId: selectedOption.targetSubcategoryId || "",
      targetSubcategoryName: selectedOption.targetSubcategoryName || "",
      productId: selectedOption.id,
      targetIngredientId: selectedOption.targetIngredientId || "",
      consumptionMl: selectedOption.consumptionMl || 0,
      productName: selectedOption.name,
      unitPrice: resolveDisplayPrice(selectedOption),
    });
  });

  return {
    selectionIdsByGroup,
    selectionEntries,
    missingRequiredGroups,
  };
};

const shouldOpenProductCustomizationForAi = (
  question = "",
  product = {},
  ingredientSelectionPlan = null
) =>
  Array.isArray(product?.ingredients) &&
  product.ingredients.length > 0 &&
  /\b(sin|sacale|sacar|quitar|quitale|extra|mas|menos|doble|personalizar|modificar|cambiar)\b/i.test(
    normalizeAssistantProductKey(question)
  ) &&
  !ingredientSelectionPlan?.hasChanges;

const summarizeAiRelatedProduct = (product = {}) => ({
  productId: normalizeEntityId(product?.id || product?.productId),
  name: getAssistantVisibleProductName(product?.name || product?.nombre || "", "Producto"),
  description: String(product?.description || product?.descripcion || "").trim().slice(0, 160),
  imageUrl: product?.imageUrl || product?.fotoUrl || "",
  price: resolveDisplayPrice(product),
});

const buildCartItem = (
  product,
  selection = {},
  quantity = 1,
  linkedSelections = [],
  {
    pizzaSizeOption = null,
    pizzaPortionMode = "full",
    empanadaSaleOption = null,
    empanadaCookingOption = null,
  } = {}
) => {
  const safeQuantity = Math.max(1, Number(quantity || 1));
  const customizationSummary = getCustomizationSummary(product, selection);
  const resolvedPizzaSizeOption =
    pizzaSizeOption || (productHasPizzaSizeOptions(product) ? findPizzaSizeOption(product) : null);
  const resolvedEmpanadaSaleOption =
    empanadaSaleOption ||
    (productHasEmpanadaSaleOptions(product) ? findEmpanadaSaleOption(product) : null);
  const resolvedEmpanadaCookingOption =
    empanadaCookingOption || findEmpanadaCookingOption(product);
  const normalizedLinkedSelections = normalizeLinkedSelectionEntries(linkedSelections);
  const pizzaHalfSelection = getPizzaHalfAndHalfSelection(normalizedLinkedSelections);
  const normalizedPizzaPortionMode =
    pizzaHalfSelection
      ? "half_and_half"
      : ["half", "slice"].includes(pizzaPortionMode)
        ? pizzaPortionMode
        : "full";
  const basePrice = resolvedPizzaSizeOption
    ? resolvePizzaProductPrice(product, {
        sizeOption: resolvedPizzaSizeOption,
        portionMode: normalizedPizzaPortionMode,
      })
    : resolvedEmpanadaSaleOption
      ? resolveEmpanadaProductPrice(product, {
          saleOption: resolvedEmpanadaSaleOption,
        })
      : resolveDisplayPrice(product);
  const linkedUnitPrice = normalizedLinkedSelections.reduce(
    (total, linkedSelection) => total + Number(linkedSelection.unitPrice || 0),
    0
  );
  const extraCost = Number(customizationSummary?.extraCost || 0);
  const unitPrice = basePrice + linkedUnitPrice + extraCost;
  const signature = createSelectionSignature(
    product,
    selection,
    normalizedLinkedSelections,
    {
      pizzaSizeOption: resolvedPizzaSizeOption,
      pizzaPortionMode: normalizedPizzaPortionMode,
      empanadaSaleOption: resolvedEmpanadaSaleOption,
      empanadaCookingOption: resolvedEmpanadaCookingOption,
    }
  );
  const primaryLinkedSelection = normalizedLinkedSelections[0] || null;
  const displayProductName = pizzaHalfSelection
    ? `${pizzaHalfSelection.baseHalfProductName || getPizzaProductName(product)} / ${
        pizzaHalfSelection.linkedHalfProductName ||
        pizzaHalfSelection.productName ||
        "Pizza"
      }`
    : getAssistantVisibleProductName(product?.name, "Producto");

  return {
    id: `${product.id}::${signature || "base"}`,
    productId: product.id,
    categoryId: product.categoryId || product.categoria || product.category_id || "",
    categoryName: product.categoryName || product.categoriaNombre || "",
    subcategoryId:
      product.subcategoryId || product.subcategoriaId || product.subcategory_id || "",
    subcategoryName: product.subcategoryName || product.subcategoria || "",
    linkedSelections: normalizedLinkedSelections,
    linkedProductId: primaryLinkedSelection?.productId || "",
    linkedProductName: primaryLinkedSelection?.productName || "",
    linkedUnitPrice,
    pizzaSizeOptionId: resolvedPizzaSizeOption?.id || "",
    pizzaSizeLabel: resolvedPizzaSizeOption?.label || "",
    pizzaSizeFullUnitPrice: Number(resolvedPizzaSizeOption?.fullPrice || 0),
    pizzaSizeHalfUnitPrice: Number(resolvedPizzaSizeOption?.halfPrice || 0),
    pizzaSizeSliceUnitPrice: Number(resolvedPizzaSizeOption?.slicePrice || 0),
    pizzaSlices: Number(resolvedPizzaSizeOption?.slices || 0),
    pizzaSizeRecipeScale: Number(resolvedPizzaSizeOption?.recipeScale || 1),
    pizzaPortionMode: normalizedPizzaPortionMode,
    empanadaSaleOptionId: resolvedEmpanadaSaleOption?.id || "",
    empanadaSaleLabel: resolvedEmpanadaSaleOption?.label || "",
    empanadaUnitCount: Number(resolvedEmpanadaSaleOption?.unitCount || 1),
    empanadaSaleUnitPrice: Number(resolvedEmpanadaSaleOption?.price || 0),
    empanadaCookingOptionId: resolvedEmpanadaCookingOption?.id || "",
    empanadaCookingLabel: resolvedEmpanadaCookingOption?.label || "",
    signature,
    productName: displayProductName,
    imageUrl: product.imageUrl || "",
    quantity: safeQuantity,
    basePrice: basePrice + linkedUnitPrice,
    extraCost,
    unitPrice,
    selection,
    selectionDetails: buildSelectionDetails(
      product,
      selection,
      normalizedLinkedSelections,
      {
        pizzaSizeOption: resolvedPizzaSizeOption,
        pizzaPortionMode: normalizedPizzaPortionMode,
        empanadaSaleOption: resolvedEmpanadaSaleOption,
        empanadaCookingOption: resolvedEmpanadaCookingOption,
      }
    ),
  };
};

const formatAssistantOrderItemSummary = (
  quantity = 1,
  productName = "Producto",
  linkedNames = ""
) => {
  const safeQuantity = Math.max(1, Number(quantity || 1));
  const safeProductName = getAssistantVisibleProductName(productName, "Producto");
  const safeLinkedNames = sanitizeDisplayText(linkedNames)
    .replace(/\s+/g, " ")
    .trim();
  const quantityLabel =
    safeQuantity === 1 ? "una unidad de" : `${safeQuantity} unidades de`;

  return `${quantityLabel} ${safeProductName}${
    safeLinkedNames ? ` con ${safeLinkedNames}` : ""
  }`;
};

const buildOrderSummary = (items = []) =>
  items
    .map((item) =>
      formatAssistantOrderItemSummary(item?.quantity, item?.productName || "Producto")
    )
    .join(", ");

const createPricingSnapshot = (pricing) => ({
  ...compactPromotionPaymentFields(pricing),
  listTotal: Number(pricing?.listTotal || 0),
  comboDiscountTotal: Number(pricing?.comboDiscountTotal || 0),
  promotionDiscountTotal: Number(pricing?.promotionDiscountTotal || 0),
  totalDiscount: Number(pricing?.totalDiscount || 0),
  finalTotal: Number(pricing?.finalTotal || 0),
  adjustments: Array.isArray(pricing?.adjustments)
    ? pricing.adjustments.map((adjustment) => ({
        id: adjustment.id,
        type: adjustment.type,
        paymentCondition: adjustment.paymentCondition || "any",
        title: adjustment.title,
        amount: Number(adjustment.amount || 0),
        summary: adjustment.summary || adjustment.title || "",
      }))
    : [],
});

const buildCartItemConsumptions = (product, cartItem, productsById = null, linkedRules = []) => {
  const linkedSelections = getCartItemLinkedSelections(cartItem, productsById);
  const pizzaHalfSelection = getPizzaHalfAndHalfSelection(linkedSelections);
  const basePizzaRecipeScale = Number(cartItem?.pizzaSizeRecipeScale || 1);
  const basePizzaPortionScale =
    getPizzaPortionScale({ slices: cartItem?.pizzaSlices }, pizzaHalfSelection ? "half_and_half" : cartItem?.pizzaPortionMode);
  const baseEmpanadaUnitScale = Number(cartItem?.empanadaUnitCount || 1);
  const baseConsumptions = buildOperationalConsumptions(withoutBeverageVarietyIngredients(product, linkedRules), {
    selection: cartItem?.selection || {},
    quantity: cartItem?.quantity || 1,
    quantityScale:
      basePizzaPortionScale * basePizzaRecipeScale * baseEmpanadaUnitScale,
  }).concat(buildBeverageConsumptions(product, cartItem, linkedRules));

  if (!productsById) {
    return baseConsumptions;
  }

  return linkedSelections.reduce(
    (acc, linkedSelection) => {
      const linkedProduct = productsById.get(linkedSelection.productId);

      if (!linkedProduct) {
        return acc;
      }

      const linkedPizzaSizeOption = isPizzaHalfAndHalfSelection(linkedSelection)
        ? findPizzaSizeOption(
            linkedProduct,
            linkedSelection.pizzaSizeOptionId || cartItem?.pizzaSizeOptionId || ""
          )
        : null;

      return acc.concat(
        buildOperationalConsumptions(linkedProduct, {
          quantity: cartItem?.quantity || 1,
          quantityScale:
            (isPizzaHalfAndHalfSelection(linkedSelection) ? 0.5 : 1) *
            Number(linkedPizzaSizeOption?.recipeScale || 1),
        })
      );
    },
    [...baseConsumptions]
  );
};

const mergeCartEntries = (currentItems = [], incomingItems = []) => {
  const nextItems = [...currentItems];

  incomingItems.forEach((incomingItem) => {
    const existingIndex = nextItems.findIndex((item) => item.id === incomingItem.id);

    if (existingIndex < 0) {
      nextItems.push(incomingItem);
      return;
    }

    nextItems[existingIndex] = {
      ...nextItems[existingIndex],
      quantity:
        Number(nextItems[existingIndex].quantity || 0) +
        Number(incomingItem.quantity || 0),
    };
  });

  return nextItems;
};

function MenuComensalQR({
  experienceMode = "qr",
  kioskBranchId = "",
  kioskStationNumber = 1,
  qrTokenOverride = "",
  tabletSessionKey = "",
  onTabletOrderStateChange,
}) {
  const { qrToken: routeQrToken = "" } = useParams();
  const qrToken = qrTokenOverride || routeQrToken;
  const { availableBranches, currentBranch } = useAuth();
  const isKioskExperience = experienceMode === "kiosk";
  const normalizedKioskStationNumber = useMemo(
    () => normalizeKioskStationNumber(kioskStationNumber),
    [kioskStationNumber]
  );
  const kioskBranch = useMemo(() => {
    if (!isKioskExperience || !kioskBranchId) {
      return null;
    }

    return (
      availableBranches.find((branch) => branch.id === kioskBranchId) ||
      (currentBranch?.id === kioskBranchId ? currentBranch : null)
    );
  }, [availableBranches, currentBranch, isKioskExperience, kioskBranchId]);
  const sessionKey = isKioskExperience
    ? buildKioskSessionKey(kioskBranchId, normalizedKioskStationNumber)
    : tabletSessionKey || qrToken;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState("");
  const [activeSubcategoryId, setActiveSubcategoryId] = useState("__all__");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedPromotionId, setSelectedPromotionId] = useState("");
  const [draftSelection, setDraftSelection] = useState({});
  const [draftLinkedSelectionsByUnit, setDraftLinkedSelectionsByUnit] = useState([{}]);
  const [activeLinkedUnit, setActiveLinkedUnit] = useState(0);
  const draftLinkedSelections = draftLinkedSelectionsByUnit[activeLinkedUnit] || {};
  const [draftPizzaSizeOptionId, setDraftPizzaSizeOptionId] = useState("");
  const [draftPizzaPortionMode, setDraftPizzaPortionMode] = useState("full");
  const [draftPizzaHalfProductId, setDraftPizzaHalfProductId] = useState("");
  const [draftEmpanadaSaleOptionId, setDraftEmpanadaSaleOptionId] = useState("");
  const [draftEmpanadaCookingOptionId, setDraftEmpanadaCookingOptionId] = useState("");
  const [customizationsByProduct, setCustomizationsByProduct] = useState({});
  const [linkedSelectionsByProduct, setLinkedSelectionsByProduct] = useState({});
  const [draftProductQuantity, setDraftProductQuantity] = useState(1);
  const [lockedProductQuantity, setLockedProductQuantity] = useState(0);
  const [productDetailPromotionId, setProductDetailPromotionId] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [submittedOrderSnapshot, setSubmittedOrderSnapshot] = useState(null);
  const [paymentOperationsSnapshot, setPaymentOperationsSnapshot] = useState([]);
  const [ingredientStockSnapshot, setIngredientStockSnapshot] = useState({});
  const [editingCartItemId, setEditingCartItemId] = useState("");
  const [isEditingSubmittedOrder, setIsEditingSubmittedOrder] = useState(false);
  const [customerIdentity, setCustomerIdentity] = useState(null);
  const [customerNameDraft, setCustomerNameDraft] = useState("");
  const [isKioskKeyboardOpen, setIsKioskKeyboardOpen] = useState(false);
  const [isKioskKeyboardUppercase, setIsKioskKeyboardUppercase] = useState(true);
  const [isKioskLanguageMenuOpen, setIsKioskLanguageMenuOpen] = useState(false);
  const [kioskHasStarted, setKioskHasStarted] = useState(() =>
    isKioskExperience ? hasKioskCartInStorage(sessionKey) : true
  );
  const [kioskCatalogMode, setKioskCatalogMode] = useState("picker");
  const [kioskLanguage, setKioskLanguage] = useState(() =>
    loadKioskLanguageFromStorage(sessionKey)
  );
  const [kioskServiceType, setKioskServiceType] = useState(() =>
    isKioskExperience && hasKioskCartInStorage(sessionKey)
      ? loadKioskServiceTypeFromStorage(sessionKey)
      : ""
  );
  const [isKioskInactivityPromptOpen, setIsKioskInactivityPromptOpen] = useState(false);
  const [kioskInactivityCountdown, setKioskInactivityCountdown] = useState(
    KIOSK_INACTIVITY_CONFIRMATION_SECONDS
  );
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartModalStep, setCartModalStep] = useState("review");
  const [checkoutMethod, setCheckoutMethod] = useState(COUNTER_CHECKOUT_METHOD);
  const [selectedMenuWalletProviderId, setSelectedMenuWalletProviderId] = useState("");
  const [checkoutMessage, setCheckoutMessage] = useState({
    type: "",
    text: "",
  });
  const [isKioskNameWarningOpen, setIsKioskNameWarningOpen] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [kioskCompletionStage, setKioskCompletionStage] = useState("");
  const [promotions, setPromotions] = useState([]);
  const [waiterAssistanceConfig, setWaiterAssistanceConfig] = useState(() =>
    getWaiterAssistanceConfig(null)
  );
  const [isWaiterHelpOpen, setIsWaiterHelpOpen] = useState(false);
  const [waiterOptionsLoading, setWaiterOptionsLoading] = useState(false);
  const [waiterOptionsNotice, setWaiterOptionsNotice] = useState("");
  const [selectedWaiterOptions, setSelectedWaiterOptions] = useState([]);
  const [waiterCustomText, setWaiterCustomText] = useState("");
  const [isSubmittingWaiterHelp, setIsSubmittingWaiterHelp] = useState(false);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [isAiAssistantPanelVisible, setIsAiAssistantPanelVisible] = useState(false);
  const [aiConversation, setAiConversation] = useState([]);
  const [aiQuestionDraft, setAiQuestionDraft] = useState("");
  const [isAiAssistantLoading, setIsAiAssistantLoading] = useState(false);
  const [isAiListening, setIsAiListening] = useState(false);
  const [, setAiVoiceStatus] = useState("");
  const [, setAiSpeechAwaitingManualPlay] = useState(false);
  const [isAiAssistantFabDragging, setIsAiAssistantFabDragging] = useState(false);
  const [aiAssistantFabPosition, setAiAssistantFabPosition] = useState(null);
  const [aiSpotlightProductId, setAiSpotlightProductId] = useState("");
  const [isCompactViewport, setIsCompactViewport] = useState(() =>
    resolveCompactViewport(isKioskExperience)
  );
  const [promotionNow, setPromotionNow] = useState(() => new Date());
  const [operationalOrdersSnapshot, setOperationalOrdersSnapshot] = useState(null);
  const [pendingTableAccounts, setPendingTableAccounts] = useState([]);
  const [pendingTableAccountsLoading, setPendingTableAccountsLoading] = useState(false);
  const [pendingTableAccountsError, setPendingTableAccountsError] = useState("");
  const [selectedPendingAccountIds, setSelectedPendingAccountIds] = useState([]);
  const [isPendingAccountsPaymentOpen, setIsPendingAccountsPaymentOpen] =
    useState(false);
  const [isCreatingPendingAccountsPayment, setIsCreatingPendingAccountsPayment] =
    useState(false);
  const [isOpeningSubmittedOrderPayment, setIsOpeningSubmittedOrderPayment] =
    useState(false);
  const [isCancellingSubmittedOrder, setIsCancellingSubmittedOrder] =
    useState(false);
  const [qrAccessExpired, setQrAccessExpired] = useState(false);
  const [qrAccessExpiresAt, setQrAccessExpiresAt] = useState("");
  const [qrAccessExpiredMessage, setQrAccessExpiredMessage] = useState(
    DEFAULT_QR_ACCESS_EXPIRED_MESSAGE
  );
  const checkoutNameInputRef = useRef(null);
  const pendingAccountsPaymentLockRef = useRef(false);
  const aiAssistantThreadRef = useRef(null);
  const aiAssistantLatestReplyRef = useRef(null);
  const aiAssistantFloatingResultsRef = useRef(null);
  const aiAssistantFabRef = useRef(null);
  const aiAssistantFabDragRef = useRef(null);
  const aiAssistantFabSuppressClickRef = useRef(false);
  const productCardRefs = useRef(new Map());
  const aiProductScrollTimerRef = useRef(null);
  const aiProductFocusTimerRef = useRef(null);
  const aiProductSpotlightTimerRef = useRef(null);
  const activeCategoryIdRef = useRef(activeCategoryId);
  const activeSubcategoryIdRef = useRef(activeSubcategoryId);
  const aiSpeechRecognitionRef = useRef(null);
  const aiSpeechVoiceRef = useRef(null);
  const aiSpeechOutputPrimedRef = useRef(false);
  const aiSpeechRetryTimeoutRef = useRef(null);
  const aiSpeechStartWatchdogRef = useRef(null);
  const aiSpeechSequenceRef = useRef(0);
  const aiMicrophonePermissionGrantedRef = useRef(false);
  const aiVoiceDraftRef = useRef("");
  const aiVoiceFinalDraftRef = useRef("");
  const aiVoiceShouldSubmitRef = useRef(false);
  const aiVoiceSilenceTimeoutRef = useRef(null);
  const kioskKeyboardOpeningRef = useRef(false);
  const kioskKeyboardIgnoreNextOverlayClickRef = useRef(false);
  const kioskKeyboardOpenTimeoutRef = useRef(null);
  const kioskLanguageMenuRef = useRef(null);
  const productDetailHistoryTokenRef = useRef("");
  const orderSubmissionLockRef = useRef(false);
  useEffect(() => {
    onTabletOrderStateChange?.({
      hasDraft: cartItems.length > 0,
      busy: isSubmittingOrder || isCreatingPendingAccountsPayment || isOpeningSubmittedOrderPayment,
      submitted: Boolean(submittedOrderSnapshot?.orderId),
    });
  }, [cartItems.length, isSubmittingOrder, isCreatingPendingAccountsPayment,
    isOpeningSubmittedOrderPayment, submittedOrderSnapshot?.orderId, onTabletOrderStateChange]);
  const kioskPaymentFinalizeRef = useRef("");
  const menuPaymentFinalizeRef = useRef("");
  const commissionSettlementTrackingRef = useRef(new Set());
  const mzUsageTrackingRef = useRef(new Set());
  const orderStockTrackingRef = useRef(new Set());
  const isKioskQrPaymentPending = useMemo(
    () =>
      isKioskExperience &&
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) ===
        KIOSK_QR_CHECKOUT_METHOD &&
      (submittedOrderSnapshot?.awaitingPaymentApproval || isSubmittingOrder),
    [
      isKioskExperience,
      isSubmittingOrder,
      submittedOrderSnapshot?.awaitingPaymentApproval,
      submittedOrderSnapshot?.checkoutMethod,
    ]
  );
  const clearProductDetailSelection = useCallback(() => {
    setSelectedProductId("");
    setDraftSelection({});
    setDraftLinkedSelectionsByUnit([{}]);
    setActiveLinkedUnit(0);
    setDraftPizzaSizeOptionId("");
    setDraftPizzaPortionMode("full");
    setDraftPizzaHalfProductId("");
    setDraftEmpanadaSaleOptionId("");
    setDraftEmpanadaCookingOptionId("");
    setDraftProductQuantity(1);
    setLockedProductQuantity(0);
    setProductDetailPromotionId("");
    setEditingCartItemId("");
  }, []);
  const pushProductDetailHistoryEntry = useCallback(() => {
    if (
      isKioskExperience ||
      typeof window === "undefined" ||
      productDetailHistoryTokenRef.current
    ) {
      return;
    }

    const token = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const currentState =
      window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {};

    window.history.pushState(
      {
        ...currentState,
        [PRODUCT_DETAIL_HISTORY_STATE_KEY]: token,
      },
      document.title,
      window.location.href
    );
    productDetailHistoryTokenRef.current = token;
  }, [isKioskExperience]);
  const consumeProductDetailHistoryEntry = useCallback(() => {
    if (isKioskExperience || typeof window === "undefined") {
      return;
    }

    const token = productDetailHistoryTokenRef.current;
    productDetailHistoryTokenRef.current = "";

    if (
      token &&
      window.history.state?.[PRODUCT_DETAIL_HISTORY_STATE_KEY] === token
    ) {
      window.history.back();
    }
  }, [isKioskExperience]);

  useEffect(() => {
    if (isKioskExperience || typeof window === "undefined") {
      return undefined;
    }

    const handleProductDetailPopState = (event) => {
      const token = productDetailHistoryTokenRef.current;

      if (
        !token ||
        event.state?.[PRODUCT_DETAIL_HISTORY_STATE_KEY] === token
      ) {
        return;
      }

      productDetailHistoryTokenRef.current = "";
      clearProductDetailSelection();
    };

    window.addEventListener("popstate", handleProductDetailPopState);

    return () => {
      window.removeEventListener("popstate", handleProductDetailPopState);
    };
  }, [clearProductDetailSelection, isKioskExperience]);

  const resetKioskExperienceFlow = useCallback(() => {
    if (!isKioskExperience) {
      return;
    }

    clearGuestSessionArtifacts(sessionKey, true);
    persistKioskServiceTypeToStorage(sessionKey, "");
    kioskPaymentFinalizeRef.current = "";
    setKioskHasStarted(false);
    setKioskServiceType("");
    setIsCartOpen(false);
    setCartModalStep("review");
    setCartItems([]);
    setSubmittedOrderSnapshot(null);
    setEditingCartItemId("");
    setIsEditingSubmittedOrder(false);
    setSelectedProductId("");
    setSelectedPromotionId("");
    setKioskCatalogMode("picker");
    setDraftSelection({});
    setDraftLinkedSelectionsByUnit([{}]);
    setActiveLinkedUnit(0);
    setCustomizationsByProduct({});
    setLinkedSelectionsByProduct({});
    setDraftProductQuantity(1);
    setCheckoutMethod(COUNTER_CHECKOUT_METHOD);
    setCheckoutMessage({ type: "", text: "" });
    setCustomerNameDraft("");
    setIsKioskKeyboardOpen(false);
    setIsKioskKeyboardUppercase(true);
    setIsKioskInactivityPromptOpen(false);
    setKioskInactivityCountdown(KIOSK_INACTIVITY_CONFIRMATION_SECONDS);
    setCustomerIdentity({
      customerName: "",
      customerSessionId: buildCustomerSessionId(),
      createdAt: new Date().toISOString(),
    });
    setActiveSubcategoryId("__all__");
    setSelectedWaiterOptions([]);
    setWaiterCustomText("");
    setIsWaiterHelpOpen(false);
  }, [isKioskExperience, sessionKey]);

  const continueKioskAfterInactivityPrompt = useCallback(() => {
    if (!isKioskExperience) {
      return;
    }

    setIsKioskInactivityPromptOpen(false);
    setKioskInactivityCountdown(KIOSK_INACTIVITY_CONFIRMATION_SECONDS);
  }, [isKioskExperience]);

  const cancelKioskAfterInactivityPrompt = useCallback(() => {
    resetKioskExperienceFlow();
  }, [resetKioskExperienceFlow]);

  useEffect(() => {
    if (!isKioskQrPaymentPending) {
      return;
    }

    setIsCartOpen(true);
    setCartModalStep("review");
    setIsKioskInactivityPromptOpen(false);
    setKioskInactivityCountdown(KIOSK_INACTIVITY_CONFIRMATION_SECONDS);
  }, [isKioskQrPaymentPending]);

  const lockQrAccessSession = useCallback(
    (reason = DEFAULT_QR_ACCESS_EXPIRED_MESSAGE, expiresAt = "") => {
      if (isKioskExperience) {
        return;
      }

      const resolvedExpiresAt = expiresAt || new Date().toISOString();
      markQrCustomerEntryExpired(qrToken);
      clearGuestSessionArtifacts(sessionKey, false);
      setQrAccessExpired(true);
      setQrAccessExpiresAt(resolvedExpiresAt);
      setQrAccessExpiredMessage(
        String(reason || "").trim() || DEFAULT_QR_ACCESS_EXPIRED_MESSAGE
      );
      setPayload(null);
      setLoading(false);
      setError("");
      setCartItems([]);
      setSubmittedOrderSnapshot(null);
      setCustomerIdentity(null);
      setCustomerNameDraft("");
      setIsCartOpen(false);
      setCartModalStep("review");
      setCheckoutMessage({ type: "", text: "" });
    },
    [isKioskExperience, sessionKey, qrToken]
  );

  useEffect(() => {
    if (!isKioskExperience) {
      return;
    }

    const shouldResumeKioskFlow = hasKioskCartInStorage(sessionKey);
    setKioskHasStarted(shouldResumeKioskFlow);
    setKioskServiceType(
      shouldResumeKioskFlow ? loadKioskServiceTypeFromStorage(sessionKey) : ""
    );
  }, [isKioskExperience, sessionKey]);

  useEffect(() => {
    if (!isKioskExperience) {
      return;
    }

    persistKioskServiceTypeToStorage(sessionKey, kioskServiceType);
  }, [isKioskExperience, kioskServiceType, sessionKey]);

  useEffect(() => {
    setKioskLanguage(loadKioskLanguageFromStorage(sessionKey));
  }, [sessionKey]);

  useEffect(() => {
    persistKioskLanguageToStorage(sessionKey, kioskLanguage);
  }, [kioskLanguage, sessionKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (
        kioskLanguageMenuRef.current &&
        !kioskLanguageMenuRef.current.contains(event.target)
      ) {
        setIsKioskLanguageMenuOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("touchstart", handlePointerDown);

    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("touchstart", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (
      !isKioskExperience ||
      isKioskQrPaymentPending ||
      !kioskHasStarted ||
      isKioskInactivityPromptOpen ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    let inactivityTimeout = null;

    const restartTimer = () => {
      if (inactivityTimeout) {
        window.clearTimeout(inactivityTimeout);
      }

      inactivityTimeout = window.setTimeout(() => {
        setIsKioskKeyboardOpen(false);
        setIsKioskInactivityPromptOpen(true);
        setKioskInactivityCountdown(KIOSK_INACTIVITY_CONFIRMATION_SECONDS);
      }, KIOSK_INACTIVITY_TIMEOUT_MS);
    };

    const activityEvents = [
      "pointerdown",
      "pointermove",
      "touchstart",
      "keydown",
      "wheel",
      "scroll",
    ];

    restartTimer();
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, restartTimer, { passive: true });
    });

    return () => {
      if (inactivityTimeout) {
        window.clearTimeout(inactivityTimeout);
      }

      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, restartTimer);
      });
    };
  }, [
    isKioskExperience,
    isKioskInactivityPromptOpen,
    isKioskQrPaymentPending,
    kioskHasStarted,
  ]);

  useEffect(() => {
    if (
      !isKioskExperience ||
      !isKioskInactivityPromptOpen ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    if (kioskInactivityCountdown <= 0) {
      resetKioskExperienceFlow();
      return undefined;
    }

    const countdownTimeout = window.setTimeout(() => {
      setKioskInactivityCountdown((previous) => previous - 1);
    }, 1000);

    return () => {
      window.clearTimeout(countdownTimeout);
    };
  }, [
    isKioskExperience,
    isKioskInactivityPromptOpen,
    kioskInactivityCountdown,
    resetKioskExperienceFlow,
  ]);

  useEffect(() => {
    if (isKioskExperience) {
      setQrAccessExpired(false);
      setQrAccessExpiresAt("");
      setQrAccessExpiredMessage(DEFAULT_QR_ACCESS_EXPIRED_MESSAGE);
      return;
    }

    const handleAccess = ({ detail }) => {
      if (detail?.qrToken !== qrToken) return;
      if (!detail.valid) {
        lockQrAccessSession(detail.message);
        return;
      }
      setQrAccessExpiresAt(detail.checkAt || "");
      setCustomerIdentity((previous) => ({ ...previous, customerSessionId: detail.customerSessionToken }));
    };
    window.addEventListener(QR_SESSION_CHANGED, handleAccess);
    return () => window.removeEventListener(QR_SESSION_CHANGED, handleAccess);
  }, [isKioskExperience, qrToken, lockQrAccessSession]);

  useEffect(() => {
    if (isKioskExperience || qrAccessExpired || !qrAccessExpiresAt) {
      return undefined;
    }

    const remainingMs = Date.parse(qrAccessExpiresAt) - Date.now();

    const timeoutId = window.setTimeout(() => {
      validateQrCustomerSession(qrToken).catch((cause) => {
        if (cause.code === 'QR_ACCESS_ENDED') lockQrAccessSession(cause.message);
        else { setPayload(null); setError(cause.message); }
      });
    }, Math.max(0, Number.isFinite(remainingMs) ? remainingMs : 0));

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isKioskExperience, lockQrAccessSession, qrAccessExpiresAt, qrAccessExpired, qrToken]);

  useEffect(() => {
    let active = true;

    const loadPublicMenu = async () => {
      if (!isKioskExperience && qrAccessExpired) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        let nextPayload = null;

        if (isKioskExperience) {
          if (!kioskBranchId) {
            throw new Error("No encontramos la sucursal del totem para abrir la carta.");
          }

          if (isSupabaseConfigured) {
            try {
              nextPayload = await getPublicKioskMenuByBranchId(
                kioskBranchId,
                normalizedKioskStationNumber
              );
            } catch (publicKioskError) {
              if (!canFallbackToLocalKioskSnapshot(publicKioskError)) {
                throw publicKioskError;
              }

              const branchReference =
                kioskBranch ||
                (currentBranch?.id === kioskBranchId
                  ? currentBranch
                  : {
                      id: kioskBranchId,
                      name: "Sucursal activa",
                    });
              const branchProfile = getRestaurantProfile(branchReference);
              let menuSnapshot = null;

              try {
                menuSnapshot = await getBranchMenuSnapshot(kioskBranchId);
              } catch (snapshotError) {
                menuSnapshot = loadLocalMenuSnapshot(kioskBranchId);

                if (!menuSnapshot) {
                  throw publicKioskError;
                }
              }

              nextPayload = buildKioskPayload({
                branch: branchReference,
                branchProfile,
                menuSnapshot,
                kioskStationNumber: normalizedKioskStationNumber,
              });
            }
          } else {
            const branchReference =
              kioskBranch ||
              (currentBranch?.id === kioskBranchId
                ? currentBranch
                : {
                    id: kioskBranchId,
                    name: "Sucursal activa",
                  });
            const branchProfile = getRestaurantProfile(branchReference);
            const menuSnapshot = loadLocalMenuSnapshot(kioskBranchId);

            nextPayload = buildKioskPayload({
              branch: branchReference,
              branchProfile,
              menuSnapshot,
              kioskStationNumber: normalizedKioskStationNumber,
            });
          }
        } else {
          if (!isSupabaseConfigured) {
            throw new Error(
              "La app todavia no tiene Supabase configurado para abrir el menu publico."
            );
          }

          const savedIdentity = loadCustomerIdentityFromStorage(qrToken, resolveGuestSessionStorage(false));
          const access = await validateQrCustomerSession(qrToken, {
            initial: true,
            legacyToken: savedIdentity?.customerSessionId || '',
          });
          if (!active) return;
          if (access.restarted || (savedIdentity?.customerSessionId && savedIdentity.customerSessionId !== getQrCustomerSessionToken(qrToken))) {
            clearGuestSessionArtifacts(sessionKey, false);
            setCartItems([]);
            setSubmittedOrderSnapshot(null);
            setCustomerIdentity({ customerSessionId: getQrCustomerSessionToken(qrToken), customerName: '' });
            setCustomerNameDraft('');
            setIsCartOpen(false);
          }
          nextPayload = await getPublicMenuByQrToken(qrToken);
        }

        const resolvedBranchId = String(nextPayload?.branch?.id || "").trim();

        if (!active) {
          return;
        }

        const normalizedPayload = normalizeMenuPayload(nextPayload);

        setPayload(normalizedPayload);
        persistPublicMenuSnapshot(normalizedPayload);
        setActiveCategoryId(orderQrCategories(normalizedPayload?.categories || [], normalizedPayload?.branch?.qrCategoryOrder)[0]?.id || "");
        setActiveSubcategoryId("__all__");

        if (resolvedBranchId && isSupabaseConfigured) {
          void getPublicBranchPaymentSetup(resolvedBranchId)
            .then((livePaymentSetup) => {
              if (!active || !livePaymentSetup || typeof livePaymentSetup !== "object") {
                return;
              }

              setPayload((currentPayload) =>
                currentPayload
                  ? {
                      ...currentPayload,
                      branch: mergeBranchPaymentSetupIntoPublicBranch(
                        currentPayload.branch || {},
                        livePaymentSetup
                      ),
                    }
                  : currentPayload
              );
            })
            .catch((paymentSetupError) => {
              console.error(
                "No pudimos complementar el estado publico de pagos de la sucursal:",
                paymentSetupError
              );
            });
        }
      } catch (loadError) {
        if (active) {
          const resolvedErrorMessage =
            loadError.message ||
            (isKioskExperience
              ? "No pudimos abrir la carta de este totem."
              : "No pudimos abrir el menu de esta mesa.");

          if (
            !isKioskExperience &&
            isQrAccessTerminationMessage(resolvedErrorMessage)
          ) {
            lockQrAccessSession(resolvedErrorMessage);
            return;
          }

          setError(
            resolvedErrorMessage
          );
          setPayload(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPublicMenu();

    return () => {
      active = false;
    };
  }, [
    currentBranch,
    isKioskExperience,
    kioskBranch,
    kioskBranchId,
    lockQrAccessSession,
    normalizedKioskStationNumber,
    qrAccessExpired,
    qrToken,
    sessionKey,
  ]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setPromotionNow(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const syncViewport = () => {
      setIsCompactViewport(resolveCompactViewport(isKioskExperience));
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);

    return () => {
      window.removeEventListener("resize", syncViewport);
    };
  }, [isKioskExperience]);

  useEffect(() => {
    if (!isKioskExperience && qrAccessExpired) {
      setCartItems([]);
      setSubmittedOrderSnapshot(null);
      setCustomerIdentity(null);
      setCustomerNameDraft("");
      setIsCartOpen(false);
      return;
    }

    const guestSessionStorage = resolveGuestSessionStorage(isKioskExperience);
    const nextCartItems = loadCartFromStorage(sessionKey, guestSessionStorage);
    const nextSubmittedOrderSnapshot = loadSubmittedOrderFromStorage(
      sessionKey,
      guestSessionStorage
    );
    const nextCustomerIdentity =
      loadCustomerIdentityFromStorage(sessionKey, guestSessionStorage) ||
      (sanitizeCustomerName(nextSubmittedOrderSnapshot?.customerName)
        ? {
            customerName: sanitizeCustomerName(nextSubmittedOrderSnapshot?.customerName),
            customerSessionId:
              String(nextSubmittedOrderSnapshot?.customerSessionId || "").trim() ||
              buildCustomerSessionId(),
            createdAt:
              nextSubmittedOrderSnapshot?.submittedAt || new Date().toISOString(),
          }
        : null);

    setCartItems(nextCartItems);
    setSubmittedOrderSnapshot(nextSubmittedOrderSnapshot);
    setCustomerIdentity(nextCustomerIdentity);
    setCustomerNameDraft(
      isKioskExperience ? "" : nextCustomerIdentity?.customerName || ""
    );
    setIsCartOpen(false);
    setCustomizationsByProduct({});
    setLinkedSelectionsByProduct({});
    setEditingCartItemId("");
    setIsEditingSubmittedOrder(false);
    setIsWaiterHelpOpen(false);
    setSelectedWaiterOptions([]);
    setWaiterCustomText("");
    setCheckoutMethod(COUNTER_CHECKOUT_METHOD);
    setCheckoutMessage({ type: "", text: "" });
    setKioskCompletionStage("");
    setKioskCatalogMode("picker");
    menuPaymentFinalizeRef.current = "";
    kioskPaymentFinalizeRef.current = "";
  }, [isKioskExperience, qrAccessExpired, sessionKey]);

  useEffect(() => {
    persistCartToStorage(
      sessionKey,
      cartItems,
      resolveGuestSessionStorage(isKioskExperience)
    );
  }, [cartItems, isKioskExperience, sessionKey]);

  useEffect(() => {
    persistSubmittedOrderToStorage(
      sessionKey,
      submittedOrderSnapshot,
      resolveGuestSessionStorage(isKioskExperience)
    );
  }, [isKioskExperience, sessionKey, submittedOrderSnapshot]);

  useEffect(() => {
    if (!isCartOpen) {
      setIsKioskKeyboardOpen(false);
    }
  }, [isCartOpen]);

  useEffect(
    () => () => {
      if (kioskKeyboardOpenTimeoutRef.current) {
        window.clearTimeout(kioskKeyboardOpenTimeoutRef.current);
        kioskKeyboardOpenTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (typeof window === "undefined" || !payload?.branch?.id) {
      return undefined;
    }

    setOperationalOrdersSnapshot(getOperationalOrdersState({ id: payload.branch.id }));

    const handleOperationalOrdersUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      const currentRestaurantId = payload.branch.id || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      setOperationalOrdersSnapshot(
        event.detail?.payload || getOperationalOrdersState({ id: payload.branch.id })
      );
    };

    const handleStorage = (event) => {
      if (event.key !== buildOperationalOrdersStorageKey(payload.branch.id)) {
        return;
      }

      setOperationalOrdersSnapshot(getOperationalOrdersState({ id: payload.branch.id }));
    };

    window.addEventListener(
      OPERATIONAL_ORDERS_UPDATED_EVENT,
      handleOperationalOrdersUpdate
    );
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        OPERATIONAL_ORDERS_UPDATED_EVENT,
        handleOperationalOrdersUpdate
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [payload?.branch?.id]);

  useEffect(() => {
    const branchId = payload?.branch?.id;

    if (typeof window === "undefined" || !branchId) {
      setPaymentOperationsSnapshot([]);
      return undefined;
    }

    const syncPaymentOperations = () => {
      setPaymentOperationsSnapshot(getPaymentOperations({ id: branchId }));
    };

    const handlePaymentOperationsUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      const currentRestaurantId = branchId || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      setPaymentOperationsSnapshot(
        Array.isArray(event.detail?.operations)
          ? event.detail.operations
          : getPaymentOperations({ id: branchId })
      );
    };

    const handleStorage = (event) => {
      if (!event.key || event.key === buildPaymentOperationsStorageKey(branchId)) {
        syncPaymentOperations();
      }
    };

    syncPaymentOperations();
    window.addEventListener(
      PAYMENT_OPERATIONS_UPDATED_EVENT,
      handlePaymentOperationsUpdate
    );
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        PAYMENT_OPERATIONS_UPDATED_EVENT,
        handlePaymentOperationsUpdate
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [payload?.branch?.id]);

  useEffect(() => {
    const branchId = payload?.branch?.id;
    const payloadIngredientStock =
      payload?.ingredientStock &&
      typeof payload.ingredientStock === "object" &&
      !Array.isArray(payload.ingredientStock)
        ? payload.ingredientStock
        : {};
    const hasPayloadIngredientStock = Object.keys(payloadIngredientStock).length > 0;

    if (
      typeof window === "undefined" ||
      !branchId ||
      payload?.branch?.stockEnabled === false
    ) {
      setIngredientStockSnapshot(
        hasPayloadIngredientStock ? payloadIngredientStock : {}
      );
      return undefined;
    }

    let active = true;
    const branchRef = { id: branchId };

    const applyIngredientStockSnapshot = (stockMap) => {
      if (!active) {
        return;
      }

      const normalizedStockMap =
        stockMap && typeof stockMap === "object" && !Array.isArray(stockMap)
          ? stockMap
          : {};

      if (Object.keys(normalizedStockMap).length > 0) {
        setIngredientStockSnapshot(normalizedStockMap);
        return;
      }

      setIngredientStockSnapshot(
        hasPayloadIngredientStock ? payloadIngredientStock : {}
      );
    };

    const syncLocalStock = () => {
      applyIngredientStockSnapshot(getIngredientStock(branchRef));
    };

    if (hasPayloadIngredientStock) {
      setIngredientStockSnapshot(payloadIngredientStock);
    } else {
      syncLocalStock();
    }

    hydrateIngredientStockFromRemote(branchRef)
      .then((remoteStock) => {
        applyIngredientStockSnapshot(
          remoteStock && typeof remoteStock === "object"
            ? remoteStock
            : getIngredientStock(branchRef)
        );
      })
      .catch(() => {
        syncLocalStock();
      });

    const handleStockUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      const currentRestaurantId = branchId || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      applyIngredientStockSnapshot(
        event.detail?.stockMap && typeof event.detail.stockMap === "object"
          ? event.detail.stockMap
          : getIngredientStock(branchRef)
      );
    };

    const handleStorage = (event) => {
      if (!event.key || event.key === buildIngredientStockStorageKey(branchId)) {
        syncLocalStock();
      }
    };

    window.addEventListener(INGREDIENT_STOCK_UPDATED_EVENT, handleStockUpdate);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", syncLocalStock);

    return () => {
      active = false;
      window.removeEventListener(
        INGREDIENT_STOCK_UPDATED_EVENT,
        handleStockUpdate
      );
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", syncLocalStock);
    };
  }, [payload?.branch?.id, payload?.branch?.stockEnabled, payload?.ingredientStock]);

  useEffect(() => {
    if (!isKioskExperience || !payload?.branch?.id) {
      return undefined;
    }

    let active = true;

    const syncSharedOperationalState = async () => {
      try {
        const result = await hydrateSharedOperationalStateToLocal({ id: payload.branch.id });

        if (!active || !result?.supported) {
          return;
        }

        setOperationalOrdersSnapshot({
          mesasActivas: Array.isArray(result.payload?.mesasActivas)
            ? result.payload.mesasActivas
            : [],
          pedidosHistoricos: Array.isArray(result.payload?.pedidosHistoricos)
            ? result.payload.pedidosHistoricos
            : [],
        });
      } catch (error) {
        console.error("No pudimos sincronizar el totem con el estado compartido:", error);
      }
    };

    void syncSharedOperationalState();
    const intervalId = window.setInterval(syncSharedOperationalState, 4000);
    window.addEventListener("focus", syncSharedOperationalState);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", syncSharedOperationalState);
    };
  }, [isKioskExperience, payload?.branch?.id]);

  useEffect(() => {
    persistCustomerIdentityToStorage(
      sessionKey,
      customerIdentity,
      resolveGuestSessionStorage(isKioskExperience)
    );
  }, [customerIdentity, isKioskExperience, sessionKey]);

  useEffect(() => {
    if (!isKioskExperience) {
      return;
    }

    setCustomerIdentity((prev) => {
      if (prev?.customerSessionId) {
        return prev;
      }

      return {
        customerName: "",
        customerSessionId: buildCustomerSessionId(),
        createdAt: new Date().toISOString(),
      };
    });
  }, [isKioskExperience, sessionKey]);

  const branch = useMemo(
    () => {
      const nextBranch = payload?.branch || {};

      return {
        ...nextBranch,
        name: sanitizeDisplayText(nextBranch?.name || ""),
      };
    },
    [payload?.branch]
  );
  const table = useMemo(
    () => {
      const nextTable = payload?.table || {};

      return {
        ...nextTable,
        label: sanitizeDisplayText(
          nextTable?.label ||
            (isKioskExperience
              ? `Totem ${normalizedKioskStationNumber}`
              : `Mesa ${nextTable?.tableNumber || ""}`)
        ),
      };
    },
    [isKioskExperience, normalizedKioskStationNumber, payload?.table]
  );
  const menu = payload?.menu || {};
  const customerName = customerIdentity?.customerName || "";
  const branchName = branch?.name || "";
  const refreshPendingTableAccounts = useCallback(async ({ throwOnError = false } = {}) => {
    if (isKioskExperience || !qrToken || !table?.id) {
      setPendingTableAccounts([]);
      setSelectedPendingAccountIds([]);
      return [];
    }

    setPendingTableAccountsLoading(true);

    try {
      const result = await getPublicQrPendingAccounts(qrToken);
      const nextAccounts = Array.isArray(result?.accounts) ? result.accounts : [];
      const nextAccountIds = new Set(nextAccounts.map((account) => account.orderId));

      setPendingTableAccounts(nextAccounts);
      setSelectedPendingAccountIds((currentIds) =>
        currentIds.filter((orderId) => nextAccountIds.has(orderId))
      );
      setPendingTableAccountsError("");
      return nextAccounts;
    } catch (pendingAccountsError) {
      setPendingTableAccountsError(
        pendingAccountsError instanceof Error ? pendingAccountsError.message : ""
      );

      if (throwOnError) {
        throw pendingAccountsError;
      }

      return [];
    } finally {
      setPendingTableAccountsLoading(false);
    }
  }, [isKioskExperience, qrToken, table?.id]);

  useEffect(() => {
    if (isKioskExperience || !qrToken || !table?.id) {
      return undefined;
    }

    let active = true;
    const syncPendingAccounts = async () => {
      if (!active) {
        return;
      }
      await refreshPendingTableAccounts();
    };

    void syncPendingAccounts();
    const intervalId = window.setInterval(syncPendingAccounts, 10000);
    window.addEventListener("focus", syncPendingAccounts);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", syncPendingAccounts);
    };
  }, [isKioskExperience, qrToken, refreshPendingTableAccounts, table?.id]);

  const isStockFeatureEnabled = branch?.stockEnabled !== false;
  const hasOperationalIngredientStockSnapshot = useMemo(
    () =>
      Boolean(ingredientStockSnapshot) &&
      typeof ingredientStockSnapshot === "object" &&
      !Array.isArray(ingredientStockSnapshot) &&
      Object.keys(ingredientStockSnapshot).length > 0,
    [ingredientStockSnapshot]
  );
  const shouldEnforceOperationalStockValidation =
    isStockFeatureEnabled && hasOperationalIngredientStockSnapshot;
  const isWaiterFeatureEnabled = branch?.waiterEnabled !== false;
  const isAiFeatureEnabled = branch?.aiEnabled !== false;
  const t = useCallback(
    (key, variables = {}) => getKioskTranslation(kioskLanguage, key, variables),
    [kioskLanguage]
  );
  const { tx, status: translationStatus, retry: retryTranslation } = useQrMenuTranslation({
    language: kioskLanguage, qrToken, payload, promotions,
    enabled: !isKioskExperience && !qrAccessExpired,
  });
  const formatKioskCount = useCallback(
    (count, singularKey = "option", pluralKey = "options") =>
      `${count} ${t(count === 1 ? singularKey : pluralKey)}`,
    [t]
  );
  const tableTitle =
    table?.label || (table?.tableNumber ? `Mesa ${table.tableNumber}` : "");
  const displayStationLabel =
    table?.label ||
    (isKioskExperience
      ? `Totem ${normalizedKioskStationNumber}`
      : `Mesa ${table?.tableNumber || ""}`);
  const browserUserAgent = useMemo(() => resolveBrowserUserAgent(), []);
  const isSamsungInternetForAi = useMemo(
    () => isSamsungInternetBrowser(browserUserAgent),
    [browserUserAgent]
  );
  const isAndroidChromeForAi = useMemo(
    () => isAndroidChromeBrowser(browserUserAgent),
    [browserUserAgent]
  );
  const isIosChromeForAi = useMemo(
    () => isIosChromeBrowser(browserUserAgent),
    [browserUserAgent]
  );
  const isAppleAiVoiceBrowser = useMemo(
    () => isAppleTouchDevice(browserUserAgent),
    [browserUserAgent]
  );
  const shouldRedirectAiVoiceToChrome = Boolean(
    !isKioskExperience && isSamsungInternetForAi && !isAndroidChromeForAi
  );
  const shouldOfferAiVoiceChromeOption = Boolean(
    !isKioskExperience && isAppleAiVoiceBrowser && !isIosChromeForAi
  );
  const aiSpeechRecognitionConstructor = useMemo(
    () => resolveAiSpeechRecognitionConstructor(),
    []
  );
  const isAiVoiceInputSupported = Boolean(aiSpeechRecognitionConstructor);
  const isAiMicrophonePermissionSupported = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }

    if (
      isMozzoAndroidBridgeAvailable() &&
      canRequestMozzoAndroidMicrophonePermission()
    ) {
      aiMicrophonePermissionGrantedRef.current = true;
      return true;
    }

    if (typeof navigator === "undefined") {
      return false;
    }

    const mediaDevices = navigator.mediaDevices;
    return Boolean(
      mediaDevices && typeof mediaDevices.getUserMedia === "function"
    );
  }, []);
  const isAiVoiceOutputSupported = hasBrowserSpeechSynthesis();
  const openCurrentPageInChrome = useCallback(() => {
    if (typeof window === "undefined") {
      return false;
    }

    const currentUrl = window.location.href;
    const targetUrl = isAppleAiVoiceBrowser
      ? buildIosChromeUrl(currentUrl)
      : buildAndroidChromeIntentUrl(currentUrl);

    if (!targetUrl) {
      return false;
    }

    const didNavigate = navigateToExternalUrl(targetUrl);

    if (!didNavigate) {
      window.location.href = targetUrl;
    }

    if (isAppleAiVoiceBrowser) {
      const legacyUrl = buildIosChromeLegacyUrl(currentUrl);

      if (legacyUrl && legacyUrl !== targetUrl) {
        window.setTimeout(() => {
          if (document.visibilityState === "visible") {
            const didNavigateLegacy = navigateToExternalUrl(legacyUrl);

            if (!didNavigateLegacy) {
              window.location.href = legacyUrl;
            }
          }
        }, 650);
      }
    }

    return true;
  }, [isAppleAiVoiceBrowser]);
  const promptAiVoiceChromeOption = useCallback(() => {
    if (typeof window === "undefined" || !shouldOfferAiVoiceChromeOption) {
      return false;
    }

    const shouldOpenInChrome = window.confirm(
      "Estas usando Safari en iPhone. Si quieres, MozzoApp puede abrir esta misma pantalla en Chrome para probar la voz. ¿Quieres abrirlo en Chrome ahora?"
    );

    if (!shouldOpenInChrome) {
      return false;
    }

    setCheckoutMessage({
      type: "success",
      text: "Abrimos esta pagina en Chrome para continuar con la IA.",
    });
    openCurrentPageInChrome();
    return true;
  }, [openCurrentPageInChrome, shouldOfferAiVoiceChromeOption]);
  const createAiConversationMessage = useCallback(
    (role, text, extra = {}) => ({
      id: `${role}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      role: role === "assistant" ? "assistant" : "user",
      text:
        role === "assistant"
          ? sanitizeAiAssistantReplyText(text)
          : sanitizeDisplayText(text),
      suggestions: Array.isArray(extra?.suggestions) ? extra.suggestions : [],
      relatedProducts: Array.isArray(extra?.relatedProducts)
        ? extra.relatedProducts
        : [],
      fallback: extra?.fallback === true,
      source: extra?.source || "",
      degradedReason: extra?.degradedReason || "",
      createdAt: new Date().toISOString(),
    }),
    []
  );
  const resolveAiSpeechVoice = useCallback(() => {
    if (!hasBrowserSpeechSynthesis()) {
      aiSpeechVoiceRef.current = null;
      return null;
    }

    try {
      const availableVoices = Array.isArray(window.speechSynthesis.getVoices?.())
        ? window.speechSynthesis.getVoices()
        : [];

      if (!availableVoices.length) {
        aiSpeechVoiceRef.current = null;
        return null;
      }

      const scoreVoice = (voice = {}) => {
        const normalizedLang = String(voice?.lang || "")
          .trim()
          .toLowerCase();
        const normalizedName = String(voice?.name || "")
          .trim()
          .toLowerCase();

        let score = 0;

        AI_ASSISTANT_SPEECH_LOCALE_CANDIDATES.forEach((candidate, index) => {
          if (normalizedLang === candidate.toLowerCase()) {
            score += 120 - index * 5;
          }
        });

        if (normalizedLang.startsWith("es")) {
          score += 80;
        }

        if (/spanish|espanol|español|monica|paulina|jorge|juan|carlos|soledad/i.test(normalizedName)) {
          score += 25;
        }

        if (voice?.default) {
          score += 10;
        }

        return score;
      };

      const selectedVoice = [...availableVoices].sort(
        (leftVoice, rightVoice) => scoreVoice(rightVoice) - scoreVoice(leftVoice)
      )[0] || null;

      aiSpeechVoiceRef.current = selectedVoice;
      return selectedVoice;
    } catch (error) {
      console.warn("No pudimos resolver una voz para la IA.", error);
      aiSpeechVoiceRef.current = null;
      return null;
    }
  }, []);
  const buildAiSpeechChunks = useCallback((value = "") => {
    const safeValue = String(value || "")
      .replace(/\s+/g, " ")
      .trim();

    if (!safeValue) {
      return [];
    }

    const maxChunkLength = 170;
    const sentenceLikeSegments = safeValue
      .split(/(?<=[.!?;:])\s+/)
      .map((segment) => segment.trim())
      .filter(Boolean);

    const segmentsToProcess = sentenceLikeSegments.length
      ? sentenceLikeSegments
      : [safeValue];
    const chunks = [];

    segmentsToProcess.forEach((segment) => {
      if (segment.length <= maxChunkLength) {
        chunks.push(segment);
        return;
      }

      const words = segment.split(/\s+/).filter(Boolean);
      let currentChunk = "";

      words.forEach((word) => {
        const nextChunk = currentChunk ? `${currentChunk} ${word}` : word;

        if (nextChunk.length <= maxChunkLength) {
          currentChunk = nextChunk;
          return;
        }

        if (currentChunk) {
          chunks.push(currentChunk);
        }

        currentChunk = word;
      });

      if (currentChunk) {
        chunks.push(currentChunk);
      }
    });

    return chunks.filter(Boolean);
  }, []);
  const stopAiVoiceOutput = useCallback(() => {
    aiSpeechSequenceRef.current += 1;

    if (aiSpeechRetryTimeoutRef.current) {
      window.clearTimeout(aiSpeechRetryTimeoutRef.current);
      aiSpeechRetryTimeoutRef.current = null;
    }

    if (aiSpeechStartWatchdogRef.current) {
      window.clearTimeout(aiSpeechStartWatchdogRef.current);
      aiSpeechStartWatchdogRef.current = null;
    }

    if (!hasBrowserSpeechSynthesis()) {
      return;
    }

    try {
      if (typeof window.speechSynthesis.resume === "function") {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.cancel();
    } catch (error) {
      console.warn("No pudimos detener la lectura de la IA.", error);
    }
  }, []);
  const primeAiSpeechOutputFromUserGesture = useCallback(() => {
    if (!hasBrowserSpeechSynthesis()) {
      return false;
    }

    try {
      const selectedVoice = resolveAiSpeechVoice();

      if (typeof window.speechSynthesis.resume === "function") {
        window.speechSynthesis.resume();
      }

      if (aiSpeechOutputPrimedRef.current) {
        return true;
      }

      const warmupUtterance = new SpeechSynthesisUtterance(".");

      if (selectedVoice) {
        warmupUtterance.voice = selectedVoice;
        warmupUtterance.lang =
          selectedVoice.lang || AI_ASSISTANT_FIXED_SPEECH_LOCALE;
      } else {
        warmupUtterance.lang = AI_ASSISTANT_FIXED_SPEECH_LOCALE;
      }

      warmupUtterance.volume = 0;
      warmupUtterance.rate = 1;
      warmupUtterance.pitch = 1;

      aiSpeechOutputPrimedRef.current = true;
      window.speechSynthesis.speak(warmupUtterance);
      return true;
    } catch (error) {
      console.warn("No pudimos desbloquear la voz de la IA.", error);
      aiSpeechOutputPrimedRef.current = false;
      return false;
    }
  }, [resolveAiSpeechVoice]);
  useEffect(() => {
    if (!hasBrowserSpeechSynthesis()) {
      return undefined;
    }

    const speechSynthesisApi = window.speechSynthesis;
    const syncVoices = () => {
      resolveAiSpeechVoice();
    };

    syncVoices();

    if (typeof speechSynthesisApi.addEventListener === "function") {
      speechSynthesisApi.addEventListener("voiceschanged", syncVoices);

      return () => {
        speechSynthesisApi.removeEventListener("voiceschanged", syncVoices);
      };
    }

    const previousVoicesChangedHandler = speechSynthesisApi.onvoiceschanged;
    speechSynthesisApi.onvoiceschanged = syncVoices;

    return () => {
      speechSynthesisApi.onvoiceschanged = previousVoicesChangedHandler || null;
    };
  }, [resolveAiSpeechVoice]);
  const clearAiVoiceSilenceTimeout = useCallback(() => {
    if (aiVoiceSilenceTimeoutRef.current) {
      window.clearTimeout(aiVoiceSilenceTimeoutRef.current);
      aiVoiceSilenceTimeoutRef.current = null;
    }
  }, []);
  const scheduleAiVoiceAutoStop = useCallback(
    (delayMs = 2400) => {
      clearAiVoiceSilenceTimeout();

      if (typeof window === "undefined") {
        return;
      }

      aiVoiceSilenceTimeoutRef.current = window.setTimeout(() => {
        const recognition = aiSpeechRecognitionRef.current;

        if (!recognition) {
          aiVoiceSilenceTimeoutRef.current = null;
          return;
        }

        try {
          recognition.stop();
        } catch (error) {
          console.warn("No pudimos cerrar la escucha automatica de la IA.", error);
        } finally {
          aiVoiceSilenceTimeoutRef.current = null;
        }
      }, Math.max(1200, Number(delayMs || 0)));
    },
    [clearAiVoiceSilenceTimeout]
  );
  const requestAiMicrophonePermission = useCallback(async () => {
    if (typeof navigator === "undefined") {
      return false;
    }

    if (!isAiMicrophonePermissionSupported) {
      return false;
    }

    const mediaDevices = navigator.mediaDevices;

    if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function") {
      setAiVoiceStatus(
        "Este navegador no permite pedir permiso de micrófono desde el chat IA."
      );
      return false;
    }

    try {
      const permissionApi = navigator.permissions;

      if (permissionApi && typeof permissionApi.query === "function") {
        const permissionStatus = await permissionApi.query({
          name: "microphone",
        });

        if (permissionStatus?.state === "granted") {
          aiMicrophonePermissionGrantedRef.current = true;
          setAiVoiceStatus(
            "Micrófono habilitado. Cuando quieras, toca Hablar para dictarle a la IA."
          );
          return true;
        }
      }
    } catch (error) {
      console.warn("No pudimos consultar el estado del micrófono.", error);
    }

    try {
      const mediaStream = await mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      mediaStream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.warn("No pudimos cerrar una pista de micrófono.", error);
        }
      });

      setAiVoiceStatus(
        "Micrófono habilitado. Cuando quieras, toca Hablar para dictarle a la IA."
      );
      aiMicrophonePermissionGrantedRef.current = true;
      return true;
    } catch (error) {
      aiMicrophonePermissionGrantedRef.current = false;
      setAiVoiceStatus(resolveAiSpeechErrorMessage(error?.name || error?.message));
      return false;
    }
  }, [isAiMicrophonePermissionSupported]);
  const requestAiMicrophonePermissionFromUserGesture = useCallback(async () => {
    if (typeof navigator === "undefined") {
      return false;
    }

    if (!isAiMicrophonePermissionSupported) {
      return false;
    }

    const mediaDevices = navigator.mediaDevices;

    if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function") {
      setAiVoiceStatus(
        "Este navegador no permite pedir permiso de microfono desde el chat IA."
      );
      return false;
    }

    try {
      if (
        isMozzoAndroidBridgeAvailable() &&
        canRequestMozzoAndroidMicrophonePermission()
      ) {
        const androidPermissionGranted =
          await requestMozzoAndroidMicrophonePermission();

        if (androidPermissionGranted === false) {
          setAiVoiceStatus(
            "Mozzo Kiosk no obtuvo permiso para usar el microfono en esta tablet."
          );
          return false;
        }
      }

      const mediaStream = await mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      mediaStream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.warn("No pudimos cerrar una pista de microfono.", error);
        }
      });

      setAiVoiceStatus(
        "Microfono habilitado. Cuando quieras, toca Hablar para dictarle a la IA."
      );
      aiMicrophonePermissionGrantedRef.current = true;
      return true;
    } catch (error) {
      aiMicrophonePermissionGrantedRef.current = false;
      setAiVoiceStatus(
        resolveAiMicrophonePermissionMessage(error?.name || error?.message)
      );
      return false;
    }
  }, [isAiMicrophonePermissionSupported]);
  const speakAiAssistantText = useCallback(
    (text = "") => {
      const safeText = sanitizeAiAssistantReplyText(text)
        .replace(/\$\s*([0-9][0-9.,]*)/g, "$1 pesos argentinos")
        .replace(/\bUSD\s*([0-9][0-9.,]*)/gi, "$1 pesos argentinos")
        .replace(/\bdolares\b/gi, "pesos argentinos")
        .replace(/\bdólares\b/gi, "pesos argentinos")
        .replace(/\s+/g, " ")
        .trim();

      if (!safeText) {
        return;
      }

      const speechChunks = buildAiSpeechChunks(safeText);

      if (!speechChunks.length) {
        return;
      }

      if (!isAiVoiceOutputSupported) {
        setAiVoiceStatus("La lectura en voz alta no está disponible aquí.");
        return;
      }

      if (shouldRedirectAiVoiceToChrome) {
        setCheckoutMessage({
          type: "success",
          text: "Abrimos esta pagina en Chrome para usar la voz de la IA.",
        });
        setAiVoiceStatus(
          "Este navegador no reproduce bien la voz de la IA. Abrimos Chrome para continuar."
        );
        openCurrentPageInChrome();
        return;
      }

      setAiSpeechAwaitingManualPlay(false);

      const performSpeechAttempt = (attemptNumber = 0) => {
        const speechSequenceId = aiSpeechSequenceRef.current + 1;
        aiSpeechSequenceRef.current = speechSequenceId;

        const speakChunkAtIndex = (chunkIndex = 0) => {
          if (aiSpeechSequenceRef.current !== speechSequenceId) {
            return;
          }

          const currentChunk = speechChunks[chunkIndex];

          if (!currentChunk) {
            setAiVoiceStatus("Listo. Puedes tocar IA otra vez si quieres seguir.");
            return;
          }

          let speechStarted = false;

          try {
            const selectedVoice = resolveAiSpeechVoice();
            const utterance = new SpeechSynthesisUtterance(currentChunk);

            if (selectedVoice) {
              utterance.voice = selectedVoice;
              utterance.lang =
                selectedVoice.lang || AI_ASSISTANT_FIXED_SPEECH_LOCALE;
            } else {
              utterance.lang = AI_ASSISTANT_FIXED_SPEECH_LOCALE;
            }

            utterance.rate = isAppleAiVoiceBrowser ? 0.96 : 1;
            utterance.pitch = 1;
            utterance.volume = 1;
            utterance.onstart = () => {
              if (aiSpeechSequenceRef.current !== speechSequenceId) {
                return;
              }

              speechStarted = true;
              setAiSpeechAwaitingManualPlay(false);
              if (aiSpeechStartWatchdogRef.current) {
                window.clearTimeout(aiSpeechStartWatchdogRef.current);
                aiSpeechStartWatchdogRef.current = null;
              }
              setAiVoiceStatus("Reproduciendo la respuesta de la IA...");
            };
            utterance.onend = () => {
              if (aiSpeechSequenceRef.current !== speechSequenceId) {
                return;
              }

              if (aiSpeechRetryTimeoutRef.current) {
                window.clearTimeout(aiSpeechRetryTimeoutRef.current);
                aiSpeechRetryTimeoutRef.current = null;
              }
              if (aiSpeechStartWatchdogRef.current) {
                window.clearTimeout(aiSpeechStartWatchdogRef.current);
                aiSpeechStartWatchdogRef.current = null;
              }

              if (chunkIndex < speechChunks.length - 1) {
                aiSpeechRetryTimeoutRef.current = window.setTimeout(() => {
                  speakChunkAtIndex(chunkIndex + 1);
                }, isAppleAiVoiceBrowser ? 80 : 35);
                return;
              }

              setAiVoiceStatus("Listo. Puedes tocar IA otra vez si quieres seguir.");
            };
            utterance.onerror = () => {
              if (aiSpeechSequenceRef.current !== speechSequenceId) {
                return;
              }

              if (aiSpeechStartWatchdogRef.current) {
                window.clearTimeout(aiSpeechStartWatchdogRef.current);
                aiSpeechStartWatchdogRef.current = null;
              }
              if (attemptNumber === 0 && isAppleAiVoiceBrowser) {
                aiSpeechRetryTimeoutRef.current = window.setTimeout(() => {
                  performSpeechAttempt(1);
                }, 180);
                return;
              }

              setAiSpeechAwaitingManualPlay(true);
              setAiVoiceStatus("No pudimos reproducir esa respuesta en voz alta.");
            };

            if (typeof window.speechSynthesis.resume === "function") {
              window.speechSynthesis.resume();
            }

            window.speechSynthesis.speak(utterance);

            if (isAppleAiVoiceBrowser) {
              aiSpeechStartWatchdogRef.current = window.setTimeout(() => {
                if (speechStarted || aiSpeechSequenceRef.current !== speechSequenceId) {
                  return;
                }

                setAiSpeechAwaitingManualPlay(true);
                setAiVoiceStatus(
                  "Safari no reprodujo la voz automaticamente. Toca Escuchar respuesta."
                );
              }, 1400);
            }
          } catch (error) {
            console.warn("No pudimos reproducir la respuesta de la IA.", error);

            if (aiSpeechSequenceRef.current !== speechSequenceId) {
              return;
            }

            if (aiSpeechStartWatchdogRef.current) {
              window.clearTimeout(aiSpeechStartWatchdogRef.current);
              aiSpeechStartWatchdogRef.current = null;
            }

            if (attemptNumber === 0 && isAppleAiVoiceBrowser) {
              aiSpeechRetryTimeoutRef.current = window.setTimeout(() => {
                performSpeechAttempt(1);
              }, 180);
              return;
            }

            setAiSpeechAwaitingManualPlay(true);
            setAiVoiceStatus("No pudimos reproducir esa respuesta en voz alta.");
          }
        };

        speakChunkAtIndex(0);
      };

      try {
        primeAiSpeechOutputFromUserGesture();
        stopAiVoiceOutput();

        if (isAppleAiVoiceBrowser) {
          aiSpeechRetryTimeoutRef.current = window.setTimeout(() => {
            performSpeechAttempt(0);
          }, 90);
          return;
        }

        performSpeechAttempt(0);
      } catch (error) {
        console.warn("No pudimos iniciar la voz de la IA.", error);
        setAiVoiceStatus("No pudimos reproducir esa respuesta en voz alta.");
      }
    },
    [
      buildAiSpeechChunks,
      isAiVoiceOutputSupported,
      isAppleAiVoiceBrowser,
      openCurrentPageInChrome,
      primeAiSpeechOutputFromUserGesture,
      resolveAiSpeechVoice,
      shouldRedirectAiVoiceToChrome,
      stopAiVoiceOutput,
    ]
  );
  const stopAiVoiceInput = useCallback(
    ({ cancel = false, clearDraft = false } = {}) => {
      aiVoiceShouldSubmitRef.current = !cancel;
      clearAiVoiceSilenceTimeout();

      const recognition = aiSpeechRecognitionRef.current;

      if (recognition) {
        try {
          recognition.stop();
        } catch (error) {
          console.warn("No pudimos detener la escucha de la IA.", error);
        }
      }

      if (cancel) {
        aiVoiceDraftRef.current = "";
        aiVoiceFinalDraftRef.current = "";
        aiVoiceShouldSubmitRef.current = false;

        if (clearDraft) {
          setAiQuestionDraft("");
        }
      }

      setIsAiListening(false);
    },
    [clearAiVoiceSilenceTimeout]
  );
  const branchProfile = useMemo(
    () => {
      if (!branch?.id) {
        return null;
      }

      const localProfile = getRestaurantProfile({
        id: branch.id,
        name: branch.name || "",
        phone: branch.phone || "",
        address: branch.address || "",
        description: branch.description || "",
      });

      return {
        ...localProfile,
        // Si la lectura publica todavia no trae estas imagenes, respetamos la
        // ultima version local guardada para no vaciar el ingreso del kiosk.
        kioskDineInImageUrl:
          branch?.kioskDineInImageUrl ||
          branch?.kiosk_dine_in_image_url ||
          localProfile?.kioskDineInImageUrl ||
          "",
        kioskTakeawayImageUrl:
          branch?.kioskTakeawayImageUrl ||
          branch?.kiosk_takeaway_image_url ||
          localProfile?.kioskTakeawayImageUrl ||
          "",
        imagenPortada: branch?.coverUrl || localProfile?.imagenPortada || "",
        showPrices:
          typeof branch?.showPrices === "boolean"
            ? branch.showPrices
            : localProfile?.showPrices !== false,
        branding: {
          ...(localProfile?.branding || {}),
          logoPrimaryUrl:
            branch?.logoUrl || localProfile?.branding?.logoPrimaryUrl || "",
          logoCompactUrl:
            branch?.logoCompactUrl ||
            branch?.logoUrl ||
            localProfile?.branding?.logoCompactUrl ||
            localProfile?.branding?.logoPrimaryUrl ||
            "",
          iconLogoUrl:
            branch?.iconLogoUrl ||
            branch?.logoCompactUrl ||
            branch?.logoUrl ||
            localProfile?.branding?.iconLogoUrl ||
            "",
          loadingLogoUrl:
            branch?.loadingLogoUrl ||
            branch?.iconLogoUrl ||
            branch?.logoCompactUrl ||
            branch?.logoUrl ||
            localProfile?.branding?.loadingLogoUrl ||
            "",
          primaryColor:
            branch?.primaryColor || localProfile?.branding?.primaryColor || "",
          secondaryColor:
            branch?.secondaryColor || localProfile?.branding?.secondaryColor || "",
          fontFamily:
            branch?.fontFamily || localProfile?.branding?.fontFamily || "",
          loadingAnimation:
            branch?.loadingAnimation ||
            localProfile?.branding?.loadingAnimation ||
            "spin",
        },
        kitchenMode:
          branch?.kitchenMode === "screen" || branch?.kitchenMode === "printer"
            ? branch.kitchenMode
            : localProfile.kitchenMode,
      };
    },
    [
      branch?.address,
      branch?.coverUrl,
      branch?.description,
      branch?.fontFamily,
      branch?.id,
      branch?.iconLogoUrl,
      branch?.kiosk_dine_in_image_url,
      branch?.kiosk_takeaway_image_url,
      branch?.kitchenMode,
      branch?.kioskDineInImageUrl,
      branch?.kioskTakeawayImageUrl,
      branch?.loadingAnimation,
      branch?.loadingLogoUrl,
      branch?.logoCompactUrl,
      branch?.logoUrl,
      branch?.name,
      branch?.phone,
      branch?.primaryColor,
      branch?.secondaryColor,
      branch?.showPrices,
    ]
  );
  const branchPaymentSetup = useMemo(
    () =>
      buildBranchPaymentSetupSnapshot({
        ...(branchProfile || {}),
        ...(branch || {}),
      }),
    [branch, branchProfile]
  );
  const configuredMenuQrWalletProviders = useMemo(
    () =>
      getOperationalMenuQrWalletProviders(
        branch?.menuQrWalletProviders || branchProfile?.menuQrWalletProviders || []
      ),
    [branch?.menuQrWalletProviders, branchProfile?.menuQrWalletProviders]
  );
  const menuQrWalletProviders = useMemo(
    () =>
      getCompatibleMenuQrWalletProviders(
        branch?.menuQrWalletProviders || branchProfile?.menuQrWalletProviders || [],
        branchPaymentSetup.paymentProvider
      ),
    [
      branch?.menuQrWalletProviders,
      branchProfile?.menuQrWalletProviders,
      branchPaymentSetup.paymentProvider,
    ]
  );
  const incompatibleMenuQrWalletProvidersCount = Math.max(
    0,
    configuredMenuQrWalletProviders.length - menuQrWalletProviders.length
  );
  const compatibleMenuQrWalletProviderIds = useMemo(
    () => new Set(menuQrWalletProviders.map((provider) => provider.id)),
    [menuQrWalletProviders]
  );
  const activeMenuQrWalletProviderLabel =
    menuQrWalletProviders[0]?.label ||
    formatMenuQrCheckoutProviderLabel(branchPaymentSetup.paymentProvider);
  const selectedMenuWalletProvider = useMemo(
    () =>
      findMenuQrWalletProvider(menuQrWalletProviders, selectedMenuWalletProviderId) ||
      menuQrWalletProviders[0] ||
      null,
    [menuQrWalletProviders, selectedMenuWalletProviderId]
  );
  const selectedPendingTableAccounts = useMemo(() => {
    const selectedIds = new Set(selectedPendingAccountIds);
    return pendingTableAccounts.filter((account) => selectedIds.has(account.orderId));
  }, [pendingTableAccounts, selectedPendingAccountIds]);
  const selectedPendingTableAccountsTotal = useMemo(
    () =>
      selectedPendingTableAccounts.reduce(
        (total, account) => total + Number(account.digitalTotalAmount ?? account.totalAmount ?? 0),
        0
      ),
    [selectedPendingTableAccounts]
  );
  const counterCheckoutAvailable = useMemo(
    () =>
      isKioskExperience
        ? branchPaymentSetup.kioskCounterEnabled &&
          branchPaymentSetup.manualOperationsAvailable
        : branchPaymentSetup.qrPayLaterEnabled &&
          branchPaymentSetup.manualOperationsAvailable,
    [
      branchPaymentSetup.kioskCounterEnabled,
      branchPaymentSetup.manualOperationsAvailable,
      branchPaymentSetup.qrPayLaterEnabled,
      isKioskExperience,
    ]
  );
  const digitalCheckoutAvailable = useMemo(
    () =>
      !tabletSessionKey &&
      branchPaymentSetup.operationalAccessAllowed &&
      (isKioskExperience
        ? branchPaymentSetup.kioskDigitalConfigured
        : branchPaymentSetup.qrDigitalConfigured &&
          menuQrWalletProviders.length > 0),
    [
      branchPaymentSetup.kioskDigitalConfigured,
      branchPaymentSetup.operationalAccessAllowed,
      branchPaymentSetup.qrDigitalConfigured,
      isKioskExperience,
      menuQrWalletProviders.length,
      tabletSessionKey,
    ]
  );
  const checkoutMethodOptions = useMemo(() => {
    if (!isKioskExperience) {
      const nextOptions = {};

      if (counterCheckoutAvailable) {
        nextOptions[COUNTER_CHECKOUT_METHOD] = t("orderNowPayLater");
      }

      if (digitalCheckoutAvailable) {
        nextOptions.deep_link = t("deepLink");
      }

      return nextOptions;
    }

    const nextOptions = {};

    if (counterCheckoutAvailable) {
      nextOptions[COUNTER_CHECKOUT_METHOD] = t("payAtCounter");
    }

    if (digitalCheckoutAvailable) {
      nextOptions[KIOSK_QR_CHECKOUT_METHOD] = t("payWithPhone");
    }

    return nextOptions;
  }, [
    counterCheckoutAvailable,
    digitalCheckoutAvailable,
    isKioskExperience,
    t,
  ]);
  const anyCheckoutMethodAvailable = Object.keys(checkoutMethodOptions).length > 0;
  const checkoutMethodBlockingNotice = useMemo(() => {
    if (!anyCheckoutMethodAvailable) {
      if (tabletSessionKey && branchPaymentSetup.manualOperationsAvailable) {
        return "Para tomar pedidos en esta tablet, la sucursal debe tener habilitado Pedir y pagar despues.";
      }
      if (!branchPaymentSetup.manualOperationsAvailable) {
        return (
          branchPaymentSetup.operationalBlockingReason ||
          branchPaymentSetup.manualOperationsBlockingReason ||
          "La sucursal no tiene creditos MZ disponibles para seguir operando en cobros manuales."
        );
      }

      if (isKioskExperience) {
        return (
          branchPaymentSetup.kioskQrBlockingReason ||
          branchPaymentSetup.operationalBlockingReason ||
          "Este totem no tiene ningun medio de cobro disponible en este momento."
        );
      }

      if (
        branchPaymentSetup.qrDigitalConfigured &&
        menuQrWalletProviders.length === 0
      ) {
        return configuredMenuQrWalletProviders.length > 0
          ? t("menuWalletsIncompatible", {
              provider: activeMenuQrWalletProviderLabel,
            })
          : t("menuWalletsMissing");
      }

      return (
        branchPaymentSetup.qrDeepLinkBlockingReason ||
        branchPaymentSetup.operationalBlockingReason ||
        "Esta mesa no tiene un medio de cobro operativo disponible en este momento."
      );
    }

    const onlyCounterCheckoutAvailable =
      Object.keys(checkoutMethodOptions).length === 1 &&
      Boolean(checkoutMethodOptions[COUNTER_CHECKOUT_METHOD]);

    if (!onlyCounterCheckoutAvailable) {
      return "";
    }

    if (isKioskExperience) {
      return branchPaymentSetup.kioskQrBlockingReason || "";
    }

    if (
      branchPaymentSetup.qrDigitalConfigured &&
      menuQrWalletProviders.length === 0
    ) {
      return configuredMenuQrWalletProviders.length > 0
        ? t("menuWalletsIncompatible", {
            provider: activeMenuQrWalletProviderLabel,
          })
        : t("menuWalletsMissing");
    }

    return branchPaymentSetup.qrDeepLinkBlockingReason || "";
  }, [
    activeMenuQrWalletProviderLabel,
    configuredMenuQrWalletProviders.length,
    branchPaymentSetup.kioskQrBlockingReason,
    branchPaymentSetup.manualOperationsAvailable,
    branchPaymentSetup.manualOperationsBlockingReason,
    branchPaymentSetup.operationalBlockingReason,
    branchPaymentSetup.qrDigitalConfigured,
    branchPaymentSetup.qrDeepLinkBlockingReason,
    anyCheckoutMethodAvailable,
    checkoutMethodOptions,
    isKioskExperience,
    menuQrWalletProviders.length,
    tabletSessionKey,
    t,
  ]);
  const isOrderingLocked = !anyCheckoutMethodAvailable;

  useEffect(() => {
    if (isKioskExperience) {
      return;
    }

    if (menuQrWalletProviders.length === 0) {
      if (selectedMenuWalletProviderId) {
        setSelectedMenuWalletProviderId("");
      }
      return;
    }

    if (
      !menuQrWalletProviders.some(
        (provider) => provider.id === selectedMenuWalletProviderId
      )
    ) {
      setSelectedMenuWalletProviderId(menuQrWalletProviders[0].id);
    }
  }, [
    isKioskExperience,
    menuQrWalletProviders,
    selectedMenuWalletProviderId,
  ]);

  useEffect(() => {
    if (!checkoutMethodOptions[checkoutMethod]) {
      setCheckoutMethod(Object.keys(checkoutMethodOptions)[0] || COUNTER_CHECKOUT_METHOD);
    }
  }, [checkoutMethod, checkoutMethodOptions]);

  useEffect(() => {
    setDocumentTitle(isKioskExperience ? "Totem" : "Menu QR", tableTitle, branchName);
  }, [branchName, isKioskExperience, tableTitle]);

  const submittedOperationalOrder = useMemo(() => {
    if (!branch?.id || !table?.id || !submittedOrderSnapshot) {
      return null;
    }

    return findActiveQrOrderForCustomer({
      ordersState:
        operationalOrdersSnapshot || getOperationalOrdersState({ id: branch.id }),
      orderId: submittedOrderSnapshot.orderId || "",
      tableId: table.id,
      qrToken,
      sessionKey,
      customerSessionId:
        submittedOrderSnapshot.customerSessionId ||
        customerIdentity?.customerSessionId ||
        "",
    });
  }, [
    branch?.id,
    customerIdentity?.customerSessionId,
    operationalOrdersSnapshot,
    qrToken,
    sessionKey,
    submittedOrderSnapshot,
    table?.id,
  ]);
  const submittedOrderEditLock = useMemo(
    () => getQrOrderEditLockState(submittedOperationalOrder),
    [submittedOperationalOrder]
  );
  const submittedPaymentOperation = useMemo(() => {
    if (!submittedOrderSnapshot?.paymentOperationId) {
      return null;
    }

    return (
      paymentOperationsSnapshot.find(
        (operation) => operation.id === submittedOrderSnapshot.paymentOperationId
      ) || null
    );
  }, [paymentOperationsSnapshot, submittedOrderSnapshot?.paymentOperationId]);
  const submittedDigitalPaymentIntent = useMemo(() => {
    if (!submittedOrderSnapshot?.paymentIntent) {
      return null;
    }

    const normalizedIntent = normalizePaymentIntentSnapshot(
      submittedOrderSnapshot.paymentIntent,
      {
        branch: {
          ...branch,
          ...branchPaymentSetup,
        },
        order: {
          id: submittedOrderSnapshot?.orderId || "",
          paymentOperationId: submittedOrderSnapshot?.paymentOperationId || "",
          totalAmount:
            submittedOrderSnapshot?.pricing?.finalTotal ||
            submittedOrderSnapshot?.pricing?.listTotal ||
            0,
          pricing: submittedOrderSnapshot?.pricing || null,
          serviceType: submittedOrderSnapshot?.serviceType || "",
          source: isKioskExperience ? "kiosk" : "app",
          selectedMethod: submittedOrderSnapshot?.checkoutMethod || "",
          requestedMethod: submittedOrderSnapshot?.checkoutMethod || "",
        },
        customerName: submittedOrderSnapshot?.customerName || "",
        source: isKioskExperience ? "kiosk" : "app",
        checkoutMethod: submittedOrderSnapshot?.checkoutMethod || "",
        paymentProvider:
          branchPaymentSetup.paymentProvider || branch?.paymentProvider || "",
        paymentSplitMode:
          branchPaymentSetup.paymentSplitMode || branch?.paymentSplitMode || "",
        isKioskExperience,
      }
    );

    return normalizedIntent.id ? normalizedIntent : null;
  }, [
    branch,
    branchPaymentSetup,
    isKioskExperience,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.customerName,
    submittedOrderSnapshot?.orderId,
    submittedOrderSnapshot?.paymentIntent,
    submittedOrderSnapshot?.paymentOperationId,
    submittedOrderSnapshot?.pricing,
    submittedOrderSnapshot?.serviceType,
  ]);
  const submittedKioskDigitalPaymentIntent =
    isKioskExperience &&
    normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) ===
      KIOSK_QR_CHECKOUT_METHOD
      ? submittedDigitalPaymentIntent
      : null;
  const submittedKioskPaymentLandingUrl = useMemo(
    () =>
      buildPublicKioskPaymentLandingUrl({
        intentId: submittedKioskDigitalPaymentIntent?.id || "",
        orderId: submittedOrderSnapshot?.orderId || "",
        branchId: branch?.id || "",
      }),
    [
      branch?.id,
      submittedKioskDigitalPaymentIntent?.id,
      submittedOrderSnapshot?.orderId,
    ]
  );
  const submittedMenuDigitalPaymentIntent =
    !isKioskExperience &&
    normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) === "deep_link"
      ? submittedDigitalPaymentIntent
      : null;
  const submittedKioskUsesCheckoutLinkQr =
    submittedKioskDigitalPaymentIntent?.qr?.kind === "checkout_link";
  const submittedKioskUsesNativePaymentQr =
    Boolean(submittedKioskDigitalPaymentIntent?.qr?.imageUrl) ||
    Boolean(submittedKioskDigitalPaymentIntent?.qr?.qrData);
  const submittedMenuWalletProvider = useMemo(
    () =>
      findMenuQrWalletProvider(
        branch?.menuQrWalletProviders || branchProfile?.menuQrWalletProviders || [],
        submittedOrderSnapshot?.menuWalletProviderId
      ) || null,
    [
      branch?.menuQrWalletProviders,
      branchProfile?.menuQrWalletProviders,
      submittedOrderSnapshot?.menuWalletProviderId,
    ]
  );
  const submittedMenuWalletLabel =
    submittedMenuWalletProvider?.label || "billetera";
  const submittedMenuLaunchUrl = useMemo(
    () =>
      resolveMenuQrWalletLaunchUrl({
        paymentIntent: submittedMenuDigitalPaymentIntent,
        provider: submittedMenuWalletProvider,
        branchName: branch?.name || branchProfile?.nombre || "MozzoApp",
        customerName:
          submittedOrderSnapshot?.customerName || customerNameDraft || "Comensal",
      }),
    [
      branch?.name,
      branchProfile?.nombre,
      customerNameDraft,
      submittedMenuDigitalPaymentIntent,
      submittedMenuWalletProvider,
      submittedOrderSnapshot?.customerName,
    ]
  );
  const submittedKioskPaymentStatus = useMemo(
    () => {
      const paymentIntentStatus = String(
        submittedKioskDigitalPaymentIntent?.status || ""
      )
        .trim()
        .toLowerCase();
      const paymentOperationStatus = String(
        submittedPaymentOperation?.status || ""
      )
        .trim()
        .toLowerCase();
      const operationalOrderPaymentStatus = String(
        submittedOperationalOrder?.paymentStatus || ""
      )
        .trim()
        .toLowerCase();

      if (submittedOrderSnapshot?.awaitingPaymentApproval) {
        return (
          paymentIntentStatus ||
          paymentOperationStatus ||
          operationalOrderPaymentStatus
        );
      }

      return (
        paymentOperationStatus ||
        operationalOrderPaymentStatus ||
        paymentIntentStatus
      );
    },
    [
      submittedOrderSnapshot?.awaitingPaymentApproval,
      submittedKioskDigitalPaymentIntent?.status,
      submittedOperationalOrder?.paymentStatus,
      submittedPaymentOperation?.status,
    ]
  );
  const isSubmittedKioskPaymentSettled = useMemo(
    () => isSettledPaymentStatus(submittedKioskPaymentStatus),
    [submittedKioskPaymentStatus]
  );
  const isSubmittedKioskPaymentApprovedOrSettled = useMemo(
    () => isApprovedOrSettledPaymentStatus(submittedKioskPaymentStatus),
    [submittedKioskPaymentStatus]
  );
  const isSubmittedKioskPaymentFailed = useMemo(
    () => isFailedPaymentStatus(submittedKioskPaymentStatus),
    [submittedKioskPaymentStatus]
  );
  const hasSubmittedKioskPaymentRecoveryTimedOut = useMemo(() => {
    if (
      !isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        KIOSK_QR_CHECKOUT_METHOD ||
      !submittedOrderSnapshot?.awaitingPaymentApproval ||
      !submittedOrderSnapshot?.submittedAt
    ) {
      return false;
    }

    const submittedAtTimestamp = new Date(
      submittedOrderSnapshot.submittedAt
    ).getTime();

    if (!Number.isFinite(submittedAtTimestamp)) {
      return false;
    }

    return (
      Date.now() - submittedAtTimestamp >= KIOSK_PENDING_PAYMENT_RECOVERY_MS
    );
  }, [
    isKioskExperience,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.submittedAt,
  ]);
  const canRecoverSubmittedKioskPendingPayment = useMemo(
    () => isKioskQrPaymentPending,
    [isKioskQrPaymentPending]
  );
  const registerApprovedDigitalCommissionSettlement = useCallback(
    async ({
      branchId,
      orderId,
      paymentOperationId,
      paymentIntentId = "",
      sourceChannel = "unknown",
      sourceMode = "unknown",
      metadata = {},
    } = {}) => {
      const normalizedBranchId = String(branchId || "").trim();
      const normalizedOrderId = String(orderId || "").trim();
      const normalizedPaymentOperationId = String(paymentOperationId || "").trim();
      const normalizedPaymentIntentId = String(paymentIntentId || "").trim();

      if (
        !normalizedBranchId ||
        !normalizedOrderId ||
        !normalizedPaymentOperationId ||
        !normalizedPaymentIntentId
      ) {
        return null;
      }

      const fingerprint = [
        normalizedBranchId,
        normalizedOrderId,
        normalizedPaymentOperationId,
        normalizedPaymentIntentId,
        sourceChannel,
        sourceMode,
      ].join(":");

      if (commissionSettlementTrackingRef.current.has(fingerprint)) {
        return null;
      }

      commissionSettlementTrackingRef.current.add(fingerprint);

      try {
        const settlementResult = await finalizeApprovedDigitalCommission({
          branchId: normalizedBranchId,
          orderId: normalizedOrderId,
          paymentOperationId: normalizedPaymentOperationId,
          paymentIntentId: normalizedPaymentIntentId,
          sourceChannel,
          sourceMode,
          metadata,
        });

        if (settlementResult?.ok) {
          try {
            savePaymentOperationCommissionSettlement(
              { id: normalizedBranchId },
              normalizedPaymentOperationId,
              settlementResult
            );
          } catch (storageError) {
            console.warn(
              "No pudimos refrescar el resumen local de deuda despues del cobro digital aprobado:",
              storageError
            );
          }
        }

        return settlementResult;
      } catch (error) {
        commissionSettlementTrackingRef.current.delete(fingerprint);
        console.warn(
          "No pudimos cerrar la comision del cobro digital aprobado:",
          error
        );
        return null;
      }
    },
    []
  );

  useEffect(
    () => () => {
      if (aiSpeechRetryTimeoutRef.current) {
        window.clearTimeout(aiSpeechRetryTimeoutRef.current);
        aiSpeechRetryTimeoutRef.current = null;
      }

      if (aiSpeechStartWatchdogRef.current) {
        window.clearTimeout(aiSpeechStartWatchdogRef.current);
        aiSpeechStartWatchdogRef.current = null;
      }
    },
    []
  );
  const registerApprovedMzTicketUsage = useCallback(
    async ({
      branchId,
      orderId,
      paymentOperationId,
      paymentIntentId = "",
      eventKey,
      sourceChannel = "unknown",
      sourceMode = "unknown",
      metadata = {},
    } = {}) => {
      const normalizedBranchId = String(branchId || "").trim();
      const normalizedOrderId = String(orderId || "").trim();
      const normalizedPaymentOperationId = String(paymentOperationId || "").trim();
      const normalizedEventKey = String(eventKey || "").trim();

      if (
        !normalizedBranchId ||
        !normalizedOrderId ||
        !normalizedPaymentOperationId ||
        !normalizedEventKey
      ) {
        return null;
      }

      const fingerprint = [
        normalizedBranchId,
        normalizedOrderId,
        normalizedPaymentOperationId,
        normalizedEventKey,
        String(paymentIntentId || "").trim(),
      ].join(":");

      if (mzUsageTrackingRef.current.has(fingerprint)) {
        return null;
      }

      mzUsageTrackingRef.current.add(fingerprint);

      try {
        return await recordMzRuntimeUsage({
          branchId: normalizedBranchId,
          eventKey: normalizedEventKey,
          orderId: normalizedOrderId,
          paymentOperationId: normalizedPaymentOperationId,
          paymentIntentId,
          sourceChannel,
          sourceMode,
          metadata,
        });
      } catch (error) {
        mzUsageTrackingRef.current.delete(fingerprint);
        console.warn("No pudimos registrar el consumo MZ del ticket:", error);
        return null;
      }
    },
    []
  );
  const registerOperationalOrderStockConsumption = useCallback(
    async ({
      branchId,
      orderId,
      sourceChannel = "unknown",
      sourceMode = "unknown",
      metadata = {},
    } = {}) => {
      const normalizedBranchId = String(branchId || "").trim();
      const normalizedOrderId = String(orderId || "").trim();

      if (!normalizedBranchId || !normalizedOrderId) {
        return null;
      }

      const fingerprint = [
        normalizedBranchId,
        normalizedOrderId,
        sourceChannel,
        sourceMode,
      ].join(":");

      if (orderStockTrackingRef.current.has(fingerprint)) {
        return null;
      }

      orderStockTrackingRef.current.add(fingerprint);

      try {
        const result = await applyOperationalOrderStock({
          branchId: normalizedBranchId,
          orderId: normalizedOrderId,
          sourceChannel,
          sourceMode,
          metadata,
        });

        if (result?.state) {
          saveOperationalOrdersState({ id: normalizedBranchId }, result.state);
        }

        setSubmittedOrderSnapshot((prev) =>
          prev && prev.orderId === normalizedOrderId
            ? {
                ...prev,
                pendingOrder: prev.pendingOrder
                  ? {
                      ...prev.pendingOrder,
                      stockDiscounted: true,
                    }
                  : prev.pendingOrder,
              }
            : prev
        );

        return result;
      } catch (error) {
        orderStockTrackingRef.current.delete(fingerprint);
        console.warn(
          "No pudimos descontar el stock del pedido confirmado:",
          error
        );
        return null;
      }
    },
    []
  );
  const localMenuSnapshot = useMemo(
    () => (branch?.id ? loadLocalMenuSnapshot(branch.id) : null),
    [branch?.id]
  );
  const brandTheme = useMemo(
    () => buildRestaurantBrandTheme(branchProfile || {}),
    [branchProfile]
  );
  const restaurantFaviconUrl =
    brandTheme.effectiveIconLogoUrl || brandTheme.effectiveLogoCompactUrl || "";

  useEffect(() => {
    if (restaurantFaviconUrl) {
      applyHeadBrandingAssets(restaurantFaviconUrl);
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [restaurantFaviconUrl]);

  useEffect(() => {
    if (
      isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        "deep_link" ||
      submittedOrderSnapshot?.awaitingPaymentApproval ||
      !submittedOrderSnapshot?.paymentApprovedAt ||
      !submittedOrderSnapshot?.orderId ||
      !submittedOrderSnapshot?.paymentOperationId
    ) {
      return;
    }

    void registerApprovedDigitalCommissionSettlement({
      branchId: branch?.id,
      orderId: submittedOrderSnapshot.orderId,
      paymentOperationId: submittedOrderSnapshot.paymentOperationId,
      paymentIntentId:
        submittedMenuDigitalPaymentIntent?.id ||
        submittedOrderSnapshot?.paymentIntent?.id ||
        "",
      sourceChannel: "menu_qr",
      sourceMode: "deep_link",
      metadata: {
        checkoutMethod: "deep_link",
        customerName: submittedOrderSnapshot?.customerName || "",
        customerSessionId: submittedOrderSnapshot?.customerSessionId || "",
        menuWalletProviderId: submittedOrderSnapshot?.menuWalletProviderId || "",
        menuWalletProviderLabel: submittedMenuWalletProvider?.label || "",
        paymentApprovedAt: submittedOrderSnapshot?.paymentApprovedAt || "",
        itemsSummary: buildOrderSummary(
          Array.isArray(submittedOrderSnapshot?.items)
            ? submittedOrderSnapshot.items
            : []
        ),
      },
    });

    void registerApprovedMzTicketUsage({
      branchId: branch?.id,
      orderId: submittedOrderSnapshot.orderId,
      paymentOperationId: submittedOrderSnapshot.paymentOperationId,
      paymentIntentId:
        submittedMenuDigitalPaymentIntent?.id ||
        submittedOrderSnapshot?.paymentIntent?.id ||
        "",
      eventKey: "menu_qr_order_paid",
      sourceChannel: "menu_qr",
      sourceMode: "deep_link",
      metadata: {
        checkoutMethod: "deep_link",
        grossAmount:
          submittedOrderSnapshot?.pricing?.finalTotal ||
          submittedOrderSnapshot?.pricing?.listTotal ||
          submittedMenuDigitalPaymentIntent?.grossAmount ||
          0,
        customerName: submittedOrderSnapshot?.customerName || "",
        customerSessionId: submittedOrderSnapshot?.customerSessionId || "",
        menuWalletProviderId: submittedOrderSnapshot?.menuWalletProviderId || "",
        menuWalletProviderLabel: submittedMenuWalletProvider?.label || "",
        paymentApprovedAt: submittedOrderSnapshot?.paymentApprovedAt || "",
      },
    });

    if (
      submittedOrderSnapshot?.paymentContext !== "table_pending_accounts" &&
      isStockFeatureEnabled
    ) {
      void registerOperationalOrderStockConsumption({
        branchId: branch?.id,
        orderId: submittedOrderSnapshot.orderId,
        sourceChannel: "menu_qr",
        sourceMode: "deep_link",
        metadata: {
          checkoutMethod: "deep_link",
          customerName: submittedOrderSnapshot?.customerName || "",
          customerSessionId: submittedOrderSnapshot?.customerSessionId || "",
          paymentStatus: "paid",
          itemsSummary: buildOrderSummary(
            Array.isArray(submittedOrderSnapshot?.items)
              ? submittedOrderSnapshot.items
              : []
          ),
        },
      });
    }

  }, [
    branch?.id,
    isKioskExperience,
    isStockFeatureEnabled,
    registerApprovedDigitalCommissionSettlement,
    registerApprovedMzTicketUsage,
    registerOperationalOrderStockConsumption,
    submittedMenuDigitalPaymentIntent?.grossAmount,
    submittedMenuDigitalPaymentIntent?.id,
    submittedMenuWalletProvider?.label,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.customerName,
    submittedOrderSnapshot?.customerSessionId,
    submittedOrderSnapshot?.items,
    submittedOrderSnapshot?.menuWalletProviderId,
    submittedOrderSnapshot?.orderId,
    submittedOrderSnapshot?.paymentApprovedAt,
    submittedOrderSnapshot?.paymentIntent?.id,
    submittedOrderSnapshot?.paymentOperationId,
    submittedOrderSnapshot?.paymentContext,
    submittedOrderSnapshot?.pricing,
  ]);

  useEffect(() => {
    if (
      !isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        KIOSK_QR_CHECKOUT_METHOD ||
      !submittedOrderSnapshot?.awaitingPaymentApproval ||
      !submittedOrderSnapshot?.paymentIntent?.id
    ) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const syncIntentStatus = async () => {
      try {
        const nextIntent = await getPublicKioskDigitalPaymentIntent({
          intentId: submittedOrderSnapshot?.paymentIntent?.id || "",
          orderId: submittedOrderSnapshot?.orderId || "",
          branchId: branch?.id || "",
        });

        if (cancelled || !nextIntent?.id) {
          return;
        }

        setSubmittedOrderSnapshot((prev) => {
          if (!prev || prev.paymentIntent?.id !== nextIntent.id) {
            return prev;
          }

          const previousStatus = String(prev.paymentIntent?.status || "").trim();
          const nextStatus = String(nextIntent.status || "").trim();
          const previousApprovedAt = String(
            prev.paymentIntent?.approvedAt || ""
          ).trim();
          const nextApprovedAt = String(nextIntent.approvedAt || "").trim();

          if (
            previousStatus === nextStatus &&
            previousApprovedAt === nextApprovedAt
          ) {
            if (!prev.paymentIntentError) {
              return prev;
            }

            return {
              ...prev,
              paymentIntentError: "",
            };
          }

          return {
            ...prev,
            paymentIntent: {
              ...prev.paymentIntent,
              ...nextIntent,
            },
            paymentIntentError: "",
          };
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        const nextMessage =
          error instanceof Error
            ? error.message
            : "No pudimos revisar si el pago QR ya fue confirmado.";

        const isTransientPollingError =
          String(nextMessage || "")
            .toLowerCase()
            .includes("mercado pago no devolvio el estado actual del") ||
          String(nextMessage || "")
            .toLowerCase()
            .includes("no pudimos actualizar el estado del cobro del kiosk");

        if (isTransientPollingError) {
          console.warn(
            "Seguimos esperando la confirmacion del pago del kiosk. La verificacion automatica todavia no devolvio un estado util.",
            error
          );
          return;
        }

        setSubmittedOrderSnapshot((prev) =>
          prev
            ? {
                ...prev,
                paymentIntentError: nextMessage,
              }
            : prev
        );
      }
    };

    void syncIntentStatus();
    intervalId = window.setInterval(syncIntentStatus, 2000);

    return () => {
      cancelled = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [
    branch?.id,
    isKioskExperience,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.orderId,
    submittedOrderSnapshot?.paymentIntent?.id,
  ]);

  useEffect(() => {
    if (
      !isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        KIOSK_QR_CHECKOUT_METHOD ||
      !submittedOrderSnapshot?.awaitingPaymentApproval ||
      !isSubmittedKioskPaymentFailed
    ) {
      return;
    }

    const nextMessage =
      submittedOrderSnapshot?.paymentIntentError || t("failedKioskPaymentHint");

    setSubmittedOrderSnapshot((prev) =>
      prev && !prev.paymentIntentError
        ? {
            ...prev,
            paymentIntentError: nextMessage,
          }
        : prev
    );
    setCheckoutMessage((prev) =>
      prev?.text === nextMessage && prev?.type === "error"
        ? prev
        : {
            type: "error",
            text: nextMessage,
          }
    );
  }, [
    isKioskExperience,
    isSubmittedKioskPaymentFailed,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.paymentIntentError,
    t,
  ]);

  const finalizeSubmittedKioskQrOrder = useCallback(
    async (approvedIntentSnapshot = null) => {
      if (
        !isKioskExperience ||
        normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
          KIOSK_QR_CHECKOUT_METHOD ||
        !submittedOrderSnapshot?.awaitingPaymentApproval ||
        !submittedOrderSnapshot?.pendingOrder ||
        !submittedOrderSnapshot?.pendingPaymentOperation
      ) {
        return false;
      }

      const now =
        String(
          approvedIntentSnapshot?.approvedAt ||
            approvedIntentSnapshot?.approved_at ||
            ""
        ).trim() || new Date().toISOString();
      const pendingOrder = submittedOrderSnapshot.pendingOrder;
      const pendingPaymentOperation = submittedOrderSnapshot.pendingPaymentOperation;
      const currentCustomerSessionId =
        String(
          submittedOrderSnapshot.customerSessionId ||
            customerIdentity?.customerSessionId ||
            ""
        ).trim() || buildCustomerSessionId();
      const approvedIntent = normalizePaymentIntentSnapshot(
        approvedIntentSnapshot || {
          ...(submittedOrderSnapshot.paymentIntent || {}),
          status: "approved",
          approvedAt: now,
        },
        {
          branch: {
            ...branch,
            ...branchPaymentSetup,
          },
          order: {
            id: submittedOrderSnapshot?.orderId || "",
            paymentOperationId: submittedOrderSnapshot?.paymentOperationId || "",
            totalAmount:
              submittedOrderSnapshot?.pricing?.finalTotal ||
              submittedOrderSnapshot?.pricing?.listTotal ||
              0,
            pricing: submittedOrderSnapshot?.pricing || null,
            serviceType: submittedOrderSnapshot?.serviceType || "",
            source: "kiosk",
            selectedMethod: submittedOrderSnapshot?.checkoutMethod || "",
            requestedMethod: submittedOrderSnapshot?.checkoutMethod || "",
          },
          customerName: submittedOrderSnapshot?.customerName || "",
          source: "kiosk",
          checkoutMethod: submittedOrderSnapshot?.checkoutMethod || "",
          paymentProvider:
            branchPaymentSetup.paymentProvider || branch?.paymentProvider || "",
          paymentSplitMode:
            branchPaymentSetup.paymentSplitMode || branch?.paymentSplitMode || "",
          isKioskExperience: true,
        }
      );
      const approvedOrder = {
        ...pendingOrder,
        paymentStatus: "paid",
        paidAt: now,
        updatedAt: now,
      };
      const approvedPaymentOperation = {
        ...pendingPaymentOperation,
        status: "paid",
        paidAt: now,
        history: [
          ...((Array.isArray(pendingPaymentOperation?.history)
            ? pendingPaymentOperation.history
            : []
          ).slice(-11)),
          {
            id: `history-${Date.now()}-approved`,
            label: "Pago QR aprobado. El pedido ya puede entrar en preparacion.",
            at: now,
          },
        ],
      };

      let submissionCompleted = false;

      setIsSubmittingOrder(true);
      setKioskCompletionStage("submitting");
      setCheckoutMessage({
        type: "success",
        text: "Pago confirmado. Estamos enviando tu pedido a la sucursal.",
      });

      try {
        const sharedSubmitResult = await submitPublicKioskOrderToSharedState({
          branchId: branch.id,
          stationNumber: normalizedKioskStationNumber,
          customerName:
            submittedOrderSnapshot.customerName || customerNameDraft || "Cliente",
          customerSessionToken: currentCustomerSessionId,
          orderSnapshot: approvedOrder,
          checkoutMethod: KIOSK_QR_CHECKOUT_METHOD,
        });

        if (!sharedSubmitResult.supported) {
          throw new Error(
            String(sharedSubmitResult.reason || "").trim() ||
              "El pago quedo aprobado, pero no pudimos enviar el pedido del totem a la sucursal."
          );
        }

        if (sharedSubmitResult.terminal) {
          const terminalReason =
            String(sharedSubmitResult.terminalReason || "").trim() ||
            "Este pedido fue cancelado en la sucursal y no puede volver a ingresar.";

          setSubmittedOrderSnapshot((prev) =>
            prev
              ? {
                  ...prev,
                  awaitingPaymentApproval: false,
                  terminalReason,
                }
              : prev
          );
          submissionCompleted = true;
          throw new Error(
            `El pago fue confirmado, pero ${terminalReason.toLowerCase()} Consulta al personal para revisar el cobro.`
          );
        }

        submissionCompleted = true;
        let localOperationalMirrorSaved = true;
        let localPaymentMirrorSaved = true;
        const resolvedCustomerSessionId =
          String(sharedSubmitResult.customerSessionToken || "").trim() ||
          currentCustomerSessionId;
        const nextPaymentOperations = [
          approvedPaymentOperation,
          ...getPaymentOperations({ id: branch.id }).filter(
            (operation) => operation.id !== approvedPaymentOperation.id
          ),
        ];

        try {
          saveOperationalOrdersState({ id: branch.id }, sharedSubmitResult.payload);
        } catch (localOperationalMirrorError) {
          localOperationalMirrorSaved = false;
          console.warn(
            "El pago QR del totem se confirmo y el pedido llego a la sucursal, pero no pudimos espejarlo en este dispositivo:",
            localOperationalMirrorError
          );
        }

        try {
          savePaymentOperations({ id: branch.id }, nextPaymentOperations);
        } catch (localPaymentMirrorError) {
          localPaymentMirrorSaved = false;
          console.warn(
            "El pago QR del totem se confirmo, pero no pudimos refrescar las operaciones de pago en este dispositivo:",
            localPaymentMirrorError
          );
        }

        setSubmittedOrderSnapshot((prev) =>
          prev
            ? {
                ...prev,
                awaitingPaymentApproval: false,
                submittedAt: prev.submittedAt || now,
                customerSessionId: resolvedCustomerSessionId,
                paymentIntent: approvedIntent,
                paymentIntentError: "",
                paymentApprovedAt: now,
                pendingOrder: approvedOrder,
                pendingPaymentOperation: approvedPaymentOperation,
              }
            : prev
        );
        setCustomerIdentity((prev) => ({
          customerName:
            prev?.customerName ||
            submittedOrderSnapshot.customerName ||
            customerNameDraft ||
            "",
          customerSessionId: resolvedCustomerSessionId,
          createdAt: prev?.createdAt || now,
        }));

        const approvedOrderId =
          approvedOrder.id || pendingOrder.id || submittedOrderSnapshot.orderId;
        const approvedPaymentIntentId =
          approvedIntent?.id ||
          approvedIntentSnapshot?.id ||
          submittedOrderSnapshot?.paymentIntent?.id ||
          "";

        // These bookkeeping tasks are idempotent and must not delay the physical
        // kitchen command or the customer's pickup ticket.
        void Promise.allSettled([
          isStockFeatureEnabled
            ? registerOperationalOrderStockConsumption({
                branchId: branch?.id,
                orderId: approvedOrderId,
                sourceChannel: "kiosk",
                sourceMode: KIOSK_QR_CHECKOUT_METHOD,
                metadata: {
                  checkoutMethod: KIOSK_QR_CHECKOUT_METHOD,
                  customerName:
                    submittedOrderSnapshot.customerName || customerNameDraft || "",
                  customerSessionId: resolvedCustomerSessionId,
                  paymentStatus: approvedPaymentOperation.status,
                  preparationNumber:
                    submittedOrderSnapshot.preparationNumber || null,
                  serviceType: submittedOrderSnapshot.serviceType || "",
                },
              })
            : Promise.resolve(null),
          registerApprovedDigitalCommissionSettlement({
            branchId: branch?.id,
            orderId: approvedOrderId,
            paymentOperationId: approvedPaymentOperation.id,
            paymentIntentId: approvedPaymentIntentId,
            sourceChannel: "kiosk",
            sourceMode: KIOSK_QR_CHECKOUT_METHOD,
            metadata: {
              checkoutMethod: KIOSK_QR_CHECKOUT_METHOD,
              customerName:
                submittedOrderSnapshot.customerName || customerNameDraft || "",
              customerSessionId: resolvedCustomerSessionId,
              paymentStatus: approvedPaymentOperation.status,
              preparationNumber: submittedOrderSnapshot.preparationNumber || null,
              serviceType: submittedOrderSnapshot.serviceType || "",
              paymentApprovedAt: now,
              itemsSummary: buildOrderSummary(
                Array.isArray(submittedOrderSnapshot?.items)
                  ? submittedOrderSnapshot.items
                  : []
              ),
            },
          }),
          registerApprovedMzTicketUsage({
            branchId: branch?.id,
            orderId: approvedOrderId,
            paymentOperationId: approvedPaymentOperation.id,
            paymentIntentId: approvedPaymentIntentId,
            eventKey: "kiosk_order_paid",
            sourceChannel: "kiosk",
            sourceMode: KIOSK_QR_CHECKOUT_METHOD,
            metadata: {
              checkoutMethod: KIOSK_QR_CHECKOUT_METHOD,
              grossAmount:
                submittedOrderSnapshot?.pricing?.finalTotal ||
                submittedOrderSnapshot?.pricing?.listTotal ||
                approvedIntent?.grossAmount ||
                0,
              customerName:
                submittedOrderSnapshot.customerName || customerNameDraft || "",
              customerSessionId: resolvedCustomerSessionId,
              paymentStatus: approvedPaymentOperation.status,
              preparationNumber: submittedOrderSnapshot.preparationNumber || null,
              serviceType: submittedOrderSnapshot.serviceType || "",
            },
          }),
        ]);

        const restaurantName =
          branchProfile?.nombre || branch?.name || "MozzoApp";
        const branchName = branch?.name || "";
        const paperWidth = branchProfile?.kitchenTicketPaper || "80mm";
        const shouldPrintKitchenCommand =
          String(branchProfile?.kitchenMode || "").trim().toLowerCase() === "printer";

        try {
          if (shouldPrintKitchenCommand && orderRequiresKitchen(approvedOrder)) {
            setKioskCompletionStage("printing_kitchen");
            if (isMozzoWindowsBridgeAvailable()) {
              await printTicketWithMozzoWindows(
                buildMozzoWindowsKitchenTicketPayload({
                  order: approvedOrder,
                  restaurantName,
                  paperWidthMm: paperWidth === "58mm" ? 58 : 80,
                })
              );
            } else if (isMozzoAndroidBridgeAvailable()) {
              await printTicketWithMozzoAndroid(
                buildMozzoAndroidKitchenTicketPayload({
                  order: approvedOrder,
                  restaurantName,
                  paperWidthMm: paperWidth === "58mm" ? 58 : 80,
                })
              );
            } else {
              await printKitchenTicket({
                restaurantName,
                branchName,
                order: approvedOrder,
                paperWidth,
                preferCurrentDocumentPrint: true,
                cleanupDelayMs: KIOSK_PRINT_RESET_DELAY_MS,
              });
            }
          }

          setKioskCompletionStage("printing_ticket");
          if (isMozzoWindowsBridgeAvailable()) {
            await printTicketWithMozzoWindows(
              buildMozzoWindowsCustomerTicketPayload({
                order: approvedOrder,
                restaurantName,
                paymentMethodLabel: "QR",
                footer: "Retire cuando aparezca su numero en pantalla",
                paperWidthMm: paperWidth === "58mm" ? 58 : 80,
              })
            );
          } else if (isMozzoAndroidBridgeAvailable()) {
            await printTicketWithMozzoAndroid(
              buildMozzoAndroidCustomerTicketPayload({
                order: approvedOrder,
                restaurantName,
                paymentMethodLabel: "QR",
                footer: "Retire cuando aparezca su numero en pantalla",
                paperWidthMm: paperWidth === "58mm" ? 58 : 80,
              })
            );
          } else {
            await printCustomerPickupTicket({
              restaurantName,
              branchName,
              order: approvedOrder,
              paperWidth,
              preferCurrentDocumentPrint: true,
              cleanupDelayMs: KIOSK_PRINT_RESET_DELAY_MS,
            });
          }
        } catch (ticketError) {
          setKioskCompletionStage("");
          const ticketErrorMessage =
            ticketError instanceof Error && ticketError.message.trim()
              ? ticketError.message.trim()
              : "No pudimos imprimir el ticket del totem.";
          setCheckoutMessage({
            type: "error",
            text: `${
              !localOperationalMirrorSaved || !localPaymentMirrorSaved
                ? "Pago confirmado y pedido enviado. En este dispositivo no pudimos refrescar todo el espejo local. "
                : "Pago confirmado y pedido enviado. "
            }${ticketErrorMessage}`,
          });
          return true;
        }

        resetKioskExperienceFlow();
        return true;
      } catch (error) {
        setKioskCompletionStage("");
        if (!submissionCompleted) {
          kioskPaymentFinalizeRef.current = "";
        }

        setCheckoutMessage({
          type: "error",
          text:
            error instanceof Error
              ? error.message
              : "El pago quedo aprobado, pero no pudimos terminar de enviar el pedido del totem.",
        });
        return false;
      } finally {
        setIsSubmittingOrder(false);
      }
    },
    [
      branch,
      branchPaymentSetup,
      branchProfile?.kitchenMode,
      branchProfile?.kitchenTicketPaper,
      branchProfile?.nombre,
      customerIdentity?.customerSessionId,
      customerNameDraft,
      isStockFeatureEnabled,
      isKioskExperience,
      normalizedKioskStationNumber,
      registerApprovedDigitalCommissionSettlement,
      registerApprovedMzTicketUsage,
      registerOperationalOrderStockConsumption,
      resetKioskExperienceFlow,
      submittedOrderSnapshot,
    ]
  );

  useEffect(() => {
    if (
      !isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        KIOSK_QR_CHECKOUT_METHOD ||
      !submittedOrderSnapshot?.awaitingPaymentApproval ||
      !submittedOrderSnapshot?.paymentIntent?.id ||
      !isSubmittedKioskPaymentApprovedOrSettled
    ) {
      return;
    }

    const finalizeKey = `${
      submittedOrderSnapshot.orderId || "pending-kiosk-order"
    }:${submittedOrderSnapshot.paymentIntent.id}:${submittedKioskPaymentStatus || "approved"}`;

    if (kioskPaymentFinalizeRef.current === finalizeKey) {
      return;
    }

    kioskPaymentFinalizeRef.current = finalizeKey;
    void finalizeSubmittedKioskQrOrder(submittedKioskDigitalPaymentIntent);
  }, [
    finalizeSubmittedKioskQrOrder,
    isKioskExperience,
    isSubmittedKioskPaymentApprovedOrSettled,
    submittedKioskDigitalPaymentIntent,
    submittedKioskPaymentStatus,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.orderId,
    submittedOrderSnapshot?.paymentIntent?.id,
  ]);

  useEffect(() => {
    if (
      !isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        KIOSK_QR_CHECKOUT_METHOD ||
      submittedOrderSnapshot?.awaitingPaymentApproval ||
      !submittedOrderSnapshot?.paymentApprovedAt ||
      !submittedOrderSnapshot?.orderId ||
      !submittedOrderSnapshot?.paymentOperationId ||
      !isSubmittedKioskPaymentSettled
    ) {
      return;
    }

    if (isStockFeatureEnabled) {
      void registerOperationalOrderStockConsumption({
        branchId: branch?.id,
        orderId: submittedOrderSnapshot.orderId,
        sourceChannel: "kiosk",
        sourceMode: KIOSK_QR_CHECKOUT_METHOD,
        metadata: {
          checkoutMethod: KIOSK_QR_CHECKOUT_METHOD,
          customerName: submittedOrderSnapshot?.customerName || "",
          preparationNumber: submittedOrderSnapshot?.preparationNumber || null,
          paymentStatus: submittedKioskPaymentStatus || "",
          serviceType: submittedOrderSnapshot?.serviceType || "",
          itemsSummary: buildOrderSummary(
            Array.isArray(submittedOrderSnapshot?.items)
              ? submittedOrderSnapshot.items
              : []
          ),
        },
      });
    }

    void registerApprovedDigitalCommissionSettlement({
      branchId: branch?.id,
      orderId: submittedOrderSnapshot.orderId,
      paymentOperationId: submittedOrderSnapshot.paymentOperationId,
      paymentIntentId:
        submittedKioskDigitalPaymentIntent?.id ||
        submittedOrderSnapshot?.paymentIntent?.id ||
        "",
      sourceChannel: "kiosk",
      sourceMode: KIOSK_QR_CHECKOUT_METHOD,
      metadata: {
        checkoutMethod: KIOSK_QR_CHECKOUT_METHOD,
        customerName: submittedOrderSnapshot?.customerName || "",
        preparationNumber: submittedOrderSnapshot?.preparationNumber || null,
        paymentStatus: submittedKioskPaymentStatus || "",
        serviceType: submittedOrderSnapshot?.serviceType || "",
      },
    });

    void registerApprovedMzTicketUsage({
      branchId: branch?.id,
      orderId: submittedOrderSnapshot.orderId,
      paymentOperationId: submittedOrderSnapshot.paymentOperationId,
      paymentIntentId:
        submittedKioskDigitalPaymentIntent?.id ||
        submittedOrderSnapshot?.paymentIntent?.id ||
        "",
      eventKey: "kiosk_order_paid",
      sourceChannel: "kiosk",
      sourceMode: KIOSK_QR_CHECKOUT_METHOD,
      metadata: {
        checkoutMethod: KIOSK_QR_CHECKOUT_METHOD,
        grossAmount:
          submittedOrderSnapshot?.pricing?.finalTotal ||
          submittedOrderSnapshot?.pricing?.listTotal ||
          submittedKioskDigitalPaymentIntent?.grossAmount ||
          0,
        customerName: submittedOrderSnapshot?.customerName || "",
        preparationNumber: submittedOrderSnapshot?.preparationNumber || null,
        paymentStatus: submittedKioskPaymentStatus || "",
        serviceType: submittedOrderSnapshot?.serviceType || "",
      },
    });

  }, [
    branch?.id,
    isKioskExperience,
    isStockFeatureEnabled,
    isSubmittedKioskPaymentSettled,
    registerOperationalOrderStockConsumption,
    registerApprovedDigitalCommissionSettlement,
    registerApprovedMzTicketUsage,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedKioskDigitalPaymentIntent?.id,
    submittedKioskPaymentStatus,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.customerName,
    submittedOrderSnapshot?.orderId,
    submittedOrderSnapshot?.paymentApprovedAt,
    submittedOrderSnapshot?.paymentIntent?.id,
    submittedOrderSnapshot?.paymentOperationId,
    submittedOrderSnapshot?.pricing,
    submittedOrderSnapshot?.items,
    submittedOrderSnapshot?.preparationNumber,
    submittedOrderSnapshot?.serviceType,
    submittedKioskDigitalPaymentIntent?.grossAmount,
  ]);

  const finalizeSubmittedMenuDeepLinkOrder = useCallback(
    async (paymentReturnPayload = null) => {
      if (
        isKioskExperience ||
        normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
          "deep_link" ||
        !submittedOrderSnapshot?.awaitingPaymentApproval ||
        !submittedOrderSnapshot?.pendingPaymentOperation ||
        !qrToken
      ) {
        return false;
      }

      const now = new Date().toISOString();
      const pendingOrder = submittedOrderSnapshot.pendingOrder || {};
      const pendingPaymentOperation = submittedOrderSnapshot.pendingPaymentOperation;
      const currentCustomerSessionId =
        String(
          submittedOrderSnapshot.customerSessionId ||
            customerIdentity?.customerSessionId ||
            ""
        ).trim() || buildCustomerSessionId();
      const paymentIntentSnapshot = submittedOrderSnapshot.paymentIntent || null;
      const approvedIntent = paymentIntentSnapshot
        ? {
            ...paymentIntentSnapshot,
            status: "approved",
            approvedAt: now,
            paymentReturn: paymentReturnPayload || null,
          }
        : paymentIntentSnapshot;
      const approvedOrder = {
        ...pendingOrder,
        paymentStatus: "paid",
        paidAt: now,
        updatedAt: now,
      };
      const approvedPaymentOperation = {
        ...pendingPaymentOperation,
        status: "paid",
        paidAt: now,
        history: [
          ...((Array.isArray(pendingPaymentOperation?.history)
            ? pendingPaymentOperation.history
            : []
          ).slice(-11)),
          {
            id: `history-${Date.now()}-approved`,
            label: "Pago digital aprobado. El pedido ya puede entrar en preparacion.",
            at: now,
          },
        ],
      };

      setIsSubmittingOrder(true);
      setCheckoutMessage({
        type: "success",
        text: "Pago aprobado. Estamos enviando tu pedido a la sucursal.",
      });

      try {
        const databaseFinalization = await finalizeApprovedPublicMenuPayment({
          qrToken,
          paymentIntentId:
            approvedIntent?.id ||
            paymentIntentSnapshot?.id ||
            submittedOrderSnapshot?.paymentIntent?.id ||
            "",
        });

        if (databaseFinalization.supported && databaseFinalization.finalized) {
          const finalizedCustomerSessionId =
            databaseFinalization.customerSessionToken || currentCustomerSessionId;
          const isTableAccountsPayment =
            databaseFinalization.paymentContext === "table_pending_accounts" ||
            submittedOrderSnapshot?.paymentContext === "table_pending_accounts";

          setSubmittedOrderSnapshot((prev) =>
            prev
              ? {
                  ...prev,
                  awaitingPaymentApproval: false,
                  customerSessionId: finalizedCustomerSessionId,
                  paymentIntent: approvedIntent,
                  paymentIntentError: "",
                  paymentApprovedAt: now,
                  paymentReturn: paymentReturnPayload || null,
                  pendingOrder: isTableAccountsPayment
                    ? null
                    : databaseFinalization.order || approvedOrder,
                  pendingPaymentOperation: approvedPaymentOperation,
                }
              : prev
          );
          setCustomerIdentity((prev) => ({
            customerName:
              prev?.customerName ||
              submittedOrderSnapshot.customerName ||
              customerNameDraft ||
              "",
            customerSessionId: finalizedCustomerSessionId,
            createdAt: prev?.createdAt || now,
          }));
          setCheckoutMessage({
            type: "success",
            text: isTableAccountsPayment
              ? "Pago aprobado. Las cuentas elegidas ya figuran como pagadas."
              : "Pago aprobado. El pedido ya fue enviado a la sucursal.",
          });
          await refreshPendingTableAccounts();
          clearDigitalPaymentReturnPayload();
          return true;
        }

        if (submittedOrderSnapshot?.paymentContext === "table_pending_accounts") {
          throw new Error(
            databaseFinalization.reason ||
              "El pago fue aprobado, pero falta habilitar la actualizacion segura de cuentas en la base."
          );
        }

        const sharedSubmitResult = await submitPublicQrOrderToSharedState({
          qrToken,
          customerName:
            submittedOrderSnapshot.customerName || customerNameDraft || "Comensal",
          customerSessionToken: currentCustomerSessionId,
          orderSnapshot: approvedOrder,
          checkoutMethod: "deep_link",
        });

        if (!sharedSubmitResult.supported) {
          throw new Error(
            String(sharedSubmitResult.reason || "").trim() ||
              "El pago quedo aprobado, pero no pudimos enviar el pedido a la sucursal."
          );
        }

        const resolvedCustomerSessionId =
          String(sharedSubmitResult.customerSessionToken || "").trim() ||
          currentCustomerSessionId;
        let resolvedOperationalState = sharedSubmitResult.payload;
        const nextPaymentOperations = [
          approvedPaymentOperation,
          ...getPaymentOperations({ id: branch.id }).filter(
            (operation) => operation.id !== approvedPaymentOperation.id
          ),
        ];

        saveOperationalOrdersState({ id: branch.id }, resolvedOperationalState);
        const savedPaymentOperations = savePaymentOperations(
          { id: branch.id },
          nextPaymentOperations
        );
        try {
          await pushSharedPaymentOperationsState(
            { id: branch.id },
            savedPaymentOperations
          );
        } catch (sharedPaymentError) {
          console.warn(
            "El pago aprobado quedo guardado localmente, pero no pudimos sincronizarlo con caja:",
            sharedPaymentError
          );
        }

        if (isStockFeatureEnabled) {
          const stockSettlement = await registerOperationalOrderStockConsumption({
            branchId: branch?.id,
            orderId:
              approvedOrder.id || pendingOrder.id || submittedOrderSnapshot.orderId,
            sourceChannel: "menu_qr",
            sourceMode: "deep_link",
            metadata: {
              checkoutMethod: "deep_link",
              customerName:
                submittedOrderSnapshot.customerName || customerNameDraft || "",
              customerSessionId: resolvedCustomerSessionId,
            },
          });

          if (stockSettlement?.state) {
            resolvedOperationalState = stockSettlement.state;
          }
        }

        setSubmittedOrderSnapshot((prev) =>
          prev
            ? {
                ...prev,
                awaitingPaymentApproval: false,
                submittedAt: prev.submittedAt || now,
                customerSessionId: resolvedCustomerSessionId,
                paymentIntent: approvedIntent,
                paymentIntentError: "",
                paymentApprovedAt: now,
                paymentReturn: paymentReturnPayload || null,
                pendingOrder: approvedOrder,
                pendingPaymentOperation: approvedPaymentOperation,
              }
            : prev
        );
        setCustomerIdentity((prev) => ({
          customerName:
            prev?.customerName ||
            submittedOrderSnapshot.customerName ||
            customerNameDraft ||
            "",
          customerSessionId: resolvedCustomerSessionId,
          createdAt: prev?.createdAt || now,
        }));
        setCheckoutMessage({
          type: "success",
          text: "Pago aprobado. El pedido ya fue enviado a la sucursal.",
        });
        clearDigitalPaymentReturnPayload();
        return true;
      } catch (error) {
        menuPaymentFinalizeRef.current = "";
        setCheckoutMessage({
          type: "error",
          text:
            error instanceof Error
              ? error.message
              : "El pago quedo aprobado, pero no pudimos enviar el pedido.",
        });
        return false;
      } finally {
        setIsSubmittingOrder(false);
      }
    },
    [
      branch?.id,
      customerIdentity?.customerSessionId,
      customerNameDraft,
      isStockFeatureEnabled,
      isKioskExperience,
      qrToken,
      refreshPendingTableAccounts,
      registerOperationalOrderStockConsumption,
      submittedOrderSnapshot,
    ]
  );

  useEffect(() => {
    if (
      isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        "deep_link" ||
      !submittedOrderSnapshot?.awaitingPaymentApproval
    ) {
      return;
    }

    const paymentReturnPayload = readDigitalPaymentReturnPayload();

    if (!paymentReturnPayload) {
      return;
    }

    const expectedExternalReference = String(
      submittedMenuDigitalPaymentIntent?.externalReference || ""
    ).trim();
    const returnedExternalReference = String(
      paymentReturnPayload.externalReference || ""
    ).trim();

    if (
      expectedExternalReference &&
      returnedExternalReference &&
      expectedExternalReference !== returnedExternalReference
    ) {
      clearDigitalPaymentReturnPayload();
      return;
    }

    if (paymentReturnPayload.status !== "approved") {
      clearDigitalPaymentReturnPayload();
      setCheckoutMessage({
        type: "error",
        text: "El pago no quedo aprobado. Tu pedido todavia no se envio.",
      });
      return;
    }

    const finalizeKey = `${
      submittedOrderSnapshot.orderId || "pending-order"
    }:${returnedExternalReference || paymentReturnPayload.paymentId || "approved"}`;

    if (menuPaymentFinalizeRef.current === finalizeKey) {
      return;
    }

    menuPaymentFinalizeRef.current = finalizeKey;

    const verifyAndFinalizePayment = async () => {
      try {
        const verifiedIntent = await getPublicKioskDigitalPaymentIntent({
          intentId: submittedMenuDigitalPaymentIntent?.id || "",
          orderId: submittedOrderSnapshot?.orderId || "",
          branchId: branch?.id || "",
          paymentId: paymentReturnPayload.paymentId || "",
          merchantOrderId: paymentReturnPayload.merchantOrderId || "",
          preferenceId: paymentReturnPayload.preferenceId || "",
          externalReference:
            paymentReturnPayload.externalReference ||
            submittedMenuDigitalPaymentIntent?.externalReference ||
            "",
          providerStatus: paymentReturnPayload.status || "",
        });
        const verifiedStatus = String(verifiedIntent?.status || "")
          .trim()
          .toLowerCase();
        const isVerifiedApproved =
          ["approved", "paid", "closed", "completed", "accredited"].includes(
            verifiedStatus
          ) || Boolean(verifiedIntent?.approvedAt);

        if (!isVerifiedApproved) {
          menuPaymentFinalizeRef.current = "";
          setCheckoutMessage({
            type: "error",
            text: "Mercado Pago todavia no confirmo el cobro. El pedido sigue sin enviarse.",
          });
          return;
        }

        await finalizeSubmittedMenuDeepLinkOrder({
          ...paymentReturnPayload,
          status: "approved",
          verifiedIntent,
        });
      } catch (paymentVerificationError) {
        menuPaymentFinalizeRef.current = "";
        setCheckoutMessage({
          type: "error",
          text:
            paymentVerificationError instanceof Error
              ? paymentVerificationError.message
              : "No pudimos confirmar el pago con Mercado Pago. El pedido no se envio.",
        });
      }
    };

    void verifyAndFinalizePayment();
  }, [
    branch?.id,
    finalizeSubmittedMenuDeepLinkOrder,
    isKioskExperience,
    submittedMenuDigitalPaymentIntent?.id,
    submittedMenuDigitalPaymentIntent?.externalReference,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.orderId,
  ]);

  useEffect(() => {
    if (
      isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        "deep_link" ||
      !submittedOrderSnapshot?.awaitingPaymentApproval ||
      !submittedMenuDigitalPaymentIntent?.id ||
      !branch?.id
    ) {
      return undefined;
    }

    let active = true;

    const syncMenuPaymentStatus = async () => {
      try {
        const verifiedIntent = await getPublicKioskDigitalPaymentIntent({
          intentId: submittedMenuDigitalPaymentIntent.id,
          orderId: submittedOrderSnapshot?.orderId || "",
          branchId: branch.id,
          externalReference:
            submittedMenuDigitalPaymentIntent?.externalReference || "",
        });

        if (!active) {
          return;
        }

        const verifiedStatus = String(verifiedIntent?.status || "")
          .trim()
          .toLowerCase();
        const isVerifiedApproved =
          ["approved", "paid", "closed", "completed", "accredited"].includes(
            verifiedStatus
          ) || Boolean(verifiedIntent?.approvedAt);

        if (!isVerifiedApproved) {
          return;
        }

        const finalizeKey = `${
          submittedOrderSnapshot.orderId || "pending-order"
        }:${verifiedIntent?.externalReference || verifiedIntent?.id || "approved"}`;

        if (menuPaymentFinalizeRef.current === finalizeKey) {
          return;
        }

        menuPaymentFinalizeRef.current = finalizeKey;
        await finalizeSubmittedMenuDeepLinkOrder({
          status: "approved",
          source: "provider_status",
          verifiedIntent,
        });
      } catch {
        // La billetera puede seguir pendiente; reintentamos al volver a enfocar o en el proximo ciclo.
      }
    };

    void syncMenuPaymentStatus();
    const intervalId = window.setInterval(syncMenuPaymentStatus, 5000);
    window.addEventListener("focus", syncMenuPaymentStatus);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", syncMenuPaymentStatus);
    };
  }, [
    branch?.id,
    finalizeSubmittedMenuDeepLinkOrder,
    isKioskExperience,
    submittedMenuDigitalPaymentIntent?.externalReference,
    submittedMenuDigitalPaymentIntent?.id,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.orderId,
  ]);

  useEffect(() => {
    if (!branch?.id) {
      setPromotions([]);
      return undefined;
    }

    let active = true;

    const syncPromotions = () => {
      const nextPromotions = normalizePromotionsPayload(getPromotions({ id: branch.id }));
      setPromotions(nextPromotions);
    };

    const syncRemotePromotions = async () => {
      if (!isSupabaseConfigured) {
        syncPromotions();
        return;
      }

      try {
        const remotePromotions = await hydrateBranchPromotionsToLocal(
          { id: branch.id },
          branch.id,
          { publicRead: true }
        );

        if (!active) {
          return;
        }

        setPromotions(normalizePromotionsPayload(remotePromotions));
      } catch (error) {
        if (!active) {
          return;
        }

        syncPromotions();
      }
    };

    const handlePromotionsUpdated = (event) => {
      if (!event?.detail?.restaurantId || event.detail.restaurantId === branch.id) {
        if (Array.isArray(event.detail?.promotions)) {
          setPromotions(normalizePromotionsPayload(event.detail.promotions));
          return;
        }

        void syncRemotePromotions();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key === buildPromotionsStorageKey(branch.id)) {
        void syncRemotePromotions();
      }
    };

    void syncRemotePromotions();
    window.addEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      active = false;
      window.removeEventListener(PROMOTIONS_UPDATED_EVENT, handlePromotionsUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [branch?.id]);

  useEffect(() => {
    if (!branch?.id) {
      setWaiterAssistanceConfig(getWaiterAssistanceConfig(null));
      return undefined;
    }

    if (isSupabaseConfigured) {
      setWaiterAssistanceConfig(normalizeWaiterAssistanceConfig(branch.waiterAssistanceConfig));
      return undefined;
    }

    const syncAssistance = () => {
      setWaiterAssistanceConfig(getWaiterAssistanceConfig(branch.id));
    };

    const handleAssistanceUpdated = (event) => {
      if ((event.detail?.scopeId || "default") !== branch.id) {
        return;
      }

      syncAssistance();
    };

    const handleStorage = (event) => {
      if (!event.key || event.key === buildWaiterAssistanceStorageKey(branch.id)) {
        syncAssistance();
      }
    };

    syncAssistance();
    window.addEventListener(WAITER_ASSISTANCE_UPDATED_EVENT, handleAssistanceUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(WAITER_ASSISTANCE_UPDATED_EVENT, handleAssistanceUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [branch?.id, branch?.waiterAssistanceConfig]);

  useEffect(() => {
    if (!isWaiterHelpOpen || !isSupabaseConfigured || !qrToken || !branch?.id) return undefined;
    let active = true;
    setWaiterOptionsLoading(true);
    setWaiterOptionsNotice("");
    // Refresh on opening so an already-open menu sees newly published options.
    getPublicMenuByQrToken(qrToken, { forceRefresh: true }).then((fresh) => {
      if (!active || fresh?.branch?.id !== branch.id) return;
      const config = normalizeWaiterAssistanceConfig(fresh.branch.waiterAssistanceConfig);
      setWaiterAssistanceConfig(config);
      setSelectedWaiterOptions((selected) => selected.filter((id) =>
        config.options.some((option) => option.id === id && option.active)));
    }).catch(() => {
      if (active) setWaiterOptionsNotice("No pudimos actualizar las opciones. Se muestra la ultima lista disponible.");
    }).finally(() => {
      if (active) setWaiterOptionsLoading(false);
    });
    return () => { active = false; };
  }, [isWaiterHelpOpen, qrToken, branch?.id]);

  const linkedSubcategoryRules = useMemo(
    () => {
      const remoteRules = Array.isArray(payload?.linkedRules)
        ? payload.linkedRules
        : Array.isArray(payload?.linkedSubcategoryRules)
          ? payload.linkedSubcategoryRules
          : [];
      const localRules = Array.isArray(localMenuSnapshot?.linkedSubcategoryRules)
        ? localMenuSnapshot.linkedSubcategoryRules
        : [];

      if (localRules.length === 0) {
        return remoteRules;
      }

      const mergedRules = new Map();
      [...remoteRules, ...localRules].forEach((rule) => {
        const sourceKey = getLinkedRuleSourceSubcategoryId(rule);
        const targetCategoryKey = getLinkedRuleTargetCategoryId(rule);
        const targetSubcategoryKey = getLinkedRuleTargetSubcategoryId(rule);

        if ((sourceKey || getRuleField(rule, "source", "Category")) && (targetCategoryKey || targetSubcategoryKey || isIngredientLinkedRule(rule))) {
          mergedRules.set(buildLinkedRuleKey(rule), {
            ...rule,
            sourceSubcategoryId: sourceKey,
            targetCategoryId: targetCategoryKey,
            targetSubcategoryId: targetSubcategoryKey,
            selectionMode: normalizeLinkedRuleSelectionMode(
              rule?.selectionMode || rule?.selection_mode
            ),
          });
        }
      });

      return Array.from(mergedRules.values());
    },
    [
      localMenuSnapshot?.linkedSubcategoryRules,
      payload?.linkedRules,
      payload?.linkedSubcategoryRules,
    ]
  );
  const hiddenStandaloneProductIds = useMemo(
    () => getLinkedOnlyProductIds(linkedSubcategoryRules, payload?.products || []),
    [linkedSubcategoryRules, payload?.products]
  );
  const aiAssistantQuickSuggestions = useMemo(
    () => buildPublicMenuAssistantPromptSuggestions({
      products: payload?.products || [],
      linkedRules: linkedSubcategoryRules,
      cartItems,
    }),
    [cartItems, payload?.products, linkedSubcategoryRules]
  );
  const sections = useMemo(() => {
    const categories = orderQrCategories(payload?.categories || [], payload?.branch?.qrCategoryOrder);
    const products = payload?.products || [];
    return buildCategorySections(categories, products, hiddenStandaloneProductIds).map((category) => ({
      ...category,
      subcategories: orderQrSubcategories(category.subcategories, payload?.branch?.qrSubcategoryOrder, category.id),
    }));
  }, [hiddenStandaloneProductIds, payload]);
  const productCatalogLocationById = useMemo(() => {
    const locationMap = new Map();

    sections.forEach((section) => {
      (section.products || []).forEach((product) => {
        const productId = normalizeEntityId(product?.id || product?.productId);

        if (!productId || locationMap.has(productId)) {
          return;
        }

        const matchingSubcategory = (section.subcategories || []).find((subcategory) =>
          (subcategory.products || []).some(
            (subcategoryProduct) =>
              normalizeEntityId(
                subcategoryProduct?.id || subcategoryProduct?.productId
              ) === productId
          )
        );

        locationMap.set(productId, {
          categoryId: section.id,
          subcategoryId: matchingSubcategory?.id || "__all__",
        });
      });
    });

    return locationMap;
  }, [sections]);

  useEffect(() => {
    stopAiVoiceInput({ cancel: true, clearDraft: true });
    stopAiVoiceOutput();
    setIsAiAssistantOpen(false);
    setIsAiAssistantPanelVisible(false);
    setAiConversation([]);
    setAiQuestionDraft("");
    setIsAiAssistantLoading(false);
    setIsAiListening(false);
    setAiVoiceStatus("");
    clearAiVoiceSilenceTimeout();
    setIsAiAssistantFabDragging(false);
    setAiAssistantFabPosition(null);
    aiAssistantFabDragRef.current = null;
    aiAssistantFabSuppressClickRef.current = false;
  }, [
    clearAiVoiceSilenceTimeout,
    payload?.branch?.id,
    payload?.table?.id,
    qrToken,
    sessionKey,
    stopAiVoiceInput,
    stopAiVoiceOutput,
  ]);

  const latestAiAssistantMessageId = useMemo(
    () =>
      [...aiConversation]
        .reverse()
        .find((message) => message.role === "assistant")?.id || "",
    [aiConversation]
  );

  useEffect(() => {
    if (!isAiAssistantOpen) {
      return;
    }

    const threadElement = aiAssistantThreadRef.current;

    if (!threadElement) {
      return;
    }

    const scrollToLatestMessage = () => {
      if (isAiAssistantLoading) {
        threadElement.scrollTop = threadElement.scrollHeight;
        return;
      }

      const replyElement = aiAssistantLatestReplyRef.current;

      if (!replyElement) {
        return;
      }

      const threadRect = threadElement.getBoundingClientRect();
      const replyRect = replyElement.getBoundingClientRect();
      const replyStart =
        threadElement.scrollTop + replyRect.top - threadRect.top - 4;

      threadElement.scrollTop = Math.max(0, replyStart);
    };
    const frameId = window.requestAnimationFrame(scrollToLatestMessage);
    const timeoutId = window.setTimeout(scrollToLatestMessage, 120);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [
    aiConversation,
    isAiAssistantLoading,
    isAiAssistantOpen,
    latestAiAssistantMessageId,
  ]);

  useEffect(
    () => () => {
      stopAiVoiceInput({ cancel: true });
      stopAiVoiceOutput();
      clearAiVoiceSilenceTimeout();
    },
    [clearAiVoiceSilenceTimeout, stopAiVoiceInput, stopAiVoiceOutput]
  );

  const aiAssistantVisibleProducts = useMemo(() => {
    const lastAssistantMessage = [...aiConversation]
      .reverse()
      .find(
        (message) =>
          message.role === "assistant" &&
          Array.isArray(message.relatedProducts) &&
          message.relatedProducts.length > 0
      );

    return Array.isArray(lastAssistantMessage?.relatedProducts)
      ? lastAssistantMessage.relatedProducts
      : [];
  }, [aiConversation]);
  const aiAssistantProductLookup = useMemo(() => {
    const lookup = new Map();

    (payload?.products || []).forEach((product) => {
      if (!isVisibleMenuProduct(product) || isLinkedOnlyProduct(product, hiddenStandaloneProductIds)) return;
      const productId = String(product?.id || "").trim();
      const productNameKey = normalizeAssistantProductKey(product?.name);

      if (productId && !lookup.has(productId)) {
        lookup.set(productId, product);
      }

      if (productNameKey && !lookup.has(productNameKey)) {
        lookup.set(productNameKey, product);
      }
    });

    return lookup;
  }, [payload?.products, hiddenStandaloneProductIds]);

  useEffect(() => {
    activeCategoryIdRef.current = activeCategoryId;
  }, [activeCategoryId]);

  useEffect(() => {
    activeSubcategoryIdRef.current = activeSubcategoryId;
  }, [activeSubcategoryId]);

  const aiAssistantVisibleProductIds = useMemo(
    () =>
      aiAssistantVisibleProducts
        .map((product) => normalizeEntityId(product?.productId || product?.id))
        .filter(Boolean)
        .filter((productId, index, collection) => collection.indexOf(productId) === index)
        .slice(0, 12),
    [aiAssistantVisibleProducts]
  );

  const registerProductCardRef = useCallback((productId, node) => {
    const normalizedProductId = normalizeEntityId(productId);

    if (!normalizedProductId) {
      return;
    }

    if (node) {
      productCardRefs.current.set(normalizedProductId, node);
      return;
    }

    productCardRefs.current.delete(normalizedProductId);
  }, []);

  const clearAiProductScrollTimers = useCallback(() => {
    if (aiProductScrollTimerRef.current) {
      window.clearTimeout(aiProductScrollTimerRef.current);
      aiProductScrollTimerRef.current = null;
    }

    if (aiProductFocusTimerRef.current) {
      window.clearTimeout(aiProductFocusTimerRef.current);
      aiProductFocusTimerRef.current = null;
    }

    if (aiProductSpotlightTimerRef.current) {
      window.clearTimeout(aiProductSpotlightTimerRef.current);
      aiProductSpotlightTimerRef.current = null;
    }
  }, []);

  const scrollProductCardIntoView = useCallback((productId) => {
    const normalizedProductId = normalizeEntityId(productId);

    if (!normalizedProductId) {
      return false;
    }

    const productNode = productCardRefs.current.get(normalizedProductId);

    if (!productNode || typeof productNode.scrollIntoView !== "function") {
      return false;
    }

    setAiSpotlightProductId(normalizedProductId);
    productNode.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    if (aiProductSpotlightTimerRef.current) {
      window.clearTimeout(aiProductSpotlightTimerRef.current);
    }

    aiProductSpotlightTimerRef.current = window.setTimeout(() => {
      setAiSpotlightProductId((currentProductId) =>
        currentProductId === normalizedProductId ? "" : currentProductId
      );
      aiProductSpotlightTimerRef.current = null;
    }, 2200);

    return true;
  }, []);

  const scrollAiFloatingProductIntoView = useCallback((productId) => {
    const normalizedProductId = normalizeEntityId(productId);
    const scrollerNode = aiAssistantFloatingResultsRef.current;

    if (!normalizedProductId || !scrollerNode || typeof scrollerNode.querySelector !== "function") {
      return false;
    }

    const targetNode = scrollerNode.querySelector(
      `[data-ai-related-product-id="${normalizedProductId}"]`
    );

    if (!targetNode || typeof targetNode.scrollIntoView !== "function") {
      return false;
    }

    targetNode.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });

    return true;
  }, []);

  useEffect(() => {
    if (!aiAssistantVisibleProductIds.length) {
      clearAiProductScrollTimers();
      setAiSpotlightProductId("");
      return undefined;
    }

    clearAiProductScrollTimers();

    let cancelled = false;
    let currentIndex = 0;
    const productIds = aiAssistantVisibleProductIds.slice(0, 8);

    const focusNextProduct = () => {
      if (cancelled || currentIndex >= productIds.length) {
        return;
      }

      const productId = productIds[currentIndex];
      const location = productCatalogLocationById.get(productId);

      scrollAiFloatingProductIntoView(productId);

      if (
        location?.categoryId &&
        location.categoryId !== activeCategoryIdRef.current
      ) {
        setKioskCatalogMode("category");
        setActiveCategoryId(location.categoryId);
        activeCategoryIdRef.current = location.categoryId;
      }

      if (
        location?.subcategoryId &&
        location.subcategoryId !== "__all__" &&
        location.subcategoryId !== activeSubcategoryIdRef.current
      ) {
        setActiveSubcategoryId(location.subcategoryId);
        activeSubcategoryIdRef.current = location.subcategoryId;
      }

      aiProductFocusTimerRef.current = window.setTimeout(() => {
        if (cancelled) {
          return;
        }

        scrollProductCardIntoView(productId);
        aiProductFocusTimerRef.current = null;
      }, 120);

      currentIndex += 1;

      if (currentIndex < productIds.length) {
        aiProductScrollTimerRef.current = window.setTimeout(focusNextProduct, 1350);
      }
    };

    aiProductScrollTimerRef.current = window.setTimeout(focusNextProduct, 220);

    return () => {
      cancelled = true;
      clearAiProductScrollTimers();
    };
  }, [
    aiAssistantVisibleProductIds,
    clearAiProductScrollTimers,
    productCatalogLocationById,
    scrollAiFloatingProductIntoView,
    scrollProductCardIntoView,
  ]);

  useEffect(
    () => () => {
      clearAiProductScrollTimers();
    },
    [clearAiProductScrollTimers]
  );

  const visibleWaiterOptions = useMemo(
    () =>
      (waiterAssistanceConfig?.options || []).filter(
        (option) => option.active !== false && option.label
      ),
    [waiterAssistanceConfig]
  );
  const cartItemCount = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + Math.max(1, Number(item.quantity || 0)),
        0
      ),
    [cartItems]
  );
  const aiAssistantFloatingButtonSize = 78;
  const resolveAiAssistantFabInitialPosition = useCallback(() => {
    if (typeof window === "undefined") {
      return {
        left: 24,
        top: 120,
      };
    }

    const viewportWidth = Math.max(window.innerWidth || 0, 320);
    const viewportHeight = Math.max(window.innerHeight || 0, 480);
    const rightInset = 18;
    const bottomInset = cartItemCount > 0 ? 108 : 24;

    return {
      left: Math.max(12, viewportWidth - aiAssistantFloatingButtonSize - rightInset),
      top: Math.max(
        12,
        viewportHeight - aiAssistantFloatingButtonSize - bottomInset
      ),
    };
  }, [cartItemCount]);
  const shouldShowAiAssistantFloatingPanel =
    !isKioskExperience &&
    isAiAssistantOpen &&
    isAiAssistantPanelVisible;
  const aiAssistantFloatingResolvedPosition =
    aiAssistantFabPosition || resolveAiAssistantFabInitialPosition();
  const cartQuantityByProduct = useMemo(
    () =>
      cartItems.reduce((acc, item) => {
        const productId = normalizeEntityId(item.productId);
        acc[productId] = (acc[productId] || 0) + Number(item.quantity || 0);
        return acc;
      }, {}),
    [cartItems]
  );
  const productsById = useMemo(
    () =>
      new Map(
        (payload?.products || []).map((product) => [
          normalizeEntityId(product.id || product.productId),
          product,
        ])
      ),
    [payload?.products]
  );
  const productsByName = useMemo(
    () =>
      new Map(
        (payload?.products || [])
          .map((product) => [normalizeEntityLabel(getProductDisplayName(product)), product])
          .filter(([name]) => Boolean(name))
      ),
    [payload?.products]
  );
  const validateCartStock = useCallback(
    (items) => {
      const pizzaValidation = validatePizzaCartItems(items, [...productsById.values()]);
      if (!pizzaValidation.ok) return { ok: false, selectionError: pizzaValidation.message };
      const invalidBeverage = (items || []).find((item) => !resolveBeverageSelection(
        productsById.get(item.productId), getCartItemLinkedSelections(item), linkedSubcategoryRules
      ).valid);
      if (invalidBeverage) return { ok: false, selectionError: `Elegi una variedad disponible para cada ${invalidBeverage.productName || "bebida"} antes de continuar.` };
      if (!shouldEnforceOperationalStockValidation) {
        return { ok: true, items: [], reasons: [] };
      }

      return validateIngredientStock(
        { id: branch?.id || "" },
        (Array.isArray(items) ? items : []).reduce((acc, item) => {
          const product = productsById.get(item?.productId);

          if (!product) {
            return acc;
          }

          return acc.concat(buildCartItemConsumptions(product, item, productsById, linkedSubcategoryRules));
        }, []),
        {
          stockMap: ingredientStockSnapshot,
          allowMissingStockRecords: !shouldEnforceOperationalStockValidation,
        }
      );
    },
    [
      branch?.id,
      ingredientStockSnapshot,
      productsById,
      linkedSubcategoryRules,
      shouldEnforceOperationalStockValidation,
    ]
  );
  const linkedRulesByProductId = useMemo(
    () => indexLinkedRulesByProduct(linkedSubcategoryRules, payload?.products || []),
    [linkedSubcategoryRules, payload?.products]
  );
  const totalProducts = useMemo(
    () => sections.reduce((total, section) => total + (section.products?.length || 0), 0),
    [sections]
  );
  const selectedProduct = useMemo(() => withoutBeverageVarietyIngredients(
    payload?.products?.find((product) => product.id === selectedProductId) || null, linkedSubcategoryRules
  ), [payload?.products, selectedProductId, linkedSubcategoryRules]);
  const selectedPromotion = useMemo(
    () =>
      promotions.find(
        (promotion) =>
          promotion.id === selectedPromotionId && promotion?.active !== false
      ) || null,
    [promotions, selectedPromotionId]
  );
  const selectedProductLinkedRules = useMemo(
    () => linkedRulesByProductId.get(selectedProduct?.id) || [],
    [linkedRulesByProductId, selectedProduct]
  );
  const selectedLinkedRuleGroups = useMemo(
    () =>
      buildLinkedRuleChoiceGroups(
        selectedProductLinkedRules,
        payload?.products || [],
        selectedProductId
      ),
    [payload?.products, selectedProductId, selectedProductLinkedRules]
  );
  const selectedPizzaSizeOptions = useMemo(
    () => getPizzaSizeOptions(selectedProduct),
    [selectedProduct]
  );
  const selectedEmpanadaSaleOptions = useMemo(
    () => getEmpanadaSaleOptions(selectedProduct),
    [selectedProduct]
  );
  const selectedEmpanadaCookingOptions = useMemo(
    () => getEmpanadaCookingOptions(selectedProduct),
    [selectedProduct]
  );
  const selectedProductIsPizza = useMemo(
    () =>
      isPizzaCategoryProduct(
        selectedProduct,
        payload?.categories || [],
        payload?.subcategories || payload?.subcategorias || []
      ),
    [
      payload?.categories,
      payload?.subcategories,
      payload?.subcategorias,
      selectedProduct,
    ]
  );
  const selectedProductIsEmpanada = useMemo(
    () =>
      isEmpanadaCategoryProduct(
        selectedProduct,
        payload?.categories || [],
        payload?.subcategories || payload?.subcategorias || []
      ),
    [
      payload?.categories,
      payload?.subcategories,
      payload?.subcategorias,
      selectedProduct,
    ]
  );
  const selectedPizzaNeedsSizeSetup =
    selectedProductIsPizza && selectedPizzaSizeOptions.length === 0;
  const selectedEmpanadaNeedsSaleSetup =
    selectedProductIsEmpanada && selectedEmpanadaSaleOptions.length === 0;
  const selectedPizzaSizeOption = useMemo(
    () =>
      selectedPizzaSizeOptions.find(
        (option) => option.id === draftPizzaSizeOptionId
      ) ||
      selectedPizzaSizeOptions[0] ||
      null,
    [draftPizzaSizeOptionId, selectedPizzaSizeOptions]
  );
  const selectedEmpanadaSaleOption = useMemo(
    () =>
      selectedEmpanadaSaleOptions.find(
        (option) => option.id === draftEmpanadaSaleOptionId
      ) ||
      selectedEmpanadaSaleOptions[0] ||
      null,
    [draftEmpanadaSaleOptionId, selectedEmpanadaSaleOptions]
  );
  const selectedEmpanadaCookingOption = useMemo(
    () =>
      selectedEmpanadaCookingOptions.find(
        (option) => option.id === draftEmpanadaCookingOptionId
      ) ||
      selectedEmpanadaCookingOptions[0] ||
      null,
    [draftEmpanadaCookingOptionId, selectedEmpanadaCookingOptions]
  );
  const selectedPizzaHalfOptions = useMemo(
    () => {
      if (selectedPizzaNeedsSizeSetup) {
        return [];
      }

      return buildPizzaHalfAndHalfOptions(
        selectedProduct,
        payload?.products || [],
        payload?.categories || [],
        {
          sizeOptionId: selectedPizzaSizeOption?.id || "",
          subcategories: payload?.subcategories || payload?.subcategorias || [],
        }
      );
    },
    [
      payload?.categories,
      payload?.products,
      payload?.subcategories,
      payload?.subcategorias,
      selectedPizzaNeedsSizeSetup,
      selectedPizzaSizeOption?.id,
      selectedProduct,
    ]
  );
  const selectedPizzaHalfProduct = useMemo(
    () =>
      selectedPizzaHalfOptions.find(
        (option) => option.id === draftPizzaHalfProductId
      ) || null,
    [draftPizzaHalfProductId, selectedPizzaHalfOptions]
  );
  const selectedPizzaHalfSelection = useMemo(
    () =>
      createPizzaHalfAndHalfSelection(selectedProduct, selectedPizzaHalfProduct, {
        sizeOptionId: selectedPizzaSizeOption?.id || "",
      }),
    [selectedPizzaHalfProduct, selectedPizzaSizeOption?.id, selectedProduct]
  );
  const selectedLinkedSelectionsByUnit = Array.from(
    { length: draftProductQuantity },
    (_, unitIndex) => {
      const unitSelections = draftLinkedSelectionsByUnit[unitIndex] || {};
      const linkedSelections = selectedLinkedRuleGroups
        .map((group) => {
          const linkedProductId = normalizeEntityId(unitSelections[group.groupKey]);
          const linkedProduct = group.options.find((option) => option.id === linkedProductId) || null;

          if (!linkedProduct) {
            return null;
          }

          return {
            ruleKey: group.groupKey,
            ruleTitle: group.promptTitle,
            groupLabel: group.groupLabel,
            targetCategoryId: linkedProduct.targetCategoryId || "",
            targetCategoryName: linkedProduct.targetCategoryName || "",
            targetSubcategoryId: linkedProduct.targetSubcategoryId || "",
            targetSubcategoryName: linkedProduct.targetSubcategoryName || "",
            productId: linkedProduct.id,
            targetIngredientId: linkedProduct.targetIngredientId || "",
            consumptionMl: linkedProduct.consumptionMl || 0,
            productName: linkedProduct.name,
            unitPrice: resolveDisplayPrice(linkedProduct),
          };
        })
        .filter(Boolean);

      if (draftPizzaPortionMode === "half_and_half" && selectedPizzaHalfSelection) {
        linkedSelections.push(selectedPizzaHalfSelection);
      }

      return linkedSelections;
    }
  );
  const selectedDraftCartItems = selectedProduct
    ? mergeCartEntries([], selectedLinkedSelectionsByUnit.map((linkedSelections) =>
        buildCartItem(selectedProduct, draftSelection, 1, linkedSelections, {
          pizzaSizeOption: selectedPizzaSizeOption,
          pizzaPortionMode: draftPizzaPortionMode,
          empanadaSaleOption: selectedEmpanadaSaleOption,
          empanadaCookingOption: selectedEmpanadaCookingOption,
        })
      ))
    : [];
  const incompleteBeverageUnit = selectedLinkedSelectionsByUnit.findIndex((selections) =>
    !resolveBeverageSelection(selectedProduct, selections, selectedProductLinkedRules).valid
  );
  const baseProductStockStateById = useMemo(() => {
    const nextState = new Map();

    (payload?.products || []).forEach((product) => {
      const productId = normalizeEntityId(product?.id || product?.productId);

      if (!productId) {
        return;
      }

      const validation = !shouldEnforceOperationalStockValidation
        ? { ok: true, items: [], reasons: [] }
        : getOperationalProductStockState(
            { id: branch?.id || "" },
            withoutBeverageVarietyIngredients(product, linkedSubcategoryRules),
            {
              stockMap: ingredientStockSnapshot,
              stockEnabled: shouldEnforceOperationalStockValidation,
              selection: createDraftSelection(product, null),
              allowMissingStockRecords: !shouldEnforceOperationalStockValidation,
              unavailablePrefix: "Sin stock suficiente de",
              incompatiblePrefix: "Hay una configuracion de stock incompatible en",
              fallbackMessage: "Este producto no tiene stock suficiente ahora.",
            }
          );
      const available = validation?.available !== false;

      nextState.set(productId, {
        available,
        validation: validation?.validation || validation,
        reason: validation?.reason || "",
      });
    });
    return nextState;
  }, [
    branch?.id,
    ingredientStockSnapshot,
    payload?.products,
    linkedSubcategoryRules,
    shouldEnforceOperationalStockValidation,
  ]);
  // "Nada" siempre esta disponible, por lo que el stock de una combinacion
  // nunca debe bloquear al producto principal.
  const productStockStateById = baseProductStockStateById;
  const getProductStockState = useCallback(
    (product) =>
      productStockStateById.get(
        normalizeEntityId(product?.id || product?.productId)
      ) || {
        available: true,
        reason: "",
        validation: null,
      },
    [productStockStateById]
  );
  const getLinkedOptionStockState = useCallback(
    (product) =>
      product?.targetIngredientId ? getOperationalProductStockState(
        { id: branch?.id || "" }, product,
        { stockMap: ingredientStockSnapshot, stockEnabled: shouldEnforceOperationalStockValidation }
      ) : baseProductStockStateById.get(
        normalizeEntityId(product?.id || product?.productId)
      ) || {
        available: true,
        reason: "",
        validation: null,
      },
    [baseProductStockStateById, branch?.id, ingredientStockSnapshot, shouldEnforceOperationalStockValidation]
  );
  const selectedPromotionProducts = useMemo(() => {
    if (!selectedPromotion) {
      return [];
    }

    if (selectedPromotion.type === "combo") {
      return (selectedPromotion.items || [])
        .map((item) => {
          const product = resolvePromotionItemProduct(item, productsById, productsByName);

          if (!product) {
            return null;
          }

          return {
            ...product,
            comboQuantity: Math.max(1, Number(item.quantity || 1)),
          };
        })
        .filter(Boolean);
    }

    return (payload?.products || []).filter((product) => {
      if (isLinkedOnlyProduct(product, hiddenStandaloneProductIds)) {
        return false;
      }

      if (selectedPromotion.scopeType === "product") {
        return (
          normalizeEntityId(product.id || product.productId) ===
          normalizeEntityId(selectedPromotion.productId)
        );
      }

      if (selectedPromotion.scopeType === "subcategory") {
        return getProductSubcategoryId(product) === selectedPromotion.subcategoryId;
      }

      return getProductCategoryId(product) === selectedPromotion.categoryId;
    });
  }, [
    hiddenStandaloneProductIds,
    payload?.products,
    productsById,
    productsByName,
    selectedPromotion,
  ]);
  const selectedPromotionIsSelectableNow = selectedPromotion
    ? isPromotionSelectableNow(selectedPromotion, new Date())
    : false;

  const activeSection =
    sections.find((section) => section.id === activeCategoryId) || sections[0] || null;
  const activeSubcategories = useMemo(() => {
    if (!activeSection) {
      return [];
    }

    const subcategories = Array.isArray(activeSection.subcategories)
      ? activeSection.subcategories.filter((subcategory) => subcategory?.products?.length > 0)
      : [];

    if (subcategories.length > 0) {
      return subcategories;
    }

    return [
      {
        id: "__general__",
        name: "Especialidades",
        products: activeSection.products || [],
      },
    ];
  }, [activeSection]);
  const showSubcategorySelector = useMemo(
    () =>
      activeSubcategories.length > 1 ||
      activeSubcategories.some((subcategory) => subcategory.id !== "__general__"),
    [activeSubcategories]
  );
  const visibleProducts = useMemo(() => {
    if (!activeSection) {
      return [];
    }

    if (!showSubcategorySelector || !activeSubcategoryId || activeSubcategoryId === "__all__") {
      return activeSection.products || [];
    }

    return (
      activeSubcategories.find((subcategory) => subcategory.id === activeSubcategoryId)?.products ||
      activeSection.products ||
      []
    );
  }, [activeSection, activeSubcategories, activeSubcategoryId, showSubcategorySelector]);
  const unavailableVisibleProductsCount = useMemo(
    () =>
      visibleProducts.reduce((total, product) => {
        const productStockState = getProductStockState(product);
        return total + (productStockState.available === false ? 1 : 0);
      }, 0),
    [getProductStockState, visibleProducts]
  );
  const availableVisibleProductsCount = Math.max(
    0,
    visibleProducts.length - unavailableVisibleProductsCount
  );
  const activePromotions = useMemo(
    () =>
      promotions.filter(
        (promotion) =>
          isRenderableRecord(promotion) &&
          promotion?.active !== false &&
          (!isDailyMenuPromotion(promotion) ||
            isPromotionAvailableNow(promotion, promotionNow))
      ),
    [promotionNow, promotions]
  );
  const availablePromotions = useMemo(
    () => activePromotions.filter((promotion) => isPromotionAvailableNow(promotion, promotionNow)),
    [activePromotions, promotionNow]
  );
  const availablePromotionIds = useMemo(
    () =>
      new Set(
        availablePromotions.map((promotion) => promotion.id)
      ),
    [availablePromotions]
  );
  const cartPricing = useMemo(
    () => buildPromotionPricing(cartItems, availablePromotions, payload?.products || [], checkoutMethod),
    [cartItems, availablePromotions, payload?.products, checkoutMethod]
  );
  const cartTotalAmount = cartPricing.finalTotal;
  const submittedOrderItems = useMemo(
    () => (Array.isArray(submittedOrderSnapshot?.items) ? submittedOrderSnapshot.items : []),
    [submittedOrderSnapshot]
  );
  const hasDraftCart = cartItems.length > 0;
  const reviewUsesSubmittedOrder =
    !isEditingSubmittedOrder &&
    !hasDraftCart &&
    (submittedOrderItems.length > 0 ||
      Boolean(
        submittedOrderSnapshot?.paymentIntent &&
          normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) ===
            "deep_link"
      ));
  const reviewOrderItems = useMemo(
    () =>
      hasDraftCart
        ? cartItems
        : reviewUsesSubmittedOrder
          ? submittedOrderItems
          : [],
    [cartItems, hasDraftCart, reviewUsesSubmittedOrder, submittedOrderItems]
  );
  const reviewItemCount = useMemo(
    () =>
      reviewOrderItems.reduce(
        (total, item) => total + Math.max(1, Number(item.quantity || 0)),
        0
      ),
    [reviewOrderItems]
  );
  const reviewPricing = hasDraftCart
    ? cartPricing
    : reviewUsesSubmittedOrder
      ? submittedOrderSnapshot?.pricing || {
        listTotal: submittedOrderItems.reduce(
          (total, item) =>
            total +
            Math.max(1, Number(item.quantity || 0)) * Number(item.unitPrice || 0),
          0
        ),
        comboDiscountTotal: 0,
        promotionDiscountTotal: 0,
        totalDiscount: 0,
        finalTotal: submittedOrderItems.reduce(
          (total, item) =>
            total +
            Math.max(1, Number(item.quantity || 0)) * Number(item.unitPrice || 0),
          0
        ),
        adjustments: [],
        }
      : {
          listTotal: 0,
          comboDiscountTotal: 0,
          promotionDiscountTotal: 0,
          totalDiscount: 0,
          finalTotal: 0,
          adjustments: [],
        };
  const reviewCustomerName = useMemo(
    () =>
      sanitizeCustomerName(
        isKioskExperience ? customerNameDraft : customerName
      ),
    [customerName, customerNameDraft, isKioskExperience]
  );
  const kioskKeyboardRows = useMemo(
    () =>
      KIOSK_KEYBOARD_LAYOUT.map((row) =>
        row.map((key) => {
          if (key.length === 1 && /[A-Z\u00d1]/.test(key)) {
            return isKioskKeyboardUppercase ? key : key.toLowerCase();
          }

          return key;
        })
      ),
    [isKioskKeyboardUppercase]
  );
  const reviewListAmount = Number(reviewPricing.listTotal || 0);
  const reviewTotalAmount = Number(reviewPricing.finalTotal || 0);
  const hasReviewableOrder = reviewOrderItems.length > 0;
  const submittedCheckoutMethod = normalizeCheckoutMethod(
    submittedOrderSnapshot?.checkoutMethod
  );
  const isSubmittedKioskQrPaymentView =
    isKioskExperience &&
    reviewUsesSubmittedOrder &&
    submittedCheckoutMethod === KIOSK_QR_CHECKOUT_METHOD &&
    submittedOrderSnapshot?.awaitingPaymentApproval;
  const isSubmittedMenuDigitalPaymentView =
    !isKioskExperience &&
    reviewUsesSubmittedOrder &&
    submittedCheckoutMethod === "deep_link";
  const isSubmittedDigitalPaymentView =
    isSubmittedKioskQrPaymentView || isSubmittedMenuDigitalPaymentView;
  const submittedOrderPaymentStatus = String(
    submittedOrderSnapshot?.paymentStatus || "pending"
  )
    .trim()
    .toLowerCase();
  const submittedOrderCancellationSource =
    submittedOperationalOrder || submittedOrderSnapshot?.pendingOrder || submittedOrderSnapshot;
  const submittedOrderCancellationPhase = String(
    submittedOrderCancellationSource?.phase || "new"
  )
    .trim()
    .toLowerCase();
  const submittedOrderCancellationKitchenStatus = String(
    submittedOrderCancellationSource?.kitchenDispatchStatus || ""
  )
    .trim()
    .toLowerCase();
  const submittedOrderCancellationPaymentStatus = String(
    submittedOrderCancellationSource?.paymentStatus || submittedOrderPaymentStatus
  )
    .trim()
    .toLowerCase();
  const submittedPaymentOperationStatus = String(
    submittedPaymentOperation?.status || ""
  )
    .trim()
    .toLowerCase();
  const submittedPaymentIntentStatus = String(
    submittedOrderSnapshot?.paymentIntent?.status || ""
  )
    .trim()
    .toLowerCase();
  const submittedOrderHasDeliveredItems = (
    Array.isArray(submittedOrderCancellationSource?.items)
      ? submittedOrderCancellationSource.items
      : submittedOrderItems
  ).some(
    (item) =>
      item?.delivered === true ||
      String(item?.deliveryStatus || "").trim().toLowerCase() === "delivered"
  );
  const submittedOrderHasPaymentInProgress = Boolean(
    submittedOrderSnapshot?.awaitingPaymentApproval ||
      String(submittedOrderCancellationSource?.pendingDigitalPaymentIntentId || "").trim() ||
      (submittedOrderSnapshot?.paymentIntent?.id &&
        ![
          "cancelled",
          "canceled",
          "rejected",
          "failed",
          "expired",
          "voided",
        ].includes(submittedPaymentIntentStatus)) ||
      (["created", "link_ready", "pending_confirmation", "link_sent"].includes(
        submittedPaymentOperationStatus
      ) && String(submittedPaymentOperation?.paymentIntentId || "").trim())
  );
  const canCancelSubmittedOrderNow =
    !isKioskExperience &&
    reviewUsesSubmittedOrder &&
    submittedOrderItems.length > 0 &&
    Boolean(String(submittedOrderSnapshot?.orderId || "").trim()) &&
    submittedOrderCancellationPhase === "new" &&
    !submittedOrderHasDeliveredItems &&
    !["screen_confirmed", "screen_ready"].includes(
      submittedOrderCancellationKitchenStatus
    ) &&
    !String(submittedOrderCancellationSource?.kitchenPrintedAt || "").trim() &&
    !submittedOrderHasPaymentInProgress &&
    ![
      "paid",
      "approved",
      "closed",
      "completed",
      "accredited",
      "settled",
      "cancelled",
      "canceled",
      "refunded",
      "resolved",
    ].includes(submittedOrderCancellationPaymentStatus) &&
    ![
      "paid",
      "approved",
      "closed",
      "completed",
      "accredited",
      "settled",
      "refunded",
    ].includes(submittedPaymentOperationStatus);
  const canPaySubmittedOrderNow =
    !tabletSessionKey &&
    !isKioskExperience &&
    reviewUsesSubmittedOrder &&
    submittedOrderItems.length > 0 &&
    Boolean(String(submittedOrderSnapshot?.orderId || "").trim()) &&
    !submittedOrderSnapshot?.awaitingPaymentApproval &&
    submittedCheckoutMethod !== "deep_link" &&
    ![
      "paid",
      "approved",
      "closed",
      "completed",
      "accredited",
      "cancelled",
      "resolved",
    ].includes(
      submittedOrderPaymentStatus
    );
  const isCartCheckoutStep =
    !reviewUsesSubmittedOrder && cartModalStep === "checkout";
  const showCartCloseIcon =
    !isKioskExperience && !isCartCheckoutStep && !isKioskQrPaymentPending;
  const isCartPaymentView = isCartCheckoutStep || isSubmittedDigitalPaymentView;
  const shouldShowCartReviewList =
    !isSubmittedDigitalPaymentView &&
    (reviewUsesSubmittedOrder || !isCartCheckoutStep);

  useEffect(() => {
    if (reviewUsesSubmittedOrder || !hasDraftCart) {
      setCartModalStep("review");
    }
  }, [hasDraftCart, reviewUsesSubmittedOrder]);

  const featuredPromotions = useMemo(() => {
    const activeCategory = activeSection?.id || "";

    return [...activePromotions]
      .sort((left, right) => {
        const leftAvailability = availablePromotionIds.has(left.id) ? 1 : 0;
        const rightAvailability = availablePromotionIds.has(right.id) ? 1 : 0;
        const leftDisplayPriority = getPromotionDisplayPriority(left);
        const rightDisplayPriority = getPromotionDisplayPriority(right);
        const leftPriority = left.categoryId === activeCategory ? 1 : 0;
        const rightPriority = right.categoryId === activeCategory ? 1 : 0;

        if (leftAvailability !== rightAvailability) {
          return rightAvailability - leftAvailability;
        }

        if (leftDisplayPriority !== rightDisplayPriority) {
          return rightDisplayPriority - leftDisplayPriority;
        }

        if (leftPriority !== rightPriority) {
          return rightPriority - leftPriority;
        }

        return new Date(right.createdAt || 0) - new Date(left.createdAt || 0);
      })
      .slice(0, 6);
  }, [activePromotions, activeSection?.id, availablePromotionIds]);
  const activeCategoryPromotions = useMemo(() => {
    if (isKioskExperience || !activeSection?.id) {
      return [];
    }

    return [...activePromotions]
      .filter((promotion) => promotion.categoryId === activeSection.id)
      .sort((left, right) => {
        const leftAvailability = availablePromotionIds.has(left.id) ? 1 : 0;
        const rightAvailability = availablePromotionIds.has(right.id) ? 1 : 0;
        const leftDisplayPriority = getPromotionDisplayPriority(left);
        const rightDisplayPriority = getPromotionDisplayPriority(right);

        if (leftAvailability !== rightAvailability) {
          return rightAvailability - leftAvailability;
        }

        if (leftDisplayPriority !== rightDisplayPriority) {
          return rightDisplayPriority - leftDisplayPriority;
        }

        return new Date(right.createdAt || 0) - new Date(left.createdAt || 0);
      })
      .slice(0, 4);
  }, [activePromotions, activeSection?.id, availablePromotionIds, isKioskExperience]);
  const kioskCatalogPromotions = useMemo(
    () =>
      [...activePromotions].sort((left, right) => {
        const leftAvailability = availablePromotionIds.has(left.id) ? 1 : 0;
        const rightAvailability = availablePromotionIds.has(right.id) ? 1 : 0;
        const leftDisplayPriority = getPromotionDisplayPriority(left);
        const rightDisplayPriority = getPromotionDisplayPriority(right);

        if (leftAvailability !== rightAvailability) {
          return rightAvailability - leftAvailability;
        }

        if (leftDisplayPriority !== rightDisplayPriority) {
          return rightDisplayPriority - leftDisplayPriority;
        }

        return new Date(right.createdAt || 0) - new Date(left.createdAt || 0);
      }),
    [activePromotions, availablePromotionIds]
  );
  const kioskCategoryCards = useMemo(() => {
    const categoryCards = sections.map((section) => ({
      id: section.id,
      type: "category",
      icon: resolveKioskSectionIcon(section),
      label: getCategoryDisplayName(section),
      imageUrl: section.imageUrl || "",
      description:
        section.description ||
        `${Number(section?.products?.length || 0)} opcion${
          Number(section?.products?.length || 0) === 1 ? "" : "es"
        } para elegir`,
      meta:
        Number(section?.products?.length || 0) > 0
          ? `${section.products.length} opcion${section.products.length === 1 ? "" : "es"}`
          : "",
    }));

    if (activePromotions.length === 0) {
      return categoryCards;
    }

    return [
      {
        id: KIOSK_PROMOTIONS_SECTION_ID,
        type: "promotions",
        icon: resolveKioskSectionIcon({ type: "promotions", label: "Promos y combos" }),
        label: "Promos y combos",
        imageUrl:
          activePromotions
            .map((promotion) =>
              resolvePromotionUiImageUrl(
                promotion,
                MAX_KIOSK_PROMOTION_DATA_IMAGE_LENGTH
              )
            )
            .find(Boolean) || "",
        description: "Combos y promociones activas para este momento.",
        meta: `${activePromotions.length} disponible${activePromotions.length === 1 ? "" : "s"}`,
      },
      ...categoryCards,
    ];
  }, [activePromotions, sections]);

  const recoverCatalogRender = useCallback(() => {
    setSelectedPromotionId("");
    setSelectedProductId("");
    setActiveSubcategoryId("__all__");
    setKioskCatalogMode("picker");
    setActiveCategoryId(sections[0]?.id || "");
  }, [sections]);

  useEffect(() => {
    if (!activeSection) {
      setActiveSubcategoryId("");
      return;
    }

    if (!showSubcategorySelector) {
      setActiveSubcategoryId("__all__");
      return;
    }

    setActiveSubcategoryId((previous) => {
      if (previous === "__all__") {
        return previous;
      }

      return activeSubcategories.some((subcategory) => subcategory.id === previous)
        ? previous
        : "__all__";
    });
  }, [activeSection, activeSubcategories, showSubcategorySelector]);

  const primaryColor = branch.primaryColor || brandTheme.primaryColor || "#3498db";
  const qrMenuStyle = normalizeQrMenuStyle(branch?.qrMenuStyle ?? branchProfile?.qrMenuStyle);
  const isVisualMenu = !isKioskExperience && qrMenuStyle !== "classic";
  const secondaryColor =
    branch.secondaryColor || brandTheme.secondaryColor || "#ffffff";
  const qrFontFamily = brandTheme.fontFamily;
  const heroBrandLogo = brandTheme.effectiveLogoCompactUrl || "";
  const promotionBackdropLogoUrl = sanitizeUiImageUrl(
    brandTheme.effectiveLogoCompactUrl ||
      branchProfile?.branding?.logoPrimaryUrl ||
      branchProfile?.branding?.logoCompactUrl ||
      "",
    MAX_QR_HERO_DATA_IMAGE_LENGTH
  );
  const loadingLogoUrl = brandTheme.effectiveLoadingLogoUrl || heroBrandLogo || "";
  const loadingLogoAnimation =
    brandTheme.loadingAnimation === "pulse"
      ? { animation: "mozzoapp-loading-pulse 1.45s ease-in-out infinite" }
      : brandTheme.loadingAnimation === "float"
        ? { animation: "mozzoapp-loading-float 1.9s ease-in-out infinite" }
        : { animation: "mozzoapp-loading-spin 1.8s linear infinite" };
  const showPrices = branch.showPrices !== false;
  const heroImageUrl = sanitizeUiImageUrl(
    branch.coverUrl || branchProfile?.imagenPortada || "",
    MAX_QR_HERO_DATA_IMAGE_LENGTH
  );
  const kioskInstitutionalLogoUrl = sanitizeUiImageUrl(
    MOZZOAPP_KIOSK_APP_LOGO_URL,
    MAX_QR_HERO_DATA_IMAGE_LENGTH
  );
  const kioskDineInImageUrl = sanitizeUiImageUrl(
    branch?.kioskDineInImageUrl ||
      branch?.kiosk_dine_in_image_url ||
      branchProfile?.kioskDineInImageUrl ||
      "",
    MAX_QR_HERO_DATA_IMAGE_LENGTH
  );
  const kioskTakeawayImageUrl = sanitizeUiImageUrl(
    branch?.kioskTakeawayImageUrl ||
      branch?.kiosk_takeaway_image_url ||
      branchProfile?.kioskTakeawayImageUrl ||
      "",
    MAX_QR_HERO_DATA_IMAGE_LENGTH
  );
  const kioskServiceTypeLabel =
    kioskServiceType === "dine_in"
      ? t("dineIn")
      : kioskServiceType === "takeaway"
        ? t("takeaway")
        : t("chooseMode");
  const kioskWelcomeChoices = useMemo(() => {
    return [
      {
        id: "dine_in",
        title: t("dineIn"),
        subtitle: t("dineInSubtitle"),
        imageUrl: kioskDineInImageUrl || "",
        fallbackLabel: t("dineInFallback"),
        emoji: "ðŸ”",
      },
      {
        id: "takeaway",
        title: t("takeaway"),
        subtitle: t("takeawaySubtitle"),
        imageUrl: kioskTakeawayImageUrl || "",
        fallbackLabel: t("takeawayFallback"),
        emoji: "ðŸ¥¡",
      },
    ];
  }, [
    kioskDineInImageUrl,
    kioskTakeawayImageUrl,
    t,
  ]);
  const kioskLanguageControl = (
    <div ref={kioskLanguageMenuRef} style={styles.kioskLanguageSelector}>
      <button
        type="button"
        style={styles.kioskLanguageTrigger}
        aria-label={tx("Idioma")}
        aria-expanded={isKioskLanguageMenuOpen}
        onClick={() => setIsKioskLanguageMenuOpen((prev) => !prev)}
      >
        <span style={styles.kioskLanguageTriggerIcon}>
          <GlobeLanguageIcon color="#475569" />
        </span>
        <span style={styles.kioskLanguageTriggerText}>
          {
            KIOSK_LANGUAGE_OPTIONS.find((option) => option.id === kioskLanguage)?.label ||
            KIOSK_LANGUAGE_OPTIONS[0].label
          }
        </span>
        <span style={styles.kioskLanguageChevron}>
          <ChevronDownIcon color="#64748b" open={isKioskLanguageMenuOpen} />
        </span>
      </button>
      {isKioskLanguageMenuOpen ? (
        <div style={styles.kioskLanguageDropdown}>
          {KIOSK_LANGUAGE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                setKioskLanguage(option.id);
                setIsKioskLanguageMenuOpen(false);
              }}
              style={{
                ...styles.kioskLanguageOption,
                ...(kioskLanguage === option.id ? styles.kioskLanguageOptionActive : {}),
              }}
            >
              <span style={styles.kioskLanguageOptionCode}>{option.short}</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
  const getKioskKeyboardKeyLabel = useCallback(
    (key) => {
      if (key === "ESPACIO") {
        return t("space");
      }

      if (key === "BORRAR") {
        return t("delete");
      }

      if (key === "LIMPIAR") {
        return t("clear");
      }

      if (key === "LISTO") {
        return t("done");
      }

      if (key === "SHIFT") {
        return isKioskKeyboardUppercase ? t("lower") : t("upper");
      }

      return key;
    },
    [isKioskKeyboardUppercase, t]
  );
  const fallbackHeroPalette = useMemo(
    () =>
      buildAdaptiveHeroPalette({
        backgroundLuminance: getCssColorLuminance(primaryColor, 0.45),
        preferredTextColor: secondaryColor,
      }),
    [primaryColor, secondaryColor]
  );
  const [heroPalette, setHeroPalette] = useState(fallbackHeroPalette);

  useEffect(() => {
    let cancelled = false;

    if (!heroImageUrl) {
      setHeroPalette(fallbackHeroPalette);
      return () => {
        cancelled = true;
      };
    }

    analyzeImageLuminance(heroImageUrl)
      .then((backgroundLuminance) => {
        if (cancelled) {
          return;
        }

        setHeroPalette(
          buildAdaptiveHeroPalette({
            backgroundLuminance,
            preferredTextColor: secondaryColor,
          })
        );
      })
      .catch(() => {
        if (!cancelled) {
          setHeroPalette(fallbackHeroPalette);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackHeroPalette, heroImageUrl, secondaryColor]);

  const effectiveHeroTextColor = heroImageUrl
    ? heroPalette.textColor
    : secondaryColor;

  const renderProductCard = (product, showSubcategoryBadge = false) => {
    const productId = normalizeEntityId(product?.id || product?.productId);
    const productName = toRenderableText(product?.name, "Producto");
    const productDescription = toRenderableText(product?.description, "");
    const customizationSummary = getCustomizationSummary(
      product,
      customizationsByProduct[product.id]
    );
    const productCartQuantity = cartQuantityByProduct[product.id] || 0;
    const productStockState = getProductStockState(product);
    const isOutOfStock = productStockState.available === false;
    const isAiSpotlightProduct = productId && aiSpotlightProductId === productId;

    if (isCompactViewport) {
      return (
        <button
          key={product.id}
          ref={(node) => registerProductCardRef(productId, node)}
          type="button"
          style={{
            ...styles.productTileButton,
            ...(isOutOfStock
              ? {
                  cursor: "not-allowed",
                  opacity: 0.62,
                }
              : {}),
          }}
          onClick={() => {
            if (isOutOfStock) {
              return;
            }

            openProductDetail(product);
          }}
          disabled={isOutOfStock}
        >
          <article
            style={{
              ...styles.productTileCard,
              ...(isAiSpotlightProduct
                ? styles.aiSpotlightProductCard
                : {}),
              ...(isOutOfStock
                ? {
                    borderColor: "#fca5a5",
                    backgroundColor: "#fff5f5",
                  }
                : {}),
            }}
          >
              <CatalogImage
                src={product.imageUrl}
                alt={tx(productName)}
                loading="lazy"
                decoding="async"
                style={{
                  ...styles.productTileImage,
                ...(isKioskExperience
                  ? {
                        objectFit: "cover",
                        objectPosition: "center",
                      }
                    : {}),
                }}
              />

            <div style={styles.productTileBody}>
              <div style={styles.productTileTop}>
                {showSubcategoryBadge && product.subcategoryName ? (
                  <span style={styles.productTileCategoryBadge}>
                    {tx(product.subcategoryName)}
                  </span>
                ) : null}
                <h4 style={styles.productTileName}>{tx(productName)}</h4>
                {showPrices ? (
                  <strong
                    style={{
                      ...styles.productTilePrice,
                      color: primaryColor,
                    }}
                  >
                    {currencyFormatter.format(resolveDisplayPrice(product))}
                  </strong>
                ) : null}
              </div>

              <div style={styles.productTileMeta}>
                {isOutOfStock ? (
                  <span
                    style={{
                      ...styles.productTileBadge,
                      borderColor: "#dc2626",
                      color: "#b91c1c",
                      backgroundColor: "#fef2f2",
                    }}
                  > {tx("Sin stock")} </span>
                ) : productCartQuantity > 0 ? (
                  <span
                    style={{
                      ...styles.productTileBadge,
                      color: primaryColor,
                      borderColor: primaryColor,
                    }}
                  >
                    {productCartQuantity} {tx("en pedido")} </span>
                ) : customizationSummary ? (
                  <span
                    style={{
                      ...styles.productTileBadge,
                      color: primaryColor,
                      borderColor: primaryColor,
                    }}
                  > {tx("Personalizado")} </span>
                ) : product.ingredients?.length > 0 ? (
                  <span style={styles.productTileHint}>
                    {product.ingredients.length} {tx("opciones")} </span>
                ) : (
                  <span style={styles.productTileHint}>{tx("Ver detalle")}</span>
                )}
              </div>
              {isOutOfStock && productStockState.reason ? (
                <span
                  style={{
                    ...styles.productTileHint,
                    color: "#b91c1c",
                    lineHeight: 1.35,
                  }}
                >
                  {productStockState.reason}
                </span>
              ) : null}
            </div>
          </article>
        </button>
      );
    }

    return (
      <article
        key={product.id}
        ref={(node) => registerProductCardRef(productId, node)}
        style={{
          ...styles.productCard,
          ...(isAiSpotlightProduct ? styles.aiSpotlightProductCard : {}),
          ...(isOutOfStock
            ? {
                borderColor: "#fca5a5",
                backgroundColor: "#fff8f8",
                opacity: 0.74,
              }
            : {}),
          ...(isKioskExperience
            ? {
                borderRadius: 20,
                border: "1px solid #d1d5db",
                boxShadow: "0 16px 30px rgba(15, 23, 42, 0.09)",
              }
            : {}),
        }}
      >
          <CatalogImage
            src={product.imageUrl}
            alt={tx(productName)}
            loading="lazy"
            decoding="async"
            style={{
              ...styles.productImage,
              ...(isKioskExperience
                ? {
                    aspectRatio: "5 / 4",
                    objectFit: "cover",
                    objectPosition: "center",
                  }
                : {}),
              ...(isOutOfStock
                ? {
                    cursor: "not-allowed",
                    filter: "grayscale(0.12)",
                  }
                : {}),
            }}
            onClick={() => {
              if (isOutOfStock) {
                return;
              }

              openProductDetail(product);
            }}
            role="button"
            tabIndex={isOutOfStock ? -1 : 0}
            onKeyDown={(event) => {
              if (isOutOfStock) {
                return;
              }

              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openProductDetail(product);
              }
            }}
          />

          <div
            style={{
              ...styles.productBody,
              ...(isKioskExperience
                ? {
                    gap: 8,
                    padding: 12,
                  }
                : {}),
            }}
          >
          <div style={styles.productHead}>
            <div style={styles.productHeadText}>
              {showSubcategoryBadge && product.subcategoryName ? (
                <span style={styles.productCategoryBadge}>
                  {tx(product.subcategoryName)}
                </span>
              ) : null}
              <h4 style={styles.productName}>{tx(productName)}</h4>
              {customizationSummary && (
                <span
                  style={{
                    ...styles.customBadge,
                    borderColor: primaryColor,
                    color: primaryColor,
                  }}
                > {tx("Personalizado")} </span>
              )}
              {isOutOfStock ? (
                <span
                  style={{
                    ...styles.customBadge,
                    borderColor: "#dc2626",
                    color: "#b91c1c",
                    backgroundColor: "#fef2f2",
                  }}
                > {tx("Sin stock")} </span>
              ) : null}
            </div>
            {showPrices ? (
              <strong style={{ ...styles.productPrice, color: primaryColor }}>
                {productHasPizzaSizeOptions(product) ||
                productHasEmpanadaSaleOptions(product)
                  ? tx("Desde ")
                  : ""}
                {currencyFormatter.format(resolveDisplayPrice(product))}
              </strong>
            ) : null}
          </div>

          {productDescription ? (
            <p style={styles.productDescription}>{tx(productDescription)}</p>
          ) : null}

          {product.ingredients?.length > 0 ? (
            <div style={styles.productMetaRow}>
              <span style={styles.productMeta}>
                {product.ingredients.length} {tx("ingredientes configurables")} </span>
              {customizationSummary?.extraCost > 0 ? (
                <span style={styles.productMetaStrong}>
                  + {currencyFormatter.format(customizationSummary.extraCost)}
                </span>
              ) : null}
              {productCartQuantity > 0 ? (
                <span style={styles.productMetaStrong}>
                  {productCartQuantity} {tx("en pedido")} </span>
              ) : null}
              {isOutOfStock && productStockState.reason ? (
                <span
                  style={{
                    ...styles.productMetaStrong,
                    color: "#b91c1c",
                  }}
                >
                  {productStockState.reason}
                </span>
              ) : null}
            </div>
          ) : (
            <div style={styles.productMetaRow}>
              <span style={styles.productMeta}> {tx("Sin ingredientes configurables cargados")} </span>
              {productCartQuantity > 0 ? (
                <span style={styles.productMetaStrong}>
                  {productCartQuantity} {tx("en pedido")} </span>
              ) : null}
              {isOutOfStock && productStockState.reason ? (
                <span
                  style={{
                    ...styles.productMetaStrong,
                    color: "#b91c1c",
                  }}
                >
                  {productStockState.reason}
                </span>
              ) : null}
            </div>
          )}

          <button
            type="button"
            style={{
              ...styles.productAction,
              backgroundColor: primaryColor,
              color: secondaryColor,
                ...(isKioskExperience
                  ? {
                      minHeight: 46,
                      borderRadius: 14,
                      fontSize: 15,
                    }
                  : {}),
              ...(isOutOfStock
                ? {
                    backgroundColor: "#e2e8f0",
                    color: "#64748b",
                    cursor: "not-allowed",
                  }
                : {}),
            }}
            onClick={() => {
              if (isOutOfStock) {
                return;
              }

              openProductDetail(product);
            }}
            disabled={isOutOfStock}
          >
            {isOutOfStock
              ? tx("Sin stock")
              : product.ingredients?.length > 0
                ? isKioskExperience
                  ? tx("Elegir y personalizar")
                  : tx("Personalizar y agregar")
                : isKioskExperience
                  ? tx("Elegir producto")
                  : tx("Ver y agregar")}
          </button>
        </div>
      </article>
    );
  };

  const openProductDetail = (product, options = {}) => {
    if (reviewUsesSubmittedOrder && submittedOrderEditLock.locked) {
      setCheckoutMessage({
        type: "error",
        text: submittedOrderEditLock.reason,
      });
      return;
    }

    const productStockState = getProductStockState(product);

    if (productStockState.available === false) {
      setCheckoutMessage({
        type: "error",
        text:
          productStockState.reason ||
          "Este producto no tiene stock suficiente ahora.",
      });
      return;
    }

    const linkedRules = linkedRulesByProductId.get(product.id) || [];
    const linkedRuleGroups = buildLinkedRuleChoiceGroups(
      linkedRules,
      payload?.products || [],
      product.id
    );
    const preferredLinkedSelections =
      options?.preferredLinkedSelections &&
      typeof options.preferredLinkedSelections === "object"
        ? options.preferredLinkedSelections
        : {};
    const previousLinkedSelections =
      linkedSelectionsByProduct[product.id] &&
      typeof linkedSelectionsByProduct[product.id] === "object"
        ? linkedSelectionsByProduct[product.id]
        : {};
    const initialDraftLinkedSelections = buildDraftLinkedSelectionsForGroups(
      linkedRuleGroups,
      Object.keys(preferredLinkedSelections).length > 0
        ? preferredLinkedSelections
        : previousLinkedSelections
    );
    const nextDraftLinkedSelections = Object.entries(
      initialDraftLinkedSelections
    ).reduce((acc, [groupKey, linkedProductId]) => {
      const linkedProductState = getLinkedOptionStockState(
        linkedRuleGroups.find((group) => group.groupKey === groupKey)?.options.find((option) => option.id === linkedProductId) || { id: linkedProductId }
      );

      if (linkedProductState.available === false) {
        return acc;
      }

      acc[groupKey] = linkedProductId;
      return acc;
    }, {});
    const pizzaSizeOptions = getPizzaSizeOptions(product);
    const previousPizzaHalfSelection = getPizzaHalfAndHalfSelection(
      options?.previousLinkedSelections || []
    );
    const preferredPizzaSizeOptionId = normalizeEntityId(
      options?.preferredPizzaSizeOptionId ||
        previousPizzaHalfSelection?.pizzaSizeOptionId ||
        ""
    );
    const nextPizzaSizeOption =
      pizzaSizeOptions.find((option) => option.id === preferredPizzaSizeOptionId) ||
      pizzaSizeOptions[0] ||
      null;
    const pizzaHalfOptions = buildPizzaHalfAndHalfOptions(
      product,
      payload?.products || [],
      payload?.categories || [],
      {
        sizeOptionId: nextPizzaSizeOption?.id || "",
        subcategories: payload?.subcategories || payload?.subcategorias || [],
      }
    );
    const preferredPizzaHalfProductId = normalizeEntityId(
      options?.preferredPizzaHalfProductId
    );
    const nextPizzaHalfProductId = pizzaHalfOptions.some(
      (candidate) => candidate.id === preferredPizzaHalfProductId
    )
      ? preferredPizzaHalfProductId
      : "";
    const empanadaSaleOptions = getEmpanadaSaleOptions(product);
    const preferredEmpanadaSaleOptionId = normalizeEntityId(
      options?.preferredEmpanadaSaleOptionId || ""
    );
    const nextEmpanadaSaleOption =
      empanadaSaleOptions.find(
        (option) => option.id === preferredEmpanadaSaleOptionId
      ) ||
      empanadaSaleOptions[0] ||
      null;
    const empanadaCookingOptions = getEmpanadaCookingOptions(product);
    const preferredEmpanadaCookingOptionId = normalizeEntityId(
      options?.preferredEmpanadaCookingOptionId || ""
    );
    const nextEmpanadaCookingOption =
      empanadaCookingOptions.find(
        (option) => option.id === preferredEmpanadaCookingOptionId
      ) ||
      empanadaCookingOptions[0] ||
      null;
    const nextLockedQuantity = Math.max(0, Number(options?.lockedQuantity || 0));
    const nextInitialQuantity = Math.max(1, Number(options?.initialQuantity || 1));
    const preferredSelection =
      options?.preferredSelection && typeof options.preferredSelection === "object"
        ? options.preferredSelection
        : null;

    setCheckoutMessage({ type: "", text: "" });
    pushProductDetailHistoryEntry();
    setSelectedProductId(product.id);
    setDraftSelection(
      createDraftSelection(
        product,
        preferredSelection || customizationsByProduct[product.id]
      )
    );
    setDraftLinkedSelectionsByUnit(Array.from(
      { length: nextLockedQuantity > 0 ? nextLockedQuantity : nextInitialQuantity },
      () => ({ ...nextDraftLinkedSelections })
    ));
    setActiveLinkedUnit(0);
    setDraftPizzaSizeOptionId(nextPizzaSizeOption?.id || "");
    setDraftPizzaPortionMode(nextPizzaHalfProductId ? "half_and_half" : getDefaultPizzaSaleMode(nextPizzaSizeOption));
    setDraftPizzaHalfProductId(nextPizzaHalfProductId);
    setDraftEmpanadaSaleOptionId(nextEmpanadaSaleOption?.id || "");
    setDraftEmpanadaCookingOptionId(nextEmpanadaCookingOption?.id || "");
    setDraftProductQuantity(
      nextLockedQuantity > 0 ? nextLockedQuantity : nextInitialQuantity
    );
    setLockedProductQuantity(nextLockedQuantity);
    setProductDetailPromotionId(options?.promotionId || "");
  };

  const closeProductDetail = () => {
    clearProductDetailSelection();
    consumeProductDetailHistoryEntry();
  };

  const handleChatbotProductSelection = (product, relatedEntry = {}) => {
    if (!product || !aiAssistantProductLookup.has(normalizeEntityId(product.id))) {
      return;
    }

    const productName = getAssistantVisibleProductName(product?.name, "este plato");
    const requestedQuestion = sanitizeDisplayText(
      relatedEntry?.assistantSelectionQuestion || ""
    )
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 320);
    const requestedLabel = sanitizeDisplayText(
      relatedEntry?.assistantSelectionLabel || requestedQuestion
    )
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 160);
    const ingredientSelectionPlan = requestedQuestion
      ? buildPublicMenuAssistantIngredientSelectionPlan(requestedQuestion, product)
      : null;
    const productLinkedRules = linkedRulesByProductId.get(product.id) || [];
    const linkedRuleGroups = buildLinkedRuleChoiceGroups(
      productLinkedRules,
      payload?.products || [],
      product.id
    );
    const linkedSelectionPlan = requestedQuestion
      ? buildAiLinkedSelectionPlan({
          groups: linkedRuleGroups,
          question: requestedQuestion,
          getLinkedOptionStockState,
        })
      : { selectionIdsByGroup: {}, selectionEntries: [] };
    const ingredientNames = (Array.isArray(product?.ingredients) ? product.ingredients : [])
      .map((ingredient) => getAssistantVisibleProductName(ingredient?.name, ""))
      .filter(Boolean)
      .slice(0, 6);
    const ingredientHint = ingredientNames.length
      ? ` Tiene ${ingredientNames.join(", ")}. Puedes quitar o sumar lo que permita el plato.`
      : " Puedes revisar sus detalles antes de agregarlo.";
    const adjustmentHint = ingredientSelectionPlan?.hasChanges
      ? ` Ya deje marcado: ${ingredientSelectionPlan.summaryText}.`
      : ingredientSelectionPlan?.needsManualReview
        ? " Revisa los ingredientes: uno de los cambios pedidos necesita confirmacion manual."
        : requestedLabel
          ? ` Voy a respetar esta indicacion: ${requestedLabel}.`
          : "";
    const linkedHint = linkedSelectionPlan.selectionEntries.length
      ? ` Tambien seleccione ${linkedSelectionPlan.selectionEntries
          .map((entry) => entry.productName)
          .join(", ")}.`
      : "";
    const answer = `Elegiste ${productName}.${adjustmentHint}${linkedHint}${ingredientHint}`;

    setAiConversation((prev) => [
      ...prev,
      createAiConversationMessage("assistant", answer, {
        suggestions: ["Sin algun ingrediente", "Agregar un extra", "Ver mi pedido"],
        relatedProducts: [],
        source: "local",
      }),
    ]);
    // Keep the chat ready when the product dialog closes so the diner can continue ordering.
    setIsAiAssistantPanelVisible(true);
    openProductDetail(product, {
      preferredSelection: ingredientSelectionPlan?.selection || null,
      preferredLinkedSelections: linkedSelectionPlan.selectionIdsByGroup,
    });
  };
  const openPromotionDetail = (promotion) => {
    if (!promotion) {
      return;
    }

    setSelectedPromotionId(promotion.id);
  };
  const closePromotionDetail = () => {
    setSelectedPromotionId("");
  };

  const updateIngredientQuantity = (ingredient, change) => {
    setDraftSelection((prev) => {
      const baseQuantity = Number(ingredient.baseQuantity || 0);
      const current = Number(prev?.[ingredient.id] ?? baseQuantity);
      const minValue = ingredient.canRemove ? 0 : baseQuantity;
      const maxValue = ingredient.canAddMore
        ? baseQuantity +
          Math.max(
            1,
            Number(ingredient.maxExtraQuantity || DEFAULT_MAX_EXTRA_QUANTITY)
          )
        : baseQuantity;
      const nextValue = Math.min(
        maxValue,
        Math.max(minValue, current + change)
      );

      return {
        ...prev,
        [ingredient.id]: nextValue,
      };
    });
  };

  const updateProductQuantity = (change) => {
    const nextQuantity = Math.max(1, draftProductQuantity + change);
    setDraftProductQuantity(nextQuantity);
    // New units start without accompaniments; removed units must not reappear later.
    setDraftLinkedSelectionsByUnit((current) => Array.from(
      { length: nextQuantity }, (_, index) => ({ ...(current[index] || {}) })
    ));
    setActiveLinkedUnit((current) => Math.min(current, nextQuantity - 1));
  };

  const updateLinkedSelection = (groupKey, productId) => {
    setDraftLinkedSelectionsByUnit((current) => current.map((selection, index) =>
      index === activeLinkedUnit ? { ...selection, [groupKey]: productId } : selection
    ));
  };

  const addSelectedProductToCart = () => {
    if (!selectedProduct) {
      return;
    }

    if (selectedPizzaNeedsSizeSetup) {
      setCheckoutMessage({
        type: "error",
        text: "Esta pizza todavia no tiene tamanos cargados. Editala desde Admin y carga Mediana, Grande u otras presentaciones.",
      });
      return;
    }

    if (selectedPizzaSizeOption && !isPizzaSaleModeEnabled(selectedPizzaSizeOption, draftPizzaPortionMode)) {
      setCheckoutMessage({ type: "error", text: "Elegi una modalidad habilitada para este tamano." });
      return;
    }

    if (selectedEmpanadaNeedsSaleSetup) {
      setCheckoutMessage({
        type: "error",
        text: "Esta empanada todavia no tiene precios por Unidad, Media docena o Docena. Editala desde Admin y carga al menos una presentacion.",
      });
      return;
    }

    if (draftPizzaPortionMode === "half_and_half" && !selectedPizzaHalfSelection) {
      setCheckoutMessage({
        type: "error",
        text: "Elegi la otra mitad de la pizza antes de agregarla al pedido.",
      });
      return;
    }

    const shouldMergeWithSubmittedOrder =
      reviewUsesSubmittedOrder && !submittedOrderEditLock.locked;
    const workingCartItems = shouldMergeWithSubmittedOrder
      ? submittedOrderItems.map((item) => ({
          ...item,
          selection: item.selection ? { ...item.selection } : {},
          selectionDetails: Array.isArray(item.selectionDetails)
            ? [...item.selectionDetails]
            : [],
        }))
      : cartItems;
    const nextSelection =
      selectedProduct.ingredients?.length > 0 ? { ...draftSelection } : {};
    const returnPromotion =
      !editingCartItemId && productDetailPromotionId
        ? promotions.find((promotion) => promotion.id === productDetailPromotionId) || null
        : null;
    // Each cart line has one configuration, so pricing, kitchen and stock keep
    // using the exact quantity for that accompaniment instead of the whole batch.
    const nextCartItems = mergeCartEntries(
      workingCartItems.filter((item) => item.id !== editingCartItemId),
      selectedDraftCartItems
    );
    const stockValidation = validateCartStock(nextCartItems);

    if (!stockValidation.ok) {
      setCheckoutMessage({
        type: "error",
        text: getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para agregar ese producto al pedido.",
        }),
      });
      return;
    }

    setCustomizationsByProduct((prev) => ({
      ...prev,
      [selectedProduct.id]: nextSelection,
    }));
    setLinkedSelectionsByProduct((prev) => ({
      ...prev,
      [selectedProduct.id]: { ...(draftLinkedSelectionsByUnit[0] || {}) },
    }));
    setCartItems(nextCartItems);
    if (shouldMergeWithSubmittedOrder) {
      setIsEditingSubmittedOrder(true);
      setCheckoutMethod(
        normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod)
      );
    }
    if (returnPromotion?.type === "combo") {
      setCheckoutMessage({
        type: "success",
        text: `Agregamos "${selectedProduct.name}" al combo. Completa los otros productos y el descuento se aplica solo.`,
      });
    }

    closeProductDetail();
    if (returnPromotion?.type === "combo") {
      setSelectedPromotionId(returnPromotion.id);
    }
  };

  const resetCustomization = () => {
    if (!selectedProduct) {
      return;
    }

    const nextDraft = createDraftSelection(selectedProduct, null);
    const nextLinkedSelections = buildDraftLinkedSelectionsForGroups(
      selectedLinkedRuleGroups,
      {}
    );
    setDraftSelection(nextDraft);
    setDraftLinkedSelectionsByUnit(Array.from(
      { length: draftProductQuantity }, () => ({ ...nextLinkedSelections })
    ));
    setActiveLinkedUnit(0);
    setDraftPizzaSizeOptionId(selectedPizzaSizeOptions[0]?.id || "");
    setDraftPizzaPortionMode(getDefaultPizzaSaleMode(selectedPizzaSizeOptions[0]));
    setDraftPizzaHalfProductId("");
    setDraftEmpanadaSaleOptionId(selectedEmpanadaSaleOptions[0]?.id || "");
    setDraftEmpanadaCookingOptionId(
      selectedEmpanadaCookingOptions[0]?.id || ""
    );
    setCustomizationsByProduct((prev) => ({
      ...prev,
      [selectedProduct.id]: nextDraft,
    }));
    setLinkedSelectionsByProduct((prev) => ({
      ...prev,
      [selectedProduct.id]: nextLinkedSelections,
    }));
  };

  const focusPromotionTarget = (promotion) => {
    if (!promotion) {
      return;
    }

    if (promotion.categoryId) {
      setActiveCategoryId(promotion.categoryId);
    }

    if (promotion.scopeType === "subcategory" && promotion.subcategoryId) {
      setActiveSubcategoryId(promotion.subcategoryId);
      return;
    }

    if (promotion.scopeType === "product" && promotion.productId) {
      const targetProduct = productsById.get(promotion.productId);

      if (targetProduct) {
        setActiveCategoryId(targetProduct.categoryId || promotion.categoryId || "");
        setActiveSubcategoryId(targetProduct.subcategoryId || "__all__");
        openProductDetail(targetProduct);
        return;
      }
    }

    setActiveSubcategoryId("__all__");
  };

  const addComboPromotionToCart = (promotion) => {
    if (!isPromotionSelectableNow(promotion, new Date())) {
      setCheckoutMessage({
        type: "error",
        text: "Este combo no esta disponible en este dia u horario.",
      });
      return;
    }

    const promotionItems = Array.isArray(promotion?.items) ? promotion.items : [];

    if (!promotionItems.length) {
      setCheckoutMessage({
        type: "error",
        text: "Este combo todavia no tiene productos listos para pedir.",
      });
      return;
    }

    const nextItems = promotionItems
      .map((item) => {
        const product = resolvePromotionItemProduct(item, productsById, productsByName);

        if (!product) {
          return null;
        }

        return buildCartItem(product, {}, Math.max(1, Number(item.quantity || 1)));
      })
      .filter(Boolean);

    if (!nextItems.length) {
      setCheckoutMessage({
        type: "error",
        text: "No encontramos los productos reales de este combo en la carta actual.",
      });
      return;
    }

    const nextCartItems = mergeCartEntries(cartItems, nextItems);
    const stockValidation = validateCartStock(nextCartItems);

    if (!stockValidation.ok) {
      setCheckoutMessage({
        type: "error",
        text: getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para agregar este combo al pedido.",
        }),
      });
      return;
    }

    setCartItems(nextCartItems);
    setCheckoutMessage({
      type: "success",
      text: `Agregamos "${promotion.title}" a tu pedido.`,
    });
  };

  const openPromotionProductDetail = (product, promotion = null) => {
    if (!product) {
      return;
    }

    if (promotion && !isPromotionSelectableNow(promotion, new Date())) {
      setCheckoutMessage({
        type: "error",
        text: "Esta promo no esta disponible en este dia u horario.",
      });
      return;
    }

    if (promotion?.categoryId || product.categoryId) {
      setActiveCategoryId(product.categoryId || promotion?.categoryId || "");
    }

    if (product.subcategoryId || product.subcategory_id) {
      setActiveSubcategoryId(
        product.subcategoryId || product.subcategory_id || "__all__"
      );
    } else {
      setActiveSubcategoryId("__all__");
    }

    closePromotionDetail();
    openProductDetail(product, {
      promotionId: promotion?.id || "",
      lockedQuantity:
        promotion?.type === "combo"
          ? Math.max(1, Number(product?.comboQuantity || 1))
          : 0,
    });
  };

  const handlePromotionQuickAction = (promotion) => {
    if (!promotion) {
      return;
    }

    if (!isPromotionSelectableNow(promotion, new Date())) {
      setCheckoutMessage({
        type: "error",
        text: "Esta promo o combo no esta disponible en este dia u horario.",
      });
      return;
    }

    if (promotion.type === "combo") {
      addComboPromotionToCart(promotion);
      closePromotionDetail();
      return;
    }

    if (selectedPromotionProducts.length === 1) {
      openPromotionProductDetail(selectedPromotionProducts[0], promotion);
      return;
    }

    focusPromotionTarget(promotion);
    closePromotionDetail();
  };

  const getPromotionActionLabel = () => "Ver detalle";

  const promotionCardTheme = {
    "--menu-offer-accent": primaryColor,
    "--menu-offer-ink": getCssColorLuminance(primaryColor, 0.45) > 0.179 ? "#172033" : "#fff",
    fontFamily: qrFontFamily,
  };

  const renderQrPromotionCard = (promotion) => {
    const selectable = isPromotionSelectableNow(promotion, new Date());
    const items = Array.isArray(promotion.items) ? promotion.items : [];
    const schedule = toRenderableText(promotion.scheduleSummary) ||
      buildPromotionScheduleSummary(promotion.schedule);
    return (
      <MenuPromotionCard
        tx={tx}
        key={promotion.id}
        title={tx(toRenderableText(promotion.title, "Promocion"))}
        kind={isDailyMenuPromotion(promotion) ? "Menu del dia" : promotion.type === "combo" ? "Combo" : "Promocion"}
        headline={getPromotionHeadline(promotion)}
        description={getPromotionDescription(promotion)}
        imageUrl={resolvePromotionUiImageUrl(promotion)}
        scope={getPromotionScopeLabel(promotion)}
        pricingSummary={toRenderableText(promotion.pricingSummary)}
        items={items.map((item) => `${Math.max(1, Number(item?.quantity || 1))}x ${tx(toRenderableText(item?.name, "Producto"))}`)}
        schedule={schedule.replace(/\u00c2(?=\u00b7)/g, "")}
        validity={toRenderableText(promotion.validity)}
        cashOnly={promotion.paymentCondition === "cash_only"}
        available={availablePromotionIds.has(promotion.id)}
        selectable={selectable}
        onOpen={() => { if (selectable) openPromotionDetail(promotion); }}
      />
    );
  };

  const executeAiAssistantOrderDraft = useCallback(
    (orderDraft, safeQuestion = "", options = {}) => {
      const pushAssistantReply = (answer, extra = {}) => {
        const resolvedAnswer = options.answerOverride || answer;
        const resolvedExtra = {
          ...extra,
          suggestions: options.suggestions || extra.suggestions,
          relatedProducts: options.relatedProducts || extra.relatedProducts,
        };

        if (!resolvedAnswer) {
          return;
        }

        setAiConversation((prev) => [
          ...prev,
          createAiConversationMessage("assistant", resolvedAnswer, resolvedExtra),
        ]);
      };

      if (!orderDraft?.shouldHandle) {
        return false;
      }

      if (orderDraft.status === "not_found") {
        pushAssistantReply(
          orderDraft.answer ||
            "No encontre con claridad ese producto en el menu. Decime el nombre exacto.",
          {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts: orderDraft.relatedProducts || [],
            fallback: true,
            source: "local",
          }
        );
        return true;
      }

      if (orderDraft.status === "clarify") {
        pushAssistantReply(orderDraft.answer, {
          suggestions: orderDraft.suggestions || [],
          relatedProducts: [],
          source: "local",
        });
        return true;
      }

      const orderAction = orderDraft.action || "add";
      const shouldMergeWithSubmittedOrder =
        reviewUsesSubmittedOrder && !submittedOrderEditLock.locked;
      const resolveWorkingCartItems = () =>
        shouldMergeWithSubmittedOrder
          ? submittedOrderItems.map((item) => ({
              ...item,
              selection: item.selection ? { ...item.selection } : {},
              selectionDetails: Array.isArray(item.selectionDetails)
                ? [...item.selectionDetails]
                : [],
            }))
          : cartItems;
      const findOrderDraftCartItem = (items = []) => {
        const draftItemId = normalizeEntityId(orderDraft.cartItemId);
        const draftProductName = normalizeAssistantProductKey(
          orderDraft.cartProductName || ""
        );

        return (Array.isArray(items) ? items : []).find((item) => {
          if (draftItemId && normalizeEntityId(item?.id) === draftItemId) {
            return true;
          }

          if (
            draftProductName &&
            normalizeAssistantProductKey(item?.productName || item?.name || "") ===
              draftProductName
          ) {
            return true;
          }

          return false;
        });
      };

      if (orderAction === "open_cart") {
        setIsAiAssistantPanelVisible(false);
        setIsCartOpen(true);
        setCartModalStep("review");
        pushAssistantReply(
          orderDraft.answer || "Te abro Mi pedido para revisarlo.",
          {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts: orderDraft.relatedProducts || [],
            source: "local",
          }
        );
        return true;
      }

      if (orderAction === "remove") {
        if (reviewUsesSubmittedOrder && submittedOrderEditLock.locked) {
          pushAssistantReply(submittedOrderEditLock.reason, {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts: [],
            fallback: true,
            source: "local",
          });
          return true;
        }

        const workingCartItems = resolveWorkingCartItems();
        const targetCartItem = findOrderDraftCartItem(workingCartItems);

        if (!targetCartItem) {
          setIsAiAssistantPanelVisible(false);
          setIsCartOpen(true);
          setCartModalStep("review");
          pushAssistantReply(
            "No encontre ese producto dentro de Mi pedido. Te lo abro para revisarlo.",
            {
              suggestions: aiAssistantQuickSuggestions,
              relatedProducts: [],
              fallback: true,
              source: "local",
            }
          );
          return true;
        }

        const nextCartItems = workingCartItems.filter(
          (item) => item.id !== targetCartItem.id
        );
        setCartItems(nextCartItems);
        if (shouldMergeWithSubmittedOrder) {
          setIsEditingSubmittedOrder(true);
          setCheckoutMethod(
            normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod)
          );
        }

        const answer = `Listo, saque ${targetCartItem.productName} de Mi pedido.`;
        setCheckoutMessage({ type: "success", text: answer });
        pushAssistantReply(answer, {
          suggestions: ["Como va mi pedido?", "Seguir eligiendo", "Llamar al mozo"],
          relatedProducts: [],
          source: "local",
        });
        return true;
      }

      if (orderAction === "add_many") {
        if (reviewUsesSubmittedOrder && submittedOrderEditLock.locked) {
          pushAssistantReply(submittedOrderEditLock.reason, {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts: [],
            fallback: true,
            source: "local",
          });
          return true;
        }

        const orderItems = Array.isArray(orderDraft.items)
          ? orderDraft.items.slice(0, 8)
          : [];

        if (!orderItems.length) {
          pushAssistantReply(
            "No pude identificar con claridad que productos queres agregar.",
            {
              suggestions: aiAssistantQuickSuggestions,
              relatedProducts: orderDraft.relatedProducts || [],
              fallback: true,
              source: "local",
            }
          );
          return true;
        }

        let workingCartItems = resolveWorkingCartItems();
        const linkedSelectionsByProductPatch = {};
        const addedSummaryParts = [];
        const addedRelatedProducts = [];

        for (const itemDraft of orderItems) {
          const itemProductId = normalizeEntityId(
            itemDraft.productId || itemDraft.product?.id
          );
          const itemProduct =
            (payload?.products || []).find(
              (candidate) =>
                normalizeEntityId(candidate?.id || candidate?.productId) ===
                itemProductId
            ) ||
            itemDraft.product ||
            null;

          if (!itemProduct || !aiAssistantProductLookup.has(normalizeEntityId(itemProduct.id))) {
            pushAssistantReply(
              "No encontre uno de esos productos en la carta visible.",
              {
                suggestions: aiAssistantQuickSuggestions,
                relatedProducts: orderDraft.relatedProducts || [],
                fallback: true,
                source: "local",
              }
            );
            return true;
          }

          const itemProductName = getAssistantVisibleProductName(
            itemProduct?.name,
            "Producto"
          );

          const productStockState = getProductStockState(itemProduct);
          if (productStockState.available === false) {
            pushAssistantReply(
              productStockState.reason ||
                `No hay stock suficiente para agregar ${itemProductName}.`,
              {
                suggestions: aiAssistantQuickSuggestions,
                relatedProducts: [summarizeAiRelatedProduct(itemProduct)],
                fallback: true,
                source: "local",
              }
            );
            return true;
          }

          const itemQuestion = itemDraft.question || safeQuestion;
          const itemLinkedRules = linkedRulesByProductId.get(itemProduct.id) || [];
          const itemLinkedRuleGroups = buildLinkedRuleChoiceGroups(
            itemLinkedRules,
            payload?.products || [],
            itemProduct.id
          );
          const itemLinkedSelectionPlan = buildAiLinkedSelectionPlan({
            groups: itemLinkedRuleGroups,
            question: itemQuestion,
            getLinkedOptionStockState,
          });
          const itemIngredientSelectionPlan =
            itemDraft?.ingredientSelectionPlan || null;
          const itemQuantity = Math.max(1, Number(itemDraft.quantity || 1));

          if (
            itemLinkedSelectionPlan.missingRequiredGroups.length > 0 ||
            itemIngredientSelectionPlan?.needsManualReview ||
            shouldOpenProductCustomizationForAi(
              itemQuestion,
              itemProduct,
              itemIngredientSelectionPlan
            )
          ) {
            const missingLabels = itemLinkedSelectionPlan.missingRequiredGroups
              .map((group) => group.promptTitle || group.groupLabel)
              .filter(Boolean)
              .join(", ");
            const answer =
              itemLinkedSelectionPlan.missingRequiredGroups.length > 0
                ? `Te abri ${itemProductName} para que elijas ${
                    missingLabels || "las opciones obligatorias"
                  }${
                    itemIngredientSelectionPlan?.hasChanges
                      ? ` y ya te deje marcado ${itemIngredientSelectionPlan.summaryText}`
                      : ""
                  }. Despues toca Agregar a mi pedido.`
                : itemIngredientSelectionPlan?.hasChanges
                  ? `Te abri ${itemProductName} ya marcado ${itemIngredientSelectionPlan.summaryText} para que lo confirmes.`
                  : `Te abri ${itemProductName} para ajustar ingredientes antes de agregarlo.`;

            // Keep the chatbot conversation available after product customization.
            setIsAiAssistantPanelVisible(true);
            setCheckoutMessage({
              type: "success",
              text: answer,
            });
            openProductDetail(itemProduct, {
              initialQuantity: itemQuantity,
              preferredLinkedSelections:
                itemLinkedSelectionPlan.selectionIdsByGroup,
              preferredSelection: itemIngredientSelectionPlan?.selection || null,
            });
            pushAssistantReply(answer, {
              suggestions: aiAssistantQuickSuggestions,
              relatedProducts: [summarizeAiRelatedProduct(itemProduct)],
              source: "local",
            });
            return true;
          }

          const nextCartItem = buildCartItem(
            itemProduct,
            createDraftSelection(
              itemProduct,
              itemIngredientSelectionPlan?.selection || null
            ),
            itemQuantity,
            itemLinkedSelectionPlan.selectionEntries
          );
          workingCartItems = mergeCartEntries(workingCartItems, [nextCartItem]);
          linkedSelectionsByProductPatch[itemProduct.id] =
            itemLinkedSelectionPlan.selectionIdsByGroup;

          const linkedNames = itemLinkedSelectionPlan.selectionEntries
            .map((entry) => entry.productName)
            .filter(Boolean)
            .join(", ");
          addedSummaryParts.push(
            `${formatAssistantOrderItemSummary(
              itemQuantity,
              itemProductName,
              linkedNames
            )}${
              itemIngredientSelectionPlan?.summaryText
                ? ` (${itemIngredientSelectionPlan.summaryText})`
                : ""
            }`
          );
          addedRelatedProducts.push(summarizeAiRelatedProduct(itemProduct));
        }

        const stockValidation = validateCartStock(workingCartItems);

        if (!stockValidation.ok) {
          const answer = getIngredientStockValidationMessage(stockValidation, {
            fallbackMessage:
              "No hay stock suficiente para agregar esos productos al pedido.",
          });
          pushAssistantReply(answer, {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts: orderDraft.relatedProducts || addedRelatedProducts,
            fallback: true,
            source: "local",
          });
          return true;
        }

        setLinkedSelectionsByProduct((prev) => ({
          ...prev,
          ...linkedSelectionsByProductPatch,
        }));
        setCartItems(workingCartItems);
        if (shouldMergeWithSubmittedOrder) {
          setIsEditingSubmittedOrder(true);
          setCheckoutMethod(
            normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod)
          );
        }

        const answer = `Listo, agregue ${addedSummaryParts.join(
          ", "
        )} a Mi pedido.`;
        setCheckoutMessage({
          type: "success",
          text: answer,
        });
        pushAssistantReply(answer, {
          suggestions: ["Como va mi pedido?", "Seguir eligiendo", "Llamar al mozo"],
          relatedProducts: orderDraft.relatedProducts || addedRelatedProducts,
          source: "local",
        });
        return true;
      }

      const productId = normalizeEntityId(orderDraft.productId || orderDraft.product?.id);
      const product =
        (payload?.products || []).find(
          (candidate) =>
            normalizeEntityId(candidate?.id || candidate?.productId) === productId
        ) ||
        orderDraft.product ||
        null;

      if (!product || !aiAssistantProductLookup.has(normalizeEntityId(product.id))) {
        pushAssistantReply("No encontre ese producto en la carta visible.", {
          suggestions: aiAssistantQuickSuggestions,
          relatedProducts: orderDraft.relatedProducts || [],
          fallback: true,
          source: "local",
        });
        return true;
      }

      const productDisplayName = getAssistantVisibleProductName(product?.name, "Producto");

      if (orderAction === "open_product") {
        const quantity = Math.max(1, Number(orderDraft.quantity || 1));
        // Keep the chatbot conversation available after product customization.
        setIsAiAssistantPanelVisible(true);
        openProductDetail(product, {
          initialQuantity: quantity,
        });
        pushAssistantReply(`Te abri ${productDisplayName} para que lo veas.`, {
          suggestions: aiAssistantQuickSuggestions,
          relatedProducts: orderDraft.relatedProducts || [
            summarizeAiRelatedProduct(product),
          ],
          source: "local",
        });
        return true;
      }

      if (reviewUsesSubmittedOrder && submittedOrderEditLock.locked) {
        pushAssistantReply(submittedOrderEditLock.reason, {
          suggestions: aiAssistantQuickSuggestions,
          relatedProducts: [summarizeAiRelatedProduct(product)],
          fallback: true,
          source: "local",
        });
        return true;
      }

      const productStockState = getProductStockState(product);
      if (productStockState.available === false) {
        pushAssistantReply(
          productStockState.reason ||
            "Ese producto no tiene stock suficiente para agregarlo ahora.",
          {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts: [summarizeAiRelatedProduct(product)],
            fallback: true,
            source: "local",
          }
        );
        return true;
      }

      const linkedRules = linkedRulesByProductId.get(product.id) || [];
      const linkedRuleGroups = buildLinkedRuleChoiceGroups(
        linkedRules,
        payload?.products || [],
        product.id
      );
      const linkedSelectionPlan = buildAiLinkedSelectionPlan({
        groups: linkedRuleGroups,
        question: safeQuestion,
        getLinkedOptionStockState,
      });
      const ingredientSelectionPlan = orderDraft?.ingredientSelectionPlan || null;
      const quantity = Math.max(1, Number(orderDraft.quantity || 1));
      const relatedProducts = Array.isArray(orderDraft.relatedProducts)
        ? orderDraft.relatedProducts
        : [summarizeAiRelatedProduct(product)];
      const workingCartItemsBeforeAdd = resolveWorkingCartItems();
      const replacedCartItem =
        orderAction === "replace"
          ? findOrderDraftCartItem(workingCartItemsBeforeAdd)
          : null;

      if (orderAction === "replace" && !replacedCartItem) {
        setIsAiAssistantPanelVisible(false);
        setIsCartOpen(true);
        setCartModalStep("review");
        pushAssistantReply(
          "No encontre el producto que querias cambiar dentro de Mi pedido. Te lo abro para revisarlo.",
          {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts,
            fallback: true,
            source: "local",
          }
        );
        return true;
      }

      if (
        linkedSelectionPlan.missingRequiredGroups.length > 0 ||
        ingredientSelectionPlan?.needsManualReview ||
        shouldOpenProductCustomizationForAi(
          safeQuestion,
          product,
          ingredientSelectionPlan
        )
      ) {
        const missingLabels = linkedSelectionPlan.missingRequiredGroups
          .map((group) => group.promptTitle || group.groupLabel)
          .filter(Boolean)
          .join(", ");
        const answer =
          linkedSelectionPlan.missingRequiredGroups.length > 0
            ? `Te abri ${productDisplayName} para que elijas ${
                missingLabels || "las opciones obligatorias"
              }${
                ingredientSelectionPlan?.hasChanges
                  ? ` y ya te deje marcado ${ingredientSelectionPlan.summaryText}`
                  : ""
              }. Despues toca Agregar a mi pedido.`
            : ingredientSelectionPlan?.hasChanges
              ? `Te abri ${productDisplayName} ya marcado ${ingredientSelectionPlan.summaryText} para que lo confirmes.`
              : `Te abri ${productDisplayName} para ajustar ingredientes antes de agregarlo.`;
        const resolvedAnswer =
          orderAction === "replace" && replacedCartItem
            ? `${answer} Cuando lo agregues, revisa Mi pedido para terminar el cambio por ${replacedCartItem.productName}.`
            : answer;

        // Keep the chatbot conversation available after product customization.
        setIsAiAssistantPanelVisible(true);
        setCheckoutMessage({
          type: "success",
          text: resolvedAnswer,
        });
        openProductDetail(product, {
          initialQuantity: quantity,
          preferredLinkedSelections: linkedSelectionPlan.selectionIdsByGroup,
          preferredSelection: ingredientSelectionPlan?.selection || null,
        });
        pushAssistantReply(resolvedAnswer, {
          suggestions: aiAssistantQuickSuggestions,
          relatedProducts,
          source: "local",
        });
        return true;
      }

      const workingCartItems =
        orderAction === "replace" && replacedCartItem
          ? workingCartItemsBeforeAdd.filter((item) => item.id !== replacedCartItem.id)
          : workingCartItemsBeforeAdd;
      const nextCartItem = buildCartItem(
        product,
        createDraftSelection(product, ingredientSelectionPlan?.selection || null),
        quantity,
        linkedSelectionPlan.selectionEntries
      );
      const nextCartItems = mergeCartEntries(workingCartItems, [nextCartItem]);
      const stockValidation = validateCartStock(nextCartItems);

      if (!stockValidation.ok) {
        const answer = getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para agregar ese producto al pedido.",
        });
        pushAssistantReply(answer, {
          suggestions: aiAssistantQuickSuggestions,
          relatedProducts,
          fallback: true,
          source: "local",
        });
        return true;
      }

      setLinkedSelectionsByProduct((prev) => ({
        ...prev,
        [product.id]: linkedSelectionPlan.selectionIdsByGroup,
      }));
      setCartItems(nextCartItems);
      if (shouldMergeWithSubmittedOrder) {
        setIsEditingSubmittedOrder(true);
        setCheckoutMethod(
          normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod)
        );
      }

      const linkedNames = linkedSelectionPlan.selectionEntries
        .map((entry) => entry.productName)
        .filter(Boolean)
        .join(", ");
      const customizationSuffix = ingredientSelectionPlan?.summaryText
        ? ` (${ingredientSelectionPlan.summaryText})`
        : "";
      const answer =
        orderAction === "replace" && replacedCartItem
          ? `Listo, cambie ${replacedCartItem.productName} por ${formatAssistantOrderItemSummary(
              quantity,
              productDisplayName,
              linkedNames
            )}${customizationSuffix} en Mi pedido.`
          : `Listo, agregue ${formatAssistantOrderItemSummary(
              quantity,
              productDisplayName,
              linkedNames
            )}${customizationSuffix} a Mi pedido.`;

      setCheckoutMessage({
        type: "success",
        text: answer,
      });
      pushAssistantReply(answer, {
        suggestions: ["Como va mi pedido?", "Seguir eligiendo", "Llamar al mozo"],
        relatedProducts,
        source: "local",
      });
      return true;
    },
    [
      aiAssistantQuickSuggestions,
      aiAssistantProductLookup,
      cartItems,
      createAiConversationMessage,
      getLinkedOptionStockState,
      getProductStockState,
      linkedRulesByProductId,
      openProductDetail,
      payload?.products,
      reviewUsesSubmittedOrder,
      submittedOrderEditLock.locked,
      submittedOrderEditLock.reason,
      submittedOrderItems,
      submittedOrderSnapshot?.checkoutMethod,
      validateCartStock,
    ]
  );

  const buildAiOrderDraftFromRemoteAction = useCallback(
    (reply = {}) => {
      const action = reply?.action || null;
      const visibleRelatedProducts = Array.isArray(reply?.relatedProducts)
        ? reply.relatedProducts
        : [];
      const singleVisibleProduct =
        visibleRelatedProducts.length === 1 ? visibleRelatedProducts[0] : null;
      const actionType = String(action?.type || "").trim().toLowerCase();
      const actionProductId =
        action?.productId ||
        action?.targetProductId ||
        singleVisibleProduct?.productId ||
        singleVisibleProduct?.id ||
        "";

      if (!action || !actionType || ["none", "show_products", "clarify"].includes(actionType)) {
        return null;
      }

      if (actionType === "open_product") {
        return {
          shouldHandle: true,
          status: "ready",
          action: "open_product",
          productId: actionProductId,
          quantity: Math.max(1, Number(action.quantity || 1) || 1),
          relatedProducts: reply.relatedProducts || [],
        };
      }

      return null;
    },
    []
  );

  const handleCallWaiter = useCallback(() => {
    if (!isWaiterFeatureEnabled) {
      setCheckoutMessage({
        type: "error",
        text: "Esta sucursal tiene desactivado el modulo de mozos por ahora.",
      });
      return;
    }

    setIsWaiterHelpOpen(true);
  }, [isWaiterFeatureEnabled]);

  const saveCustomerIdentity = () => {
    const trimmedName = sanitizeCustomerName(customerNameDraft);

    if (!trimmedName) {
      setCheckoutMessage({
        type: "error",
        text: "Antes de entrar al menu necesitamos un nombre para identificar tu pedido.",
      });
      return;
    }

    setCustomerIdentity((prev) => ({
      customerName: trimmedName,
      customerSessionId: (isKioskExperience ? '' : getQrCustomerSessionToken(qrToken)) || prev?.customerSessionId || buildCustomerSessionId(),
      createdAt: prev?.createdAt || new Date().toISOString(),
    }));
    setCustomerNameDraft(trimmedName);
    setCheckoutMessage({ type: "", text: "" });
  };

  const handleCustomerIdentitySubmit = (event) => {
    event.preventDefault();
    saveCustomerIdentity();
  };

  const closeWaiterHelp = () => {
    setIsWaiterHelpOpen(false);
    setSelectedWaiterOptions([]);
    setWaiterCustomText("");
  };

  const toggleWaiterOption = (optionId) => {
    setSelectedWaiterOptions((prev) =>
      prev.includes(optionId)
        ? prev.filter((item) => item !== optionId)
        : [...prev, optionId]
    );
  };

  const ensureActiveQrTable = useCallback(async () => {
    if (isKioskExperience) {
      if (!branch?.id || !table?.id) {
        throw new Error("No encontramos el totem activo para seguir con este pedido.");
      }

      return payload;
    }

    try {
      const access = await validateQrCustomerSession(qrToken);
      const freshTableId = access.tableId || "";

      if (!freshTableId || (table?.id && freshTableId !== table.id)) {
        throw new Error(
          "Esta mesa ya no esta disponible. Pide al personal que vuelva a generar el QR."
        );
      }

      return payload;
    } catch (error) {
      const resolvedErrorMessage =
        error?.message || "No pudimos validar la mesa activa.";

      if (isQrAccessTerminationMessage(resolvedErrorMessage)) {
        lockQrAccessSession(resolvedErrorMessage);
      }

      throw new Error(resolvedErrorMessage);
    }
  }, [
    branch?.id,
    isKioskExperience,
    lockQrAccessSession,
    payload,
    qrToken,
    table?.id,
  ]);

  useEffect(() => {
    if (isKioskExperience || qrAccessExpired || !payload || !table?.id) {
      return undefined;
    }

    let active = true;

    const validateQrAccess = async () => {
      try {
        await ensureActiveQrTable();
      } catch (error) {
        if (!active) {
          return;
        }
        if (!isQrAccessTerminationMessage(error?.message)) {
          setPayload(null);
          setError(error?.message || 'No pudimos comprobar el acceso. Revisa la conexion y vuelve a intentar.');
        }
      }
    };

    const intervalId = window.setInterval(validateQrAccess, 15 * 1000);
    window.addEventListener("focus", validateQrAccess);
    document.addEventListener("visibilitychange", validateQrAccess);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", validateQrAccess);
      document.removeEventListener("visibilitychange", validateQrAccess);
    };
  }, [
    ensureActiveQrTable,
    isKioskExperience,
    payload,
    qrAccessExpired,
    table?.id,
  ]);

  const submitWaiterHelp = async () => {
    if (isSubmittingWaiterHelp) {
      return;
    }

    if (!customerName && !isKioskExperience) {
      setCheckoutMessage({
        type: "error",
        text: isKioskExperience
          ? "Primero necesitamos tu nombre para identificar el pedido en este totem."
          : "Primero necesitamos tu nombre para identificar el pedido en esta mesa.",
      });
      return;
    }

    setIsSubmittingWaiterHelp(true);

    const selectedLabels = visibleWaiterOptions
      .filter((option) => selectedWaiterOptions.includes(option.id))
      .map((option) => option.label);
    const trimmedText = waiterCustomText.trim();

    try {
      await ensureActiveQrTable();
    } catch (error) {
      if (isQrAccessTerminationMessage(error?.message)) {
        setIsSubmittingWaiterHelp(false);
        return;
      }

      setCheckoutMessage({
        type: "error",
        text: error.message || "No pudimos validar la mesa activa.",
      });
      setIsSubmittingWaiterHelp(false);
      return;
    }

    const summary = [
      selectedLabels.length ? selectedLabels.join(", ") : "",
      trimmedText,
    ]
      .filter(Boolean)
      .join(" \u00b7 ");

    const normalizedSummary = sanitizeDisplayText(summary);
    const restaurantRef = { id: branch.id };
    const requestId = `waiter-request-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2, 8)}`;
    const requestedAt = new Date().toISOString();
    const mesaLabel = table.label || `Mesa ${table.tableNumber || ""}`;
    const showWaiterRequestSuccess = (
      assignedWaiterName = "",
      alreadyPending = false
    ) => {
      const normalizedAssignedWaiterName = String(assignedWaiterName || "").trim();

      closeWaiterHelp();
      setIsSubmittingWaiterHelp(false);
      setCheckoutMessage({
        type: "success",
        text: alreadyPending
          ? "Ya hay un llamado pendiente para tu mesa. El mozo ya fue avisado."
          : normalizedAssignedWaiterName
          ? normalizedSummary
            ? `Llamamos a ${normalizedAssignedWaiterName}: ${normalizedSummary}.`
            : `Llamamos a ${normalizedAssignedWaiterName} para tu mesa.`
          : normalizedSummary
            ? `Registramos el llamado al mozo: ${normalizedSummary}.`
            : "Registramos el llamado al mozo para tu mesa.",
      });
    };

    if (isSupabaseConfigured) {
      try {
        const sharedSubmitResult = await submitPublicWaiterServiceRequestToSharedState({
          qrToken,
          customerName,
          customerSessionToken: customerIdentity?.customerSessionId || "",
          requestSnapshot: {
            id: requestId,
            branchId: branch.id,
            tableId: table.id || "",
            mesa: mesaLabel,
            source: "qr",
            customerName,
            customerSessionId: customerIdentity?.customerSessionId || "",
            requestedAt,
            options: selectedLabels,
            customText: trimmedText,
            summary: summary || "Cliente llama al mozo.",
          },
        });

        if (sharedSubmitResult.supported) {
          const assignedWaiterName = String(
            sharedSubmitResult.waiterAssignment?.waiterName ||
              sharedSubmitResult.waiterAssignment?.profileFullName ||
              sharedSubmitResult.waiterAssignment?.assignedWaiterName ||
              ""
          ).trim();
          showWaiterRequestSuccess(
            assignedWaiterName,
            sharedSubmitResult.alreadyPending
          );
          return;
        }
      } catch (error) {
        setCheckoutMessage({
          type: "error",
          text: error.message || "No pudimos registrar el llamado al mozo.",
        });
        setIsSubmittingWaiterHelp(false);
        return;
      }
    }

    const activeWaiters = getBranchActiveWaiters(restaurantRef, branch.id);
    const existingAssignment = table.id
      ? getWaiterAssignmentForTable(restaurantRef, table.id)
      : null;
    const assignedWaiterStillActive =
      existingAssignment?.waiterUserId &&
      (activeWaiters.length === 0 ||
        activeWaiters.some((waiter) => waiter.userId === existingAssignment.waiterUserId));
    const selectedWaiter = assignedWaiterStillActive
      ? {
          userId: existingAssignment.waiterUserId,
          profileFullName: existingAssignment.waiterName || "Mozo asignado",
        }
      : pickLeastBusyWaiterForServiceRequest(restaurantRef, branch.id, requestId);
    const selectedWaiterName =
      selectedWaiter?.profileFullName || selectedWaiter?.waiterName || "Mozo asignado";

    createWaiterServiceRequest(restaurantRef, {
      id: requestId,
      branchId: branch.id,
      tableId: table.id || "",
      mesa: mesaLabel,
      source: "qr",
      customerName,
      customerSessionId: customerIdentity?.customerSessionId || "",
      requestedAt,
      options: selectedLabels,
      customText: trimmedText,
      summary: summary || "Cliente llama al mozo.",
      status: selectedWaiter?.userId ? "assigned" : "pending",
      assignedWaiterUserId: selectedWaiter?.userId || "",
      assignedWaiterName: selectedWaiter?.userId ? selectedWaiterName : "",
      assignedAt: selectedWaiter?.userId ? requestedAt : "",
    });

    if (selectedWaiter?.userId) {
      if (table.id) {
        assignWaiterToTable(restaurantRef, {
          tableId: table.id,
          tableLabel: mesaLabel,
          branchId: branch.id,
          waiterUserId: selectedWaiter.userId,
          waiterName: selectedWaiterName,
          assignedAt: requestedAt,
        });
      }

      assignWaiterServiceRequest(restaurantRef, requestId, {
        status: "assigned",
        assignedWaiterUserId: selectedWaiter.userId,
        assignedWaiterName: selectedWaiterName,
        assignedAt: requestedAt,
      });
    }

    createDeliveryAlert(restaurantRef, {
      id: `waiter-help-${requestId}`,
      orderId: requestId,
      tableId: table.id || "",
      mesa: mesaLabel,
      type: "service_request",
      summary: summary || "Cliente llama al mozo.",
      source: "qr",
      requestedAt,
      broadcast: !selectedWaiter?.userId,
      targetWaiterIds: selectedWaiter?.userId ? [selectedWaiter.userId] : [],
      targetWaiterNames: selectedWaiter?.userId ? [selectedWaiterName] : [],
    });

    showWaiterRequestSuccess(selectedWaiter?.userId ? selectedWaiterName : "");
  };

  const closeAiAssistant = useCallback(() => {
    stopAiVoiceInput({ cancel: true, clearDraft: true });
    stopAiVoiceOutput();
    setIsAiAssistantOpen(false);
    setIsAiAssistantPanelVisible(false);
    setAiConversation([]);
    setAiQuestionDraft("");
    setIsAiListening(false);
    setIsAiAssistantLoading(false);
    setAiVoiceStatus("");
    setIsAiAssistantFabDragging(false);
    setAiAssistantFabPosition(null);
    aiAssistantFabDragRef.current = null;
    aiAssistantFabSuppressClickRef.current = false;
  }, [stopAiVoiceInput, stopAiVoiceOutput]);

  const hideAiAssistantPanel = useCallback(() => {
    setIsAiAssistantPanelVisible(false);
  }, []);

  const collapseAiAssistantPanel = useModalBackNavigation({
    isOpen: shouldShowAiAssistantFloatingPanel,
    onClose: hideAiAssistantPanel,
    stateKey: AI_ASSISTANT_HISTORY_STATE_KEY,
    enabled: !isKioskExperience,
  });

  useEffect(() => {
    if (!shouldShowAiAssistantFloatingPanel || typeof window === "undefined") {
      return undefined;
    }

    const handleAiAssistantEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      collapseAiAssistantPanel();
    };

    window.addEventListener("keydown", handleAiAssistantEscape);
    return () => window.removeEventListener("keydown", handleAiAssistantEscape);
  }, [collapseAiAssistantPanel, shouldShowAiAssistantFloatingPanel]);

  const ensureAiAssistantIsActive = useCallback(() => {
    setIsAiAssistantOpen(true);
    setIsAiAssistantPanelVisible(true);
    setAiAssistantFabPosition((currentPosition) =>
      currentPosition || resolveAiAssistantFabInitialPosition()
    );
  }, [resolveAiAssistantFabInitialPosition]);

  const openWaiterHelpFromAi = useCallback(() => {
    closeAiAssistant();
    handleCallWaiter();
  }, [closeAiAssistant, handleCallWaiter]);

  const aiVoiceComparableCandidateTexts = useMemo(() => {
    const texts = [];
    const seen = new Set();
    const pushText = (candidateValue) => {
      const candidateText = getAssistantVisibleProductName(candidateValue, "").trim();
      const normalizedCandidate = normalizeAssistantProductKey(candidateText);

      if (!candidateText || !normalizedCandidate || seen.has(normalizedCandidate)) {
        return;
      }

      seen.add(normalizedCandidate);
      texts.push(candidateText);
    };

    (Array.isArray(payload?.categories) ? payload.categories : []).forEach((category) => {
      pushText(category?.name || category?.nombre || "");
    });

    (Array.isArray(payload?.products) ? payload.products : []).forEach((product) => {
      pushText(product?.subcategoryName || product?.subcategoria || "");
      pushText(product?.name || product?.nombre || "");

      (Array.isArray(product?.ingredients) ? product.ingredients : []).forEach((ingredient) => {
        pushText(ingredient?.name || ingredient?.nombre || "");
      });
    });

    return texts;
  }, [payload?.categories, payload?.products]);

  const resolveAiVoiceBestTranscriptAlternative = useCallback(
    (alternatives = []) => {
      const normalizedAlternatives = Array.from(
        new Set(
          (Array.isArray(alternatives) ? alternatives : [])
            .map((entry) =>
              sanitizeDisplayText(entry)
                .replace(/\s+/g, " ")
                .trim()
                .slice(0, 320)
            )
            .filter(Boolean)
        )
      );

      if (!normalizedAlternatives.length) {
        return "";
      }

      let bestAlternative = normalizedAlternatives[0];
      let bestScore = Number.NEGATIVE_INFINITY;

      normalizedAlternatives.forEach((alternative) => {
        let score = scoreAiVoiceTranscriptQuality(alternative);
        const bestMenuMatchScore = aiVoiceComparableCandidateTexts.reduce(
          (currentBest, candidateText) =>
            Math.max(
              currentBest,
              scoreAssistantComparablePhrases(alternative, candidateText)
            ),
          0
        );

        score += bestMenuMatchScore * 24;

        if (isAiAssistantExplicitOrderRequest(alternative)) {
          score += 8;
        }

        if (
          /\b(sin|con|extra|mas|menos|doble|quitar|sacar|agregar|sumar)\b/i.test(
            normalizeAssistantProductKey(alternative)
          )
        ) {
          score += 5;
        }

        if (bestMenuMatchScore >= 0.9) {
          score += 6;
        }

        if (score > bestScore) {
          bestScore = score;
          bestAlternative = alternative;
        }
      });

      return bestAlternative;
    },
    [aiVoiceComparableCandidateTexts]
  );

  const resolveAiVoiceDraftAgainstMenu = useCallback(
    (value = "") => {
      const safeValue = sanitizeDisplayText(value)
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 320);

      if (!safeValue) {
        return "";
      }

      const normalizedSafeValue = normalizeAssistantProductKey(safeValue);

      if (isAiAssistantExplicitOrderRequest(safeValue)) {
        return safeValue;
      }

      const safeTokens = tokenizeAssistantComparableText(safeValue);
      if (
        /\b(que|tenes|tienes|hay|para|precio|sale|cuanto|recom|suger|mostrar|mostrame|ver|info|con|sin)\b/.test(
          normalizedSafeValue
        ) &&
        safeTokens.length >= 3
      ) {
        return safeValue;
      }

      if (safeTokens.length === 0 || safeTokens.length > 4 || safeValue.length > 36) {
        return safeValue;
      }

      const candidateTexts = [];
      const seenCandidates = new Set();
      const pushCandidate = (candidateValue) => {
        const candidateText = getAssistantVisibleProductName(candidateValue, "").trim();
        const normalizedCandidate = normalizeAssistantProductKey(candidateText);

        if (!candidateText || !normalizedCandidate || seenCandidates.has(normalizedCandidate)) {
          return;
        }

        seenCandidates.add(normalizedCandidate);
        candidateTexts.push(candidateText);
      };

      (Array.isArray(payload?.categories) ? payload.categories : []).forEach((category) => {
        pushCandidate(category?.name || category?.nombre || "");
      });

      (Array.isArray(payload?.products) ? payload.products : []).forEach((product) => {
        pushCandidate(product?.subcategoryName || product?.subcategoria || "");
        pushCandidate(product?.name || product?.nombre || "");
      });

      let bestCandidate = "";
      let bestScore = 0;

      candidateTexts.forEach((candidateText) => {
        const score = scoreAssistantComparablePhrases(safeValue, candidateText);

        if (score > bestScore) {
          bestScore = score;
          bestCandidate = candidateText;
        }
      });

      const threshold = safeTokens.length <= 2 ? 0.62 : 0.84;
      return bestScore >= threshold && bestCandidate ? bestCandidate : safeValue;
    },
    [payload?.categories, payload?.products]
  );

  const submitAiAssistantQuestion = useCallback(async (forcedQuestion = "") => {
    const safeQuestion = sanitizeDisplayText(forcedQuestion || aiQuestionDraft)
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 320);

    if (!safeQuestion || isAiAssistantLoading) {
      return;
    }

    if (safeQuestion.toLowerCase() === "llamar al mozo") {
      openWaiterHelpFromAi();
      return;
    }

    const conversationForRequest = aiConversation
      .map((message) => ({
        role: message.role,
        text: message.text,
      }))
      .slice(-8);
    const userMessage = createAiConversationMessage("user", safeQuestion);

    setCheckoutMessage({ type: "", text: "" });
    setAiConversation((prev) => [...prev, userMessage]);
    setAiQuestionDraft("");
    setAiVoiceStatus("");

    setIsAiAssistantLoading(true);

    try {
      const contextualOrderQuestion = buildPublicMenuAssistantContextualQuestion({
        question: safeQuestion,
        conversation: conversationForRequest,
        products: payload?.products || [],
      });
      const localOrderDraft = buildPublicMenuAssistantOrderDraft({
        question: contextualOrderQuestion,
        products: payload?.products || [],
        linkedRules: linkedSubcategoryRules,
        cartItems,
      });

      // Los pedidos y sus aclaraciones se resuelven primero contra la carta real.
      if (
        localOrderDraft?.shouldHandle &&
        executeAiAssistantOrderDraft(localOrderDraft, contextualOrderQuestion, {
          suggestions:
            localOrderDraft.suggestions?.length > 0
              ? localOrderDraft.suggestions
              : aiAssistantQuickSuggestions,
          relatedProducts: localOrderDraft.relatedProducts || [],
        })
      ) {
        return;
      }

      const reply = await createPublicMenuAssistantReply({
        qrToken,
        question: safeQuestion,
        branch,
        table,
        products: payload?.products || [],
        linkedRules: linkedSubcategoryRules,
        cartItems,
        submittedOrderSnapshot,
        customerName: customerName || customerNameDraft || "Comensal",
        customerSessionId: customerIdentity?.customerSessionId || "",
        conversation: conversationForRequest,
      });
      const normalizedReply = {
        ...reply,
        answer: looksLikeEnglishAssistantReply(reply?.answer)
          ? "Te muestro opciones de la carta en español. Si quieres, te digo cuál te conviene más."
          : stripLeadingAssistantCodeFragments(reply?.answer || ""),
      };
      if (normalizedReply?.action?.type === "call_waiter") {
        setAiConversation((prev) => [
          ...prev,
          createAiConversationMessage("assistant", normalizedReply.answer, {
            suggestions: normalizedReply.suggestions,
            relatedProducts: normalizedReply.relatedProducts,
            fallback: normalizedReply.fallback,
            source: normalizedReply.source,
            degradedReason: normalizedReply.degradedReason,
          }),
        ]);
        openWaiterHelpFromAi();
        return;
      }

      const remoteOrderDraft = buildAiOrderDraftFromRemoteAction(
        normalizedReply,
        safeQuestion
      );

      if (
        remoteOrderDraft &&
        executeAiAssistantOrderDraft(remoteOrderDraft, safeQuestion, {
          suggestions: normalizedReply.suggestions,
          relatedProducts: normalizedReply.relatedProducts,
        })
      ) {
        return;
      }

      setAiConversation((prev) => [
        ...prev,
        createAiConversationMessage("assistant", normalizedReply.answer, {
          suggestions: normalizedReply.suggestions,
          relatedProducts: normalizedReply.relatedProducts,
          fallback: normalizedReply.fallback,
          source: normalizedReply.source,
          degradedReason: normalizedReply.degradedReason,
        }),
      ]);
      if (
        Array.isArray(normalizedReply.relatedProducts) &&
        normalizedReply.relatedProducts.length > 0
      ) {
        setIsAiAssistantPanelVisible(true);
        setCheckoutMessage({
          type: "success",
          text: "Te muestro esas opciones en la carta.",
        });
      }
    } catch (error) {
      const contextualOrderQuestion = buildPublicMenuAssistantContextualQuestion({
        question: safeQuestion,
        conversation: conversationForRequest,
        products: payload?.products || [],
      });
      const localOrderDraft = buildPublicMenuAssistantOrderDraft({
        question: contextualOrderQuestion,
        products: payload?.products || [],
        linkedRules: linkedSubcategoryRules,
        cartItems,
      });

      if (
        localOrderDraft?.shouldHandle &&
        executeAiAssistantOrderDraft(localOrderDraft, contextualOrderQuestion, {
          suggestions:
            localOrderDraft.suggestions?.length > 0
              ? localOrderDraft.suggestions
              : aiAssistantQuickSuggestions,
          relatedProducts: localOrderDraft.relatedProducts || [],
        })
      ) {
        return;
      }

      const fallbackAnswer =
        error?.message ||
        "No pude responder ahora. Si quieres, vuelve a intentarlo o llama al mozo.";
      setAiConversation((prev) => [
        ...prev,
        createAiConversationMessage(
          "assistant",
          fallbackAnswer,
          {
            suggestions: aiAssistantQuickSuggestions,
            relatedProducts: [],
            fallback: true,
            source: "local",
          }
        ),
      ]);
    } finally {
      setIsAiAssistantLoading(false);
    }
  }, [
    aiAssistantQuickSuggestions,
    aiConversation,
    aiQuestionDraft,
    branch,
    buildAiOrderDraftFromRemoteAction,
    cartItems,
    createAiConversationMessage,
    customerIdentity?.customerSessionId,
    customerName,
    customerNameDraft,
    executeAiAssistantOrderDraft,
    isAiAssistantLoading,
    linkedSubcategoryRules,
    openWaiterHelpFromAi,
    payload?.products,
    qrToken,
    submittedOrderSnapshot,
    table,
  ]);

  const startAiVoiceInput = useCallback(() => {
    if (isAiAssistantLoading || isAiListening) {
      return;
    }

    const SpeechRecognition = aiSpeechRecognitionConstructor;

    if (!SpeechRecognition) {
      setAiVoiceStatus(
        "Tu navegador no permite hablar con la IA desde esta pantalla."
      );
      return;
    }

    stopAiVoiceOutput();

    const recognition = new SpeechRecognition();
    aiSpeechRecognitionRef.current = recognition;
    aiVoiceDraftRef.current = "";
    aiVoiceFinalDraftRef.current = "";
    aiVoiceShouldSubmitRef.current = true;
    clearAiVoiceSilenceTimeout();

    recognition.lang = AI_ASSISTANT_FIXED_SPEECH_LOCALE;
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 3;

    recognition.onstart = () => {
      setIsAiListening(true);
      setAiVoiceStatus("Te escucho... habla cuando quieras.");
      scheduleAiVoiceAutoStop(24000);
    };

    recognition.onresult = (event) => {
      const transcriptParts = [];

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];

        if (!result?.isFinal) {
          continue;
        }

        const transcriptAlternatives = Array.from({
          length: Number(result?.length || 0),
        })
          .map((_, alternativeIndex) => result?.[alternativeIndex]?.transcript || "")
          .filter(Boolean);
        const transcript = resolveAiVoiceBestTranscriptAlternative(
          transcriptAlternatives
        );

        if (!transcript) {
          continue;
        }

        transcriptParts.push(transcript);
      }

      if (!transcriptParts.length) {
        scheduleAiVoiceAutoStop(14000);
        return;
      }

      const nextDraft = sanitizeDisplayText(
        `${aiVoiceFinalDraftRef.current} ${transcriptParts.join(" ")}`
      )
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 320);

      aiVoiceFinalDraftRef.current = nextDraft;
      aiVoiceDraftRef.current = nextDraft;
      setAiQuestionDraft(nextDraft);
      setAiVoiceStatus(
        nextDraft
          ? "Escuchando en tiempo real... al soltar se envía solo."
          : "Sigo escuchando..."
      );
      if (nextDraft) {
        setAiVoiceStatus("Te sigo escuchando... cuando hagas una pausa lo envio.");
      } else {
        setAiVoiceStatus("Sigo escuchando...");
      }
      scheduleAiVoiceAutoStop(nextDraft ? 12000 : 14000);
    };

    recognition.onerror = (event) => {
      clearAiVoiceSilenceTimeout();
      aiSpeechRecognitionRef.current = null;
      aiVoiceShouldSubmitRef.current = false;
      aiVoiceDraftRef.current = "";
      aiVoiceFinalDraftRef.current = "";
      setIsAiListening(false);
      setAiVoiceStatus(
        resolveAiSpeechRuntimeMessage(event?.error, {
          microphonePermissionGranted:
            aiMicrophonePermissionGrantedRef.current === true,
          isAppleTouch: isAppleAiVoiceBrowser,
          isIosChrome: isIosChromeForAi,
        })
      );
    };

    recognition.onend = () => {
      const capturedDraft = sanitizeDisplayText(aiVoiceDraftRef.current)
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 320);
      const resolvedDraft = resolveAiVoiceDraftAgainstMenu(capturedDraft);
      const shouldSubmit = aiVoiceShouldSubmitRef.current;

      clearAiVoiceSilenceTimeout();
      aiSpeechRecognitionRef.current = null;
      aiVoiceShouldSubmitRef.current = false;
      aiVoiceDraftRef.current = "";
      aiVoiceFinalDraftRef.current = "";
      setIsAiListening(false);

      if (shouldSubmit && resolvedDraft && !isAiAssistantLoading) {
        if (looksLikeLowSignalVoiceTranscript(resolvedDraft)) {
          setAiQuestionDraft("");
          setAiVoiceStatus(
            "No entendi bien la consulta. Prueba diciendo, por ejemplo, que bebidas tenes o quiero una milanesa con papas."
          );
          return;
        }

        setAiVoiceStatus("Enviando tu consulta por voz...");
        setAiQuestionDraft(resolvedDraft);
        void submitAiAssistantQuestion(resolvedDraft);
        return;
      }

      if (shouldSubmit && !resolvedDraft) {
        setAiVoiceStatus("No llegué a escuchar una consulta clara.");
      }
    };

    try {
      recognition.start();
    } catch (error) {
      clearAiVoiceSilenceTimeout();
      aiSpeechRecognitionRef.current = null;
      aiVoiceShouldSubmitRef.current = false;
      aiVoiceDraftRef.current = "";
      aiVoiceFinalDraftRef.current = "";
      setIsAiListening(false);
      setAiVoiceStatus(
        resolveAiSpeechRuntimeMessage(error?.name || error?.message, {
          microphonePermissionGranted:
            aiMicrophonePermissionGrantedRef.current === true,
          isAppleTouch: isAppleAiVoiceBrowser,
          isIosChrome: isIosChromeForAi,
        })
      );
    }
  }, [
    aiSpeechRecognitionConstructor,
    isAppleAiVoiceBrowser,
    isAiAssistantLoading,
    isAiListening,
    isIosChromeForAi,
    clearAiVoiceSilenceTimeout,
    scheduleAiVoiceAutoStop,
    stopAiVoiceOutput,
    resolveAiVoiceBestTranscriptAlternative,
    resolveAiVoiceDraftAgainstMenu,
    submitAiAssistantQuestion,
  ]);

  const toggleAiVoiceInput = useCallback(async () => {
    if (shouldRedirectAiVoiceToChrome) {
      setCheckoutMessage({
        type: "success",
        text: "Abrimos esta pagina en Chrome para usar la voz de la IA.",
      });
      openCurrentPageInChrome();
      return;
    }

    if (!isAiVoiceInputSupported) {
      setAiVoiceStatus(
        isIosChromeForAi
          ? "Chrome en iPhone no expone el dictado por voz que necesita la IA. Usa Safari para hablarle a la IA o escribe tu consulta."
          : "Tu navegador no tiene reconocimiento de voz disponible para este chat."
      );
      return;
    }

    if (isAiListening) {
      stopAiVoiceInput();
      return;
    }

    const hasPermission = await requestAiMicrophonePermissionFromUserGesture();

    if (!hasPermission && !(await requestAiMicrophonePermission())) {
      return;
    }

    startAiVoiceInput();
  }, [
    isAiListening,
    isIosChromeForAi,
    isAiVoiceInputSupported,
    openCurrentPageInChrome,
    requestAiMicrophonePermission,
    requestAiMicrophonePermissionFromUserGesture,
    startAiVoiceInput,
    stopAiVoiceInput,
    shouldRedirectAiVoiceToChrome,
  ]);

  const primeAiMicrophonePermissionRequest = useCallback(() => {
    primeAiSpeechOutputFromUserGesture();

    if (shouldRedirectAiVoiceToChrome || !isAiMicrophonePermissionSupported) {
      return;
    }

    void requestAiMicrophonePermissionFromUserGesture();
  }, [
    isAiMicrophonePermissionSupported,
    primeAiSpeechOutputFromUserGesture,
    requestAiMicrophonePermissionFromUserGesture,
    shouldRedirectAiVoiceToChrome,
  ]);

  const handleAiAssistant = useCallback(() => {
    if (!isAiFeatureEnabled) {
      setCheckoutMessage({
        type: "error",
        text: "El chatbot del menu esta desactivado para esta sucursal.",
      });
      return;
    }

    setCheckoutMessage({ type: "", text: "" });
    ensureAiAssistantIsActive();
    setIsAiAssistantPanelVisible(true);
    setAiConversation((currentConversation) =>
      currentConversation.length
        ? currentConversation
        : [
            createAiConversationMessage(
              "assistant",
              "Hola, soy el chatbot del menu. Escribime que te gustaria comer o beber y te muestro las opciones para elegir.",
              {
                suggestions: aiAssistantQuickSuggestions,
                source: "local",
              }
            ),
          ]
    );
  }, [
    aiAssistantQuickSuggestions,
    createAiConversationMessage,
    isAiFeatureEnabled,
    ensureAiAssistantIsActive,
  ]);

  const handleAiAssistantFloatingButtonClick = useCallback(() => {
    // When minimized the button is a simple recovery action, never a drag action.
    if (aiAssistantFabSuppressClickRef.current && isAiAssistantPanelVisible) {
      aiAssistantFabSuppressClickRef.current = false;
      return;
    }

    aiAssistantFabSuppressClickRef.current = false;

    if (!isAiFeatureEnabled) {
      setCheckoutMessage({
        type: "error",
        text: "El chatbot del menu esta desactivado para esta sucursal.",
      });
      return;
    }

    if (!isAiAssistantPanelVisible) {
      ensureAiAssistantIsActive();
      return;
    }

    setIsAiAssistantPanelVisible(false);
  }, [
    isAiFeatureEnabled,
    isAiAssistantPanelVisible,
    ensureAiAssistantIsActive,
  ]);

  const startAiAssistantFabDrag = useCallback(
    (event) => {
      if (!isAiAssistantOpen) {
        return;
      }

      const currentPosition =
        aiAssistantFabPosition || resolveAiAssistantFabInitialPosition();

      aiAssistantFabDragRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startLeft: Number(currentPosition?.left || 0),
        startTop: Number(currentPosition?.top || 0),
        didMove: false,
      };

      if (event.currentTarget?.setPointerCapture) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (error) {
          console.warn("No pudimos capturar el arrastre del boton IA.", error);
        }
      }
    },
    [
      aiAssistantFabPosition,
      isAiAssistantOpen,
      resolveAiAssistantFabInitialPosition,
    ]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const clampAiAssistantFabPosition = (left, top) => {
      const viewportWidth = Math.max(window.innerWidth || 0, 320);
      const viewportHeight = Math.max(window.innerHeight || 0, 480);
      const maxLeft = viewportWidth - aiAssistantFloatingButtonSize - 12;
      const maxTop = viewportHeight - aiAssistantFloatingButtonSize - 12;

      return {
        left: Math.min(Math.max(12, left), Math.max(12, maxLeft)),
        top: Math.min(Math.max(12, top), Math.max(12, maxTop)),
      };
    };

    const handlePointerMove = (event) => {
      const dragState = aiAssistantFabDragRef.current;

      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      const deltaX = event.clientX - dragState.startClientX;
      const deltaY = event.clientY - dragState.startClientY;

      if (!dragState.didMove && Math.hypot(deltaX, deltaY) > 8) {
        dragState.didMove = true;
        setIsAiAssistantFabDragging(true);
      }

      if (!dragState.didMove) {
        return;
      }

      setAiAssistantFabPosition(
        clampAiAssistantFabPosition(
          dragState.startLeft + deltaX,
          dragState.startTop + deltaY
        )
      );
    };

    const handlePointerUp = (event) => {
      const dragState = aiAssistantFabDragRef.current;

      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      aiAssistantFabDragRef.current = null;
      const wasDragging = dragState.didMove;
      aiAssistantFabSuppressClickRef.current = wasDragging;
      window.setTimeout(() => {
        aiAssistantFabSuppressClickRef.current = false;
      }, 0);

      const isInsideDismissZone =
        wasDragging &&
        event.clientY >= window.innerHeight - 140 &&
        Math.abs(event.clientX - window.innerWidth / 2) <= 96;

      setIsAiAssistantFabDragging(false);

      if (isInsideDismissZone) {
        closeAiAssistant();
      }
    };

    const handleResize = () => {
      setAiAssistantFabPosition((currentPosition) => {
        if (!isAiAssistantOpen) {
          return currentPosition;
        }

        if (!currentPosition) {
          return resolveAiAssistantFabInitialPosition();
        }

        return clampAiAssistantFabPosition(
          Number(currentPosition.left || 0),
          Number(currentPosition.top || 0)
        );
      });
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    aiAssistantFloatingButtonSize,
    closeAiAssistant,
    isAiAssistantOpen,
    resolveAiAssistantFabInitialPosition,
  ]);

  const updateCartItemQuantity = (itemId, change) => {
    setCartItems((prev) =>
      prev.reduce((acc, item) => {
        if (item.id !== itemId) {
          acc.push(item);
          return acc;
        }

        const nextQuantity = Number(item.quantity || 0) + change;
        if (nextQuantity <= 0) {
          return acc;
        }

        acc.push({
          ...item,
          quantity: nextQuantity,
        });
        return acc;
      }, [])
    );
  };

  const removeCartItem = (itemId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    if (!cartItems.length) {
      return;
    }

    const discardsSubmittedOrderChanges = isEditingSubmittedOrder;
    const confirmationText = discardsSubmittedOrderChanges
      ? "Descartar los cambios? El pedido que ya enviaste seguira activo."
      : "Vaciar todos los productos de tu pedido?";

    if (typeof window !== "undefined" && !window.confirm(confirmationText)) {
      return;
    }

    persistCartToStorage(
      sessionKey,
      [],
      resolveGuestSessionStorage(isKioskExperience)
    );
    setCartItems([]);
    clearProductDetailSelection();
    setCartModalStep("review");
    setIsEditingSubmittedOrder(false);
    closeCartModal();
    setCheckoutMessage({
      type: "success",
      text: discardsSubmittedOrderChanges
        ? "Descartamos los cambios. El pedido enviado sigue activo."
        : "Vaciamos tu pedido.",
    });
  };

  const openKioskKeyboard = useCallback(() => {
    if (!isKioskExperience) {
      return;
    }

    if (kioskKeyboardOpenTimeoutRef.current) {
      window.clearTimeout(kioskKeyboardOpenTimeoutRef.current);
      kioskKeyboardOpenTimeoutRef.current = null;
    }

    kioskKeyboardOpeningRef.current = true;
    kioskKeyboardOpenTimeoutRef.current = window.setTimeout(() => {
      kioskKeyboardIgnoreNextOverlayClickRef.current = true;
      setIsKioskKeyboardOpen(true);
      checkoutNameInputRef.current?.focus();
      kioskKeyboardOpeningRef.current = false;
      kioskKeyboardOpenTimeoutRef.current = null;
    }, 0);
  }, [isKioskExperience]);

  const closeKioskKeyboard = useCallback(() => {
    if (kioskKeyboardOpenTimeoutRef.current) {
      window.clearTimeout(kioskKeyboardOpenTimeoutRef.current);
      kioskKeyboardOpenTimeoutRef.current = null;
    }

    kioskKeyboardOpeningRef.current = false;
    kioskKeyboardIgnoreNextOverlayClickRef.current = false;
    setIsKioskKeyboardOpen(false);
  }, []);
  const closeCartModal = useCallback(() => {
    if (isKioskExperience) {
      setCustomerNameDraft("");
      setIsKioskNameWarningOpen(false);
      closeKioskKeyboard();
    }

    setCartModalStep("review");
    setIsCartOpen(false);
  }, [closeKioskKeyboard, isKioskExperience]);

  const handleKioskNameInputClick = useCallback(
    (event) => {
      if (!isKioskExperience) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openKioskKeyboard();
    },
    [isKioskExperience, openKioskKeyboard]
  );

  const handleKioskKeyboardKeyPress = useCallback((key) => {
    if (!isKioskExperience) {
      return;
    }

    if (key === "LISTO") {
      closeKioskKeyboard();
      checkoutNameInputRef.current?.blur();
      return;
    }

    if (key === "SHIFT") {
      setIsKioskKeyboardUppercase((prev) => !prev);
      return;
    }

    setCustomerNameDraft((prev) => {
      const currentValue = String(prev || "");

      if (key === "BORRAR") {
        return currentValue.slice(0, -1);
      }

      if (key === "LIMPIAR") {
        return "";
      }

      if (key === "ESPACIO") {
        if (!currentValue.trim() || currentValue.endsWith(" ")) {
          return currentValue;
        }

        return currentValue.length >= 60 ? currentValue : `${currentValue} `;
      }

      return currentValue.length >= 60 ? currentValue : `${currentValue}${key}`;
    });

    if (checkoutMessage.type === "error") {
      setCheckoutMessage({ type: "", text: "" });
    }
    setIsKioskNameWarningOpen(false);
  }, [checkoutMessage.type, closeKioskKeyboard, isKioskExperience]);

  const restoreSubmittedOrderToCart = () => {
    if (!submittedOrderItems.length) {
      return;
    }

    if (submittedOrderEditLock.locked) {
      setCheckoutMessage({
        type: "error",
        text: submittedOrderEditLock.reason,
      });
      return;
    }

    setCartItems(
      submittedOrderItems.map((item) => ({
        ...item,
        selection: item.selection ? { ...item.selection } : {},
        selectionDetails: Array.isArray(item.selectionDetails)
          ? [...item.selectionDetails]
          : [],
      }))
    );
    setIsEditingSubmittedOrder(true);
    setCheckoutMethod(normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod));
    setSelectedMenuWalletProviderId(
      String(submittedOrderSnapshot?.menuWalletProviderId || "").trim()
    );
    setCartModalStep("review");
    setCheckoutMessage({
      type: "success",
      text: "Recargamos tu ultimo pedido para que lo puedas ajustar antes de reenviarlo.",
    });
  };
  const recoverSubmittedKioskPendingPayment = useCallback(() => {
    if (
      !isKioskExperience ||
      normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod) !==
        KIOSK_QR_CHECKOUT_METHOD ||
      !submittedOrderSnapshot?.awaitingPaymentApproval
    ) {
      return;
    }

    const confirmationMessage =
      isSubmittedKioskPaymentFailed ||
      submittedOrderSnapshot?.paymentIntentError ||
      hasSubmittedKioskPaymentRecoveryTimedOut
        ? "Vamos a cerrar este cobro pendiente y recargar el pedido para volver a intentarlo. Si el cliente ya termino de pagar, espera la confirmacion antes de cancelar."
        : "Todavia estamos esperando la confirmacion del pago. Si sales ahora, este cobro quedara sin seguimiento en el totem. Usalo solo si el pago realmente fallo. Quieres cancelar y recargar el pedido?";

    if (
      typeof window !== "undefined" &&
      typeof window.confirm === "function" &&
      !window.confirm(confirmationMessage)
    ) {
      return;
    }

    const restoredItems = submittedOrderItems.map((item) => ({
      ...item,
      selection: item.selection ? { ...item.selection } : {},
      selectionDetails: Array.isArray(item.selectionDetails)
        ? [...item.selectionDetails]
        : [],
    }));

    kioskPaymentFinalizeRef.current = "";
    setSubmittedOrderSnapshot(null);
    setCartItems(restoredItems);
    setIsEditingSubmittedOrder(false);
    setCheckoutMethod(KIOSK_QR_CHECKOUT_METHOD);
    setCheckoutMessage({
      type: "error",
      text: t("pendingKioskPaymentRecovered"),
    });
    setIsKioskInactivityPromptOpen(false);
    setKioskInactivityCountdown(KIOSK_INACTIVITY_CONFIRMATION_SECONDS);
    closeCartModal();
  }, [
    closeCartModal,
    hasSubmittedKioskPaymentRecoveryTimedOut,
    isKioskExperience,
    isSubmittedKioskPaymentFailed,
    submittedOrderItems,
    submittedOrderSnapshot?.awaitingPaymentApproval,
    submittedOrderSnapshot?.checkoutMethod,
    submittedOrderSnapshot?.paymentIntentError,
    t,
  ]);
  const retrySubmittedDigitalPayment = async () => {
    if (!submittedOrderSnapshot?.orderId || !submittedOrderSnapshot?.paymentOperationId) {
      return;
    }

    const submittedMethod = normalizeCheckoutMethod(
      submittedOrderSnapshot?.checkoutMethod
    );
    const isRetryingKioskQr =
      isKioskExperience && submittedMethod === KIOSK_QR_CHECKOUT_METHOD;
    const isRetryingMenuDeepLink =
      !isKioskExperience && submittedMethod === "deep_link";

    if (!isRetryingKioskQr && !isRetryingMenuDeepLink) {
      return;
    }

    setIsSubmittingOrder(true);
    setCheckoutMessage({
      type: "success",
      text: isRetryingKioskQr
        ? "Estamos generando otra vez el QR de cobro del kiosk."
        : "Estamos preparando otra vez el cobro digital del menu.",
    });

    try {
      if (isSupabaseConfigured) {
        const submittedTotal =
          submittedOrderSnapshot?.pricing?.finalTotal ||
          submittedOrderSnapshot?.pricing?.listTotal ||
          0;
        const mzPreflight = await checkMzRuntimeUsageAvailability({
          branchId: branch?.id,
          eventKey: resolveMzRuntimeEventKeyForCheckout({
            isKioskExperience,
            checkoutMethod: submittedMethod,
          }),
          sourceChannel: isRetryingKioskQr ? "kiosk" : "menu_qr",
          sourceMode: submittedMethod,
          metadata: {
            checkoutMethod: submittedMethod,
            grossAmount: submittedTotal,
            customerName:
              submittedOrderSnapshot.customerName || customerNameDraft || "",
            serviceType: submittedOrderSnapshot.serviceType || kioskServiceType || "",
            checkSource: "retry_submitted_digital_payment",
          },
        });

        if (isMzRuntimeBlocked(mzPreflight)) {
          throw new Error(buildMzRuntimeBlockedMessage(mzPreflight));
        }
      }

      const nextIntent = await createBranchDigitalPaymentIntent({
        branch: {
          ...branch,
          ...branchPaymentSetup,
        },
        order: {
          ...(submittedOrderSnapshot?.pendingOrder || {}),
          id: submittedOrderSnapshot.orderId,
          paymentOperationId: submittedOrderSnapshot.paymentOperationId,
          paymentContext:
            submittedOrderSnapshot?.paymentContext || "immediate_order",
          pendingAccountOrderIds:
            submittedOrderSnapshot?.pendingAccountOrderIds || [],
          qrToken: isRetryingMenuDeepLink ? qrToken : "",
          tableId:
            table?.id || submittedOrderSnapshot?.pendingOrder?.tableId || "",
          customerSessionId:
            submittedOrderSnapshot?.customerSessionId ||
            customerIdentity?.customerSessionId ||
            "",
          items: submittedOrderSnapshot?.items || [],
          totalAmount:
            submittedOrderSnapshot?.pricing?.finalTotal || submittedOrderSnapshot?.pricing?.listTotal || 0,
          pricing: submittedOrderSnapshot.pricing || null,
          preparationNumber: submittedOrderSnapshot.preparationNumber || null,
          serviceType: submittedOrderSnapshot.serviceType || kioskServiceType || "",
          kioskStationNumber: isRetryingKioskQr ? normalizedKioskStationNumber : null,
          mesa: submittedOperationalOrder?.mesa || table?.label || "",
          customerName:
            submittedOrderSnapshot.customerName || customerNameDraft || "",
          source: isRetryingKioskQr ? "kiosk" : "app",
          selectedMethod: submittedMethod,
          requestedMethod: submittedMethod,
        },
        customerName:
          submittedOrderSnapshot.customerName || customerNameDraft || "",
        forceRefresh: true,
        walletProvider:
          submittedOrderSnapshot?.menuWalletProviderId ||
          selectedMenuWalletProvider?.id ||
          "",
      });

      setSubmittedOrderSnapshot((prev) =>
        prev
          ? {
              ...prev,
              paymentIntent: nextIntent,
              paymentIntentError: "",
            }
          : prev
      );
      setCheckoutMessage({
        type: "success",
        text: isRetryingKioskQr ? t("paymentQrReady") : t("paymentLinkReady"),
      });
      if (isRetryingMenuDeepLink) {
        const nextLaunchUrl = resolveMenuQrWalletLaunchUrl({
          paymentIntent: nextIntent,
          provider:
            findMenuQrWalletProvider(
              branch?.menuQrWalletProviders || branchProfile?.menuQrWalletProviders || [],
              submittedOrderSnapshot?.menuWalletProviderId
            ) || selectedMenuWalletProvider,
          branchName: branch?.name || branchProfile?.nombre || "MozzoApp",
          customerName:
            submittedOrderSnapshot?.customerName || customerNameDraft || "",
        });

        if (nextLaunchUrl) {
          const selectedWalletLabel =
            findMenuQrWalletProvider(
              branch?.menuQrWalletProviders || branchProfile?.menuQrWalletProviders || [],
              submittedOrderSnapshot?.menuWalletProviderId
            )?.label ||
            selectedMenuWalletProvider?.label ||
            formatMenuQrCheckoutProviderLabel(nextIntent?.provider);

          setCheckoutMessage({
            type: "success",
            text: `Cobro listo. Toca Abrir ${selectedWalletLabel} para continuar.`,
          });
        } else {
          const nextMessage = t("menuWalletsIncompatible", {
            provider: formatMenuQrCheckoutProviderLabel(nextIntent?.provider),
          });

          setSubmittedOrderSnapshot((prev) =>
            prev
              ? {
                  ...prev,
                  paymentIntentError: nextMessage,
                }
              : prev
          );
          setCheckoutMessage({
            type: "error",
            text: nextMessage,
          });
        }
      }
    } catch (error) {
      const nextMessage =
        error instanceof Error
          ? error.message
          : isRetryingKioskQr
            ? "No pudimos generar el QR de cobro del kiosk."
            : "No pudimos preparar el cobro digital del menu.";

      setSubmittedOrderSnapshot((prev) =>
        prev
          ? {
              ...prev,
              paymentIntent: null,
              paymentIntentError: nextMessage,
            }
          : prev
      );
      setCheckoutMessage({
        type: "error",
        text: nextMessage,
      });
    } finally {
      setIsSubmittingOrder(false);
    }
  };
  const openCartItemDetail = (item) => {
    if (reviewUsesSubmittedOrder && submittedOrderEditLock.locked) {
      setCheckoutMessage({
        type: "error",
        text: submittedOrderEditLock.reason,
      });
      return;
    }

    const product = productsById.get(item?.productId);

    if (!product) {
      setCheckoutMessage({
        type: "error",
        text: "No encontramos este plato en la carta actual para volver a editarlo.",
      });
      return;
    }

    if (reviewUsesSubmittedOrder) {
      setCartItems(
        submittedOrderItems.map((submittedItem) => ({
          ...submittedItem,
          selection: submittedItem.selection ? { ...submittedItem.selection } : {},
          selectionDetails: Array.isArray(submittedItem.selectionDetails)
            ? [...submittedItem.selectionDetails]
            : [],
        }))
      );
      setIsEditingSubmittedOrder(true);
      setCheckoutMethod(
        normalizeCheckoutMethod(submittedOrderSnapshot?.checkoutMethod)
      );
      setSelectedMenuWalletProviderId(
        String(submittedOrderSnapshot?.menuWalletProviderId || "").trim()
      );
      setCartModalStep("review");
    }

    setEditingCartItemId(item.id);
    pushProductDetailHistoryEntry();
    setSelectedProductId(product.id);
    setDraftSelection(
      product.ingredients?.length > 0
        ? { ...(item.selection || createDraftSelection(product, customizationsByProduct[product.id])) }
        : {}
    );
    const cartLinkedSelections = getCartItemLinkedSelections(item, productsById);
    const previousPizzaHalfSelection = getPizzaHalfAndHalfSelection(cartLinkedSelections);
    const previousPizzaSizeOption = findPizzaSizeOption(
      product,
      item?.pizzaSizeOptionId || previousPizzaHalfSelection?.pizzaSizeOptionId || ""
    );
    const previousEmpanadaSaleOption = findEmpanadaSaleOption(
      product,
      item?.empanadaSaleOptionId || ""
    );
    const previousEmpanadaCookingOption = findEmpanadaCookingOption(
      product,
      item?.empanadaCookingOptionId || ""
    );

    const restoredLinkedSelections = buildDraftLinkedSelectionsForGroups(
        buildLinkedRuleChoiceGroups(
          linkedRulesByProductId.get(product.id) || [],
          payload?.products || [],
          product.id
        ),
        {},
        cartLinkedSelections
      );
    setDraftLinkedSelectionsByUnit(Array.from(
      { length: Math.max(1, Number(item.quantity || 1)) },
      () => ({ ...restoredLinkedSelections })
    ));
    setActiveLinkedUnit(0);
    setDraftPizzaHalfProductId(
      previousPizzaHalfSelection?.productId || ""
    );
    setDraftPizzaSizeOptionId(previousPizzaSizeOption?.id || "");
    setDraftPizzaPortionMode(
      previousPizzaHalfSelection
        ? "half_and_half"
        : item?.pizzaPortionMode || getDefaultPizzaSaleMode(previousPizzaSizeOption)
    );
    setDraftEmpanadaSaleOptionId(previousEmpanadaSaleOption?.id || "");
    setDraftEmpanadaCookingOptionId(previousEmpanadaCookingOption?.id || "");
    setDraftProductQuantity(Math.max(1, Number(item.quantity || 1)));
    setLockedProductQuantity(0);
    setProductDetailPromotionId("");
    setCartModalStep("review");
    setIsCartOpen(false);
    setCheckoutMessage({ type: "", text: "" });
  };

  const continueSubmittedMenuPayment = useCallback(() => {
    if (!submittedMenuLaunchUrl) {
      setCheckoutMessage({
        type: "error",
        text: "Todavia no encontramos un link valido para continuar el pago.",
      });
      return;
    }

    window.location.assign(submittedMenuLaunchUrl);
  }, [submittedMenuLaunchUrl]);

  const togglePendingTableAccount = useCallback((orderId) => {
    const safeOrderId = String(orderId || "").trim();

    if (!safeOrderId) {
      return;
    }

    setSelectedPendingAccountIds((currentIds) =>
      currentIds.includes(safeOrderId)
        ? currentIds.filter((candidateId) => candidateId !== safeOrderId)
        : [...currentIds, safeOrderId]
    );
  }, []);

  const openPendingAccountsPayment = useCallback((
    preferredCustomerName = "",
    preferredOrderId = "",
    accountsOverride = null
  ) => {
    if (tabletSessionKey) return;
    const availableAccounts = Array.isArray(accountsOverride)
      ? accountsOverride
      : pendingTableAccounts;
    const safePreferredOrderId = String(preferredOrderId || "").trim();
    const normalizedPreferredName = sanitizeCustomerName(preferredCustomerName).toLowerCase();
    const exactOrderId = safePreferredOrderId
      ? availableAccounts.find((account) => account.orderId === safePreferredOrderId)
          ?.orderId || ""
      : "";
    const preferredIds = exactOrderId
      ? [exactOrderId]
      : availableAccounts
          .filter(
            (account) =>
              normalizedPreferredName &&
              sanitizeCustomerName(account.customerName).toLowerCase() ===
                normalizedPreferredName
          )
          .map((account) => account.orderId);
    const fallbackId = availableAccounts[0]?.orderId || "";

    setSelectedPendingAccountIds(
      preferredIds.length ? preferredIds : fallbackId ? [fallbackId] : []
    );
    setSelectedMenuWalletProviderId((currentId) =>
      currentId || menuQrWalletProviders[0]?.id || ""
    );
    setPendingTableAccountsError("");
    setIsPendingAccountsPaymentOpen(true);
  }, [menuQrWalletProviders, pendingTableAccounts, tabletSessionKey]);

  const openSubmittedOrderPayment = useCallback(async () => {
    const submittedOrderId = String(submittedOrderSnapshot?.orderId || "").trim();

    if (!submittedOrderId || isOpeningSubmittedOrderPayment) {
      return;
    }

    setIsOpeningSubmittedOrderPayment(true);
    setCheckoutMessage({ type: "", text: "" });

    try {
      const refreshedAccounts = await refreshPendingTableAccounts({
        throwOnError: true,
      });
      const submittedAccount = refreshedAccounts.find(
        (account) => account.orderId === submittedOrderId
      );

      if (!submittedAccount) {
        setSubmittedOrderSnapshot((currentSnapshot) =>
          currentSnapshot
            ? {
                ...currentSnapshot,
                paymentStatus: "resolved",
              }
            : currentSnapshot
        );
        setCheckoutMessage({
          type: "error",
          text: "Esta cuenta ya no figura pendiente. Puede haber sido pagada, cancelada o cerrada.",
        });
        return;
      }

      setCartModalStep("review");
      setIsCartOpen(false);
      openPendingAccountsPayment(
        submittedAccount.customerName || submittedOrderSnapshot?.customerName || "",
        submittedOrderId,
        refreshedAccounts
      );
    } catch (paymentOpenError) {
      setCheckoutMessage({
        type: "error",
        text:
          paymentOpenError instanceof Error
            ? paymentOpenError.message
            : "No pudimos revisar esta cuenta. Intenta nuevamente.",
      });
    } finally {
      setIsOpeningSubmittedOrderPayment(false);
    }
  }, [
    isOpeningSubmittedOrderPayment,
    openPendingAccountsPayment,
    refreshPendingTableAccounts,
    submittedOrderSnapshot?.customerName,
    submittedOrderSnapshot?.orderId,
  ]);

  const cancelSubmittedOrder = useCallback(async () => {
    const submittedOrderId = String(submittedOrderSnapshot?.orderId || "").trim();
    const customerSessionToken = String(
      submittedOrderSnapshot?.customerSessionId ||
        customerIdentity?.customerSessionId ||
        ""
    ).trim();

    if (
      !canCancelSubmittedOrderNow ||
      !submittedOrderId ||
      isCancellingSubmittedOrder
    ) {
      return;
    }

    if (!customerSessionToken) {
      setCheckoutMessage({
        type: "error",
        text: "No encontramos la sesion de esta mesa. Vuelve a escanear el QR para revisar el pedido.",
      });
      return;
    }

    if (
      typeof window !== "undefined" &&
      !window.confirm(
        "Cancelar este pedido? Solo se puede cancelar mientras todavia no entro en preparacion."
      )
    ) {
      return;
    }

    setIsCancellingSubmittedOrder(true);
    setCheckoutMessage({ type: "", text: "" });

    try {
      const cancellationResult = await cancelPublicQrOrderFromSharedState({
        qrToken,
        customerSessionToken,
        orderId: submittedOrderId,
      });

      if (!cancellationResult.supported) {
        throw new Error(
          cancellationResult.reason ||
            "La cancelacion de pedidos todavia no esta habilitada en esta sucursal."
        );
      }

      if (!cancellationResult.cancelled && !cancellationResult.alreadyCancelled) {
        throw new Error(
          cancellationResult.reason || "La sucursal no confirmo la cancelacion."
        );
      }

      if (branch?.id) {
        saveOperationalOrdersState(
          { id: branch.id },
          cancellationResult.payload
        );
      }

      setOperationalOrdersSnapshot(cancellationResult.payload);
      setPaymentOperationsSnapshot(
        Array.isArray(cancellationResult.payload?.paymentOperations)
          ? cancellationResult.payload.paymentOperations
          : []
      );

      const guestSessionStorage = resolveGuestSessionStorage(false);
      persistCartToStorage(sessionKey, [], guestSessionStorage);
      persistSubmittedOrderToStorage(sessionKey, null, guestSessionStorage);
      setCartItems([]);
      setSubmittedOrderSnapshot(null);
      setIsEditingSubmittedOrder(false);
      clearProductDetailSelection();
      closeCartModal();
      await refreshPendingTableAccounts();
      setCheckoutMessage({
        type: "success",
        text: cancellationResult.alreadyCancelled
          ? "Este pedido ya estaba cancelado."
          : "Pedido cancelado. Ya no se enviara a preparacion.",
      });
    } catch (cancellationError) {
      setCheckoutMessage({
        type: "error",
        text:
          cancellationError instanceof Error
            ? cancellationError.message
            : "No pudimos cancelar el pedido. Intenta nuevamente o llama al mozo.",
      });
    } finally {
      setIsCancellingSubmittedOrder(false);
    }
  }, [
    branch?.id,
    canCancelSubmittedOrderNow,
    clearProductDetailSelection,
    closeCartModal,
    customerIdentity?.customerSessionId,
    isCancellingSubmittedOrder,
    qrToken,
    refreshPendingTableAccounts,
    sessionKey,
    submittedOrderSnapshot?.customerSessionId,
    submittedOrderSnapshot?.orderId,
  ]);

  const closePendingAccountsPayment = useCallback(() => {
    if (isCreatingPendingAccountsPayment) {
      return;
    }

    setIsPendingAccountsPaymentOpen(false);
    setSelectedPendingAccountIds([]);
    setPendingTableAccountsError("");
  }, [isCreatingPendingAccountsPayment]);

  const paySelectedPendingAccounts = useCallback(async () => {
    if (pendingAccountsPaymentLockRef.current || isCreatingPendingAccountsPayment) {
      return;
    }

    if (!selectedPendingTableAccounts.length) {
      setPendingTableAccountsError("Elige al menos una cuenta para pagar.");
      return;
    }

    if (!selectedMenuWalletProvider) {
      setPendingTableAccountsError("Elige una billetera para continuar el pago.");
      return;
    }

    const refreshedAccounts = await refreshPendingTableAccounts();
    const selectedIds = new Set(selectedPendingAccountIds);
    const verifiedAccounts = refreshedAccounts.filter((account) =>
      selectedIds.has(account.orderId)
    );

    if (verifiedAccounts.length !== selectedIds.size) {
      setPendingTableAccountsError(
        "Una de las cuentas elegidas ya cambio. Revisa la seleccion y vuelve a intentar."
      );
      return;
    }

    const verifiedTotal = verifiedAccounts.reduce(
      (total, account) => total + Number(account.digitalTotalAmount ?? account.totalAmount ?? 0),
      0
    );

    if (verifiedTotal <= 0) {
      setPendingTableAccountsError("Las cuentas elegidas ya no tienen saldo pendiente.");
      return;
    }

    if (verifiedAccounts.some((account) => account.digitalTotalAmount != null && account.digitalTotalAmount !== account.cashTotalAmount) &&
      !window.confirm(`Con este pago digital no se incluyen las ofertas solo en efectivo.\n\nConfirmar nuevo total: ${currencyFormatter.format(verifiedTotal)}?`)) return;

    pendingAccountsPaymentLockRef.current = true;
    setIsCreatingPendingAccountsPayment(true);
    setPendingTableAccountsError("");

    try {
      await ensureActiveQrTable();
      const now = new Date().toISOString();
      const accountOrderIds = verifiedAccounts.map((account) => account.orderId);
      const combinedOrderId = `table-bill-${table.id}-${Date.now()}`;
      const paymentOperationId = `payment-${combinedOrderId}`;
      const combinedCustomerName = Array.from(
        new Set(verifiedAccounts.map((account) => account.customerName).filter(Boolean))
      ).join(" + ");
      const paymentIntent = await createBranchDigitalPaymentIntent({
        branch: {
          ...branch,
          ...branchPaymentSetup,
        },
        order: {
          id: combinedOrderId,
          paymentOperationId,
          paymentContext: "table_pending_accounts",
          pendingAccountOrderIds: accountOrderIds,
          qrToken,
          tableId: table.id,
          mesa: table.label || `Mesa ${table.tableNumber || ""}`,
          customerName: combinedCustomerName || "Cuenta de mesa",
          customerSessionId: customerIdentity?.customerSessionId || "",
          source: "app",
          selectedMethod: "deep_link",
          requestedMethod: "deep_link",
          totalAmount: verifiedTotal,
          pricing: {
            listTotal: verifiedTotal,
            comboDiscountTotal: 0,
            promotionDiscountTotal: 0,
            totalDiscount: 0,
            finalTotal: verifiedTotal,
            adjustments: [],
          },
        },
        customerName: combinedCustomerName || "Cuenta de mesa",
        walletProvider: selectedMenuWalletProvider.id,
      });

      if (!paymentIntent?.id) {
        throw new Error("No pudimos generar el cobro de las cuentas elegidas.");
      }

      const launchUrl = resolveMenuQrWalletLaunchUrl({
        paymentIntent,
        provider: selectedMenuWalletProvider,
        branchName: branch?.name || branchProfile?.nombre || "MozzoApp",
        customerName: combinedCustomerName || "Cuenta de mesa",
      });

      if (!launchUrl) {
        throw new Error("La billetera no devolvio un enlace valido para pagar.");
      }

      const paymentSnapshot = {
        orderId: combinedOrderId,
        paymentOperationId,
        submittedAt: now,
        customerName: combinedCustomerName || "Cuenta de mesa",
        customerSessionId: customerIdentity?.customerSessionId || "",
        checkoutMethod: "deep_link",
        paymentContext: "table_pending_accounts",
        pendingAccountOrderIds: accountOrderIds,
        menuWalletProviderId: selectedMenuWalletProvider.id,
        pricing: {
          listTotal: verifiedTotal,
          comboDiscountTotal: 0,
          promotionDiscountTotal: 0,
          totalDiscount: 0,
          finalTotal: verifiedTotal,
          adjustments: [],
        },
        paymentIntent,
        paymentIntentError: "",
        awaitingPaymentApproval: true,
        pendingOrder: null,
        pendingPaymentOperation: {
          id: paymentOperationId,
          orderId: combinedOrderId,
          tableId: table.id,
          mesa: table.label || `Mesa ${table.tableNumber || ""}`,
          customerName: combinedCustomerName || "Cuenta de mesa",
          customerSessionId: customerIdentity?.customerSessionId || "",
          requestedMethod: "deep_link",
          selectedMethod: "deep_link",
          status: "link_sent",
          amount: verifiedTotal,
          orderedAt: now,
          paymentIntentId: paymentIntent.id,
          history: [
            {
              id: `history-${Date.now()}-table-accounts`,
              label: `Cobro digital generado para ${accountOrderIds.length} cuenta(s) de la mesa.`,
              at: now,
            },
          ],
        },
        items: [],
      };

      setSubmittedOrderSnapshot(paymentSnapshot);
      persistSubmittedOrderToStorage(
        sessionKey,
        paymentSnapshot,
        resolveGuestSessionStorage(false)
      );
      setIsPendingAccountsPaymentOpen(false);
      setIsCartOpen(true);
      setCheckoutMessage({
        type: "success",
        text: "El cobro esta listo. Las cuentas se marcaran pagadas cuando la billetera lo confirme.",
      });

      window.location.assign(launchUrl);
    } catch (pendingPaymentError) {
      const nextMessage =
        pendingPaymentError instanceof Error
          ? pendingPaymentError.message
          : "No pudimos preparar el pago de estas cuentas.";
      setPendingTableAccountsError(nextMessage);
      setCheckoutMessage({ type: "error", text: nextMessage });
    } finally {
      pendingAccountsPaymentLockRef.current = false;
      setIsCreatingPendingAccountsPayment(false);
    }
  }, [
    branch,
    branchPaymentSetup,
    branchProfile?.nombre,
    customerIdentity?.customerSessionId,
    ensureActiveQrTable,
    isCreatingPendingAccountsPayment,
    qrToken,
    refreshPendingTableAccounts,
    selectedMenuWalletProvider,
    selectedPendingAccountIds,
    selectedPendingTableAccounts.length,
    sessionKey,
    table,
  ]);

  const submitOrder = async () => {
    if (orderSubmissionLockRef.current) {
      return;
    }

    const trimmedCustomerName = isKioskExperience
      ? sanitizeCustomerName(customerNameDraft)
      : reviewCustomerName;
    const resolvedCheckoutMethod = normalizeCheckoutMethod(checkoutMethod);

    if (isOrderingLocked) {
      setCheckoutMessage({
        type: "error",
        text:
          checkoutMethodBlockingNotice ||
          branchPaymentSetup.operationalBlockingReason ||
          "La sucursal no tiene un medio de cobro operativo disponible en este momento.",
      });
      return;
    }

    if (!trimmedCustomerName) {
      if (isKioskExperience) {
        closeKioskKeyboard();
        setIsKioskNameWarningOpen(true);
      }
      setCheckoutMessage({
        type: "error",
        text: isKioskExperience
          ? t("missingPickupNameBody")
          : "Primero necesitamos tu nombre para identificar el pedido en esta mesa.",
      });
      return;
    }

    if (!cartItems.length) {
      setCheckoutMessage({
        type: "error",
        text: "Agrega al menos un plato antes de enviar el pedido.",
      });
      return;
    }

    const stockValidation = validateCartStock(cartItems);

    if (!stockValidation.ok) {
      setCheckoutMessage({
        type: "error",
        text: getIngredientStockValidationMessage(stockValidation, {
          fallbackMessage:
            "No hay stock suficiente para enviar este pedido ahora mismo.",
        }),
      });
      return;
    }

    if (
      !isKioskExperience &&
      resolvedCheckoutMethod === "deep_link" &&
      !selectedMenuWalletProvider
    ) {
      setCheckoutMessage({
        type: "error",
        text: t("chooseDigitalWalletError"),
      });
      return;
    }

    if (!branch?.id || !table?.id) {
      setCheckoutMessage({
        type: "error",
        text: isKioskExperience
          ? "No encontramos el totem activo para registrar el pedido."
          : "No encontramos la mesa activa para registrar el pedido.",
      });
      return;
    }

    const promotionNotice = promotionPaymentNotice(cartPricing, (value) => currencyFormatter.format(value));
    if (promotionNotice && !window.confirm(`${tx(promotionNotice)}\n\n${tx("Confirmar pedido por #{amount}?", { amount: currencyFormatter.format(cartPricing.finalTotal) })}`)) return;

    orderSubmissionLockRef.current = true;
    setIsSubmittingOrder(true);

    try {
      await ensureActiveQrTable();

      if (isSupabaseConfigured) {
        const mzPreflight = await checkMzRuntimeUsageAvailability({
          branchId: branch.id,
          eventKey: resolveMzRuntimeEventKeyForCheckout({
            isKioskExperience,
            checkoutMethod: resolvedCheckoutMethod,
          }),
          sourceChannel: isKioskExperience ? "kiosk" : "menu_qr",
          sourceMode: resolvedCheckoutMethod,
          metadata: {
            checkoutMethod: resolvedCheckoutMethod,
            grossAmount: cartPricing?.finalTotal || cartPricing?.listTotal || 0,
            customerName: trimmedCustomerName,
            serviceType: isKioskExperience ? kioskServiceType : "",
            checkSource: "menu_submit_order",
          },
        });

        if (isMzRuntimeBlocked(mzPreflight)) {
          throw new Error(buildMzRuntimeBlockedMessage(mzPreflight));
        }
      }

      const now = new Date().toISOString();
      const currentOperationalState = getOperationalOrdersState({ id: branch.id });
      const currentCustomerSessionId =
        customerIdentity?.customerSessionId || buildCustomerSessionId();
      const currentPaymentOperations = getPaymentOperations({ id: branch.id });

      if (
        !customerIdentity?.customerSessionId ||
        trimmedCustomerName !== customerName
      ) {
        setCustomerIdentity((prev) => ({
          customerName: trimmedCustomerName,
          customerSessionId: prev?.customerSessionId || currentCustomerSessionId,
          createdAt: prev?.createdAt || now,
        }));
        setCustomerNameDraft(trimmedCustomerName);
      }

      if (isEditingSubmittedOrder && submittedOrderEditLock.locked) {
        throw new Error(submittedOrderEditLock.reason);
      }

      const existingOrder = isEditingSubmittedOrder
        ? findActiveQrOrderForCustomer({
            ordersState: currentOperationalState,
            orderId: submittedOrderSnapshot?.orderId || "",
            tableId: table.id,
            qrToken,
            sessionKey,
            customerSessionId: currentCustomerSessionId,
          })
        : null;
      const existingOrderEditLock = getQrOrderEditLockState(existingOrder);

      if (isEditingSubmittedOrder && existingOrderEditLock.locked) {
        throw new Error(existingOrderEditLock.reason);
      }

      const isUpdatingExistingOrder = Boolean(existingOrder);
      const orderId = existingOrder?.id || `qr-order-${Date.now()}`;
      const paymentOperationId =
        existingOrder?.paymentOperationId || `payment-${orderId}`;
      const preparationNumber =
        existingOrder?.preparationNumber ||
        (isKioskExperience
          ? resolveNextPreparationNumber(currentOperationalState)
          : null);
      const formattedPreparationNumber = formatPreparationNumber(preparationNumber);
      const pricingSnapshot = createPricingSnapshot(cartPricing);
      const productsById = new Map(
        (payload?.products || []).map((product) => [product.id, product])
      );
      const items = cartItems.map((item, index) => {
        const product = productsById.get(item.productId);

        return createOperationalOrderItem({
          id: `${orderId}-item-${index + 1}`,
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          baseUnitPrice: item.basePrice,
          extraUnitCost: item.extraCost,
          unitPrice: item.unitPrice,
          selection: item.selection,
          selectionDetails: item.selectionDetails,
          linkedSelections: getCartItemLinkedSelections(item),
          delivered: false,
          imageUrl: item.imageUrl || "",
          signature: item.signature || "",
          categoryId:
            item.categoryId || product?.categoryId || product?.categoria || "",
          categoryName: item.categoryName || product?.categoryName || "",
          subcategoryId:
            item.subcategoryId || product?.subcategoryId || product?.subcategoriaId || "",
          subcategoryName:
            item.subcategoryName || product?.subcategoryName || product?.subcategoria || "",
          consumos: buildCartItemConsumptions(
            product,
            item,
            productsById,
            linkedSubcategoryRules
          ),
        });
      });
      const isKioskDigitalCheckout =
        isKioskExperience &&
        resolvedCheckoutMethod === KIOSK_QR_CHECKOUT_METHOD;
      const isMenuDigitalCheckout =
        !isKioskExperience && resolvedCheckoutMethod === "deep_link";
      const shouldAwaitPaymentApproval =
        isMenuDigitalCheckout || isKioskDigitalCheckout;
      const shouldApplyImmediateOrderStock =
        !shouldAwaitPaymentApproval;
      const paymentStatus =
        shouldAwaitPaymentApproval
          ? "link_sent"
          : "pending";
      const paymentHistory = [
        {
          id: `history-${Date.now()}-${isUpdatingExistingOrder ? "updated" : "created"}`,
          label: isUpdatingExistingOrder
            ? isKioskExperience
              ? isKioskDigitalCheckout
                ? "El cliente actualizo su pedido en el totem. Esperando confirmacion del pago QR."
                : "El cliente actualizo su pedido desde el totem."
              : "El comensal actualizo su pedido desde la carta QR."
            : isKioskExperience
              ? isKioskDigitalCheckout
                ? "Pedido cargado en el totem. Esperando confirmacion del pago QR."
                : "Pedido enviado desde el totem de autoservicio."
              : "Pedido enviado desde la carta QR de la mesa.",
          at: now,
        },
        {
          id: `history-${Date.now()}-payment`,
          label:
            resolvedCheckoutMethod === "deep_link"
              ? "El comensal solicito pagar por deep link."
              : isKioskDigitalCheckout
                ? "El cliente eligio pagar con QR desde el totem."
              : isKioskExperience
                ? "El cliente indico que pagara en caja."
                : "El comensal eligio pagar despues.",
          at: now,
        },
      ];

      const nextOrder = {
        ...(existingOrder || {}),
        id: orderId,
        mesa: table.label || `Mesa ${table.tableNumber || ""}`,
        tableId: table.id,
        qrToken: isKioskExperience ? "" : qrToken,
        sessionKey,
        customerName: trimmedCustomerName,
        customerSessionId: currentCustomerSessionId,
        comensales: Math.max(1, Number(existingOrder?.comensales || 1)),
        canal: isKioskExperience ? "Kiosk" : "QR",
        source: isKioskExperience ? "kiosk" : "app",
        kioskStationNumber: isKioskExperience ? normalizedKioskStationNumber : null,
        serviceType: isKioskExperience ? kioskServiceType : "",
        preparationNumber,
        requestedPaymentMethod: resolvedCheckoutMethod,
        selectedPaymentMethod: resolvedCheckoutMethod,
        paymentStatus,
        paymentDeferred:
          !isKioskExperience &&
          resolvedCheckoutMethod === COUNTER_CHECKOUT_METHOD,
        paymentTiming:
          !isKioskExperience &&
          resolvedCheckoutMethod === COUNTER_CHECKOUT_METHOD
            ? "later"
            : "immediate",
        cashPaymentPending:
          isKioskExperience &&
          resolvedCheckoutMethod === COUNTER_CHECKOUT_METHOD,
        kitchenModeSnapshot:
          existingOrder?.kitchenModeSnapshot || branchProfile?.kitchenMode || "manual",
        phase: existingOrder?.phase || "new",
        stockDiscounted: existingOrder?.stockDiscounted === true,
        orderedAt: existingOrder?.orderedAt || now,
        updatedAt: now,
        notes: existingOrder?.notes || "",
        totalAmount: pricingSnapshot.finalTotal,
        pricing: pricingSnapshot,
        paymentOperationId,
        items,
      };
      const nextOperationalState = isUpdatingExistingOrder
        ? {
            ...currentOperationalState,
            mesasActivas: (currentOperationalState.mesasActivas || []).map((order) =>
              order.id === existingOrder.id ? nextOrder : order
            ),
          }
        : {
            ...currentOperationalState,
            mesasActivas: [nextOrder, ...(currentOperationalState.mesasActivas || [])],
          };

      let resolvedOperationalState = nextOperationalState;
      let resolvedCustomerSessionId = currentCustomerSessionId;

      let sharedSubmitResult = {
        supported: false,
        payload: nextOperationalState,
        order: null,
        customerSessionToken: currentCustomerSessionId,
        reason: "",
      };
      let sharedSubmitErrorMessage = "";
      let localOperationalMirrorSaved = true;

      if (!shouldAwaitPaymentApproval) {
        try {
          sharedSubmitResult = isKioskExperience
            ? await submitPublicKioskOrderToSharedState({
                branchId: branch.id,
                stationNumber: normalizedKioskStationNumber,
                customerName: trimmedCustomerName,
                customerSessionToken: currentCustomerSessionId,
                orderSnapshot: nextOrder,
                checkoutMethod: resolvedCheckoutMethod,
              })
            : await submitPublicQrOrderToSharedState({
                qrToken,
                customerName: trimmedCustomerName,
                customerSessionToken: currentCustomerSessionId,
                orderSnapshot: nextOrder,
                checkoutMethod: resolvedCheckoutMethod,
              });
        } catch (sharedSubmitError) {
          sharedSubmitErrorMessage = String(sharedSubmitError?.message || "").trim();
          console.error(
            `No pudimos enviar el pedido ${isKioskExperience ? "del totem" : "QR"} al estado compartido. Seguimos con el respaldo local:`,
            sharedSubmitError
          );
        }

        if (sharedSubmitResult.supported) {
          if (sharedSubmitResult.terminal) {
            throw new Error(
              String(sharedSubmitResult.terminalReason || "").trim() ||
                "Este pedido ya fue cancelado o cerrado y no puede volver a ingresar."
            );
          }

          resolvedOperationalState = sharedSubmitResult.payload;
          resolvedCustomerSessionId =
            String(sharedSubmitResult.customerSessionToken || "").trim() ||
            currentCustomerSessionId;

          if (shouldApplyImmediateOrderStock && isStockFeatureEnabled) {
            const stockSettlement = await registerOperationalOrderStockConsumption({
              branchId: branch?.id,
              orderId,
              sourceChannel: isKioskExperience ? "kiosk" : "menu_qr",
              sourceMode: resolvedCheckoutMethod,
              metadata: {
                checkoutMethod: resolvedCheckoutMethod,
                customerName: trimmedCustomerName,
                customerSessionId: resolvedCustomerSessionId,
                serviceType: isKioskExperience ? kioskServiceType : "",
              },
            });

            if (stockSettlement?.state) {
              resolvedOperationalState = stockSettlement.state;
            }
          }

          try {
            saveOperationalOrdersState({ id: branch.id }, resolvedOperationalState);
          } catch (localOperationalMirrorError) {
            localOperationalMirrorSaved = false;
            console.warn(
              `El pedido ${isKioskExperience ? "del totem" : "QR"} se sincronizo con la sucursal, pero no pudimos espejarlo en este navegador:`,
              localOperationalMirrorError
            );
          }
        } else if (isKioskExperience) {
          throw new Error(
            sharedSubmitErrorMessage ||
              String(sharedSubmitResult.reason || "").trim() ||
              "No pudimos sincronizar el pedido del totem con la sucursal. No lo enviamos para evitar que se pierda."
          );
        } else {
          throw new Error(
            sharedSubmitErrorMessage ||
              String(sharedSubmitResult.reason || "").trim() ||
              "No pudimos sincronizar el pedido con la sucursal. Todavia no lo enviamos para evitar que se pierda."
          );
        }
      }

      const paymentRailSnapshot = buildPaymentRailSnapshot({
        source: isKioskExperience ? "kiosk" : "app",
        checkoutMethod: resolvedCheckoutMethod,
        paymentProvider:
          branchPaymentSetup.paymentProvider || branch?.paymentProvider || "",
        splitMode:
          branchPaymentSetup.paymentSplitMode || branch?.paymentSplitMode || "",
        isKioskExperience,
      });
      const resolvedCommissionRate = resolveBranchCommissionRateByChannel(
        { ...branch, ...branchPaymentSetup },
        isKioskExperience ? "kiosk" : "menu_qr"
      );

      let nextPaymentOperation = {
        ...(currentPaymentOperations.find(
          (operation) => operation.id === paymentOperationId
        ) || {}),
        id: paymentOperationId,
        orderId,
        tableId: table.id,
        mesa: table.label || `Mesa ${table.tableNumber || ""}`,
        customerName: trimmedCustomerName,
        customerSessionId: resolvedCustomerSessionId,
        canal: isKioskExperience ? "Kiosk" : "QR",
        source: isKioskExperience ? "kiosk" : "app",
        serviceType: isKioskExperience ? kioskServiceType : "",
        preparationNumber,
        requestedMethod: resolvedCheckoutMethod,
        selectedMethod: resolvedCheckoutMethod,
        paymentRail: paymentRailSnapshot.rail,
        paymentRailProvider: paymentRailSnapshot.provider,
        paymentRailLabel: paymentRailSnapshot.label,
        chargePresentation: paymentRailSnapshot.chargePresentation,
        commissionCollectionMode: paymentRailSnapshot.commissionCollectionMode,
        splitExecutionMode: paymentRailSnapshot.splitExecutionMode,
        interoperabilityObjective: paymentRailSnapshot.interoperabilityObjective,
        paymentSplitMode: paymentRailSnapshot.splitMode,
        commissionRate: resolvedCommissionRate,
        menuWalletProviderId:
          resolvedCheckoutMethod === "deep_link"
            ? selectedMenuWalletProvider?.id || ""
            : "",
        menuWalletProviderLabel:
          resolvedCheckoutMethod === "deep_link"
            ? selectedMenuWalletProvider?.label || ""
            : "",
        status: paymentStatus,
        amount: pricingSnapshot.finalTotal,
        pricing: pricingSnapshot,
        orderedAt: existingOrder?.orderedAt || now,
        paidAt: null,
        closedAt: null,
        itemsSummary: buildOrderSummary(cartItems),
        notes: "",
        history: [
          ...((currentPaymentOperations.find(
            (operation) => operation.id === paymentOperationId
          )?.history || []).slice(-8)),
          ...paymentHistory,
        ].slice(-12),
      };

      const persistPaymentTrackingSnapshot = async (operationSnapshot) => {
        try {
          const savedPaymentOperations = savePaymentOperations(
            { id: branch.id },
            [
              operationSnapshot,
              ...getPaymentOperations({ id: branch.id }).filter(
                (operation) => operation.id !== operationSnapshot.id
              ),
            ]
          );

          if (!(isMenuDigitalCheckout && shouldAwaitPaymentApproval)) {
            try {
              await pushSharedPaymentOperationsState(
                { id: branch.id },
                savedPaymentOperations
              );
            } catch (sharedPaymentError) {
              console.warn(
                `El seguimiento de pago del pedido ${
                  isKioskExperience ? "del totem" : "QR"
                } quedo local, pero no se pudo sincronizar compartido:`,
                sharedPaymentError
              );
            }
          }

          return true;
        } catch (paymentError) {
          console.error(
            `No pudimos registrar el seguimiento de pago del pedido ${
              isKioskExperience ? "del totem" : "QR"
            }:`,
            paymentError
          );
          return false;
        }
      };

      let paymentTrackingSaved = true;

      let nextDigitalPaymentIntent = null;
      let digitalPaymentErrorMessage = "";

      if (resolvedCheckoutMethod === "deep_link" || isKioskDigitalCheckout) {
        try {
          nextDigitalPaymentIntent = await createBranchDigitalPaymentIntent({
            branch: {
              ...branch,
              ...branchPaymentSetup,
            },
            order: {
              ...nextOrder,
              paymentOperationId,
              customerName: trimmedCustomerName,
              selectedMethod: resolvedCheckoutMethod,
              requestedMethod: resolvedCheckoutMethod,
            },
            customerName: trimmedCustomerName,
            walletProvider:
              resolvedCheckoutMethod === "deep_link"
                ? selectedMenuWalletProvider?.id || ""
                : "",
          });
        } catch (digitalPaymentError) {
          digitalPaymentErrorMessage =
            digitalPaymentError instanceof Error
              ? digitalPaymentError.message
              : isKioskDigitalCheckout
                ? "No pudimos generar el QR de cobro del kiosk."
                : "No pudimos preparar el cobro digital del menu.";
        }
      }

      if (isMenuDigitalCheckout && !nextDigitalPaymentIntent) {
        setCheckoutMessage({
          type: "error",
          text: digitalPaymentErrorMessage || t("paymentLinkRetry"),
        });
        return;
      }

      const resolvedPendingOrder =
        sharedSubmitResult?.order && typeof sharedSubmitResult.order === "object"
          ? {
              ...nextOrder,
              ...sharedSubmitResult.order,
              customerSessionId: resolvedCustomerSessionId,
            }
          : {
              ...nextOrder,
              customerSessionId: resolvedCustomerSessionId,
            };

      nextPaymentOperation = {
        ...nextPaymentOperation,
        customerSessionId: resolvedCustomerSessionId,
        paymentIntentId:
          nextDigitalPaymentIntent?.id || nextPaymentOperation.paymentIntentId || "",
        paymentIntentExternalReference:
          nextDigitalPaymentIntent?.externalReference ||
          nextPaymentOperation.paymentIntentExternalReference ||
          "",
        providerOperationId:
          nextDigitalPaymentIntent?.providerOperationId ||
          nextPaymentOperation.providerOperationId ||
          "",
        paymentIntentStatus:
          nextDigitalPaymentIntent?.status ||
          nextPaymentOperation.paymentIntentStatus ||
          "",
        paymentIntentCreatedAt:
          nextDigitalPaymentIntent?.createdAt ||
          nextPaymentOperation.paymentIntentCreatedAt ||
          "",
        paymentIntentExpiresAt:
          nextDigitalPaymentIntent?.expiresAt ||
          nextPaymentOperation.paymentIntentExpiresAt ||
          "",
        deeplinkUrl:
          nextDigitalPaymentIntent?.deeplinkUrl ||
          nextDigitalPaymentIntent?.qr?.deeplinkUrl ||
          nextPaymentOperation.deeplinkUrl ||
          "",
      };
      paymentTrackingSaved = await persistPaymentTrackingSnapshot(nextPaymentOperation);

      const nextSubmittedOrderSnapshot = {
        orderId,
        paymentOperationId,
        preparationNumber,
        submittedAt: now,
        customerName: trimmedCustomerName,
        customerSessionId: resolvedCustomerSessionId,
        serviceType: isKioskExperience ? kioskServiceType : "",
        checkoutMethod: resolvedCheckoutMethod,
        menuWalletProviderId:
          resolvedCheckoutMethod === "deep_link"
            ? selectedMenuWalletProvider?.id || ""
            : "",
        pricing: pricingSnapshot,
        paymentIntent: nextDigitalPaymentIntent,
        paymentIntentError: digitalPaymentErrorMessage,
        awaitingPaymentApproval: shouldAwaitPaymentApproval,
        pendingOrder: shouldAwaitPaymentApproval ? resolvedPendingOrder : null,
        pendingPaymentOperation: shouldAwaitPaymentApproval
          ? nextPaymentOperation
          : null,
        items: isKioskExperience && !isKioskDigitalCheckout
          ? []
          : cartItems.map((item) => ({
              ...item,
              selection: item.selection ? { ...item.selection } : {},
              selectionDetails: Array.isArray(item.selectionDetails)
                ? [...item.selectionDetails]
                : [],
            })),
      };

      setSubmittedOrderSnapshot(nextSubmittedOrderSnapshot);
      if (resolvedCustomerSessionId !== currentCustomerSessionId) {
        setCustomerIdentity((prev) => ({
          customerName: prev?.customerName || trimmedCustomerName,
          customerSessionId: resolvedCustomerSessionId,
        }));
      }
      setCartItems([]);
      setIsEditingSubmittedOrder(false);
      const kioskQrOrderSavedMessage = isUpdatingExistingOrder
        ? `Tu pedido del totem se actualizo. Numero de preparacion #${formattedPreparationNumber}. Quedara habilitado en Inicio > Notificaciones cuando se confirme el pago QR.`
        : `Pedido de ${trimmedCustomerName} registrado. Numero #${formattedPreparationNumber}. Quedara habilitado en Inicio > Notificaciones cuando se confirme el pago QR.`;
      const orderSavedMessage = paymentTrackingSaved
          ? resolvedCheckoutMethod === "deep_link"
            ? isUpdatingExistingOrder
              ? isKioskExperience
              ? `Tu pedido del totem se actualizo. Sigue el numero #${formattedPreparationNumber} para retirarlo.`
              : "Tu pedido se actualizo y el cobro digital tambien quedo actualizado."
            : isKioskExperience
              ? `Tu pedido ya fue enviado. Sigue el numero #${formattedPreparationNumber} para retirarlo.`
              : "El cobro digital esta listo. Enviaremos el pedido cuando el pago quede aprobado."
          : isKioskDigitalCheckout
            ? kioskQrOrderSavedMessage
          : isUpdatingExistingOrder
            ? isKioskExperience
              ? `Tu pedido del totem se actualizo correctamente. Numero de preparacion #${formattedPreparationNumber}.`
              : "Tu pedido se actualizo correctamente en la mesa."
            : isKioskExperience
              ? `Pedido de ${trimmedCustomerName} registrado. Numero #${formattedPreparationNumber}. Caja lo vera en Inicio > Notificaciones para cobrarlo y pasarlo a preparacion.`
              : "Tu pedido ya fue enviado a la mesa y quedo listo para seguimiento del salon."
        : isUpdatingExistingOrder
          ? "Tu pedido se actualizo, pero no pudimos refrescar el seguimiento del pago en este navegador."
          : isKioskExperience
            ? "Tu pedido ya fue enviado desde el totem, pero no pudimos guardar el seguimiento del pago en este navegador."
            : "Tu pedido ya fue enviado, pero no pudimos guardar el seguimiento del pago en este navegador.";
      setCheckoutMethod(COUNTER_CHECKOUT_METHOD);
      if (resolvedCheckoutMethod === "deep_link") {
        const guestSessionStorage = resolveGuestSessionStorage(false);

        persistSubmittedOrderToStorage(
          sessionKey,
          nextSubmittedOrderSnapshot,
          guestSessionStorage
        );
        persistCartToStorage(sessionKey, [], guestSessionStorage);
        persistCustomerIdentityToStorage(
          sessionKey,
          {
            customerName: trimmedCustomerName,
            customerSessionId: resolvedCustomerSessionId,
            createdAt:
              customerIdentity?.createdAt ||
              submittedOrderSnapshot?.submittedAt ||
              now,
          },
          guestSessionStorage
        );

        setIsCartOpen(true);
        setCheckoutMessage({
          type: nextDigitalPaymentIntent ? "success" : "error",
          text: nextDigitalPaymentIntent
            ? t("paymentLinkReady")
            : digitalPaymentErrorMessage || t("paymentLinkRetry"),
        });
        const nextLaunchUrl = resolveMenuQrWalletLaunchUrl({
          paymentIntent: nextDigitalPaymentIntent,
          provider: selectedMenuWalletProvider,
          branchName: branch?.name || branchProfile?.nombre || "MozzoApp",
          customerName: trimmedCustomerName,
        });
        if (nextLaunchUrl) {
          setCheckoutMessage({
            type: "success",
            text: `Abriendo ${submittedMenuWalletLabel} para completar el pago.`,
          });
          window.location.assign(nextLaunchUrl);
        } else if (nextDigitalPaymentIntent) {
          const nextMessage = t("menuWalletsIncompatible", {
            provider: formatMenuQrCheckoutProviderLabel(
              nextDigitalPaymentIntent?.provider
            ),
          });

          setSubmittedOrderSnapshot((prev) =>
            prev
              ? {
                  ...prev,
                  paymentIntentError: nextMessage,
                }
              : prev
          );
          setCheckoutMessage({
            type: "error",
            text: nextMessage,
          });
        }
        return;
      }

      if (isKioskDigitalCheckout) {
        setIsCartOpen(true);
        setCheckoutMessage({
          type: nextDigitalPaymentIntent ? "success" : "error",
          text: nextDigitalPaymentIntent
            ? `${orderSavedMessage} ${t("paymentQrReady")}`
            : `${orderSavedMessage} ${
                digitalPaymentErrorMessage || t("paymentQrRetry")
              }`,
        });
        return;
      }

      setIsCartOpen(false);
      if (!isKioskExperience) {
        void refreshPendingTableAccounts();
      }
      if (isKioskExperience) {
        try {
          setKioskCompletionStage("submitting");
          const restaurantName =
            branchProfile?.nombre || branch?.name || "MozzoApp";
          const branchName = branch?.name || "";
          const paperWidth = branchProfile?.kitchenTicketPaper || "80mm";
          const shouldPrintKitchenCommand =
            String(branchProfile?.kitchenMode || "").trim().toLowerCase() === "printer";
          const paymentMethodLabel = resolveKioskTicketPaymentMethodLabel(
            resolvedCheckoutMethod,
            selectedMenuWalletProvider?.label || ""
          );

          if (shouldPrintKitchenCommand && orderRequiresKitchen(nextOrder)) {
            setKioskCompletionStage("printing_kitchen");
            if (isMozzoWindowsBridgeAvailable()) {
              await printTicketWithMozzoWindows(
                buildMozzoWindowsKitchenTicketPayload({
                  order: nextOrder,
                  restaurantName,
                  paperWidthMm: paperWidth === "58mm" ? 58 : 80,
                })
              );
            } else if (isMozzoAndroidBridgeAvailable()) {
              await printTicketWithMozzoAndroid(
                buildMozzoAndroidKitchenTicketPayload({
                  order: nextOrder,
                  restaurantName,
                  paperWidthMm: paperWidth === "58mm" ? 58 : 80,
                })
              );
            } else {
              await printKitchenTicket({
                restaurantName,
                branchName,
                order: nextOrder,
                paperWidth,
                preferCurrentDocumentPrint: true,
                cleanupDelayMs: KIOSK_PRINT_RESET_DELAY_MS,
              });
            }
          }

          setKioskCompletionStage("printing_ticket");
          if (isMozzoWindowsBridgeAvailable()) {
            await printTicketWithMozzoWindows(
              buildMozzoWindowsCustomerTicketPayload({
                order: nextOrder,
                restaurantName,
                paymentMethodLabel,
                footer: "Retire cuando aparezca su numero en pantalla",
                paperWidthMm: paperWidth === "58mm" ? 58 : 80,
              })
            );
          } else if (isMozzoAndroidBridgeAvailable()) {
            await printTicketWithMozzoAndroid(
              buildMozzoAndroidCustomerTicketPayload({
                order: nextOrder,
                restaurantName,
                paymentMethodLabel,
                footer: "Retire cuando aparezca su numero en pantalla",
                paperWidthMm: paperWidth === "58mm" ? 58 : 80,
              })
            );
          } else {
            await printCustomerPickupTicket({
              restaurantName,
              branchName,
              order: nextOrder,
              paperWidth,
              preferCurrentDocumentPrint: true,
              cleanupDelayMs: KIOSK_PRINT_RESET_DELAY_MS,
            });
          }

          resetKioskExperienceFlow();
          return;
        } catch (ticketError) {
          setKioskCompletionStage("");
          const ticketErrorMessage =
            ticketError instanceof Error && ticketError.message.trim()
              ? ticketError.message.trim()
              : "No pudimos imprimir el ticket del totem.";
          setCheckoutMessage({
            type: "error",
            text: `${orderSavedMessage} ${ticketErrorMessage}`,
          });
          return;
        }
      }
      setCheckoutMessage({
        type: paymentTrackingSaved ? "success" : "error",
        text:
          !localOperationalMirrorSaved && paymentTrackingSaved
            ? `${orderSavedMessage} En este dispositivo no pudimos refrescar el espejo local del turno, pero la sucursal si recibio el pedido.`
            : orderSavedMessage,
        legacyText: paymentTrackingSaved
          ? resolvedCheckoutMethod === "deep_link"
            ? "Tu pedido ya fue enviado. El pago por deep link qued\u00f3 registrado para seguimiento."
            : isKioskExperience
              ? "Tu pedido ya fue enviado desde el totem y qued\u00f3 listo para seguimiento."
              : "Tu pedido ya fue enviado a la mesa y qued\u00f3 listo para seguimiento del sal\u00f3n."
          : "Tu pedido ya fue enviado, pero no pudimos guardar el seguimiento del pago en este navegador.",
      });
    } catch (error) {
      if (!isKioskExperience && isQrAccessTerminationMessage(error?.message)) {
        lockQrAccessSession(error.message || DEFAULT_QR_ACCESS_EXPIRED_MESSAGE);
        return;
      }

      setCheckoutMessage({
        type: "error",
        text: error.message || "No pudimos enviar tu pedido en este momento.",
      });
    } finally {
      orderSubmissionLockRef.current = false;
      setIsSubmittingOrder(false);
    }
  };

  const selectedProductSummary = getCustomizationSummary(selectedProduct, draftSelection);
  const productDetailPromotion = promotions.find((promotion) => promotion.id === productDetailPromotionId);
  // Quote with the same engine as checkout; keep list prices in cart lines so
  // discounts are applied once and all physical units still consume stock.
  const selectedProductsPricing = buildPromotionPricing(
    selectedDraftCartItems,
    availablePromotions,
    payload?.products || [],
    checkoutMethod
  );
  const selectedProductsTotal = selectedProductsPricing.finalTotal;
  const isComboProductDetail = productDetailPromotion?.type === "combo";
  const selectedDraftCompletesCombo = isComboProductDetail &&
    (productDetailPromotion.items || []).every((component) =>
      selectedDraftCartItems.filter((item) => item.productId === component.productId)
        .reduce((total, item) => total + item.quantity, 0) >= Math.max(1, Number(component.quantity || 1))
    );
  const isSelectedPizzaHalfAndHalfIncomplete =
    draftPizzaPortionMode === "half_and_half" && !selectedPizzaHalfSelection;
  const isSelectedPizzaConfigurationIncomplete = selectedPizzaNeedsSizeSetup ||
    (selectedPizzaSizeOption && !isPizzaSaleModeEnabled(selectedPizzaSizeOption, draftPizzaPortionMode));
  const isSelectedEmpanadaConfigurationIncomplete = selectedEmpanadaNeedsSaleSetup;

  if (loading) {
    return (
      <div
        style={{
          ...styles.page,
          backgroundColor: "#ffffff",
          fontFamily: qrFontFamily,
        }}
      >
        <style>{loadingLogoKeyframes}</style>
        <div style={styles.kioskScreensaverTopbar}>{kioskLanguageControl}</div>
        <div style={styles.centerWrap}>
          {loadingLogoUrl ? (
            <img
              src={loadingLogoUrl}
              alt="Logo de carga"
              style={{
                ...styles.loadingLogo,
                ...loadingLogoAnimation,
              }}
            />
          ) : (
            <div style={styles.statusCard}>
              {isKioskExperience
                ? "Estamos preparando el totem para tomar pedidos."
                : tx("Estamos abriendo el menu de tu mesa.")}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (qrAccessExpired && !isKioskExperience) {
    return (
      <div
        style={{
          ...styles.page,
          backgroundColor: brandTheme.pageTint,
          fontFamily: qrFontFamily,
        }}
      >
        <div style={styles.centerWrap}>
          <div style={styles.errorCard}>
            <h1 style={styles.errorTitle}>{tx("El acceso a esta mesa vencio")}</h1>
            <p style={styles.errorText}>
              {qrAccessExpiredMessage || DEFAULT_QR_ACCESS_EXPIRED_MESSAGE}
            </p>
            <p role="status" style={styles.errorText}>
              {tx("Volvé a escanear el QR con la cámara de tu teléfono.")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          ...styles.page,
          backgroundColor: brandTheme.pageTint,
          fontFamily: qrFontFamily,
        }}
      >
        <div style={styles.centerWrap}>
          <div style={styles.errorCard}>
            <h1 style={styles.errorTitle}>
              {isKioskExperience
                ? "No pudimos abrir este totem"
                : tx("No pudimos abrir esta mesa")}
            </h1>
            <p style={styles.errorText}>{error}</p>
            <button
              type="button"
              style={styles.retryButton}
              onClick={() => window.location.reload()}
            > {tx("Reintentar")} </button>
          </div>
        </div>
      </div>
    );
  }

  if (isKioskExperience && !kioskHasStarted) {
    return (
      <div
        style={{
          ...styles.page,
          ...styles.kioskLandingPage,
          fontFamily: qrFontFamily,
        }}
      >
        <style>{loadingLogoKeyframes}</style>
        <button
          type="button"
          style={styles.kioskScreensaverButton}
          onClick={() => setKioskHasStarted(true)}
        >
          <div style={styles.kioskScreensaverContent}>
            {loadingLogoUrl ? (
              <div
                style={{
                  ...styles.kioskScreensaverLogoWrap,
                  animation: "mozzoapp-loading-pulse 1.45s ease-in-out infinite",
                }}
              >
                <img
                  src={loadingLogoUrl}
                  alt={`Logo de carga de ${branch.name || "Mi Food"}`}
                  style={styles.kioskScreensaverLogo}
                />
              </div>
            ) : (
              <div style={styles.kioskScreensaverBrandName}>{branch.name || "Mi Food"}</div>
            )}
          </div>
        </button>
      </div>
    );
  }

  if (isKioskExperience && !kioskServiceType) {
    return (
      <div
        style={{
          ...styles.page,
          ...styles.kioskLandingPage,
          fontFamily: qrFontFamily,
        }}
      >
        <div style={styles.kioskLandingOverlay}>
          <div style={styles.kioskLandingTopbar}>{kioskLanguageControl}</div>
          <div style={{ ...styles.kioskLandingTopbar, display: "none" }}>
            <button type="button" style={styles.kioskUtilityButton}>
              <span style={styles.kioskUtilityIcon}>ðŸŒ</span>
              <span>{"Espa\u00f1ol"}</span>
            </button>
            <span style={styles.kioskUtilityHint}>{tx("Exclusivo pago electronico")}</span>
          </div>

          <div style={styles.kioskLandingIntro}>
            <div style={styles.kioskLandingLogoWrap}>
              {kioskInstitutionalLogoUrl ? (
                <img
                  src={kioskInstitutionalLogoUrl}
                  alt={`Marca de ${MOZZOAPP_KIOSK_APP_NAME}`}
                  style={styles.kioskLandingLogo}
                />
              ) : (
                <span style={styles.kioskInstitutionalLogoText}>
                  {MOZZOAPP_KIOSK_APP_NAME}
                </span>
              )}
            </div>
            <span style={styles.kioskLandingEyebrow}>{t("autoservice")}</span>
            <h1 style={styles.kioskLandingTitle}>{t("chooseWhere")}</h1>
            <p style={styles.kioskLandingText}>{t("chooseWhereText")}</p>
          </div>

          <div style={styles.kioskWelcomeGrid}>
            {kioskWelcomeChoices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                style={styles.kioskWelcomeCard}
                onClick={() => setKioskServiceType(choice.id)}
              >
                <div style={styles.kioskWelcomeImageWrap}>
                  {choice.imageUrl ? (
                    <img
                      src={choice.imageUrl}
                      alt={`${choice.title} en ${branch.name || MOZZOAPP_KIOSK_APP_NAME}`}
                      style={styles.kioskWelcomeImage}
                    />
                  ) : (
                    <div
                      style={styles.kioskWelcomeFallback}
                      translate="no"
                      className="notranslate"
                    >
                      {choice.fallbackLabel || choice.title}
                    </div>
                  )}
                </div>
                <div style={styles.kioskWelcomeContent}>
                  <strong style={styles.kioskWelcomeTitle}>{choice.title}</strong>
                  <span style={styles.kioskWelcomeSubtitle}>{choice.subtitle}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.page,
        backgroundColor: brandTheme.pageTint,
        fontFamily: qrFontFamily,
      }}
    >
      <style>{loadingLogoKeyframes}</style>
      {isKioskExperience && kioskCompletionStage ? (
        <div
          style={styles.kioskCompletionOverlay}
          role="status"
          aria-live="assertive"
          aria-busy="true"
        >
          <div style={styles.kioskCompletionPanel}>
            <div style={styles.kioskCompletionSpinner} aria-hidden="true" />
            <span style={styles.kioskCompletionEyebrow}>
              {t("completionEyebrow")}
            </span>
            <h2 style={styles.kioskCompletionTitle}>
              {kioskCompletionStage === "printing_kitchen"
                ? t("completionKitchenTitle")
                : kioskCompletionStage === "printing_ticket"
                  ? t("completionTicketTitle")
                  : t("completionSubmittingTitle")}
            </h2>
            <p style={styles.kioskCompletionHint}>{t("completionHint")}</p>
          </div>
        </div>
      ) : null}
      {isOrderingLocked ? (
        <div style={styles.operationalLockOverlay}>
          <div style={styles.operationalLockCard}>
            <span style={styles.operationalLockEyebrow}>
              {isKioskExperience ? "Totem pausado" : tx("Menu temporalmente pausado")}
            </span>
            <h2 style={styles.operationalLockTitle}>
              {isKioskExperience
                ? "Este totem no puede tomar pedidos ahora"
                : tx("Esta mesa no puede tomar pedidos ahora")}
            </h2>
            <p style={styles.operationalLockText}>
              {checkoutMethodBlockingNotice ||
                branchPaymentSetup.operationalBlockingReason ||
                tx("La sucursal no tiene un medio de cobro operativo disponible en este momento.")}
            </p>
            <button
              type="button"
              style={styles.retryButton}
              onClick={() => window.location.reload()}
            > {tx("Reintentar")} </button>
          </div>
        </div>
      ) : null}
      <header
        style={
          isKioskExperience
            ? {
                ...styles.kioskHero,
                background:
                  "linear-gradient(135deg, #111827 0%, #0f172a 46%, #1f2937 100%)",
                color: "#ffffff",
              }
            : {
                ...styles.hero,
                ...(isCompactViewport ? styles.heroCompact : {}),
                ...(heroImageUrl ? { minHeight: 0 } : {}),
                backgroundColor: primaryColor,
                color: effectiveHeroTextColor,
              }
        }
      >
        {isKioskExperience ? (
          <>
            {heroBrandLogo ? (
              <div style={styles.kioskHeroBackgroundMark} aria-hidden="true">
                <img
                  src={heroBrandLogo}
                  alt=""
                  style={styles.kioskHeroBackgroundLogo}
                />
              </div>
            ) : null}
            <div style={styles.kioskHeroOverlay}>
            <div style={styles.kioskHeroTopline}>
              <div style={styles.kioskHeroBrandBlock}>
                <div style={styles.kioskHeroLogoWrap}>
                  {kioskInstitutionalLogoUrl ? (
                    <img
                      src={kioskInstitutionalLogoUrl}
                      alt={`Marca de ${MOZZOAPP_KIOSK_APP_NAME}`}
                      style={styles.kioskHeroLogo}
                    />
                  ) : (
                    <span style={styles.kioskInstitutionalLogoTextCompact}>
                      {MOZZOAPP_KIOSK_APP_NAME}
                    </span>
                  )}
                </div>
                <div style={styles.kioskHeroBrandText}>
                  <span style={styles.kioskHeroEyebrow}>{t("autoservice")}</span>
                  <strong style={styles.kioskHeroBranchName}>
                    {branch.name || "Mi Food"}
                  </strong>
                </div>
              </div>

              <div style={styles.kioskHeroBadges}>
                {kioskLanguageControl}
                <span style={styles.kioskHeroBadgeSoft}>{kioskServiceTypeLabel}</span>
                <span style={styles.kioskHeroBadgeSoft}>
                  {menu.mode === "custom" ? t("ownMenu") : t("activeMenu")}
                </span>
              </div>
            </div>

            <div style={styles.kioskHeroBody}>
              <div style={styles.kioskHeroCopy}>
                <h1 style={styles.kioskHeroTitle}>{t("heroTitle")}</h1>
                <p style={styles.kioskHeroText}>{t("heroText")}</p>
                <button
                  type="button"
                  style={styles.kioskModeSwitchButton}
                  onClick={() => setKioskServiceType("")}
                >
                  {t("changeMode")}
                </button>
              </div>
            </div>
            </div>
          </>
        ) : (
          <>
        {heroImageUrl && (
          <div style={styles.heroCoverFrame}>
          <img
            src={heroImageUrl}
            alt={`Portada de ${branch.name || "Mi Food"}`}
            style={styles.heroImage}
          />
          <div
            style={{
              ...styles.heroGradient,
              background: heroPalette.overlayBackground,
            }}
          />
          </div>
        )}
        <div style={{ ...styles.heroOverlay, ...(heroImageUrl ? styles.heroCoverIdentity : {}) }}>
          <div style={{ ...styles.heroHeaderRow, ...(heroImageUrl ? { marginBottom: 0 } : {}) }}>
            {heroBrandLogo ? (
              <div style={styles.heroBrandLogoWrap}>
                <img
                  src={heroBrandLogo}
                  alt={`Marca de ${branch.name || "Mi Food"}`}
                  style={{ ...styles.heroBrandLogo, ...(heroImageUrl ? styles.heroCoverBrandLogo : {}) }}
                />
              </div>
            ) : (
              <span aria-hidden="true" />
            )}
          </div>
          <div style={{ ...styles.heroTopline, ...(heroImageUrl ? { marginBottom: 0 } : {}) }}>
            <span
              style={{
                ...styles.tableBadge,
                backgroundColor: heroPalette.secondaryBadgeBackground,
                borderColor: heroPalette.secondaryBadgeBorder,
                color: heroPalette.badgeTextColor,
                boxShadow: heroPalette.badgeShadow,
              }}
            >
              {tx(displayStationLabel)}
            </span>
          </div>

        </div>
          </>
        )}
      </header>

      <main
        style={{
          ...styles.content,
          ...(isKioskExperience
            ? {
                maxWidth: 1460,
                paddingTop: 24,
              }
            : {}),
          paddingBottom:
            isKioskExperience || cartItemCount > 0
              ? 118
              : styles.content.paddingBottom,
        }}
      >
        {!isKioskExperience && (
          <div style={styles.qrMenuTools}>
            {customerName || isPendingAccountsPaymentOpen ? kioskLanguageControl : null}
            {translationStatus === "loading" ? <span role="status">{tx("Traduciendo la carta...")}</span> : null}
            {translationStatus === "error" ? <span role="status">
              {tx("No pudimos traducir algunos textos. Mostramos los originales.")}{" "}
              <button type="button" onClick={retryTranslation}>{tx("Reintentar")}</button>
            </span> : null}
          </div>
        )}
        {checkoutMessage.text ? (
          <div
            style={{
              ...styles.checkoutNotice,
              ...(checkoutMessage.type === "error"
                ? styles.checkoutNoticeError
                : styles.checkoutNoticeSuccess),
            }}
          >
            {checkoutMessage.text}
          </div>
        ) : null}

        {!isKioskExperience && hasReviewableOrder ? (
          <div style={styles.orderShortcutWrap}>
            <button
              type="button"
              style={{
                ...styles.orderShortcutButton,
                borderColor: primaryColor,
              }}
              onClick={() => {
                setCartModalStep("review");
                setIsCartOpen(true);
              }}
            >
              <span style={{ ...styles.orderShortcutTitle, color: primaryColor }}>
                {t("myOrder")}
              </span>
              <span style={styles.orderShortcutMeta}>
                {customerName ? `${customerName} \u00b7 ` : ""}
                {reviewItemCount > 0
                  ? `${reviewItemCount} ${
                      tx(reviewItemCount === 1 ? "producto" : "productos")
                    } \u00b7 ${tx(table.label || `Mesa ${table.tableNumber || "-"}`)}`
                  : tx(table.label || `Mesa ${table.tableNumber || "-"}`)}
              </span>
            </button>
          </div>
        ) : null}

        {!isKioskExperience && !tabletSessionKey && pendingTableAccounts.length > 0 ? (
          <div style={styles.pendingAccountsShortcutWrap}>
            <button
              type="button"
              style={{
                ...styles.pendingAccountsShortcutButton,
                borderColor: `${primaryColor}70`,
              }}
              onClick={() => openPendingAccountsPayment(customerName)}
            >
              <span style={{ ...styles.orderShortcutTitle, color: primaryColor }}> {tx("Pagar cuentas pendientes")} </span>
              <span style={styles.orderShortcutMeta}>
                {pendingTableAccounts.length} {pendingTableAccounts.length === 1 ? tx("cuenta") : tx("cuentas")}
                {` · ${currencyFormatter.format(
                  pendingTableAccounts.reduce(
                    (total, account) => total + Number(account.totalAmount || 0),
                    0
                  )
                )}`}
              </span>
            </button>
          </div>
        ) : null}

        {isVisualMenu && sections.length > 0 ? (
          <BarMenuCatalog key={branch?.id} sections={sections} renderProductCard={renderProductCard} primaryColor={primaryColor} layout={qrMenuStyle} tx={tx} historyScope={tabletSessionKey ? "" : qrToken} />
        ) : null}

        {!isKioskExperience && featuredPromotions.length > 0 ? (
          <section className={`menu-offers menu-offers--${qrMenuStyle}`} style={promotionCardTheme}>
            <div className="menu-offers-heading">
              <h2>{tx("Promociones, combos y menu del dia")}</h2>
              <span>{featuredPromotions.length} {tx("propuestas en esta sucursal")}</span>
            </div>
            <div className="menu-offers-list">
              {featuredPromotions.map(renderQrPromotionCard)}
            </div>
          </section>
        ) : null}

        {isKioskExperience && sections.length > 0 ? (
          <div
            style={{
              ...styles.kioskCatalogShell,
              ...(isCompactViewport ? styles.kioskCatalogShellCompact : {}),
            }}
          >
            <div style={styles.kioskCatalogMain}>
              <MenuCatalogErrorBoundary
                resetKey={`${kioskCatalogMode}:${activeCategoryId}:${activeSubcategoryId}`}
                onRecover={recoverCatalogRender}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
              >
                {kioskCatalogMode === "picker" ? (
                  <section style={styles.kioskCategoriesBand}>
                    <div style={styles.kioskCategoryPickerHeader}>
                      <div>
                        <h2 style={styles.kioskCategoryPickerTitle}>{tx("Elegi una categoria")}</h2>
                        <span style={styles.kioskCategoryPickerMeta}>
                          {kioskCategoryCards.length} {t("sections")} {tx("disponibles")} </span>
                      </div>
                    </div>

                    <div style={styles.kioskCategoryGrid}>
                      {kioskCategoryCards.map((entry) => {
                        const isPromoEntry = entry.type === "promotions";

                        return (
                          <button
                            key={entry.id}
                            type="button"
                            onClick={() => {
                              if (isPromoEntry) {
                                setKioskCatalogMode("promotions");
                                return;
                              }

                              setKioskCatalogMode("category");
                              setActiveCategoryId(entry.id);
                              setActiveSubcategoryId("__all__");
                            }}
                            style={styles.kioskCategoryCard}
                          >
                            <div style={styles.kioskCategoryImageWrap}>
                              {entry.imageUrl ? (
                                <img
                                  src={entry.imageUrl}
                                  alt={tx(entry.label)}
                                  style={styles.kioskCategoryImage}
                                />
                              ) : (
                                <div
                                  style={styles.kioskCategoryFallback}
                                  aria-hidden="true"
                                />
                              )}
                              <div style={styles.kioskCategoryOverlay} />
                              {entry.meta ? (
                                <span style={styles.kioskCategoryBadge}>{entry.meta}</span>
                              ) : null}
                            </div>
                            <div style={styles.kioskCategoryContent}>
                              <div style={styles.kioskCategoryTitleRow}>
                                {entry.icon ? (
                                  <span
                                    style={styles.kioskCategoryIcon}
                                    translate="no"
                                    lang={NON_TRANSLATABLE_LANGUAGE}
                                    className="notranslate"
                                  >
                                    {entry.icon}
                                  </span>
                                ) : null}
                                <strong style={styles.kioskCategoryTitle}>{tx(entry.label)}</strong>
                              </div>
                              <span style={styles.kioskCategoryDescription}>
                                {tx(entry.description)}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                {kioskCatalogMode === "promotions" ? (
                  <section style={{ ...styles.menuBand, ...styles.kioskCategoryDetailBand }}>
                    <button
                      type="button"
                      style={styles.kioskBackToCategoriesButton}
                      onClick={() => setKioskCatalogMode("picker")}
                    >
                      <span style={styles.kioskBackIcon} aria-hidden="true">
                        &lt;
                      </span>
                      <span style={styles.kioskBackCopy}>
                        <strong style={styles.kioskBackTitle}>{tx("Categorias")}</strong>
                        <small style={styles.kioskBackMeta}>{tx("Volver al menu")}</small>
                      </span>
                    </button>

                    <div style={styles.sectionHeader}>
                      <h2 style={styles.sectionTitle}>{t("promotionsAndCombos")}</h2>
                      <span style={styles.sectionMeta}>
                        {formatKioskCount(kioskCatalogPromotions.length)} {t("chooseSuffix")}
                      </span>
                    </div>

                    <div style={styles.categoryPromotionsGrid}>
                      {kioskCatalogPromotions.map((promotion) => {
                        const isAvailableNow = availablePromotionIds.has(promotion.id);
                        const isSelectableNow = isPromotionSelectableNow(promotion, new Date());
                        const promotionImageUrl = resolvePromotionUiImageUrl(promotion);
                        const promotionTitle = toRenderableText(promotion?.title, "Promocion");

                        return (
                          <article
                            key={`kiosk-promo-${promotion.id}`}
                            style={{
                              ...styles.categoryPromoCard,
                              cursor: "pointer",
                              opacity: isSelectableNow ? 1 : 0.68,
                            }}
                            onClick={() => {
                              openPromotionDetail(promotion);
                            }}
                          >
                            {promotionImageUrl ? (
                              <div style={styles.categoryPromoMediaWrap}>
                                {promotionBackdropLogoUrl ? (
                                  <div
                                    aria-hidden="true"
                                    style={buildPromotionBackdropPatternStyle(
                                      promotionBackdropLogoUrl
                                    )}
                                  />
                                ) : null}
                                <img
                                  src={promotionImageUrl}
                                  alt={tx(promotionTitle)}
                                  style={styles.categoryPromoMedia}
                                />
                              </div>
                            ) : null}

                            <div style={styles.categoryPromoTop}>
                              <span
                                style={{
                                  ...styles.categoryPromoHook,
                                  fontFamily: resolvePromotionFontFamily(promotion),
                                }}
                              >
                                {tx(getPromotionHeadline(promotion))}
                              </span>
                              <span
                                style={{
                                  ...styles.categoryPromoStatus,
                                  ...(isAvailableNow
                                    ? styles.categoryPromoStatusLive
                                    : styles.categoryPromoStatusScheduled),
                                }}
                              >
                                {isAvailableNow ? tx("Ahora") : tx("Programada")}
                              </span>
                            </div>

                            <strong style={styles.categoryPromoName}>{tx(promotionTitle)}</strong>
                            <p style={styles.categoryPromoText}>
                              {tx(getPromotionDescription(promotion))}
                            </p>

                            {Array.isArray(promotion.items) && promotion.items.length > 0 ? (
                              <div style={styles.promotionComboList}>
                                {promotion.items.slice(0, 3).map((item) => (
                                  <span
                                    key={`kiosk-inline-${promotion.id}-${item.productId}`}
                                    style={styles.promotionComboChip}
                                  >
                                    {Math.max(1, Number(item?.quantity || 1))}x{" "}
                                    {tx(toRenderableText(item?.name, "Producto"))}
                                  </span>
                                ))}
                              </div>
                            ) : promotion.pricingSummary ? (
                              <div style={styles.promotionComboList}>
                                <span style={styles.promotionComboChip}>
                                  {tx(promotion.pricingSummary)}
                                </span>
                              </div>
                            ) : null}

                            <div style={styles.categoryPromoFooter}>
                              <span style={styles.categoryPromoSchedule}>
                                {tx(promotion.scheduleSummary ||
                                  buildPromotionScheduleSummary(promotion.schedule))}
                              </span>

                              <button
                                type="button"
                                style={styles.categoryPromoButton}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openPromotionDetail(promotion);
                                }}
                              >
                                {getPromotionActionLabel(promotion)}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ) : activeSection && kioskCatalogMode === "category" ? (
                  <section style={styles.menuBand}>
                    <button
                      type="button"
                      style={styles.kioskBackToCategoriesButton}
                      onClick={() => setKioskCatalogMode("picker")}
                    >
                      <span style={styles.kioskBackIcon} aria-hidden="true">
                        &lt;
                      </span>
                      <span style={styles.kioskBackCopy}>
                        <strong style={styles.kioskBackTitle}>{tx("Categorias")}</strong>
                        <small style={styles.kioskBackMeta}>{tx("Volver al menu")}</small>
                      </span>
                    </button>

                    <div style={styles.kioskCategoryDetailHeader}>
                      <h2 style={styles.sectionTitle}>{tx(getCategoryDisplayName(activeSection))}</h2>
                      <span style={styles.sectionMeta}>
                        {visibleProducts.length} {tx(visibleProducts.length === 1 ? "opcion" : "opciones")} -{" "}
                        {availableVisibleProductsCount} {tx(availableVisibleProductsCount === 1 ? "disponible" : "disponibles")}
                        {unavailableVisibleProductsCount > 0
                          ? ` - ${unavailableVisibleProductsCount} ${tx("Sin stock")}`
                          : ""}
                      </span>
                    </div>

                    {showSubcategorySelector && (
                      <div
                        style={{
                          ...styles.subcategoryRail,
                          ...styles.kioskCategoryDetailSubcategoryRail,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setActiveSubcategoryId("__all__")}
                          style={{
                            ...styles.subcategoryButton,
                            ...(activeSubcategoryId === "__all__"
                              ? {
                                  backgroundColor: primaryColor,
                                  color: secondaryColor,
                                  borderColor: primaryColor,
                                }
                              : {}),
                          }}
                        > {tx("Todas")} </button>

                        {activeSubcategories.map((subcategory) => (
                          <button
                            key={subcategory.id}
                            type="button"
                            onClick={() => setActiveSubcategoryId(subcategory.id)}
                            style={{
                              ...styles.subcategoryButton,
                              ...(activeSubcategoryId === subcategory.id
                                ? {
                                    backgroundColor: primaryColor,
                                    color: secondaryColor,
                                    borderColor: primaryColor,
                                  }
                                : {}),
                            }}
                          >
                            {tx(subcategory.name)}
                          </button>
                        ))}
                      </div>
                    )}

                    {unavailableVisibleProductsCount > 0 ? (
                      <div
                        style={{
                          padding: "14px 16px",
                          borderRadius: 16,
                          border: "1px solid #fca5a5",
                          backgroundColor: "#fef2f2",
                          color: "#991b1b",
                          fontSize: 14,
                          lineHeight: 1.45,
                          fontWeight: 700,
                        }}
                      > {tx("Hay")} {unavailableVisibleProductsCount} {tx(unavailableVisibleProductsCount === 1 ? "producto" : "productos")} {tx("sin stock y ya quedaron deshabilitados hasta reponer ingredientes.")} </div>
              ) : null}

              <div
                      style={{
                        ...styles.kioskProductGrid,
                        ...(isCompactViewport ? styles.kioskProductGridCompact : {}),
                      }}
                    >
                      {visibleProducts.map((product) =>
                        renderProductCard(
                          product,
                          showSubcategorySelector && activeSubcategoryId === "__all__"
                        )
                      )}
                    </div>
                  </section>
                ) : null}
              </MenuCatalogErrorBoundary>
            </div>
          </div>
        ) : isVisualMenu && sections.length > 0 ? (
          null
        ) : !isKioskExperience && sections.length > 0 ? (
          <>
            <section
              style={{
                ...styles.categoriesBand,
                ...(isCompactViewport ? styles.categoriesBandSticky : {}),
              }}
            >
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>{tx("Categorias")}</h2>
                <span style={styles.sectionMeta}>{totalProducts} {tx("platos disponibles")}</span>
              </div>

              {false ? (
                <div style={styles.categoryRail}>
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveCategoryId(section.id)}
                      style={{
                        ...styles.kioskCategoryCard,
                        ...(activeSection?.id === section.id
                          ? {
                              borderColor: primaryColor,
                              boxShadow: `0 28px 46px ${primaryColor}26`,
                            }
                          : {}),
                      }}
                    >
                      <div style={styles.kioskCategoryImageWrap}>
                        {section.imageUrl ? (
                          <img
                            src={section.imageUrl}
                            alt={tx(getCategoryDisplayName(section))}
                            style={styles.kioskCategoryImage}
                          />
                        ) : (
                          <div
                            style={styles.kioskCategoryFallback}
                            aria-hidden="true"
                          />
                        )}
                        <div style={styles.kioskCategoryOverlay} />
                        <span style={styles.kioskCategoryBadge}>
                          {section.itemsCount} {tx(section.itemsCount === 1 ? "opcion" : "opciones")}
                        </span>
                      </div>
                      <div style={styles.kioskCategoryContent}>
                        <div style={styles.kioskCategoryTitleRow}>
                          {resolveKioskSectionIcon(section) ? (
                            <span
                              style={styles.kioskCategoryIcon}
                              translate="no"
                              lang={NON_TRANSLATABLE_LANGUAGE}
                              className="notranslate"
                            >
                              {resolveKioskSectionIcon(section)}
                            </span>
                          ) : null}
                          <strong style={styles.kioskCategoryTitle}>{tx(getCategoryDisplayName(section))}</strong>
                        </div>
                        <span style={styles.kioskCategoryDescription}>
                          {tx(section.description)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={styles.categoryRail}>
                {sections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveCategoryId(section.id)}
                    style={{
                      ...styles.categoryButton,
                      ...(activeSection?.id === section.id
                        ? {
                            backgroundColor: primaryColor,
                            color: secondaryColor,
                            borderColor: primaryColor,
                          }
                        : {}),
                    }}
                  >
                    {resolveKioskSectionIcon(section) ? (
                      <span
                        style={styles.categoryIcon}
                        translate="no"
                        lang={NON_TRANSLATABLE_LANGUAGE}
                        className="notranslate"
                      >
                        {resolveKioskSectionIcon(section)}
                      </span>
                    ) : null}
                    <span>{tx(getCategoryDisplayName(section))}</span>
                  </button>
                ))}
                </div>
              )}
            </section>

            {activeSection && (
              <section style={styles.menuBand}>
                <div style={styles.sectionHeader}>
                  <h2 style={styles.sectionTitle}>{tx(getCategoryDisplayName(activeSection))}</h2>
                  <span style={styles.sectionMeta}>
                    {isKioskExperience
                      ? `${visibleProducts.length} opcion${
                          visibleProducts.length === 1 ? "" : "es"
                        } - ${availableVisibleProductsCount} disponible${
                          availableVisibleProductsCount === 1 ? "" : "s"
                        }${
                          unavailableVisibleProductsCount > 0
                            ? ` - ${unavailableVisibleProductsCount} sin stock`
                            : ""
                        }`
                      : `${activeSubcategories.length} subcategoria${
                          activeSubcategories.length === 1 ? "" : "s"
                        }${
                          unavailableVisibleProductsCount > 0
                            ? ` - ${unavailableVisibleProductsCount} sin stock`
                            : ""
                        }`}
                  </span>
                </div>

                {showSubcategorySelector && (
                  <div style={styles.subcategoryRail}>
                    <button
                      type="button"
                      onClick={() => setActiveSubcategoryId("__all__")}
                      style={{
                        ...styles.subcategoryButton,
                        ...(activeSubcategoryId === "__all__"
                          ? {
                              backgroundColor: primaryColor,
                              color: secondaryColor,
                              borderColor: primaryColor,
                            }
                          : {}),
                      }}
                    > {tx("Todas")} </button>

                    {activeSubcategories.map((subcategory) => (
                      <button
                        key={subcategory.id}
                        type="button"
                        onClick={() => setActiveSubcategoryId(subcategory.id)}
                        style={{
                          ...styles.subcategoryButton,
                          ...(activeSubcategoryId === subcategory.id
                            ? {
                                backgroundColor: primaryColor,
                                color: secondaryColor,
                                borderColor: primaryColor,
                              }
                            : {}),
                        }}
                      >
                        {tx(subcategory.name)}
                      </button>
                    ))}
                  </div>
                )}

                {activeCategoryPromotions.length > 0 && (
                  <section className="menu-offers menu-offers--classic" style={promotionCardTheme}>
                    <div className="menu-offers-heading">
                      <h3>{tx("Promos en")} {tx(getCategoryDisplayName(activeSection))}</h3>
                      <span>{activeCategoryPromotions.length} {tx("propuestas")}</span>
                    </div>
                    <div className="menu-offers-list">
                      {activeCategoryPromotions.map(renderQrPromotionCard)}
                    </div>
                  </section>
                )}

                {unavailableVisibleProductsCount > 0 ? (
                  <div
                    style={{
                      padding: "14px 16px",
                      borderRadius: 16,
                      border: "1px solid #fca5a5",
                      backgroundColor: "#fef2f2",
                      color: "#991b1b",
                      fontSize: 14,
                      lineHeight: 1.45,
                      fontWeight: 700,
                    }}
                  > {tx("Hay")} {unavailableVisibleProductsCount} {tx(unavailableVisibleProductsCount === 1 ? "producto" : "productos")} {tx("sin stock y ya quedaron deshabilitados hasta reponer ingredientes.")} </div>
                ) : null}

                <div
                  style={{
                    ...(isCompactViewport ? styles.productGridCompact : styles.productGrid),
                    ...(isKioskExperience && !isCompactViewport
                      ? {
                          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                          gap: 14,
                        }
                      : {}),
                  }}
                >
                  {visibleProducts.map((product) =>
                    renderProductCard(
                      product,
                      showSubcategorySelector && activeSubcategoryId === "__all__"
                    )
                  )}
                </div>
              </section>
            )}
          </>
        ) : (
          <section style={styles.emptyBand}>
            <h2 style={styles.sectionTitle}>{tx("Todavia no hay platos publicados")}</h2>
            <p style={styles.emptyText}> {tx("Esta mesa ya esta conectada, pero la carta de la sucursal aun no tiene categorias o platos disponibles.")} </p>
          </section>
        )}
      </main>

      {!isKioskExperience && (
        <MozzoFiscalMenuFooter
          key={`${activeCategoryId}:${activeSubcategoryId}`}
          language={kioskLanguage}
        />
      )}

      {selectedPromotion && (
        <div style={styles.modalOverlay} onClick={closePromotionDetail}>
          <div style={styles.promoModalCard} onClick={(event) => event.stopPropagation()}>
            {resolvePromotionUiImageUrl(selectedPromotion, MAX_KIOSK_PROMOTION_DATA_IMAGE_LENGTH) ? (
              <div
                style={{
                  ...styles.promoModalMediaWrap,
                  ...(selectedPromotion.type === "combo"
                    ? styles.promoModalMediaWrapCompact
                    : null),
                }}
              >
                <img
                  src={resolvePromotionUiImageUrl(
                    selectedPromotion,
                    MAX_KIOSK_PROMOTION_DATA_IMAGE_LENGTH
                  )}
                  alt={tx(toRenderableText(selectedPromotion?.title, "Promocion"))}
                  style={{
                    ...styles.promoModalImage,
                    ...(selectedPromotion.type === "combo"
                      ? styles.promoModalImageCompact
                      : null),
                  }}
                />
              </div>
            ) : null}

            <div style={styles.promoModalBody}>
              <div style={styles.promoModalHeader}>
                <div style={styles.promoModalHeaderText}>
                  <div style={styles.promoModalBadgeRow}>
                    <span
                      style={{
                        ...styles.promotionHookBadge,
                        fontFamily: resolvePromotionFontFamily(selectedPromotion),
                      }}
                    >
                      {tx(getPromotionHeadline(selectedPromotion))}
                    </span>
                    <span
                      style={{
                        ...styles.promotionAvailabilityBadge,
                        ...(availablePromotionIds.has(selectedPromotion.id)
                          ? styles.promotionAvailabilityBadgeLive
                          : styles.promotionAvailabilityBadgeScheduled),
                      }}
                    >
                      {availablePromotionIds.has(selectedPromotion.id) ? tx("Ahora") : tx("Programada")}
                    </span>
                  </div>

                  <h3
                    style={{
                      ...styles.promoModalTitle,
                      fontFamily: resolvePromotionFontFamily(selectedPromotion),
                    }}
                  >
                    {tx(toRenderableText(selectedPromotion?.title, "Promocion"))}
                  </h3>
                  <p style={styles.promoModalText}>
                    {tx(getPromotionDescription(selectedPromotion))}
                  </p>
                </div>

                <button type="button" style={styles.secondaryAction} onClick={closePromotionDetail}> {tx("Cerrar")} </button>
              </div>

              <div style={styles.promoModalMetaRow}>
                {getPromotionScopeLabel(selectedPromotion) ? (
                  <span style={styles.promotionCategoryPill}>
                    {tx(getPromotionScopeLabel(selectedPromotion))}
                  </span>
                ) : null}
                <span style={styles.promoModalMetaText}>
                  {tx(selectedPromotion.scheduleSummary ||
                    buildPromotionScheduleSummary(selectedPromotion.schedule))}
                  {selectedPromotion.validity ? ` \u00b7 ${tx(selectedPromotion.validity)}` : ""}
                </span>
              </div>

              {!selectedPromotionIsSelectableNow ? (
                <div style={styles.promoModalWarning}> {tx("Esta promo o combo esta visible, pero no se puede pedir en este dia u horario.")} </div>
              ) : null}

              {selectedPromotion.type === "combo" ? (
                <div style={styles.promoModalSection}>
                  <strong style={styles.promoModalSectionTitle}>
                    {isDailyMenuPromotion(selectedPromotion) ? tx("Incluye hoy") : tx("Incluye")}
                  </strong>
                  <p
                    style={{
                      margin: "8px 0 0",
                      color: "#64748b",
                      fontSize: "14px",
                      lineHeight: 1.5,
                    }}
                  >
                    {isDailyMenuPromotion(selectedPromotion)
                      ? "Toca cualquier producto si quieres personalizarlo. El precio especial del menu del dia se mantiene cuando agregas todos los items incluidos."
                      : "Toca cualquier producto si quieres personalizarlo. El descuento del combo se mantiene cuando completas todos los items del combo."}
                  </p>
                  <div style={styles.promoModalOptionList}>
                    {selectedPromotionProducts.length > 0 ? (
                      selectedPromotionProducts.map((product) => (
                        <button
                          key={`promo-combo-${product.id}`}
                          type="button"
                          style={styles.promoModalOptionButton}
                          onClick={() => openPromotionProductDetail(product, selectedPromotion)}
                        >
                          <div style={styles.promoModalOptionInfo}>
                            <strong>{product.comboQuantity}x {tx(product.name)}</strong>
                            <span style={styles.promoModalOptionMeta}>
                              {tx(product.subcategoryName || product.subcategoria || tx("Producto"))}
                            </span>
                            <span style={styles.promoModalOptionMeta}>
                              {product.ingredients?.length > 0
                                ? tx("Se puede personalizar")
                                : tx("Toca para agregarlo al combo")}
                            </span>
                          </div>
                          <span style={styles.promoModalOptionPrice}>
                            {showPrices ? currencyFormatter.format(resolveDisplayPrice(product)) : ""}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div style={styles.emptyDetailBox}>
                        {isDailyMenuPromotion(selectedPromotion)
                          ? "Este menu del dia todavia no tiene productos listos para pedir en esta carta."
                          : "Este combo todavia no tiene productos listos para pedir en esta carta."}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={styles.promoModalSection}>
                  <strong style={styles.promoModalSectionTitle}> {tx("Elegi desde la promo")} </strong>
                  {selectedPromotionProducts.length > 0 ? (
                    <div style={styles.promoModalOptionList}>
                      {selectedPromotionProducts.map((product) => (
                        <button
                          key={`promo-product-${product.id}`}
                          type="button"
                          style={styles.promoModalOptionButton}
                          onClick={() => openPromotionProductDetail(product, selectedPromotion)}
                        >
                          <div style={styles.promoModalOptionInfo}>
                            <strong>{tx(product.name)}</strong>
                            <span style={styles.promoModalOptionMeta}>
                              {tx(product.subcategoryName || product.subcategoria || tx("Producto"))}
                            </span>
                          </div>
                          {showPrices ? (
                            <span style={styles.promoModalOptionPrice}>
                              {currencyFormatter.format(resolveDisplayPrice(product))}
                            </span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.emptyDetailBox}> {tx("No encontramos platos disponibles para esta promo en la carta actual.")} </div>
                  )}
                </div>
              )}

              <div style={styles.promoModalActions}>
                {selectedPromotion.type === "combo" ? (
                  <button
                    type="button"
                    style={{
                      ...styles.primaryAction,
                      backgroundColor: primaryColor,
                      color: secondaryColor,
                    }}
                    onClick={() => handlePromotionQuickAction(selectedPromotion)}
                    disabled={
                      !selectedPromotionIsSelectableNow ||
                      selectedPromotionProducts.length === 0
                    }
                  >
                    {isDailyMenuPromotion(selectedPromotion)
                      ? tx("Agregar menu del dia")
                      : tx("Agregar combo rapido")}
                  </button>
                ) : selectedPromotionProducts.length === 1 ? (
                  <button
                    type="button"
                    style={{
                      ...styles.primaryAction,
                      backgroundColor: primaryColor,
                      color: secondaryColor,
                    }}
                    onClick={() => handlePromotionQuickAction(selectedPromotion)}
                    disabled={!selectedPromotionIsSelectableNow}
                  > {tx("Elegir este plato")} </button>
                ) : (
                  <button
                    type="button"
                    style={{
                      ...styles.primaryAction,
                      backgroundColor: primaryColor,
                      color: secondaryColor,
                    }}
                    onClick={() => handlePromotionQuickAction(selectedPromotion)}
                    disabled={!selectedPromotionIsSelectableNow}
                  > {tx("Ver productos")} </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProduct && (
        <div style={styles.modalOverlay} onClick={closeProductDetail}>
          <div style={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalMediaWrap}>
                <CatalogImage
                  src={selectedProduct.imageUrl}
                  alt={tx(selectedProduct.name)}
                  style={styles.modalImage}
                />
            </div>

            <div style={styles.modalBody}>
              <div style={styles.modalHeader}>
                <div>
                  <h3 style={styles.modalTitle}>{tx(selectedProduct.name)}</h3>
                  <p style={styles.modalDescription}>
                    {tx(selectedProduct.description || tx("Este plato no tiene descripcion cargada."))}
                  </p>
                </div>

                {showPrices ? (
                  <div style={styles.modalPriceWrap}>
                    {isComboProductDetail ? <span style={styles.orderAmountLabel}>{tx("Precio de lista por unidad")}</span> : null}
                    <strong style={{ ...styles.modalPrice, color: primaryColor }}>
                      {currencyFormatter.format(resolveDisplayPrice(selectedProduct))}
                    </strong>
                    {selectedProductSummary?.extraCost > 0 ? (
                      <span style={styles.modalExtraPrice}>
                        + {currencyFormatter.format(selectedProductSummary.extraCost)}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {selectedProduct.ingredients?.length > 0 ? (
                <>
                  <div style={styles.modalSummaryRow}>
                    <span style={styles.modalSummaryChip}>
                      {selectedProduct.ingredients.length} {tx("ingredientes")} </span>
                    <span style={styles.modalSummaryChip}>
                      {selectedProductSummary?.removedUnits || 0} {tx("quitados")} </span>
                    <span style={styles.modalSummaryChip}>
                      {selectedProductSummary?.extraUnits || 0} {tx("extras")} </span>
                  </div>

                  <div style={styles.ingredientList}>
                    {selectedProduct.ingredients.map((ingredient) => {
                      const currentQuantity = Number(
                        draftSelection?.[ingredient.id] ?? ingredient.baseQuantity ?? 0
                      );
                      const baseQuantity = Number(ingredient.baseQuantity || 0);

                      return (
                        <div key={ingredient.id} style={styles.ingredientRow}>
                          <div style={styles.ingredientMain}>
                              <CatalogImage
                                src={ingredient.imageUrl}
                                alt={tx(ingredient.name)}
                                loading="lazy"
                                decoding="async"
                                style={styles.ingredientThumb}
                              />

                            <div style={styles.ingredientInfo}>
                              <div style={styles.ingredientNameRow}>
                                <strong style={styles.ingredientName}>
                                  {tx(ingredient.name)}
                                </strong>
                                <span style={styles.ingredientState}>
                                  {tx(getIngredientStateLabel(ingredient, currentQuantity))}
                                </span>
                              </div>

                              <div style={styles.ingredientMetaRow}>
                                <span style={styles.ingredientMeta}> {tx("Base:")} {baseQuantity}
                                </span>
                                <span style={styles.ingredientMeta}>
                                  {ingredient.type === "required"
                                    ? tx("Ingrediente base")
                                    : tx("Ingrediente opcional")}
                                </span>
                                <span style={styles.ingredientMeta}>
                                  {ingredient.canRemove
                                    ? tx("Se puede quitar")
                                    : tx("No se puede quitar")}
                                </span>
                                <span style={styles.ingredientMeta}>
                                  {ingredient.canAddMore
                                    ? tx("Se puede agregar hasta #{count} extra", { count: Number(
                                        ingredient.maxExtraQuantity ||
                                          DEFAULT_MAX_EXTRA_QUANTITY
                                      ) })
                                    : tx("No se ofrece como extra")}
                                </span>
                                {Number(ingredient.price || 0) > 0 ? (
                                  <span style={styles.ingredientMeta}> {tx("Extra:")} {currencyFormatter.format(Number(ingredient.price || 0))}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <div style={styles.ingredientControls}>
                            {ingredient.canRemove || ingredient.canAddMore ? (
                              <>
                                <button
                                  type="button"
                                  style={styles.quantityButton}
                                  onClick={() => updateIngredientQuantity(ingredient, -1)}
                                  disabled={
                                    currentQuantity <=
                                    (ingredient.canRemove
                                      ? 0
                                      : Number(ingredient.baseQuantity || 0))
                                  }
                                >
                                  -
                                </button>
                                <span style={styles.quantityValue}>{currentQuantity}</span>
                                <button
                                  type="button"
                                  style={styles.quantityButton}
                                  onClick={() => updateIngredientQuantity(ingredient, 1)}
                                  disabled={
                                    currentQuantity >=
                                    (ingredient.canAddMore
                                      ? Number(ingredient.baseQuantity || 0) +
                                        Math.max(
                                          1,
                                          Number(
                                            ingredient.maxExtraQuantity ||
                                              DEFAULT_MAX_EXTRA_QUANTITY
                                          )
                                        )
                                      : Number(ingredient.baseQuantity || 0))
                                  }
                                >
                                  +
                                </button>
                              </>
                            ) : (
                              <span style={styles.fixedChip}>{tx("Fijo")}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={styles.emptyDetailBox}> {tx("Este plato todavia no tiene ingredientes cargados para personalizar.")} </div>
              )}

              {selectedEmpanadaSaleOptions.length > 0 ? (
                <div style={styles.linkedChoiceWrap}>
                  <div style={styles.linkedChoiceGroup}>
                    <div style={styles.linkedChoiceHeader}>
                      <div>
                        <strong style={styles.linkedChoiceTitle}> {tx("Elegi la presentacion")} </strong>
                        <p style={styles.linkedChoiceText}> {tx("Podes pedir una unidad, media docena o docena segun lo que haya configurado el restaurante.")} </p>
                      </div>
                      <span style={styles.modalSummaryChip}>{tx("Obligatorio")}</span>
                    </div>

                    <div style={styles.linkedChoiceList}>
                      {selectedEmpanadaSaleOptions.map((option) => {
                        const isActive = selectedEmpanadaSaleOption?.id === option.id;

                        return (
                          <button
                            key={`empanada-sale:${option.id}`}
                            type="button"
                            style={{
                              ...styles.linkedChoiceButton,
                              ...(isActive ? styles.linkedChoiceButtonActive : {}),
                            }}
                            onClick={() => {
                              setDraftEmpanadaSaleOptionId(option.id);
                            }}
                          >
                            <div>
                              <strong style={styles.linkedChoiceProductName}>
                                {tx(option.label)}
                              </strong>
                              <p style={styles.linkedChoiceProductDescription}>
                                {Number(option.unitCount || 1)}{" "}
                                {Number(option.unitCount || 1) === 1
                                  ? tx("unidad")
                                  : tx("unidades")}
                              </p>
                            </div>
                            {showPrices ? (
                              <span style={styles.linkedChoicePrice}>
                                {currencyFormatter.format(Number(option.price || 0))}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedEmpanadaCookingOptions.length > 0 ? (
                <div style={styles.linkedChoiceWrap}>
                  <div style={styles.linkedChoiceGroup}>
                    <div style={styles.linkedChoiceHeader}>
                      <div>
                        <strong style={styles.linkedChoiceTitle}> {tx("Elegi la coccion")} </strong>
                        <p style={styles.linkedChoiceText}> {tx("El restaurante define si ofrece horno, frita o ambas.")} </p>
                      </div>
                      <span style={styles.modalSummaryChip}>
                        {selectedEmpanadaCookingOptions.length > 1
                          ? tx("Obligatorio")
                          : tx("Automatico")}
                      </span>
                    </div>

                    <div style={styles.linkedChoiceList}>
                      {selectedEmpanadaCookingOptions.map((option) => {
                        const isActive =
                          selectedEmpanadaCookingOption?.id === option.id;

                        return (
                          <button
                            key={`empanada-cooking:${option.id}`}
                            type="button"
                            style={{
                              ...styles.linkedChoiceButton,
                              ...(isActive ? styles.linkedChoiceButtonActive : {}),
                            }}
                            onClick={() => {
                              setDraftEmpanadaCookingOptionId(option.id);
                            }}
                          >
                            <div>
                              <strong style={styles.linkedChoiceProductName}>
                                {tx(option.label)}
                              </strong>
                              <p style={styles.linkedChoiceProductDescription}> {tx("Se informa a cocina como detalle del pedido.")} </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedEmpanadaNeedsSaleSetup ? (
                <div style={styles.linkedChoiceWrap}>
                  <div style={styles.linkedChoiceGroup}>
                    <div style={styles.linkedChoiceHeader}>
                      <div>
                        <strong style={styles.linkedChoiceTitle}> {tx("Falta cargar precios de empanadas")} </strong>
                        <p style={styles.linkedChoiceText}> {tx("Este producto esta en una categoria/subcategoria Empanadas, pero no tiene Unidad, Media docena o Docena con precio. Editalo desde Admin para habilitar la eleccion.")} </p>
                      </div>
                      <span style={styles.modalSummaryChip}>{tx("Requiere admin")}</span>
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedPizzaSizeOptions.length > 0 ? (
                <div style={styles.linkedChoiceWrap}>
                  <div style={styles.linkedChoiceGroup}>
                    <div style={styles.linkedChoiceHeader}>
                      <div>
                        <strong style={styles.linkedChoiceTitle}> {tx("Elegi el tamano")} </strong>
                        <p style={styles.linkedChoiceText}> {tx("Elegi el tamano. Solo se muestran las modalidades de venta habilitadas para esa presentacion.")} </p>
                      </div>
                      <span style={styles.modalSummaryChip}>{tx("Obligatorio")}</span>
                    </div>

                    <div style={styles.linkedChoiceList}>
                      {selectedPizzaSizeOptions.map((option) => {
                        const isActive = selectedPizzaSizeOption?.id === option.id;

                        return (
                          <button
                            key={`pizza-size:${option.id}`}
                            type="button"
                            style={{
                              ...styles.linkedChoiceButton,
                              ...(isActive ? styles.linkedChoiceButtonActive : {}),
                            }}
                            onClick={() => {
                              setDraftPizzaSizeOptionId(option.id);
                              setDraftPizzaPortionMode(getDefaultPizzaSaleMode(option));
                              setDraftPizzaHalfProductId("");
                            }}
                          >
                            <div>
                              <strong style={styles.linkedChoiceProductName}>
                                {tx(option.label)}
                              </strong>
                              {showPrices ? (
                                <p style={styles.linkedChoiceProductDescription}>
                                  {tx(getPizzaSizePriceSummary(option, (price) => currencyFormatter.format(price)))}
                                </p>
                              ) : null}
                            </div>
                            {showPrices ? (
                              <span style={styles.linkedChoicePrice}>
                                {currencyFormatter.format(getPizzaStartingPrice(option))}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedPizzaNeedsSizeSetup ? (
                <div style={styles.linkedChoiceWrap}>
                  <div style={styles.linkedChoiceGroup}>
                    <div style={styles.linkedChoiceHeader}>
                      <div>
                        <strong style={styles.linkedChoiceTitle}> {tx("Falta cargar tamanos de pizza")} </strong>
                        <p style={styles.linkedChoiceText}> {tx("Esta pizza esta en una categoria/subcategoria Pizzas, pero no tiene Mediana, Grande, Chica ni precios por tamano guardados. Editala desde Admin para habilitar la eleccion.")} </p>
                      </div>
                      <span style={styles.modalSummaryChip}>{tx("Requiere admin")}</span>
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedPizzaSizeOption && <PizzaSaleModeSelector option={selectedPizzaSizeOption} tx={tx}
                value={draftPizzaPortionMode} canCombine={selectedPizzaHalfOptions.length > 0}
                showPrices={showPrices} onChange={(mode) => {
                  setDraftPizzaPortionMode(mode); setDraftPizzaHalfProductId("");
                }} />}

              {draftPizzaPortionMode === "half_and_half" ? (
                <div style={styles.linkedChoiceWrap}>
                  <div style={styles.linkedChoiceGroup}>
                    <div style={styles.linkedChoiceHeader}>
                      <div>
                        <strong style={styles.linkedChoiceTitle}> {tx("Elegi la otra mitad")} </strong>
                        <p style={styles.linkedChoiceText}>
                          {tx("Estas combinando 1/2 #{name} con otra pizza del mismo tamano. Aca recien se calcula el precio final.", { name: tx(getPizzaProductName(selectedProduct)) })}
                        </p>
                      </div>
                      <span style={styles.modalSummaryChip}>{tx("Obligatorio")}</span>
                    </div>

                    {selectedPizzaHalfOptions.length > 0 ? (
                      <div style={styles.linkedChoiceList}>
                        {selectedPizzaHalfOptions.map((option) => {
                          const isActive = draftPizzaHalfProductId === option.id;
                          const optionPricing = getPizzaHalfAndHalfPricing(
                            selectedProduct,
                            option,
                            { sizeOptionId: selectedPizzaSizeOption?.id || "" }
                          );

                          return (
                            <button
                              key={`pizza-half:${option.id}`}
                              type="button"
                              style={{
                                ...styles.linkedChoiceButton,
                                ...(isActive ? styles.linkedChoiceButtonActive : {}),
                              }}
                              onClick={() => {
                                setDraftPizzaHalfProductId(option.id);
                              }}
                            >
                              <div>
                                <strong style={styles.linkedChoiceProductName}>
                                  1/2 {tx(getPizzaProductName(option))}
                                </strong>
                                {showPrices ? (
                                  <p style={styles.linkedChoiceProductDescription}>
                                    1/2 {tx(getPizzaProductName(selectedProduct))}{" "}
                                    {currencyFormatter.format(
                                      optionPricing.baseHalfUnitPrice
                                    )}{" "}
                                    + 1/2 {tx(getPizzaProductName(option))}{" "}
                                    {currencyFormatter.format(
                                      optionPricing.linkedHalfUnitPrice
                                    )}
                                    {selectedPizzaSizeOption?.label
                                      ? ` · ${tx(selectedPizzaSizeOption.label)}`
                                      : ""}
                                  </p>
                                ) : option.description || option.descripcion ? (
                                  <p style={styles.linkedChoiceProductDescription}>
                                    {tx(option.description || option.descripcion)}
                                  </p>
                                ) : null}
                              </div>
                              {showPrices ? (
                                <span style={styles.linkedChoicePrice}>
                                  {currencyFormatter.format(
                                    optionPricing.totalUnitPrice
                                  )}
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={styles.emptyDetailBox}> {tx("No hay otra pizza compatible para combinar con este tamano.")} </div>
                    )}
                  </div>
                </div>
              ) : null}

              {selectedLinkedRuleGroups.length > 0 ? (
                <div style={styles.linkedChoiceWrap}>
                  {draftProductQuantity > 1 ? (
                    <div style={styles.linkedChoiceGroup}>
                      <strong style={styles.linkedChoiceTitle}>{tx("Opciones por producto")}</strong>
                      <p style={styles.linkedChoiceText}> {tx("Elegi las salsas o acompanamientos de cada unidad. El tamano y los cambios de ingredientes de arriba se aplican a todas.")} </p>
                      <div role="group" aria-label={tx("Elegir producto para sus opciones")}
                        style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {selectedLinkedSelectionsByUnit.map((selections, index) => (
                          <button type="button" key={index}
                            aria-label={tx("Producto #{number}", { number: index + 1 })}
                            aria-pressed={activeLinkedUnit === index}
                            onClick={() => setActiveLinkedUnit(index)}
                            style={{
                              ...styles.linkedChoiceButton,
                              ...(activeLinkedUnit === index ? styles.linkedChoiceButtonActive : {}),
                              flex: "1 1 150px", minWidth: 0, flexDirection: "column",
                              alignItems: "flex-start", gap: 4,
                            }}>
                            <strong>{tx("Producto")} {index + 1}</strong>
                            <span style={{ ...styles.linkedChoiceProductDescription, overflowWrap: "anywhere" }}>
                              {selections.filter((selection) => !isPizzaHalfAndHalfSelection(selection))
                                .map((selection) => tx(selection.productName)).join(", ") || tx("Sin acompanamiento")}
                            </span>
                          </button>
                        ))}
                      </div>
                      <p role="status" style={styles.linkedChoiceText}>
                        {tx("Estas eligiendo las opciones del producto #{number} de #{count}.", { number: activeLinkedUnit + 1, count: draftProductQuantity })}
                      </p>
                      <button type="button" style={styles.secondaryAction}
                        onClick={() => setDraftLinkedSelectionsByUnit(Array.from(
                          { length: draftProductQuantity }, () => ({ ...draftLinkedSelections })
                        ))}> {tx("Usar estas opciones en todos")} </button>
                    </div>
                  ) : null}
                  {selectedLinkedRuleGroups.map((group) => (
                    <div key={group.groupKey} style={styles.linkedChoiceGroup}>
                      <div style={styles.linkedChoiceHeader}>
                        <div>
                          <strong style={styles.linkedChoiceTitle}>
                            {tx(group.promptTitle)}
                          </strong>
                          <p style={styles.linkedChoiceText}>
                            {group.required ? tx("Elegi la variedad que queres tomar") : tx("Puedes elegir una opcion o dejar Nada")}{draftProductQuantity > 1
                              ? tx(" para el producto #{number}.", { number: activeLinkedUnit + 1 })
                              : "."}
                          </p>
                        </div>
                        <span style={styles.modalSummaryChip}>{group.required ? tx("Obligatorio") : tx("Opcional")}</span>
                      </div>

                      <div style={styles.linkedChoiceList}>
                        {!group.required ? (
                        <button
                          type="button"
                          style={{
                            ...styles.linkedChoiceButton,
                            ...(!normalizeEntityId(draftLinkedSelections[group.groupKey])
                              ? styles.linkedChoiceButtonActive
                              : {}),
                          }}
                          aria-pressed={!normalizeEntityId(draftLinkedSelections[group.groupKey])}
                          onClick={() => updateLinkedSelection(group.groupKey, "")}
                        >
                          <div>
                            <strong style={styles.linkedChoiceProductName}>{tx("Nada")}</strong>
                            <p style={styles.linkedChoiceProductDescription}> {tx("Sin combinacion")} </p>
                          </div>
                          {showPrices ? (
                            <span style={styles.linkedChoicePrice}>{tx("Sin adicional")}</span>
                          ) : null}
                        </button>
                        ) : null}

                        {group.options.map((option) => {
                          const isActive =
                            normalizeEntityId(draftLinkedSelections[group.groupKey]) ===
                            option.id;
                          const optionStockState = getLinkedOptionStockState(option);
                          const isOptionOutOfStock =
                            optionStockState.available === false;
                          return (
                            <button
                              key={`${group.groupKey}:${option.id}`}
                              type="button"
                              style={{
                                ...styles.linkedChoiceButton,
                                ...(isActive ? styles.linkedChoiceButtonActive : {}),
                                ...(isOptionOutOfStock && !isActive
                                  ? {
                                      borderColor: "#fca5a5",
                                      backgroundColor: "#fff5f5",
                                      color: "#b91c1c",
                                      cursor: "not-allowed",
                                      opacity: 0.68,
                                    }
                                  : {}),
                              }}
                              onClick={() => {
                                if (isOptionOutOfStock && !isActive) {
                                  return;
                                }

                                updateLinkedSelection(group.groupKey, option.id);
                              }}
                              aria-pressed={isActive}
                              disabled={isOptionOutOfStock && !isActive}
                            >
                              <div>
                                <strong style={styles.linkedChoiceProductName}>
                                  {tx(option.name)}
                                </strong>
                                {option.description ? (
                                  <p style={styles.linkedChoiceProductDescription}>
                                    {tx(option.description)}
                                  </p>
                                ) : null}
                                {isOptionOutOfStock && optionStockState.reason ? (
                                  <p
                                    style={{
                                      ...styles.linkedChoiceProductDescription,
                                      color: "#b91c1c",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {optionStockState.reason}
                                  </p>
                                ) : null}
                              </div>
                              {showPrices ? (
                                <span style={styles.linkedChoicePrice}>
                                  {option.targetIngredientId ? tx("Incluido") : currencyFormatter.format(resolveDisplayPrice(option))}
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>

                      {group.options.length === 0 ? (
                        <div style={styles.emptyDetailBox}>
                          {tx("No hay otras opciones disponibles en #{name}.", { name: tx(group.groupLabel) })}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {incompleteBeverageUnit >= 0 ? <p role="status" style={styles.linkedChoiceText}>
                {tx("Elegi la variedad del producto #{number} para agregarlo al pedido.", { number: incompleteBeverageUnit + 1 })}
              </p> : null}
              <div style={styles.modalActions}>
                <div style={styles.orderInlineTools}>
                  <div style={styles.orderQuantityWrap}>
                    <span style={styles.orderQuantityLabel}>{tx("Cantidad")}</span>
                    {lockedProductQuantity > 0 ? (
                      <div style={{ display: "grid", gap: "4px", justifyItems: "start" }}>
                        <span style={styles.quantityValue}>{draftProductQuantity}</span>
                        <span style={styles.orderAmountLabel}>{tx("Fija por combo")}</span>
                      </div>
                    ) : (
                      <div style={styles.orderQuantityControls}>
                        <button
                          type="button"
                          aria-label={tx("Disminuir cantidad de productos")}
                          style={styles.quantityButton}
                          onClick={() => updateProductQuantity(-1)}
                          disabled={draftProductQuantity <= 1}
                        >
                          -
                        </button>
                        <span style={styles.quantityValue}>{draftProductQuantity}</span>
                        <button
                          type="button"
                          aria-label={tx("Aumentar cantidad de productos")}
                          style={styles.quantityButton}
                          onClick={() => updateProductQuantity(1)}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>

                  {showPrices ? (
                    <div style={styles.orderAmountWrap} aria-label={tx("Total de la seleccion")}>
                      <span style={styles.orderAmountLabel}>
                        {selectedDraftCompletesCombo ? tx("Total del combo") : isComboProductDetail ? tx("Subtotal de estos productos") : tx("Total del plato")}
                      </span>
                      {selectedProductsPricing.totalDiscount > 0 ? (
                        <span style={styles.orderAmountLabel}> {tx("Precio de lista:")} <s>{currencyFormatter.format(selectedProductsPricing.listTotal)}</s>
                        </span>
                      ) : null}
                      <strong style={{ ...styles.orderAmountValue, color: primaryColor }}>
                        {currencyFormatter.format(
                          selectedProductsTotal
                        )}
                      </strong>
                      {selectedProductsPricing.adjustments.map((adjustment) => (
                        <span key={adjustment.id} style={styles.orderAmountLabel}> {tx("Promo aplicada automaticamente:")} {tx(adjustment.summary)}
                        </span>
                      ))}
                      {isComboProductDetail && !selectedDraftCompletesCombo ? (
                        <span style={styles.orderAmountLabel}>{tx("El descuento se aplica al completar todos los productos del combo.")}</span>
                      ) : null}
                      {selectedProductsPricing.paymentPrices ? (
                        <span style={styles.orderAmountLabel}>{tx(promotionPaymentNotice(selectedProductsPricing, (value) => currencyFormatter.format(value)))}</span>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <button type="button" style={styles.secondaryAction} onClick={closeProductDetail}> {tx("Cerrar")} </button>
                {selectedProduct.ingredients?.length > 0 ? (
                  <>
                    <button
                      type="button"
                      style={styles.secondaryAction}
                      onClick={resetCustomization}
                    > {tx("Volver a normal")} </button>
                  </>
                ) : null}
                <button
                  type="button"
                  style={{
                    ...styles.primaryAction,
                    backgroundColor: primaryColor,
                    color: secondaryColor,
                    ...(incompleteBeverageUnit >= 0 || isSelectedPizzaHalfAndHalfIncomplete ||
                    isSelectedPizzaConfigurationIncomplete ||
                    isSelectedEmpanadaConfigurationIncomplete
                      ? { opacity: 0.55, cursor: "not-allowed" }
                      : {}),
                  }}
                  onClick={addSelectedProductToCart}
                  disabled={
                    incompleteBeverageUnit >= 0 || isSelectedPizzaHalfAndHalfIncomplete ||
                    isSelectedPizzaConfigurationIncomplete ||
                    isSelectedEmpanadaConfigurationIncomplete
                  }
                >
                  {editingCartItemId
                    ? tx("Actualizar en pedido")
                    : draftPizzaPortionMode === "half_and_half" && selectedPizzaHalfSelection
                      ? tx("Agregar mitad y mitad")
                      : draftPizzaPortionMode === "half"
                        ? tx("Agregar media pizza")
                      : tx("Agregar al pedido")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(isKioskExperience || cartItemCount > 0) && (
        <div className="mozzo-qr-cart-bar" style={styles.cartBarWrap}>
          <button
            type="button"
            style={{
              ...styles.cartBarButton,
              ...(isKioskExperience ? styles.kioskCartBarButton : {}),
              backgroundColor: primaryColor,
              color: secondaryColor,
            }}
            onClick={() => {
              setCartModalStep("review");
              setIsCartOpen(true);
            }}
          >
            <div style={styles.cartBarInfo}>
              <strong style={styles.cartBarTitle}>
                {isKioskExperience ? t("viewOrder") : tx("Mi pedido")}
              </strong>
              <span style={styles.cartBarMeta}>
                {isKioskExperience
                  ? `${kioskServiceTypeLabel} \u00b7 ${cartItemCount} ${
                      cartItemCount === 1 ? "item" : "items"
                    }`
                  : `${cartItemCount} ${
                      tx(cartItemCount === 1 ? "producto" : "productos")
                    }`}
              </span>
            </div>
            <strong style={styles.cartBarAmount}>
              {currencyFormatter.format(cartTotalAmount)}
            </strong>
          </button>
        </div>
      )}

      {!isKioskExperience && (
        <div
          className="mozzo-qr-assistant-dock"
          style={{
            ...styles.assistantDock,
            bottom: cartItemCount > 0 ? 96 : 22,
          }}
        >
          {isWaiterFeatureEnabled ? (
            <button
              type="button"
              style={styles.assistantFab}
              onClick={handleCallWaiter}
              aria-label={tx("Llamar mozo")}
              title={tx("Llamar mozo")}
            >
              <BellDeskIcon color="#0f172a" />
              <span style={styles.assistantFabLabel}>{tx("Mozo")}</span>
            </button>
          ) : null}

          {isAiFeatureEnabled && (!isAiAssistantOpen || !isAiAssistantPanelVisible) && (
            <button
              type="button"
              style={styles.assistantFab}
              onClick={
                isAiAssistantOpen
                  ? handleAiAssistantFloatingButtonClick
                  : handleAiAssistant
              }
              aria-label={tx("Abrir chatbot")}
              title={tx("Abrir chatbot")}
            >
              <RobotAssistantIcon color="#0f172a" />
              <span style={styles.assistantFabLabel}>
                {isAiAssistantOpen ? tx("Volver al chatbot") : "Chatbot"}
              </span>
            </button>
          )}
        </div>
      )}

      {!isKioskExperience && isAiFeatureEnabled && isAiAssistantOpen && (
        <>
          {shouldShowAiAssistantFloatingPanel && (
            <>
              <div
                style={styles.aiAssistantFloatingPanelBackdrop}
                onClick={collapseAiAssistantPanel}
                aria-hidden="true"
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-label={tx("Chatbot del menu")}
                style={{
                  ...styles.aiAssistantFloatingPanel,
                  bottom: cartItemCount > 0 ? 118 : 34,
                }}
                onClick={(event) => event.stopPropagation()}
              >
              <div style={styles.aiAssistantFloatingPanelHeader}>
                <div style={styles.aiAssistantFloatingPanelCopy}>
                  <span style={styles.aiAssistantFloatingPanelEyebrow}> {tx("Chatbot de Mozzo")} </span>
                  <strong style={styles.aiAssistantFloatingPanelTitle}> {tx("Te ayudo a elegir y armar tu pedido")} </strong>
                </div>
                <button
                  type="button"
                  style={styles.aiAssistantFloatingPanelHideButton}
                  onClick={collapseAiAssistantPanel}
                  aria-label={tx("Volver al menu")}
                  title={tx("Cerrar el chatbot y volver al menu")}
                > {tx("Volver al menu")} </button>
              </div>

              <div style={styles.aiAssistantFloatingPanelStatus}> {tx("Escribe con libertad. Puedes pedir una categoria, elegir un plato o indicar cambios como \"sin tomate\".")} </div>

              <div ref={aiAssistantThreadRef} style={styles.aiAssistantThread}>
                {aiConversation.map((message) => (
                  <article
                    key={message.id}
                    ref={
                      message.id === latestAiAssistantMessageId
                        ? aiAssistantLatestReplyRef
                        : null
                    }
                    style={{
                      ...styles.aiAssistantBubble,
                      ...(message.role === "assistant"
                        ? styles.aiAssistantBubbleAssistant
                        : styles.aiAssistantBubbleUser),
                    }}
                  >
                    <div style={styles.aiAssistantBubbleMeta}>
                      <span>
                        {message.role === "assistant" ? "Chatbot" : tx("Vos")}
                      </span>
                    </div>
                    <p style={styles.aiAssistantBubbleText}>{message.text}</p>

                    {message.role === "assistant" &&
                    Array.isArray(message.relatedProducts) &&
                    message.relatedProducts.length > 0 ? (
                      <div style={styles.aiAssistantProductGrid}>
                        {message.relatedProducts.map((relatedProduct) => {
                          const relatedProductId = String(
                            relatedProduct.productId || relatedProduct.id || ""
                          ).trim();
                          const sourceProduct =
                            aiAssistantProductLookup.get(relatedProductId) ||
                            aiAssistantProductLookup.get(
                              normalizeAssistantProductKey(relatedProduct.name)
                            ) ||
                            null;
                          if (!sourceProduct) return null;
                          const imageUrl = sanitizeUiImageUrl(
                            sourceProduct?.imageUrl || relatedProduct.imageUrl || "",
                            MAX_KIOSK_PRODUCT_DATA_IMAGE_LENGTH
                          );
                          const price = Number.isFinite(Number(relatedProduct.price))
                            ? Number(relatedProduct.price)
                            : resolveDisplayPrice(sourceProduct);

                          return (
                            <button
                              key={`${message.id}-${relatedProductId || relatedProduct.name}`}
                              type="button"
                              style={styles.aiAssistantProductCard}
                              onClick={() =>
                                handleChatbotProductSelection(sourceProduct, relatedProduct)
                              }
                              disabled={!sourceProduct}
                            >
                              <div style={styles.aiAssistantProductImageWrap}>
                                  <CatalogImage
                                    src={imageUrl}
                                    alt={tx(relatedProduct.name)}
                                    style={styles.aiAssistantProductImage}
                                  />
                              </div>
                              <div style={styles.aiAssistantProductInfo}>
                                <strong style={styles.aiAssistantProductName}>
                                  {tx(relatedProduct.name)}
                                </strong>
                                {relatedProduct.description ? (
                                  <span style={styles.aiAssistantProductDescription}>
                                    {tx(relatedProduct.description)}
                                  </span>
                                ) : null}
                                {showPrices && price > 0 ? (
                                  <span style={styles.aiAssistantProductPrice}>
                                    {currencyFormatter.format(price)}
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    {message.role === "assistant" &&
                    Array.isArray(message.suggestions) &&
                    message.suggestions.length > 0 ? (
                      <div style={styles.aiAssistantBubbleActions}>
                        {message.suggestions.slice(0, 6).map((suggestion) => (
                          <button
                            key={`${message.id}-${suggestion}`}
                            type="button"
                            style={styles.aiAssistantBubbleActionButton}
                            onClick={() => void submitAiAssistantQuestion(suggestion)}
                            disabled={isAiAssistantLoading}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </article>
                ))}

                {isAiAssistantLoading ? (
                  <article
                    style={{
                      ...styles.aiAssistantBubble,
                      ...styles.aiAssistantBubbleAssistant,
                    }}
                  >
                    <p style={styles.aiAssistantBubbleText}>{tx("Buscando las mejores opciones de la carta...")}</p>
                  </article>
                ) : null}
              </div>

              <form
                style={styles.aiAssistantComposer}
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitAiAssistantQuestion();
                }}
              >
                <div style={styles.aiAssistantInputWrap}>
                  <label htmlFor="mozzo-chatbot-question" style={styles.aiAssistantInputLabel}> {tx("Escribe tu consulta")} </label>
                  <textarea
                    id="mozzo-chatbot-question"
                    style={styles.aiAssistantTextarea}
                    value={aiQuestionDraft}
                    onChange={(event) => setAiQuestionDraft(event.target.value)}
                    placeholder={tx("Ejemplo: quiero una milanesa, sin tomate")}
                    maxLength={320}
                    disabled={isAiAssistantLoading}
                  />
                </div>

                {aiConversation.length === 0 && aiAssistantQuickSuggestions.length > 0 ? (
                  <div style={styles.aiAssistantSuggestionRow}>
                    {aiAssistantQuickSuggestions.slice(0, 4).map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        style={styles.aiAssistantSuggestionButton}
                        onClick={() => void submitAiAssistantQuestion(suggestion)}
                        disabled={isAiAssistantLoading}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ) : null}

                <div style={styles.aiAssistantFooter}>
                  <span style={styles.aiAssistantFooterText}> {tx("El chatbot te muestra platos reales de esta carta y no agrega nada sin tu confirmacion.")} </span>
                  <button
                    type="submit"
                    style={{ ...styles.primaryAction, ...styles.aiAssistantSendButton }}
                    disabled={isAiAssistantLoading || !aiQuestionDraft.trim()}
                  >
                    {isAiAssistantLoading ? tx("Buscando...") : tx("Enviar")}
                  </button>
                </div>
              </form>

              {false && (
                <div
                  ref={aiAssistantFloatingResultsRef}
                  style={styles.aiAssistantFloatingResultsScroller}
                >
                  {isAiAssistantLoading
                    ? Array.from({ length: 5 }).map((_, index) => (
                        <div
                          key={`ai-floating-skeleton-${index + 1}`}
                          style={{
                            ...styles.aiAssistantFloatingProductCard,
                            ...styles.aiAssistantProductCardSkeleton,
                          }}
                        >
                          <div
                            style={{
                              ...styles.aiAssistantFloatingProductImageWrap,
                              ...styles.aiAssistantProductImageSkeleton,
                            }}
                          />
                        </div>
                      ))
                    : aiAssistantVisibleProducts.map((relatedProduct) => {
                        const relatedProductId = String(
                          relatedProduct.productId || ""
                        ).trim();
                        const sourceProduct =
                          aiAssistantProductLookup.get(relatedProductId) ||
                          aiAssistantProductLookup.get(
                            normalizeAssistantProductKey(relatedProduct.name)
                          ) ||
                          null;
                        if (!sourceProduct) return null;
                        const relatedProductImageUrl = sanitizeUiImageUrl(
                          sourceProduct?.imageUrl || relatedProduct.imageUrl || "",
                          MAX_KIOSK_PRODUCT_DATA_IMAGE_LENGTH
                        );
                        const relatedProductPrice =
                          Number.isFinite(Number(relatedProduct.price))
                            ? Number(relatedProduct.price)
                            : resolveDisplayPrice(sourceProduct);

                        return (
                          <button
                            key={`${relatedProduct.productId || relatedProduct.name}-${relatedProductPrice}`}
                            type="button"
                            data-ai-related-product-id={
                              normalizeEntityId(
                                relatedProduct.productId || relatedProduct.id
                              ) || undefined
                            }
                            style={styles.aiAssistantFloatingProductCard}
                            onClick={() => {
                              if (sourceProduct) {
                                handleChatbotProductSelection(sourceProduct);
                              }
                            }}
                            disabled={!sourceProduct}
                          >
                            <div style={styles.aiAssistantFloatingProductImageWrap}>
                                <CatalogImage
                                  src={relatedProductImageUrl}
                                  alt={tx(relatedProduct.name)}
                                  style={styles.aiAssistantFloatingProductImage}
                                />
                            </div>
                            <div style={styles.aiAssistantFloatingProductInfo}>
                              <strong style={styles.aiAssistantFloatingProductName}>
                                {tx(relatedProduct.name)}
                              </strong>
                              {showPrices && relatedProductPrice > 0 ? (
                                <span style={styles.aiAssistantFloatingProductPrice}>
                                  {currencyFormatter.format(relatedProductPrice)}
                                </span>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                </div>
              )}
              </div>
            </>
          )}

        </>
      )}

      {!isKioskExperience && isWaiterHelpOpen && (
        <div style={styles.modalOverlay} onClick={closeWaiterHelp}>
          <div style={styles.waiterHelpModalCard} onClick={(event) => event.stopPropagation()}>
            <div style={styles.cartHeader}>
              <div>
                <h3 style={styles.cartTitle}>{tx("Llamar al mozo")}</h3>
                <p style={styles.cartSubtitle}>
                  {tx(table.label || `Mesa ${table.tableNumber || ""}`)}{" \u00b7 "}{branch.name || "Sucursal"}
                </p>
              </div>
              <button
                type="button"
                style={styles.secondaryAction}
                onClick={closeWaiterHelp}
              > {tx("Cerrar")} </button>
            </div>

            {waiterOptionsLoading && <p role="status">{tx("Actualizando opciones del mozo...")}</p>}
            {waiterOptionsNotice && <p role="status">{waiterOptionsNotice}</p>}
            {visibleWaiterOptions.length > 0 ? (
              <div style={styles.waiterOptionGrid}>
                {visibleWaiterOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    style={{
                      ...styles.waiterOptionChip,
                      ...(selectedWaiterOptions.includes(option.id)
                        ? styles.waiterOptionChipActive
                        : {}),
                    }}
                    onClick={() => toggleWaiterOption(option.id)}
                    disabled={waiterOptionsLoading}
                  >
                    {tx(option.label)}
                  </button>
                ))}
              </div>
            ) : null}

            {waiterAssistanceConfig?.allowCustomText ? (
              <label style={styles.waiterTextareaWrap}>
                <span style={styles.waiterTextareaLabel}>
                  {waiterAssistanceConfig.customTextLabel || tx("Aclaracion adicional")}
                </span>
                <textarea
                  value={waiterCustomText}
                  onChange={(event) => setWaiterCustomText(event.target.value)}
                  placeholder={
                    waiterAssistanceConfig.customTextPlaceholder ||
                    tx("Escribe aqui tu pedido")
                  }
                  style={styles.waiterTextarea}
                  rows={4}
                />
              </label>
            ) : null}

            {!visibleWaiterOptions.length &&
            waiterAssistanceConfig?.allowCustomText !== true ? (
              <div style={styles.emptyDetailBox}> {tx("Esta sucursal todavia no cargo opciones para el boton mozo.")} </div>
            ) : null}

            <div style={styles.waiterActionBox}>
              <div style={styles.waiterActionCopy}>
                <strong style={styles.waiterActionTitle}>{tx("Llamar al mozo ahora")}</strong>
                <span style={styles.waiterActionText}> {tx("Puedes marcar una utilidad o escribir un detalle, pero tambien puedes avisar al mozo directamente desde aqui.")} </span>
              </div>
              <button
                type="button"
                style={{
                  ...styles.primaryAction,
                  ...styles.waiterActionButton,
                  backgroundColor: primaryColor,
                  color: secondaryColor,
                }}
                onClick={submitWaiterHelp}
                disabled={isSubmittingWaiterHelp || waiterOptionsLoading}
              >
                {isSubmittingWaiterHelp ? tx("Avisando...") : tx("Llamar al mozo")}
              </button>
            </div>

            <div style={styles.cartFooter}>
              <button
                type="button"
                style={styles.secondaryAction}
                onClick={closeWaiterHelp}
              > {tx("Cancelar")} </button>
            </div>
          </div>
        </div>
      )}

      {isCartOpen && !isPendingAccountsPaymentOpen && (
        <div
          style={styles.modalOverlay}
          onClick={() => {
            if (isKioskQrPaymentPending && !canRecoverSubmittedKioskPendingPayment) {
              return;
            }

            if (isKioskQrPaymentPending && canRecoverSubmittedKioskPendingPayment) {
              recoverSubmittedKioskPendingPayment();
              return;
            }

            closeCartModal();
          }}
        >
          <div style={styles.cartModalCard} onClick={(event) => event.stopPropagation()}>
            <div style={styles.cartHeader}>
              <div style={showCartCloseIcon ? styles.cartHeadingContent : undefined}>
                <h3 style={styles.cartTitle}>
                  {isCartPaymentView ? t("payOrderTitle") : t("myOrder")}
                </h3>
                <p style={styles.cartSubtitle}>
                  {tx(table.label || `Mesa ${table.tableNumber || ""}`)}{" \u00b7 "}
                  {branch.name || "Sucursal activa"}
                </p>
              </div>
              <button
                type="button"
                aria-label={showCartCloseIcon ? t("close") : undefined}
                title={showCartCloseIcon ? t("close") : undefined}
                style={{
                  ...styles.secondaryAction,
                  ...(showCartCloseIcon ? styles.cartCloseButton : {}),
                }}
                onClick={() => {
                  if (isCartCheckoutStep) {
                    setCheckoutMessage({ type: "", text: "" });
                    setCartModalStep("review");
                    return;
                  }

                  if (isKioskQrPaymentPending && !canRecoverSubmittedKioskPendingPayment) {
                    return;
                  }

                  if (isKioskQrPaymentPending && canRecoverSubmittedKioskPendingPayment) {
                    recoverSubmittedKioskPendingPayment();
                    return;
                  }

                  closeCartModal();
                }}
                disabled={
                  isKioskQrPaymentPending && !canRecoverSubmittedKioskPendingPayment
                }
              >
                {showCartCloseIcon ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : isCartCheckoutStep
                  ? t("backToOrder")
                  : isKioskQrPaymentPending
                    ? canRecoverSubmittedKioskPendingPayment
                      ? t("cancelPendingKioskPayment")
                      : tx("Esperando pago")
                    : t("continueBrowsing")}
              </button>
            </div>

            {shouldShowCartReviewList ? (
            <div style={styles.cartList}>
              {reviewUsesSubmittedOrder ? (
                <div style={styles.reviewOnlyNotice}>
                  {submittedOrderEditLock.locked
                    ? submittedOrderEditLock.reason
                    : tx("Este es el ultimo pedido enviado. Si queres cambiar algo, primero volvelo a cargar y despues lo ajustas desde aca.")}
                </div>
              ) : null}

              {reviewOrderItems.map((item) => (
                <article key={item.id} style={styles.cartItemCard}>
                  <div style={styles.cartItemMain}>
                      <CatalogImage
                        src={item.imageUrl}
                        alt={tx(item.productName)}
                        style={styles.cartItemImage}
                        onClick={() => openCartItemDetail(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openCartItemDetail(item);
                          }
                        }}
                      />

                    <div style={styles.cartItemInfo}>
                      <div style={styles.cartItemHead}>
                        <div>
                          <h4 style={styles.cartItemName}>{tx(item.productName)}</h4>
                          {item.selectionDetails?.length > 0 ? (
                            <div style={styles.cartItemDetails}>
                              {item.selectionDetails.map((detail) => (
                                <span key={`${item.id}-${detail}`} style={styles.cartItemDetailChip}>
                                  {tx(detail)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={styles.cartItemBaseLabel}>
                              {t("noChanges")}
                            </span>
                          )}
                        </div>

                        <strong style={styles.cartItemSubtotal}>
                          {currencyFormatter.format(
                            Number(item.quantity || 0) * Number(item.unitPrice || 0)
                          )}
                        </strong>
                      </div>

                      <div style={styles.cartItemFooter}>
                        <span style={styles.cartItemUnitPrice}>
                          {currencyFormatter.format(Number(item.unitPrice || 0))} {tx("c/u")} </span>

                        <div style={styles.cartItemActions}>
                          {reviewUsesSubmittedOrder ? (
                            <span style={styles.cartItemBaseLabel}>
                              {submittedOrderSnapshot?.awaitingPaymentApproval
                                ? tx("Esperando pago")
                                : tx("Pedido enviado")}
                            </span>
                          ) : (
                            <>
                              <div style={styles.orderQuantityControls}>
                                <button
                                  type="button"
                                  style={styles.quantityButton}
                                  onClick={() => updateCartItemQuantity(item.id, -1)}
                                >
                                  -
                                </button>
                                <span style={styles.quantityValue}>{item.quantity}</span>
                                <button
                                  type="button"
                                  style={styles.quantityButton}
                                  onClick={() => updateCartItemQuantity(item.id, 1)}
                                >
                                  +
                                </button>
                              </div>

                              <button
                                type="button"
                                style={styles.removeItemButton}
                                onClick={() => removeCartItem(item.id)}
                              > {tx("Quitar")} </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            ) : null}

            <div
              style={{
                ...styles.cartFooter,
                ...(!reviewUsesSubmittedOrder || isSubmittedDigitalPaymentView
                  ? styles.cartModalFooter
                  : {}),
              }}
            >
              {!isSubmittedDigitalPaymentView ? (
              <div style={styles.cartTotalsBox}>
                <span style={styles.cartTotalsLabel}>
                  {reviewItemCount} {t(reviewItemCount === 1 ? "product" : "products")}
                </span>
                {Number(reviewPricing.totalDiscount || 0) > 0 ? (
                  <div style={styles.cartPricingSummary}>
                    <div style={styles.cartPricingRow}>
                      <span>{t("listPrice")}</span>
                      <strong>{currencyFormatter.format(reviewListAmount)}</strong>
                    </div>
                    <div style={styles.cartPricingRow}>
                      <span>
                        {t("appliedDiscounts")}
                      </span>
                      <strong>-{currencyFormatter.format(reviewPricing.totalDiscount)}</strong>
                    </div>
                    <div style={styles.cartAdjustmentList}>
                      {(reviewPricing.adjustments || []).map((adjustment) => (
                        <div key={adjustment.id} style={styles.cartAdjustmentItem}>
                          <span>
                            {adjustment.type === "combo"
                              ? t("comboApplied")
                              : t("promotionApplied")}
                          </span>
                          <strong>{tx(adjustment.summary)}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <strong style={styles.cartTotalsValue}>
                  {currencyFormatter.format(reviewTotalAmount)}
                </strong>
              </div>
              ) : null}

              {isCartCheckoutStep && checkoutMessage.type === "error" && checkoutMessage.text ? (
                <div
                  style={{
                    ...styles.checkoutNotice,
                    ...styles.checkoutNoticeError,
                    marginBottom: 0,
                  }}
                >
                  {checkoutMessage.text}
                </div>
              ) : null}

              {isSubmittedKioskQrPaymentView ? (
                <div
                  style={{
                    ...styles.kioskQrPaymentPanel,
                    ...styles.paymentOnlyQrPanel,
                  }}
                >
                  <div style={styles.kioskQrPaymentHeader}>
                    {submittedKioskUsesCheckoutLinkQr ? (
                      <div style={styles.kioskQrPaymentAlert}>
                        <strong style={styles.kioskQrPaymentAlertTitle}>
                          {t("scanToOpenLinkTitle")}
                        </strong>
                        <span style={styles.kioskQrPaymentAlertText}>
                          {t("checkoutCameraOnlyBadge")}
                        </span>
                      </div>
                    ) : (
                      <>
                        <strong style={styles.kioskQrPaymentTitle}>
                          {t("scanToPayTitle")}
                        </strong>
                        <span style={styles.kioskQrPaymentText}>
                          {t("scanToPayBody")}
                        </span>
                      </>
                    )}
                  </div>

                  {submittedKioskUsesNativePaymentQr ||
                  submittedKioskDigitalPaymentIntent?.qr?.deeplinkUrl ? (
                    <div style={styles.kioskQrPaymentBody}>
                      <div style={styles.kioskQrCanvasWrap}>
                        {submittedKioskDigitalPaymentIntent?.qr?.imageUrl &&
                        !submittedKioskUsesCheckoutLinkQr ? (
                          <img
                            src={submittedKioskDigitalPaymentIntent.qr.imageUrl}
                            alt={t("scanToPayTitle")}
                            style={styles.kioskQrNativeImage}
                          />
                        ) : submittedKioskDigitalPaymentIntent?.qr?.qrData &&
                          !submittedKioskUsesCheckoutLinkQr ? (
                          <QRCode
                            value={submittedKioskDigitalPaymentIntent.qr.qrData}
                            size={198}
                            bgColor="#ffffff"
                            fgColor="#0f172a"
                          />
                        ) : (
                          <QRCode
                            value={
                              submittedKioskPaymentLandingUrl ||
                              submittedKioskDigitalPaymentIntent.qr.deeplinkUrl
                            }
                            size={198}
                            bgColor="#ffffff"
                            fgColor="#0f172a"
                          />
                        )}
                      </div>

                      <div style={styles.kioskQrPaymentMeta}>
                        <strong style={styles.kioskQrPaymentAmount}>
                          {currencyFormatter.format(
                            Number(submittedOrderSnapshot?.pricing?.finalTotal || 0)
                          )}
                        </strong>
                        <span style={styles.kioskQrPaymentText}>
                          {branch?.name || "Sucursal activa"}
                        </span>
                        {submittedKioskUsesCheckoutLinkQr ? (
                          <span style={styles.kioskQrPaymentInstruction}>
                            {t("checkoutLinkHint")}
                          </span>
                        ) : null}
                        {submittedOrderSnapshot?.paymentIntentError ? (
                          <span
                            style={{
                              ...styles.kioskQrPaymentText,
                              color: "#b42318",
                              maxWidth: 360,
                            }}
                          >
                            {submittedOrderSnapshot.paymentIntentError}
                          </span>
                        ) : null}
                        <div style={styles.kioskQrPaymentActions}>
                          <button
                            type="button"
                            style={styles.secondaryAction}
                            onClick={retrySubmittedDigitalPayment}
                            disabled={isSubmittingOrder}
                          >
                            {t("regeneratePaymentQr")}
                          </button>
                          {canRecoverSubmittedKioskPendingPayment ? (
                            <button
                              type="button"
                              style={{
                                ...styles.secondaryAction,
                                borderColor: "#f04438",
                                color: "#b42318",
                              }}
                              onClick={recoverSubmittedKioskPendingPayment}
                              disabled={isSubmittingOrder}
                            >
                              {t("cancelPendingKioskPayment")}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.kioskQrPaymentFallback}>
                      <span style={styles.kioskQrPaymentText}>
                        {submittedOrderSnapshot?.paymentIntentError ||
                          t("paymentQrRetry")}
                      </span>
                      <button
                        type="button"
                        style={styles.secondaryAction}
                        onClick={retrySubmittedDigitalPayment}
                        disabled={isSubmittingOrder}
                      >
                        {t("regeneratePaymentQr")}
                      </button>
                      {canRecoverSubmittedKioskPendingPayment ? (
                        <button
                          type="button"
                          style={{
                            ...styles.secondaryAction,
                            borderColor: "#f04438",
                            color: "#b42318",
                          }}
                          onClick={recoverSubmittedKioskPendingPayment}
                          disabled={isSubmittingOrder}
                        >
                          {t("cancelPendingKioskPayment")}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : null}

              {isSubmittedMenuDigitalPaymentView ? (
                <div
                  style={{
                    ...styles.kioskQrPaymentPanel,
                    ...styles.paymentOnlyQrPanel,
                  }}
                >
                  <div style={styles.kioskQrPaymentHeader}>
                    <strong style={styles.kioskQrPaymentTitle}>
                      {submittedOrderSnapshot?.awaitingPaymentApproval
                        ? t("openCheckoutTitle")
                        : tx("Pago confirmado")}
                    </strong>
                    <span style={styles.kioskQrPaymentText}>
                      {submittedOrderSnapshot?.awaitingPaymentApproval
                        ? t("openCheckoutBody")
                        : submittedOrderSnapshot?.paymentContext ===
                            "table_pending_accounts"
                          ? `Se pagaron ${
                              submittedOrderSnapshot?.pendingAccountOrderIds?.length || 0
                            } cuenta(s) de la mesa.`
                          : "El pedido ya fue enviado a la sucursal como pagado."}
                    </span>
                  </div>

                  {!submittedOrderSnapshot?.awaitingPaymentApproval ? (
                    <div style={styles.kioskQrPaymentBody}>
                      <div style={styles.kioskQrLinkCard}>
                        <strong style={styles.kioskQrPaymentAmount}>
                          {currencyFormatter.format(
                            Number(submittedOrderSnapshot?.pricing?.finalTotal || 0)
                          )}
                        </strong>
                        <span style={styles.kioskQrPaymentText}>
                          {submittedMenuWalletLabel}
                        </span>
                        <span style={styles.kioskQrPaymentText}>{tx("Pagado")}</span>
                      </div>
                    </div>
                  ) : submittedMenuLaunchUrl ? (
                    <div style={styles.kioskQrPaymentBody}>
                      <div style={styles.kioskQrLinkCard}>
                        <strong style={styles.kioskQrPaymentAmount}>
                          {currencyFormatter.format(
                            Number(submittedOrderSnapshot?.pricing?.finalTotal || 0)
                          )}
                        </strong>
                        <span style={styles.kioskQrPaymentText}>
                          {branch?.name || "Sucursal activa"}
                        </span>
                        <span style={styles.kioskQrPaymentText}>
                          {submittedOperationalOrder?.mesa ||
                            table?.label ||
                            "Mesa activa"}
                        </span>
                        <span style={styles.kioskQrPaymentText}>
                          {submittedMenuWalletLabel}
                        </span>
                      </div>

                      <div style={styles.kioskQrPaymentMeta}>
                        <span style={styles.kioskQrPaymentText}>
                          {submittedOrderSnapshot?.awaitingPaymentApproval
                            ? `Intentamos abrir ${submittedMenuWalletLabel} automaticamente. Si no se abrio, continua el pago desde aqui.`
                            : t("paymentLinkReady")}
                        </span>
                        <div style={styles.kioskQrPaymentActions}>
                          <button
                            type="button"
                            style={styles.secondaryAction}
                            onClick={continueSubmittedMenuPayment}
                          >
                            {`${t("openPaymentLink")} ${submittedMenuWalletLabel}`}
                          </button>
                          <button
                            type="button"
                            style={styles.secondaryAction}
                            onClick={retrySubmittedDigitalPayment}
                            disabled={isSubmittingOrder}
                          >
                            {t("regeneratePaymentQr")}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.kioskQrPaymentFallback}>
                      <span style={styles.kioskQrPaymentText}>
                        {submittedOrderSnapshot?.paymentIntentError ||
                          t("paymentLinkRetry")}
                      </span>
                      <button
                        type="button"
                        style={styles.secondaryAction}
                        onClick={retrySubmittedDigitalPayment}
                        disabled={isSubmittingOrder}
                      >
                        {t("regeneratePaymentQr")}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}

              {isCartCheckoutStep ? <div style={styles.checkoutBox}>
                <div style={styles.checkoutBoxHeader}>
                  <span style={styles.checkoutLabel}>
                    {isKioskExperience ? t("howToPay") : tx("Forma de pago")}
                  </span>
                  <span style={styles.checkoutHint}>
                    {!isKioskExperience && checkoutMethod === "deep_link"
                      ? t("openCheckoutHint")
                      : isKioskExperience
                        ? t("kioskCounterHint")
                        : t("hallHandlesPayments")}
                  </span>
                </div>

                {isKioskExperience ? (
                  <div
                    style={{
                      ...styles.checkoutNameBox,
                      ...(!reviewCustomerName ? styles.checkoutNameBoxAttention : {}),
                    }}
                  >
                    <label htmlFor="kiosk-customer-name" style={styles.checkoutNameLabel}>
                      <span>{t("pickupName")}</span>
                      <span style={styles.checkoutNameRequired}>
                        {t("required")}
                      </span>
                    </label>
                    <input
                      ref={checkoutNameInputRef}
                      id="kiosk-customer-name"
                      type="text"
                      value={customerNameDraft}
                      readOnly={isKioskExperience}
                      inputMode={isKioskExperience ? "none" : "text"}
                      onChange={(event) => {
                        setCustomerNameDraft(event.target.value);
                        if (checkoutMessage.type === "error") {
                          setCheckoutMessage({ type: "", text: "" });
                        }
                      }}
                      onClick={handleKioskNameInputClick}
                      placeholder={t("pickupNamePlaceholder")}
                      autoComplete="name"
                      maxLength={60}
                      style={{
                        ...styles.checkoutNameInput,
                        ...(checkoutMessage.type === "error" && !reviewCustomerName
                          ? styles.checkoutNameInputError
                          : {}),
                      }}
                    />
                    <span style={styles.checkoutNameHint}>
                      {t("pickupNameHint")}
                    </span>
                  </div>
                ) : null}

                {cartPricing.paymentPrices ? (
                  <p role="status" style={{ color: "#172b3a", background: "#fff4ce", padding: 12, borderRadius: 6 }}>
                    {tx(promotionPaymentNotice(cartPricing, (value) => currencyFormatter.format(value)))}
                  </p>
                ) : null}
                <div style={styles.checkoutMethodRow}>
                  {Object.entries(checkoutMethodOptions).map(([methodId, label]) => {
                    return (
                      <button
                        key={methodId}
                        type="button"
                        onClick={() => setCheckoutMethod(methodId)}
                        style={{
                          ...styles.checkoutMethodButton,
                          ...(checkoutMethod === methodId
                            ? {
                                backgroundColor: primaryColor,
                                color: secondaryColor,
                                borderColor: primaryColor,
                              }
                            : {}),
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {!isKioskExperience && checkoutMethod === "deep_link" ? (
                  <div style={styles.menuWalletChooser}>
                    <div style={styles.menuWalletChooserHeader}>
                      <strong style={styles.menuWalletChooserTitle}>
                        {t("chooseDigitalWallet")}
                      </strong>
                      <span style={styles.menuWalletChooserHint}>
                        {t("chooseDigitalWalletHint")}
                      </span>
                    </div>

                    <div style={styles.menuWalletOptionList}>
                      {configuredMenuQrWalletProviders.map((provider) => {
                        const isAvailable =
                          compatibleMenuQrWalletProviderIds.has(provider.id);
                        const isSelected =
                          isAvailable &&
                          selectedMenuWalletProvider?.id === provider.id;

                        return (
                          <button
                            key={provider.id}
                            type="button"
                            onClick={() => {
                              if (!isAvailable) {
                                return;
                              }

                              setSelectedMenuWalletProviderId(provider.id);
                            }}
                            disabled={!isAvailable}
                            style={{
                              ...styles.menuWalletOptionButton,
                              ...(isSelected
                                ? {
                                    borderColor: provider.accentColor,
                                    backgroundColor: `${provider.accentColor}12`,
                                    color: provider.accentColor,
                                  }
                                : {}),
                              ...(!isAvailable
                                ? styles.menuWalletOptionButtonDisabled
                                : {}),
                            }}
                          >
                            <span
                              style={{
                                ...styles.menuWalletOptionDot,
                                backgroundColor: provider.accentColor,
                              }}
                            />
                            <span style={styles.menuWalletOptionContent}>
                              <span>{provider.label}</span>
                              {!isAvailable ? (
                                <span style={styles.menuWalletOptionMeta}>
                                  {t("walletSoon")}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {incompatibleMenuQrWalletProvidersCount > 0 ? (
                      <div
                        style={{
                          ...styles.checkoutNotice,
                          marginBottom: 0,
                          marginTop: 10,
                          padding: "10px 12px",
                          borderRadius: 12,
                          fontSize: 12,
                        }}
                      >
                        {t("menuWalletsIncompatible", {
                          provider: activeMenuQrWalletProviderLabel,
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {checkoutMethodBlockingNotice ? (
                  <div
                    style={{
                      ...styles.checkoutNotice,
                      ...styles.checkoutNoticeError,
                      marginBottom: 0,
                      padding: "10px 12px",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  >
                    {checkoutMethodBlockingNotice}
                  </div>
                ) : null}
              </div> : null}

              {reviewUsesSubmittedOrder &&
              checkoutMessage.type === "error" &&
              checkoutMessage.text ? (
                <div
                  style={{
                    ...styles.checkoutNotice,
                    ...styles.checkoutNoticeError,
                    marginBottom: 0,
                  }}
                >
                  {checkoutMessage.text}
                </div>
              ) : null}

              <div
                style={{
                  ...styles.cartFooterActions,
                  ...(isSubmittedDigitalPaymentView
                    ? styles.paymentOnlyFooterActions
                    : {}),
                }}
              >
                {reviewUsesSubmittedOrder ? (
                  <>
                    {canPaySubmittedOrderNow ? (
                      <button
                        type="button"
                        style={{
                          ...styles.primaryAction,
                          ...styles.submittedOrderPayButton,
                          backgroundColor: primaryColor,
                          color: secondaryColor,
                          opacity: isOpeningSubmittedOrderPayment ? 0.7 : 1,
                        }}
                        onClick={openSubmittedOrderPayment}
                        disabled={isOpeningSubmittedOrderPayment}
                      >
                        {isOpeningSubmittedOrderPayment
                          ? tx("Revisando cuenta...")
                          : tx("Pagar ahora")}
                      </button>
                    ) : null}
                    {canCancelSubmittedOrderNow ? (
                      <button
                        type="button"
                        style={{
                          ...styles.secondaryAction,
                          ...styles.submittedOrderCancelButton,
                          opacity: isCancellingSubmittedOrder ? 0.7 : 1,
                        }}
                        onClick={cancelSubmittedOrder}
                        disabled={isCancellingSubmittedOrder}
                      >
                        {isCancellingSubmittedOrder
                          ? tx("Cancelando...")
                          : tx("Cancelar pedido")}
                      </button>
                    ) : null}
                    {submittedOrderItems.length > 0 &&
                    !submittedOrderEditLock.locked &&
                    !isKioskQrPaymentPending &&
                    !submittedOrderSnapshot?.awaitingPaymentApproval ? (
                      <button
                        type="button"
                        style={styles.secondaryAction}
                        onClick={restoreSubmittedOrderToCart}
                      > {tx("Modificar pedido")} </button>
                    ) : null}
                    <button
                      type="button"
                      style={styles.secondaryAction}
                      onClick={() => {
                        if (isKioskQrPaymentPending && !canRecoverSubmittedKioskPendingPayment) {
                          return;
                        }

                        if (isKioskQrPaymentPending && canRecoverSubmittedKioskPendingPayment) {
                          recoverSubmittedKioskPendingPayment();
                          return;
                        }

                        if (isKioskExperience) {
                          resetKioskExperienceFlow();
                          return;
                        }

                        closeCartModal();
                      }}
                      disabled={
                        isKioskQrPaymentPending && !canRecoverSubmittedKioskPendingPayment
                      }
                    >
                      {isKioskQrPaymentPending
                        ? canRecoverSubmittedKioskPendingPayment
                          ? t("cancelPendingKioskPayment")
                          : tx("Esperando pago")
                        : t("close")}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" style={styles.secondaryAction} onClick={clearCart}>
                      {isEditingSubmittedOrder
                        ? tx("Descartar cambios")
                        : isKioskExperience
                          ? t("emptyOrder")
                          : tx("Vaciar pedido")}
                    </button>
                    <button
                      type="button"
                      style={styles.secondaryAction}
                      onClick={closeCartModal}
                    >
                      {isKioskExperience ? t("keepChoosing") : tx("Seguir eligiendo")}
                    </button>
                    {isCartCheckoutStep ? (
                      <button
                        type="button"
                        style={{
                          ...styles.primaryAction,
                          backgroundColor: primaryColor,
                          color: secondaryColor,
                          opacity: isSubmittingOrder ? 0.75 : 1,
                        }}
                        onClick={submitOrder}
                        disabled={isSubmittingOrder}
                      >
                        {isSubmittingOrder ? t("sending") : t("confirmOrder")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        style={{
                          ...styles.primaryAction,
                          ...styles.cartPayButton,
                          backgroundColor: primaryColor,
                          color: secondaryColor,
                          opacity: hasReviewableOrder ? 1 : 0.6,
                        }}
                        onClick={() => {
                          if (!hasReviewableOrder) {
                            return;
                          }

                          setCheckoutMessage({ type: "", text: "" });
                          setCartModalStep("checkout");
                        }}
                        disabled={!hasReviewableOrder}
                      >
                        {t("payOrder")}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {isKioskExperience && isKioskNameWarningOpen ? (
            <div
              style={styles.kioskNameWarningOverlay}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                style={styles.kioskNameWarningDialog}
                onClick={(event) => event.stopPropagation()}
              >
                <div style={styles.kioskNameWarningHeader}>
                  <span style={styles.kioskNameWarningEyebrow}>
                    {t("required")}
                  </span>
                  <h3 style={styles.kioskNameWarningTitle}>
                    {t("missingPickupNameTitle")}
                  </h3>
                  <p style={styles.kioskNameWarningText}>
                    {t("missingPickupNameBody")}
                  </p>
                </div>

                <button
                  type="button"
                  style={{
                    ...styles.kioskNameWarningButton,
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                    color: secondaryColor,
                  }}
                  onClick={() => {
                    setIsKioskNameWarningOpen(false);
                    openKioskKeyboard();
                  }}
                >
                  {t("missingPickupNameAction")}
                </button>
              </div>
            </div>
          ) : null}

          {isKioskExperience && isKioskKeyboardOpen ? (
            <div
              style={styles.kioskKeyboardOverlay}
              onClick={(event) => {
                event.stopPropagation();
                if (
                  kioskKeyboardOpeningRef.current ||
                  kioskKeyboardIgnoreNextOverlayClickRef.current
                ) {
                  kioskKeyboardIgnoreNextOverlayClickRef.current = false;
                  return;
                }
                closeKioskKeyboard();
              }}
            >
              <div
                style={styles.kioskKeyboardDialog}
                onClick={(event) => event.stopPropagation()}
              >
                <div style={styles.kioskKeyboardDialogHeader}>
                  <div style={styles.kioskKeyboardHeader}>
                    <span style={styles.kioskKeyboardTitle}>{t("keyboard")}</span>
                    <span style={styles.kioskKeyboardValue}>
                      {customerNameDraft || t("typePickupName")}
                    </span>
                  </div>
                  <button
                    type="button"
                    style={styles.kioskKeyboardCloseButton}
                    onClick={closeKioskKeyboard}
                  >
                    {t("close")}
                  </button>
                </div>

                <div style={styles.kioskKeyboardRows}>
                  {kioskKeyboardRows.map((row, rowIndex) => (
                    <div key={`kiosk-keyboard-row-${rowIndex}`} style={styles.kioskKeyboardRow}>
                      {row.map((key) => {
                        const isWideKey = key === "ESPACIO";
                        const isCommandKey =
                          key === "SHIFT" ||
                          key === "BORRAR" ||
                          key === "LIMPIAR" ||
                          key === "LISTO";

                        return (
                          <button
                            key={`${rowIndex}-${key}`}
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleKioskKeyboardKeyPress(key)}
                            translate="no"
                            lang={NON_TRANSLATABLE_LANGUAGE}
                            className="notranslate"
                            style={{
                              ...styles.kioskKeyboardKey,
                              ...(isWideKey ? styles.kioskKeyboardKeyWide : {}),
                              ...(isCommandKey ? styles.kioskKeyboardKeyCommand : {}),
                              ...(key === "LISTO"
                                ? {
                                    backgroundColor: primaryColor,
                                    borderColor: primaryColor,
                                    color: secondaryColor,
                                  }
                                : {}),
                            }}
                          >
                            <span
                              translate="no"
                              lang={NON_TRANSLATABLE_LANGUAGE}
                              className="notranslate"
                            >
                              {getKioskKeyboardKeyLabel(key)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {isKioskExperience && isKioskInactivityPromptOpen ? (
            <div
              style={styles.kioskInactivityOverlay}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                style={styles.kioskInactivityDialog}
                onClick={(event) => event.stopPropagation()}
              >
                <div style={styles.kioskInactivityHeader}>
                  <span style={styles.kioskInactivityEyebrow}>MozzoApp Kiosk</span>
                  <h3 style={styles.kioskInactivityTitle}>
                    {t("inactivityPromptTitle")}
                  </h3>
                  <p style={styles.kioskInactivityText}>
                    {t("inactivityPromptText", {
                      seconds: kioskInactivityCountdown,
                    })}
                  </p>
                </div>

                <div style={styles.kioskInactivityActions}>
                  <button
                    type="button"
                    style={styles.kioskInactivitySecondaryButton}
                    onClick={cancelKioskAfterInactivityPrompt}
                  >
                    {t("inactivityPromptReset")}
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.kioskInactivityPrimaryButton,
                      backgroundColor: primaryColor,
                      borderColor: primaryColor,
                      color: secondaryColor,
                    }}
                    onClick={continueKioskAfterInactivityPrompt}
                  >
                    {t("inactivityPromptContinue")}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {!customerName && !isKioskExperience && !isPendingAccountsPaymentOpen && (
        <div style={styles.identityOverlay}>
          <div style={styles.identityCard}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>{kioskLanguageControl}</div>
            <div style={styles.identityBadgeRow}>
              <span
                style={{
                  ...styles.identityBadge,
                  backgroundColor: `${primaryColor}16`,
                  borderColor: `${primaryColor}40`,
                  color: primaryColor,
                }}
              >
              {tx(displayStationLabel)}
              </span>
            </div>
            <h2 style={styles.identityTitle}>{tx("Antes de empezar")}</h2>
            <p style={styles.identityText}>
              {isKioskExperience
                ? "Escribe tu nombre para identificar tu pedido en este totem. Asi el salon y la cocina saben de quien es cada orden."
                : tx("Escribe tu nombre para identificar tu pedido en esta mesa. Asi podemos distinguir quien pidio cada cosa si comen varios juntos.")}
            </p>

            <form style={styles.identityForm} onSubmit={handleCustomerIdentitySubmit}>
              <label htmlFor="qr-customer-name" style={styles.identityLabel}> {tx("Tu nombre o alias")} </label>
              <input
                id="qr-customer-name"
                type="text"
                value={customerNameDraft}
                onChange={(event) => setCustomerNameDraft(event.target.value)}
                placeholder={tx("Ej: Juan")}
                autoComplete="name"
                autoFocus
                maxLength={60}
                style={{
                  ...styles.identityInput,
                  borderColor: `${primaryColor}40`,
                  boxShadow: `0 0 0 3px ${primaryColor}12`,
                }}
              />
              <button
                type="submit"
                style={{
                  ...styles.identitySubmitButton,
                  backgroundColor: primaryColor,
                  color: secondaryColor,
                }}
              > {tx("Entrar al menu")} </button>
            </form>
            {!tabletSessionKey && pendingTableAccounts.length > 0 ? (
              <div style={styles.identityPendingAccountsBlock}>
                <div style={styles.identityPendingAccountsDivider}>
                  <span>{tx("Ya pidieron en esta mesa")}</span>
                </div>
                <button
                  type="button"
                  style={{
                    ...styles.identityPendingAccountsButton,
                    borderColor: primaryColor,
                    color: primaryColor,
                  }}
                  onClick={() =>
                    openPendingAccountsPayment(customerNameDraft)
                  }
                >
                  <strong>{tx("Pagar")}</strong>
                  <span> {tx("Elegir una o varias cuentas pendientes")} </span>
                </button>
              </div>
            ) : pendingTableAccountsLoading ? (
              <span style={styles.identityPendingAccountsLoading}> {tx("Revisando cuentas pendientes...")} </span>
            ) : null}
          </div>
        </div>
      )}

      {isPendingAccountsPaymentOpen && !isKioskExperience ? (
        <div style={styles.identityOverlay}>
          <div style={{ ...styles.identityCard, maxWidth: 580 }}>
            <div style={styles.pendingAccountsHeader}>
              <div>
                <span style={styles.identityLabel}>{tx(displayStationLabel)}</span>
                <h2 style={styles.identityTitle}>{tx("Pagar cuentas")}</h2>
              </div>
              <button
                type="button"
                style={styles.pendingAccountsCloseButton}
                onClick={closePendingAccountsPayment}
                aria-label={tx("Cerrar")}
              >
                X
              </button>
            </div>
            <p style={styles.identityText}> {tx("Elige tu nombre o combina varias cuentas. Las que no selecciones seguiran pendientes.")} </p>

            <div style={styles.pendingAccountsList}>
              {pendingTableAccounts.map((account) => {
                const isSelected = selectedPendingAccountIds.includes(account.orderId);
                return (
                  <label
                    key={account.orderId}
                    style={{
                      ...styles.pendingAccountOption,
                      ...(isSelected
                        ? {
                            borderColor: primaryColor,
                            backgroundColor: `${primaryColor}0d`,
                          }
                        : {}),
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => togglePendingTableAccount(account.orderId)}
                      style={styles.pendingAccountCheckbox}
                    />
                    <span style={styles.pendingAccountMain}>
                      <strong>{account.customerName || tx("Comensal")}</strong>
                      <small>
                        {account.items.length
                          ? account.items
                              .map((item) => `${item.quantity} ${item.name}`)
                              .join(", ")
                          : tx("Pedido de la mesa")}
                      </small>
                    </span>
                    <strong style={styles.pendingAccountAmount}>
                      {currencyFormatter.format(account.digitalTotalAmount ?? account.totalAmount)}
                      {account.digitalTotalAmount != null && account.digitalTotalAmount !== account.cashTotalAmount ? <small>{tx("Sin oferta en efectivo")}</small> : null}
                    </strong>
                  </label>
                );
              })}
            </div>

            <div style={styles.menuWalletChooser}>
              <strong style={styles.menuWalletChooserTitle}>{tx("Billetera")}</strong>
              <div style={styles.menuWalletOptionList}>
                {menuQrWalletProviders.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => setSelectedMenuWalletProviderId(provider.id)}
                    style={{
                      ...styles.checkoutMethodButton,
                      ...(selectedMenuWalletProvider?.id === provider.id
                        ? {
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                            color: secondaryColor,
                          }
                        : {}),
                    }}
                  >
                    {provider.label}
                  </button>
                ))}
              </div>
            </div>

            {pendingTableAccountsError ? (
              <div style={{ ...styles.checkoutNotice, ...styles.checkoutNoticeError }}>
                {pendingTableAccountsError}
              </div>
            ) : null}

            <div style={styles.pendingAccountsFooter}>
              <span style={styles.pendingAccountsTotalLabel}> {tx("Total seleccionado")} </span>
              <strong style={styles.pendingAccountsTotalAmount}>
                {currencyFormatter.format(selectedPendingTableAccountsTotal)}
              </strong>
              <button
                type="button"
                style={{
                  ...styles.identitySubmitButton,
                  ...styles.pendingAccountsFooterButton,
                  backgroundColor: primaryColor,
                  color: secondaryColor,
                  opacity:
                    selectedPendingTableAccounts.length &&
                    !isCreatingPendingAccountsPayment
                      ? 1
                      : 0.55,
                }}
                disabled={
                  !selectedPendingTableAccounts.length ||
                  isCreatingPendingAccountsPayment
                }
                onClick={paySelectedPendingAccounts}
              >
                {isCreatingPendingAccountsPayment
                  ? "Preparando pago..."
                  : selectedPendingTableAccounts.length > 1
                    ? `Pagar ${selectedPendingTableAccounts.length} cuentas juntas`
                    : "Pagar cuenta seleccionada"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
  },
  centerWrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  statusCard: {
    width: "100%",
    maxWidth: 560,
    padding: "22px 24px",
    borderRadius: 16,
    backgroundColor: "#ffffff",
    border: "1px solid #dbe3ee",
    textAlign: "center",
    fontSize: 16,
    lineHeight: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 260,
  },
  loadingLogo: {
    width: "auto",
    height: "auto",
    maxWidth: "35vw",
    maxHeight: "35vh",
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
  },
  errorCard: {
    width: "100%",
    maxWidth: 620,
    padding: 28,
    borderRadius: 18,
    backgroundColor: "#ffffff",
    border: "1px solid #fecaca",
    boxShadow: "0 22px 60px rgba(15, 23, 42, 0.08)",
  },
  errorTitle: {
    marginTop: 0,
    marginBottom: 12,
    fontSize: 28,
  },
  errorText: {
    marginTop: 0,
    marginBottom: 20,
    color: "#475569",
    lineHeight: 1.6,
  },
  retryButton: {
    appearance: "none",
    border: "none",
    borderRadius: 10,
    padding: "12px 18px",
    cursor: "pointer",
    backgroundColor: "#111827",
    color: "#ffffff",
    fontWeight: 700,
  },
  operationalLockOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    backgroundColor: "rgba(15, 23, 42, 0.56)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  operationalLockCard: {
    width: "100%",
    maxWidth: 680,
    borderRadius: 24,
    backgroundColor: "#ffffff",
    border: "1px solid rgba(248, 113, 113, 0.28)",
    boxShadow: "0 28px 70px rgba(15, 23, 42, 0.22)",
    padding: "30px clamp(22px, 4vw, 36px)",
    display: "grid",
    gap: 14,
    textAlign: "center",
  },
  operationalLockEyebrow: {
    color: "#b91c1c",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  operationalLockTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "clamp(28px, 3.8vw, 40px)",
    lineHeight: 1.05,
  },
  operationalLockText: {
    margin: 0,
    color: "#475569",
    fontSize: 16,
    lineHeight: 1.7,
  },
  kioskLandingPage: {
    position: "relative",
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  kioskScreensaverButton: {
    appearance: "none",
    width: "100%",
    minHeight: "100vh",
    border: "none",
    background:
      "radial-gradient(circle at center, rgba(248,250,252,0.92) 0%, #ffffff 64%)",
    color: "#0f172a",
    padding: "32px clamp(20px, 4vw, 44px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  kioskScreensaverTopbar: {
    position: "absolute",
    top: 20,
    right: "clamp(18px, 3vw, 34px)",
    zIndex: 3,
  },
  kioskScreensaverContent: {
    width: "100%",
    maxWidth: 720,
    display: "grid",
    justifyItems: "center",
    textAlign: "center",
    gap: 18,
  },
  kioskScreensaverLogoWrap: {
    width: "min(56vw, 560px)",
    minWidth: 240,
    aspectRatio: "1 / 1",
    borderRadius: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  kioskScreensaverLogo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  kioskScreensaverBrandName: {
    fontSize: "clamp(40px, 5vw, 64px)",
    fontWeight: 900,
    color: "#0f172a",
  },
  kioskScreensaverStation: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
    padding: "0 16px",
    borderRadius: 999,
    backgroundColor: "#f1f5f9",
    color: "#334155",
    fontSize: 15,
    fontWeight: 800,
  },
  kioskLandingBackdrop: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.92) 42%, rgba(255,255,255,0.98) 100%)",
  },
  kioskLandingBackdropImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    filter: "blur(4px)",
    opacity: 0.18,
    transform: "scale(1.04)",
  },
  kioskLandingOverlay: {
    position: "relative",
    zIndex: 1,
    minHeight: "100dvh",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px clamp(18px, 3vw, 34px) 24px",
    display: "grid",
    alignContent: "center",
    gap: 18,
  },
  kioskLandingTopbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },
  kioskLanguageSelector: {
    position: "relative",
    display: "inline-grid",
  },
  kioskLanguageTrigger: {
    border: "1px solid rgba(226,232,240,0.92)",
    backgroundColor: "rgba(255,255,255,0.92)",
    color: "#0f172a",
    borderRadius: 18,
    minHeight: 50,
    padding: "0 14px 0 12px",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    boxShadow: "0 18px 34px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(12px)",
  },
  kioskLanguageTriggerIcon: {
    width: 28,
    height: 28,
    borderRadius: 999,
    backgroundColor: "#f8fafc",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kioskLanguageTriggerText: {
    fontSize: 15,
    fontWeight: 800,
    color: "#0f172a",
    whiteSpace: "nowrap",
  },
  kioskLanguageChevron: {
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1,
  },
  kioskLanguageDropdown: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    minWidth: 188,
    padding: 8,
    borderRadius: 18,
    backgroundColor: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(226,232,240,0.94)",
    boxShadow: "0 20px 44px rgba(15, 23, 42, 0.14)",
    display: "grid",
    gap: 6,
    zIndex: 8,
  },
  kioskLanguageOption: {
    border: "1px solid transparent",
    backgroundColor: "transparent",
    color: "#0f172a",
    borderRadius: 14,
    minHeight: 46,
    padding: "0 12px",
    display: "grid",
    gridTemplateColumns: "40px 1fr",
    alignItems: "center",
    gap: 10,
    textAlign: "left",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  kioskLanguageOptionActive: {
    backgroundColor: "#f8fafc",
    borderColor: "rgba(203, 213, 225, 0.92)",
  },
  kioskLanguageOptionCode: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 32,
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    color: "#1e293b",
    fontSize: 12,
    fontWeight: 900,
  },
  kioskUtilityButton: {
    appearance: "none",
    border: "none",
    borderRadius: 18,
    minHeight: 54,
    padding: "0 20px",
    backgroundColor: "#ef4444",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    fontSize: 18,
    fontWeight: 800,
    boxShadow: "0 18px 34px rgba(239, 68, 68, 0.24)",
  },
  kioskUtilityIcon: {
    fontSize: 20,
    lineHeight: 1,
  },
  kioskUtilityHint: {
    color: "#475569",
    fontSize: 16,
    fontWeight: 700,
  },
  kioskLandingIntro: {
    display: "grid",
    justifyItems: "center",
    textAlign: "center",
    gap: 10,
  },
  kioskLandingLogoWrap: {
    width: 88,
    height: 88,
    borderRadius: 24,
    backgroundColor: "#ffffff",
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.14)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 6,
  },
  kioskLandingLogo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    transform: "scale(1.2)",
    transformOrigin: "center",
  },
  kioskInstitutionalLogoText: {
    fontSize: 22,
    lineHeight: 0.92,
    fontWeight: 900,
    color: "#0f172a",
    textAlign: "center",
    maxWidth: 68,
  },
  kioskInstitutionalLogoTextCompact: {
    fontSize: 19,
    lineHeight: 0.95,
    fontWeight: 900,
    color: "#0f172a",
    textAlign: "center",
    maxWidth: 52,
  },
  kioskLandingEyebrow: {
    color: "#ef4444",
    fontSize: 14,
    fontWeight: 900,
  },
  kioskLandingTitle: {
    margin: 0,
    fontSize: "clamp(30px, 4.2vw, 56px)",
    lineHeight: 0.98,
    fontWeight: 900,
    maxWidth: 940,
  },
  kioskLandingText: {
    margin: 0,
    maxWidth: 700,
    color: "#475569",
    fontSize: 17,
    lineHeight: 1.35,
  },
  kioskWelcomeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
    marginTop: 0,
  },
  kioskWelcomeCard: {
    appearance: "none",
    border: "1px solid #dbe4f0",
    borderRadius: 28,
    backgroundColor: "#ffffff",
    boxShadow: "0 24px 54px rgba(15, 23, 42, 0.10)",
    minHeight: 206,
    padding: "18px 18px 16px",
    display: "grid",
    alignContent: "start",
    gap: 12,
    textAlign: "center",
    cursor: "pointer",
    transition: "transform 160ms ease, box-shadow 160ms ease",
  },
  kioskWelcomeImageWrap: {
    width: "100%",
    aspectRatio: "1.9 / 1",
    borderRadius: 22,
    overflow: "hidden",
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
  },
  kioskWelcomeImage: {
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
  },
  kioskWelcomeFallback: {
    fontSize: "clamp(24px, 2.2vw, 36px)",
    lineHeight: 1,
    fontWeight: 900,
    color: "#0f172a",
    textAlign: "center",
    padding: 18,
  },
  kioskWelcomeContent: {
    display: "grid",
    gap: 8,
    alignContent: "center",
    justifyItems: "center",
    height: "100%",
    padding: "2px 6px 0",
  },
  kioskWelcomeTitle: {
    fontSize: "clamp(22px, 2.6vw, 34px)",
    lineHeight: 1.04,
    color: "#0f172a",
    fontWeight: 900,
  },
  kioskWelcomeSubtitle: {
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.28,
    fontWeight: 600,
    maxWidth: 320,
  },
  hero: {
    minHeight: 260,
    position: "relative",
    overflow: "hidden",
    zIndex: 0,
  },
  heroCompact: {
    minHeight: 190,
  },
  kioskHero: {
    position: "relative",
    overflow: "hidden",
    minHeight: 188,
    padding: "18px 24px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  kioskHeroBackgroundMark: {
    position: "absolute",
    right: "clamp(70px, 14vw, 210px)",
    top: -20,
    width: "clamp(150px, 18vw, 240px)",
    aspectRatio: "1 / 1",
    borderRadius: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.1,
    pointerEvents: "none",
    filter: "blur(0.4px)",
    transform: "rotate(-8deg)",
  },
  kioskHeroBackgroundLogo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
  },
  kioskHeroOverlay: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    display: "grid",
    gap: 14,
  },
  kioskHeroTopline: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap",
  },
  kioskHeroBrandBlock: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  kioskHeroLogoWrap: {
    width: 58,
    height: 58,
    borderRadius: 16,
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 10,
  },
  kioskHeroLogo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  kioskHeroBrandText: {
    display: "grid",
    gap: 4,
  },
  kioskHeroEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.72)",
  },
  kioskHeroBranchName: {
    fontSize: 22,
    lineHeight: 1.1,
  },
  kioskHeroBadges: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  kioskHeroBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 40,
    padding: "0 16px",
    borderRadius: 999,
    backgroundColor: "#facc15",
    color: "#111827",
    fontSize: 14,
    fontWeight: 900,
  },
  kioskHeroBadgeSoft: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 34,
    padding: "0 14px",
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.10)",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 800,
  },
  kioskHeroBody: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 10,
    alignItems: "start",
  },
  kioskHeroCopy: {
    display: "grid",
    gap: 10,
    alignContent: "start",
  },
  kioskHeroTitle: {
    margin: 0,
    fontSize: 38,
    lineHeight: 1,
    fontWeight: 900,
  },
  kioskHeroText: {
    margin: 0,
    maxWidth: 780,
    color: "rgba(255,255,255,0.78)",
    fontSize: 16,
    lineHeight: 1.35,
  },
  kioskModeSwitchButton: {
    appearance: "none",
    justifySelf: "start",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 16,
    minHeight: 44,
    padding: "0 18px",
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
  },
  heroCoverFrame: {
    position: "relative",
    overflow: "hidden",
  },
  heroImage: {
    display: "block",
    width: "100%",
    height: "auto",
    // Wide covers retain their natural height; on phones cover crops only the sides.
    minHeight: "clamp(180px, 50vw, 260px)",
    maxHeight: "max(260px, 30vw)",
    objectFit: "cover",
    objectPosition: "center",
    zIndex: 0,
  },
  heroGradient: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 34%, rgba(15,23,42,0.08) 68%, rgba(15,23,42,0.24) 100%)",
    pointerEvents: "none",
    zIndex: 0,
  },
  heroOverlay: {
    position: "relative",
    zIndex: 1,
    maxWidth: 1240,
    margin: "0 auto",
    padding: "26px clamp(12px, 4vw, 20px) 32px",
  },
  heroCoverIdentity: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px clamp(12px, 4vw, 20px)",
    // Keep the logo and table outside the artwork, regardless of its central content.
    flexWrap: "wrap",
  },
  heroCoverBrandLogo: {
    width: 44,
    height: 44,
    padding: 4,
    borderRadius: 12,
  },
  heroHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 14,
  },
  heroBrandLogoWrap: {
    flexShrink: 0,
  },
  heroBrandLogo: {
    width: 74,
    height: 74,
    borderRadius: 18,
    objectFit: "contain",
    backgroundColor: "rgba(255,255,255,0.18)",
    padding: 8,
    display: "block",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.18)",
  },
  heroTopline: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 12,
  },
  tableBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 36,
    padding: "0 14px",
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(15,23,42,0.1)",
    backdropFilter: "blur(6px)",
    fontWeight: 700,
  },
  heroTitle: {
    margin: 0,
    fontSize: "clamp(34px, 5vw, 56px)",
    lineHeight: 1.02,
  },
  qrMenuTools: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    justifyContent: "flex-end",
    paddingTop: 12,
  },
  content: {
    maxWidth: 1240,
    margin: "0 auto",
    padding: "0 clamp(10px, 3.5vw, 20px) 44px",
    position: "relative",
    zIndex: 2,
  },
  identityOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    backgroundColor: "rgba(15, 23, 42, 0.52)",
    backdropFilter: "blur(8px)",
  },
  identityCard: {
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    maxWidth: 520,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 16,
    borderRadius: 20,
    padding: "24px 22px",
    backgroundColor: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    boxShadow: "0 28px 70px rgba(15, 23, 42, 0.22)",
  },
  identityBadgeRow: {
    display: "flex",
    justifyContent: "flex-start",
  },
  identityBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 32,
    padding: "0 12px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    fontSize: 12,
    fontWeight: 800,
  },
  identityTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.05,
    color: "#0f172a",
  },
  identityText: {
    margin: 0,
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.6,
  },
  identityForm: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    minWidth: 0,
    gap: 12,
  },
  identityLabel: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
  },
  identityInput: {
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    minHeight: 50,
    borderRadius: 14,
    border: "1px solid #cbd5e1",
    padding: "0 14px",
    fontSize: 16,
    color: "#0f172a",
    outline: "none",
    backgroundColor: "#ffffff",
  },
  identitySubmitButton: {
    appearance: "none",
    border: "none",
    minHeight: 48,
    borderRadius: 14,
    padding: "0 16px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
  },
  checkoutNotice: {
    marginBottom: 18,
    padding: "14px 16px",
    borderRadius: 16,
    fontWeight: 700,
    lineHeight: 1.5,
  },
  checkoutNoticeSuccess: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  checkoutNoticeError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fca5a5",
    color: "#b91c1c",
  },
  orderShortcutWrap: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: 18,
    marginBottom: 24,
  },
  orderShortcutButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
    minHeight: 56,
    padding: "12px 16px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe3ee",
    borderRadius: 14,
    boxShadow: "0 16px 36px rgba(15, 23, 42, 0.06)",
    cursor: "pointer",
  },
  orderShortcutTitle: {
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  orderShortcutMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
  },
  kioskCatalogShell: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 24,
    alignItems: "start",
    justifyItems: "center",
    width: "100%",
  },
  kioskCatalogShellCompact: {
    gridTemplateColumns: "1fr",
    gap: 18,
  },
  kioskSidebar: {
    position: "sticky",
    top: 16,
    display: "grid",
    gap: 16,
    alignSelf: "start",
  },
  kioskSidebarCompact: {
    position: "static",
  },
  kioskSidebarBlock: {
    display: "grid",
    gap: 12,
    padding: "18px 16px",
    borderRadius: 28,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    boxShadow: "0 24px 52px rgba(15, 23, 42, 0.08)",
  },
  kioskSidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  kioskSidebarTitle: {
    color: "#0f172a",
    fontSize: 18,
    lineHeight: 1.1,
  },
  kioskSidebarMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },
  kioskSidebarList: {
    display: "grid",
    gap: 10,
  },
  kioskSidebarButton: {
    appearance: "none",
    border: "1px solid #dbe3ee",
    borderRadius: 22,
    minHeight: 70,
    padding: "0 16px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: 12,
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
  },
  kioskSidebarButtonIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    color: "#334155",
    fontSize: 20,
    lineHeight: 1,
    flex: "0 0 auto",
  },
  kioskSidebarButtonTextWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
    flex: 1,
  },
  kioskSidebarButtonText: {
    fontSize: 17,
    fontWeight: 800,
    lineHeight: 1.15,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  kioskSidebarButtonMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: "normal",
  },
  kioskSidebarPromoButton: {
    appearance: "none",
    border: "1px solid #dbe3ee",
    borderRadius: 24,
    minHeight: 132,
    padding: "18px 16px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    color: "#0f172a",
    display: "grid",
    gap: 8,
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.06)",
  },
  kioskSidebarPromoEyebrow: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0,
  },
  kioskSidebarPromoTitle: {
    fontSize: 20,
    lineHeight: 1.08,
  },
  kioskSidebarPromoMeta: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  kioskCatalogMain: {
    display: "grid",
    gap: 22,
    minWidth: 0,
    width: "100%",
    maxWidth: 1320,
  },
  kioskCategoriesBand: {
    backgroundColor: "#ffffff",
    borderRadius: 30,
    border: "1px solid #e2e8f0",
    boxShadow: "0 24px 52px rgba(15, 23, 42, 0.08)",
    padding: "24px clamp(18px, 3vw, 30px) 30px",
  },
  kioskCategoryPickerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    marginBottom: 20,
  },
  kioskCategoryPickerTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 32,
    lineHeight: 1.05,
  },
  kioskCategoryPickerMeta: {
    display: "block",
    marginTop: 8,
    color: "#64748b",
    fontSize: 15,
    fontWeight: 800,
  },
  kioskCategoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 330px))",
    justifyContent: "center",
    gap: 20,
  },
  kioskCategoryCard: {
    appearance: "none",
    border: "1px solid #e2e8f0",
    borderRadius: 28,
    overflow: "hidden",
    backgroundColor: "#ffffff",
    display: "grid",
    gap: 0,
    textAlign: "left",
    cursor: "pointer",
    padding: 0,
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
    transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
  },
  kioskCategoryImageWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "1.18 / 1",
    overflow: "hidden",
    backgroundColor: "#f8fafc",
  },
  kioskCategoryImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
    padding: 14,
    boxSizing: "border-box",
    backgroundColor: "#f8fafc",
  },
  kioskCategoryFallback: {
    width: "100%",
    height: "100%",
    background:
      "radial-gradient(circle at 28% 24%, rgba(249, 115, 22, 0.28) 0, rgba(249, 115, 22, 0.28) 16%, transparent 17%), linear-gradient(135deg, #f8fafc 0%, #fff7ed 46%, #e0f2fe 100%)",
  },
  kioskCategoryOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(15,23,42,0.02) 0%, rgba(15,23,42,0.10) 44%, rgba(15,23,42,0.56) 100%)",
    pointerEvents: "none",
  },
  kioskCategoryBadge: {
    position: "absolute",
    left: 14,
    top: 14,
    display: "inline-flex",
    alignItems: "center",
    minHeight: 30,
    padding: "0 12px",
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.94)",
    color: "#111827",
    fontSize: 12,
    fontWeight: 900,
  },
  kioskCategoryContent: {
    display: "grid",
    gap: 8,
    padding: "18px 18px 20px",
  },
  kioskCategoryTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  kioskCategoryIcon: {
    fontSize: 22,
    lineHeight: 1,
  },
  kioskCategoryTitle: {
    fontSize: 24,
    lineHeight: 1.08,
    color: "#0f172a",
  },
  kioskCategoryDescription: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 600,
  },
  promotionsBand: {
    display: "grid",
    gap: 10,
    marginBottom: 22,
  },
  promotionsRail: {
    display: "flex",
    gap: 10,
    overflowX: "auto",
    paddingBottom: 4,
  },
  promotionSpotlightCard: {
    display: "block",
    flex: "0 0 320px",
    padding: "12px",
    borderRadius: 16,
    border: "1px solid #dbe3ee",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 10px 22px rgba(15, 23, 42, 0.05)",
    minWidth: 0,
  },
  promotionSpotlightCardMuted: {
    opacity: 0.76,
  },
  promotionSpotlightBody: {
    display: "grid",
    gridTemplateColumns: "88px minmax(0, 1fr)",
    gap: 12,
    alignItems: "stretch",
  },
  promotionSpotlightContent: {
    display: "grid",
    gap: 8,
    minWidth: 0,
  },
  promotionSpotlightImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    aspectRatio: "4 / 5",
    borderRadius: 12,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
  },
  promotionSpotlightHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 6,
    flexWrap: "wrap",
  },
  promotionHeaderBadges: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  promotionHookBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 8px",
    borderRadius: 999,
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: 11,
    fontWeight: 800,
  },
  promotionCategoryPill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 8px",
    borderRadius: 999,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontSize: 11,
    fontWeight: 700,
  },
  promotionAvailabilityBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  promotionAvailabilityBadgeLive: {
    backgroundColor: "#ecfdf5",
    color: "#166534",
    border: "1px solid #86efac",
  },
  promotionAvailabilityBadgeScheduled: {
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fdba74",
  },
  promotionSpotlightTitle: {
    color: "#0f172a",
    fontSize: 15,
    lineHeight: 1.2,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  promotionSpotlightText: {
    margin: 0,
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.45,
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  promotionComboList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  promotionComboChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 8px",
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontSize: 11,
    fontWeight: 700,
  },
  promotionSpotlightFooter: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  promotionValidity: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.35,
  },
  promotionJumpButton: {
    appearance: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    minHeight: 28,
    padding: "0 10px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
  },
  categoriesBand: {
    marginBottom: 28,
  },
  categoriesBandSticky: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    paddingTop: 10,
    paddingBottom: 10,
    marginBottom: 18,
    backgroundColor: "rgba(248, 250, 252, 0.94)",
    backdropFilter: "blur(12px)",
  },
  menuBand: {
    display: "flex",
    flexDirection: "column",
    gap: 26,
  },
  kioskCategoryDetailBand: {
    width: "100%",
    maxWidth: 1320,
    margin: "0 auto",
    alignItems: "center",
    textAlign: "center",
  },
  kioskCategoryDetailHeader: {
    display: "grid",
    justifyItems: "center",
    gap: 8,
    width: "100%",
    marginBottom: 4,
  },
  kioskCategoryDetailSubcategoryRail: {
    justifyContent: "center",
    flexWrap: "wrap",
    overflowX: "visible",
    width: "100%",
  },
  kioskProductGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 400px))",
    justifyContent: "center",
    gap: 18,
    width: "100%",
    maxWidth: 1280,
  },
  kioskProductGridCompact: {
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    maxWidth: "100%",
  },
  kioskBackToCategoriesButton: {
    appearance: "none",
    alignSelf: "center",
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    minHeight: 64,
    padding: "8px 22px 8px 8px",
    borderRadius: 999,
    border: "1px solid rgba(15, 23, 42, 0.12)",
    background:
      "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.96) 100%)",
    color: "#ffffff",
    cursor: "pointer",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.22)",
  },
  kioskBackIcon: {
    display: "grid",
    placeItems: "center",
    width: 48,
    height: 48,
    borderRadius: "50%",
    backgroundColor: "#ff1f25",
    color: "#ffffff",
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1,
    boxShadow: "0 10px 22px rgba(255, 31, 37, 0.28)",
  },
  kioskBackCopy: {
    display: "grid",
    gap: 2,
    textAlign: "left",
    lineHeight: 1.1,
  },
  kioskBackTitle: {
    color: "#ffffff",
    fontSize: 16,
    fontWeight: 900,
  },
  kioskBackMeta: {
    color: "rgba(255,255,255,0.68)",
    fontSize: 11,
    fontWeight: 800,
  },
  emptyBand: {
    padding: "26px 0 10px",
  },
  emptyText: {
    color: "#475569",
    lineHeight: 1.6,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 24,
  },
  sectionMeta: {
    color: "#64748b",
    fontSize: 14,
    fontWeight: 600,
  },
  categoryRail: {
    display: "flex",
    gap: 10,
    overflowX: "auto",
    paddingBottom: 6,
  },
  categoryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    minHeight: 46,
    padding: "0 16px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  },
  categoryIcon: {
    fontSize: 18,
    lineHeight: 1,
  },
  subcategoryRail: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 4,
    marginBottom: 8,
  },
  categoryPromotionsBand: {
    display: "grid",
    gap: 12,
    marginBottom: 6,
  },
  categoryPromotionsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  categoryPromotionsTitle: {
    color: "#0f172a",
    fontSize: 16,
  },
  categoryPromotionsMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  categoryPromotionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 10,
  },
  categoryPromoCard: {
    display: "grid",
    gap: 8,
    borderRadius: 14,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
    padding: "12px 14px",
  },
  categoryPromoMediaWrap: {
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: 12,
    background:
      "linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(255,255,255,0.98) 100%)",
    aspectRatio: "16 / 10",
    minHeight: "190px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #dbe3ee",
    padding: "10px",
    boxSizing: "border-box",
  },
  categoryPromoMedia: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
  },
  categoryPromoTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flexWrap: "wrap",
  },
  categoryPromoHook: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 26,
    padding: "0 9px",
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontSize: 11,
    fontWeight: 800,
  },
  categoryPromoStatus: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  categoryPromoStatusLive: {
    backgroundColor: "#ecfdf5",
    color: "#166534",
    border: "1px solid #86efac",
  },
  categoryPromoStatusScheduled: {
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fdba74",
  },
  categoryPromoName: {
    color: "#0f172a",
    fontSize: 16,
    lineHeight: 1.2,
  },
  categoryPromoText: {
    margin: 0,
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
  },
  categoryPromoFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  categoryPromoSchedule: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  categoryPromoButton: {
    appearance: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    minHeight: 30,
    padding: "0 12px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  subcategoryButton: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 38,
    padding: "0 14px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  },
  subcategoryWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  subcategoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  subcategoryTitle: {
    margin: 0,
    fontSize: 18,
  },
  subcategoryMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  productGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  productGridCompact: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },
  productTileButton: {
    appearance: "none",
    border: "none",
    padding: 0,
    background: "transparent",
    textAlign: "left",
    cursor: "pointer",
    width: "100%",
  },
  productTileCard: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.08)",
    minHeight: 100,
  },
  productTileImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    display: "block",
  },
  productTileImagePlaceholder: {
    width: "100%",
    aspectRatio: "1 / 1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e2e8f0",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  productTileBody: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    padding: 9,
  },
  productTileTop: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  productTileName: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.25,
    color: "#0f172a",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  productTileCategoryBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 22,
    padding: "0 8px",
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    color: "#4f46e5",
    fontSize: 10,
    fontWeight: 800,
    width: "fit-content",
  },
  productTilePrice: {
    fontSize: 13,
    lineHeight: 1.2,
    fontWeight: 800,
  },
  productTileMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    minHeight: 20,
  },
  productTileBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 8px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    fontSize: 11,
    fontWeight: 800,
  },
  productTileHint: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 700,
  },
  productCard: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
    borderRadius: 18,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.08)",
  },
  aiSpotlightProductCard: {
    borderColor: "#f97316",
    boxShadow:
      "0 0 0 4px rgba(249, 115, 22, 0.2), 0 24px 42px rgba(249, 115, 22, 0.28)",
    transform: "scale(1.015)",
    transition:
      "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
  },
  productImage: {
    width: "100%",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    display: "block",
    cursor: "pointer",
  },
  productImagePlaceholder: {
    width: "100%",
    aspectRatio: "4 / 3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e2e8f0",
    color: "#475569",
    fontWeight: 700,
    cursor: "pointer",
  },
  productBody: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    padding: 11,
  },
  productHeadText: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 0,
  },
  productHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  productName: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.25,
  },
  productCategoryBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 8px",
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    color: "#4f46e5",
    fontSize: 11,
    fontWeight: 800,
    width: "fit-content",
  },
  productPrice: {
    fontSize: 15,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  productDescription: {
    margin: 0,
    color: "#334155",
    lineHeight: 1.45,
    fontSize: 13,
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  productDescriptionMuted: {
    margin: 0,
    color: "#94a3b8",
    lineHeight: 1.5,
    fontSize: 14,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  productMetaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  productMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 600,
  },
  productMetaStrong: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
  },
  customBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    fontSize: 12,
    fontWeight: 800,
    width: "fit-content",
    backgroundColor: "#ffffff",
  },
  productAction: {
    appearance: "none",
    border: "none",
    borderRadius: 10,
    minHeight: 38,
    padding: "0 14px",
    fontWeight: 800,
    cursor: "pointer",
    width: "100%",
    marginTop: 4,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.58)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 1000,
  },
  kioskCompletionOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1600,
    display: "grid",
    placeItems: "center",
    padding: 24,
    backgroundColor: "rgba(15, 23, 42, 0.82)",
  },
  kioskCompletionPanel: {
    width: "min(620px, 100%)",
    display: "grid",
    justifyItems: "center",
    gap: 18,
    padding: "44px 36px",
    borderRadius: 8,
    border: "3px solid #fb923c",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    textAlign: "center",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.38)",
  },
  kioskCompletionSpinner: {
    width: 74,
    height: 74,
    borderRadius: "50%",
    border: "9px solid #ffedd5",
    borderTopColor: "#f97316",
    animation: "mozzoapp-loading-spin 0.85s linear infinite",
  },
  kioskCompletionEyebrow: {
    color: "#c2410c",
    fontSize: 18,
    lineHeight: 1.1,
    fontWeight: 950,
  },
  kioskCompletionTitle: {
    margin: 0,
    maxWidth: 520,
    color: "#0f172a",
    fontSize: 34,
    lineHeight: 1.15,
    fontWeight: 950,
  },
  kioskCompletionHint: {
    margin: 0,
    maxWidth: 500,
    color: "#475569",
    fontSize: 18,
    lineHeight: 1.45,
    fontWeight: 750,
  },
  modalCard: {
    width: "100%",
    maxWidth: 940,
    maxHeight: "88vh",
    overflow: "auto",
    backgroundColor: "#ffffff",
    borderRadius: 18,
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
  },
  promoModalCard: {
    width: "100%",
    maxWidth: 820,
    maxHeight: "88vh",
    overflow: "auto",
    backgroundColor: "#ffffff",
    borderRadius: 18,
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
  },
  promoModalMediaWrap: {
    width: "100%",
    backgroundColor: "#ffffff",
    aspectRatio: "4 / 5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  promoModalMediaWrapCompact: {
    aspectRatio: "16 / 5",
    maxHeight: 210,
    padding: "12px 18px 0",
    background:
      "linear-gradient(180deg, rgba(248, 250, 252, 0.98) 0%, rgba(255, 255, 255, 1) 100%)",
    overflow: "hidden",
  },
  promoModalImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
  },
  promoModalImageCompact: {
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: 190,
    margin: "0 auto",
  },
  promoModalBody: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: 22,
  },
  promoModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  promoModalHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minWidth: 0,
    flex: 1,
  },
  promoModalBadgeRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  promoModalTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 28,
    lineHeight: 1.1,
  },
  promoModalText: {
    margin: 0,
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.6,
  },
  promoModalMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  promoModalMetaText: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.45,
  },
  promoModalWarning: {
    borderRadius: 12,
    border: "1px solid #fdba74",
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    padding: "12px 14px",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.45,
  },
  promoModalSection: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  promoModalSectionTitle: {
    color: "#0f172a",
    fontSize: 16,
  },
  promoModalOptionList: {
    display: "grid",
    gap: 10,
  },
  promoModalOptionCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: 14,
    borderRadius: 14,
    border: "1px solid #dbe3ee",
    backgroundColor: "#fcfdff",
  },
  promoModalOptionButton: {
    appearance: "none",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: 14,
    borderRadius: 14,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    cursor: "pointer",
    textAlign: "left",
  },
  promoModalOptionInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
    flex: 1,
  },
  promoModalOptionMeta: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
  },
  promoModalOptionPrice: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  promoModalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  modalMediaWrap: {
    width: "100%",
    backgroundColor: "#e2e8f0",
  },
  modalImage: {
    width: "100%",
    maxHeight: 320,
    objectFit: "cover",
    display: "block",
  },
  modalImagePlaceholder: {
    width: "100%",
    minHeight: 220,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#475569",
    fontWeight: 700,
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: 22,
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap",
  },
  modalTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.1,
  },
  modalDescription: {
    margin: "10px 0 0",
    color: "#475569",
    lineHeight: 1.65,
    maxWidth: 620,
  },
  modalPriceWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
  },
  modalPrice: {
    fontSize: 26,
    lineHeight: 1,
  },
  modalExtraPrice: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
  },
  modalSummaryRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  modalSummaryChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 34,
    padding: "0 12px",
    borderRadius: 999,
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 700,
  },
  ingredientList: {
    display: "grid",
    gap: 12,
  },
  ingredientRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: 14,
    borderRadius: 14,
    border: "1px solid #dbe3ee",
    backgroundColor: "#fcfdff",
    flexWrap: "wrap",
  },
  ingredientMain: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flex: 1,
    minWidth: 220,
  },
  ingredientThumb: {
    width: 56,
    height: 56,
    borderRadius: 12,
    objectFit: "cover",
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    flex: "0 0 auto",
    display: "block",
  },
  ingredientThumbPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 12,
    border: "1px solid #dbe3ee",
    backgroundColor: "#eef2f7",
    color: "#64748b",
    fontSize: 18,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  },
  ingredientInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: 1,
    minWidth: 220,
  },
  ingredientNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  ingredientName: {
    fontSize: 16,
  },
  ingredientState: {
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 800,
    padding: "4px 10px",
    borderRadius: 999,
    backgroundColor: "#e2e8f0",
  },
  ingredientMetaRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  ingredientMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 600,
  },
  ingredientControls: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  quantityButton: {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 800,
    cursor: "pointer",
  },
  quantityValue: {
    minWidth: 28,
    textAlign: "center",
    fontSize: 16,
    fontWeight: 800,
  },
  fixedChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 34,
    padding: "0 12px",
    borderRadius: 999,
    backgroundColor: "#e2e8f0",
    color: "#475569",
    fontSize: 13,
    fontWeight: 700,
  },
  emptyDetailBox: {
    padding: 18,
    borderRadius: 14,
    border: "1px dashed #cbd5e1",
    color: "#64748b",
    backgroundColor: "#f8fafc",
  },
  linkedChoiceWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 18,
    borderRadius: 16,
    border: "1px solid #dbe3ee",
    backgroundColor: "#fcfdff",
  },
  linkedChoiceGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  linkedChoiceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  linkedChoiceTitle: {
    display: "block",
    color: "#0f172a",
    fontSize: 16,
  },
  linkedChoiceText: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
  },
  linkedChoiceList: {
    display: "grid",
    gap: 10,
  },
  linkedChoiceButton: {
    appearance: "none",
    width: "100%",
    borderRadius: 14,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    padding: 14,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    textAlign: "left",
  },
  linkedChoiceButtonActive: {
    border: "1px solid #3b82f6",
    boxShadow: "0 0 0 1px rgba(59,130,246,0.16)",
    backgroundColor: "#eff6ff",
  },
  linkedChoiceProductName: {
    display: "block",
    fontSize: 15,
  },
  linkedChoiceProductDescription: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
  },
  linkedChoicePrice: {
    fontSize: 14,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  orderInlineTools: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginRight: "auto",
  },
  orderQuantityWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  orderQuantityLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  orderQuantityControls: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  orderAmountWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  orderAmountLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  orderAmountValue: {
    fontSize: 18,
    lineHeight: 1.1,
  },
  secondaryAction: {
    appearance: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    minHeight: 42,
    padding: "0 16px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
  identityPendingAccountsBlock: {
    display: "grid",
    gap: 12,
  },
  identityPendingAccountsDivider: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 24,
    borderTop: "1px solid #e2e8f0",
    paddingTop: 12,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
  },
  identityPendingAccountsButton: {
    width: "100%",
    minHeight: 52,
    display: "grid",
    gap: 3,
    alignItems: "center",
    border: "1px solid #cbd5e1",
    borderRadius: 14,
    backgroundColor: "#ffffff",
    padding: "9px 14px",
    fontSize: 15,
    cursor: "pointer",
  },
  identityPendingAccountsLoading: {
    color: "#64748b",
    fontSize: 12,
    textAlign: "center",
  },
  pendingAccountsShortcutWrap: {
    marginBottom: 14,
  },
  pendingAccountsShortcutButton: {
    width: "100%",
    minHeight: 64,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    padding: "12px 16px",
    textAlign: "left",
    cursor: "pointer",
  },
  pendingAccountsHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  pendingAccountsCloseButton: {
    width: 40,
    height: 40,
    flex: "0 0 40px",
    border: "1px solid #cbd5e1",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: 26,
    lineHeight: 1,
    cursor: "pointer",
  },
  pendingAccountsList: {
    display: "grid",
    gap: 10,
    maxHeight: "min(42vh, 380px)",
    overflowY: "auto",
    paddingRight: 2,
  },
  pendingAccountOption: {
    display: "grid",
    gridTemplateColumns: "24px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
    minHeight: 66,
    border: "1px solid #dbe3ee",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    padding: "10px 12px",
    cursor: "pointer",
  },
  pendingAccountCheckbox: {
    width: 20,
    height: 20,
    accentColor: "#ff5a1f",
  },
  pendingAccountMain: {
    minWidth: 0,
    display: "grid",
    gap: 4,
    color: "#0f172a",
  },
  pendingAccountAmount: {
    whiteSpace: "nowrap",
    color: "#0f172a",
    fontSize: 15,
  },
  pendingAccountsFooter: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 8,
    alignItems: "center",
    borderTop: "1px solid #e2e8f0",
    paddingTop: 14,
  },
  pendingAccountsTotalLabel: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  pendingAccountsTotalAmount: {
    color: "#0f172a",
    fontSize: 20,
  },
  pendingAccountsFooterButton: {
    gridColumn: "1 / -1",
  },
  secondaryActionLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    minHeight: 42,
    padding: "0 16px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
  primaryAction: {
    appearance: "none",
    border: "none",
    borderRadius: 10,
    minHeight: 42,
    padding: "0 16px",
    fontWeight: 800,
    cursor: "pointer",
  },
  cartBarWrap: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 90,
    padding: "12px clamp(10px, 3.5vw, 20px) 16px",
    background:
      "linear-gradient(180deg, rgba(248,250,252,0) 0%, rgba(248,250,252,0.96) 36%, rgba(248,250,252,1) 100%)",
  },
  cartBarButton: {
    width: "100%",
    maxWidth: 1240,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    border: "none",
    borderRadius: 16,
    padding: "16px 18px",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.18)",
    cursor: "pointer",
  },
  kioskCartBarButton: {
    maxWidth: 1440,
    minHeight: 86,
    borderRadius: 24,
    padding: "18px 22px",
    boxShadow: "0 22px 54px rgba(15, 23, 42, 0.24)",
  },
  cartBarInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
  },
  cartBarTitle: {
    fontSize: 16,
    lineHeight: 1.1,
  },
  cartBarMeta: {
    fontSize: 13,
    fontWeight: 700,
    opacity: 0.92,
  },
  cartBarAmount: {
    fontSize: 18,
    whiteSpace: "nowrap",
  },
  assistantDock: {
    position: "fixed",
    right: 18,
    zIndex: 45,
    display: "grid",
    gap: 10,
  },
  assistantFab: {
    appearance: "none",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    backgroundColor: "rgba(255,255,255,0.96)",
    color: "#0f172a",
    borderRadius: 999,
    minHeight: 52,
    padding: "0 14px",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.14)",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    backdropFilter: "blur(14px)",
  },
  assistantFabLabel: {
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
  },
  aiAssistantFloatingPanel: {
    position: "fixed",
    top: "max(12px, env(safe-area-inset-top))",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 52,
    width: "min(920px, calc(100vw - 28px))",
    maxHeight: "none",
    display: "grid",
    gridTemplateRows: "auto auto minmax(0, 1fr) auto",
    gap: 14,
    padding: "16px 16px 18px",
    borderRadius: 24,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
    backdropFilter: "blur(18px)",
    overflow: "hidden",
  },
  aiAssistantFloatingPanelBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 51,
    backgroundColor: "rgba(15, 23, 42, 0.16)",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
  },
  aiAssistantFloatingPanelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  aiAssistantFloatingPanelCopy: {
    display: "grid",
    gap: 4,
  },
  aiAssistantFloatingPanelEyebrow: {
    color: "#ef4444",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  aiAssistantFloatingPanelTitle: {
    color: "#0f172a",
    fontSize: 18,
    lineHeight: 1.1,
    fontWeight: 900,
  },
  aiAssistantFloatingPanelHideButton: {
    appearance: "none",
    border: "1px solid rgba(148, 163, 184, 0.32)",
    backgroundColor: "#ffffff",
    color: "#334155",
    borderRadius: 999,
    minHeight: 38,
    padding: "0 14px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  aiAssistantFloatingPanelStatus: {
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  aiAssistantFloatingPanelActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 10,
    marginTop: 12,
    marginBottom: 4,
  },
  aiAssistantReplayButtonUrgent: {
    borderColor: "#f97316",
    color: "#c2410c",
    backgroundColor: "#fff7ed",
    boxShadow: "0 12px 24px rgba(249, 115, 22, 0.16)",
  },
  aiAssistantFloatingResultsScroller: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "minmax(220px, 260px)",
    gap: 14,
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: 4,
    scrollbarGutter: "stable both-edges",
  },
  aiAssistantFloatingProductCard: {
    appearance: "none",
    border: "1px solid rgba(219, 234, 254, 0.9)",
    backgroundColor: "#ffffff",
    borderRadius: 22,
    padding: 12,
    display: "grid",
    gap: 10,
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
    minHeight: 238,
  },
  aiAssistantFloatingProductImageWrap: {
    width: "100%",
    aspectRatio: "4 / 3",
    borderRadius: 18,
    overflow: "hidden",
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  aiAssistantFloatingProductImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  aiAssistantFloatingProductInfo: {
    display: "grid",
    gap: 6,
  },
  aiAssistantFloatingProductName: {
    color: "#0f172a",
    fontSize: 15,
    lineHeight: 1.3,
    fontWeight: 900,
  },
  aiAssistantFloatingProductPrice: {
    color: "#ef4444",
    fontSize: 15,
    lineHeight: 1.2,
    fontWeight: 900,
  },
  aiAssistantFloatingButton: {
    position: "fixed",
    zIndex: 56,
    width: 78,
    height: 78,
    borderRadius: "50%",
    border: "none",
    background:
      "radial-gradient(circle at 30% 30%, #fb7185 0%, #ef4444 45%, #b91c1c 100%)",
    boxShadow: "0 22px 52px rgba(185, 28, 28, 0.38)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "grab",
    touchAction: "none",
    transition: "transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease",
  },
  aiAssistantFloatingButtonListening: {
    transform: "scale(1.06)",
    boxShadow: "0 0 0 10px rgba(239, 68, 68, 0.16), 0 22px 52px rgba(185, 28, 28, 0.42)",
  },
  aiAssistantFloatingButtonLoading: {
    transform: "scale(1.03)",
    boxShadow: "0 0 0 10px rgba(251, 146, 60, 0.16), 0 22px 52px rgba(194, 65, 12, 0.34)",
  },
  aiAssistantFloatingButtonCore: {
    width: 54,
    height: 54,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  aiAssistantDismissZone: {
    position: "fixed",
    left: "50%",
    bottom: 18,
    transform: "translateX(-50%)",
    zIndex: 57,
    display: "grid",
    justifyItems: "center",
    gap: 8,
    pointerEvents: "none",
  },
  aiAssistantDismissZoneTarget: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    backgroundColor: "rgba(15, 23, 42, 0.92)",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    fontWeight: 900,
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.34)",
  },
  aiAssistantDismissZoneText: {
    color: "#ffffff",
    backgroundColor: "rgba(15, 23, 42, 0.88)",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
  },
  aiAssistantModalCard: {
    width: "100%",
    maxWidth: 880,
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: "#ffffff",
    borderRadius: 24,
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
    display: "flex",
    flexDirection: "column",
    gap: 24,
    padding: 24,
  },
  aiAssistantHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  aiAssistantVoiceStage: {
    display: "grid",
    justifyItems: "center",
    alignItems: "center",
    padding: "8px 0 4px",
  },
  aiAssistantVoiceButton: {
    appearance: "none",
    width: 184,
    height: 184,
    borderRadius: "50%",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    cursor: "pointer",
    boxShadow: "0 28px 64px rgba(15, 23, 42, 0.18)",
    transition: "transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease",
  },
  aiAssistantVoiceButtonActive: {
    transform: "scale(1.05)",
    boxShadow: "0 0 0 10px rgba(59, 130, 246, 0.10), 0 28px 64px rgba(15, 23, 42, 0.20)",
  },
  aiAssistantVoiceButtonLoading: {
    opacity: 0.8,
    boxShadow: "0 0 0 10px rgba(249, 115, 22, 0.10), 0 28px 64px rgba(15, 23, 42, 0.16)",
  },
  aiAssistantVoiceButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.10)",
  },
  aiAssistantVoiceButtonLabel: {
    fontSize: 24,
    lineHeight: 1.1,
    fontWeight: 900,
    letterSpacing: 0.2,
  },
  aiAssistantResultsPanel: {
    display: "grid",
    gap: 14,
    minHeight: 260,
    maxHeight: "44vh",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 6,
    scrollbarGutter: "stable",
  },
  aiAssistantResultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  aiAssistantIntroCard: {
    display: "grid",
    gap: 8,
    padding: 16,
    borderRadius: 16,
    border: "1px solid #dbeafe",
    background:
      "linear-gradient(180deg, rgba(239, 246, 255, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
  },
  aiAssistantIntroTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 900,
  },
  aiAssistantIntroText: {
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 600,
  },
  aiAssistantStatusBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid #dbe3ee",
    backgroundColor: "#f8fafc",
  },
  aiAssistantStatusPill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 10px",
    borderRadius: 999,
    backgroundColor: "#e2e8f0",
    color: "#0f172a",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  aiAssistantStatusText: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
    flex: "1 1 280px",
  },
  aiAssistantThread: {
    display: "grid",
    gap: 12,
    minHeight: 0,
    maxHeight: "none",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 8,
    scrollbarGutter: "stable",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-y",
  },
  aiAssistantBubble: {
    display: "grid",
    gap: 8,
    padding: "14px 16px",
    borderRadius: 18,
    border: "1px solid #dbe3ee",
  },
  aiAssistantBubbleAssistant: {
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    justifySelf: "start",
    maxWidth: "92%",
  },
  aiAssistantBubbleUser: {
    backgroundColor: "#0f172a",
    color: "#ffffff",
    borderColor: "#0f172a",
    justifySelf: "end",
    maxWidth: "88%",
  },
  aiAssistantBubbleMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    opacity: 0.84,
  },
  aiAssistantFallbackBadge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 22,
    padding: "0 8px",
    borderRadius: 999,
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    fontSize: 10,
    fontWeight: 900,
  },
  aiAssistantBubbleText: {
    margin: 0,
    whiteSpace: "pre-wrap",
    lineHeight: 1.55,
    fontSize: 14,
  },
  aiAssistantBubbleActions: {
    display: "flex",
    justifyContent: "flex-start",
    gap: 8,
    flexWrap: "wrap",
  },
  aiAssistantBubbleActionButton: {
    appearance: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    minHeight: 32,
    padding: "0 12px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  aiAssistantProductGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
  },
  aiAssistantProductCard: {
    appearance: "none",
    width: "100%",
    border: "1px solid #dbe3ee",
    borderRadius: 20,
    backgroundColor: "#ffffff",
    padding: 12,
    display: "grid",
    gap: 12,
    textAlign: "left",
    cursor: "pointer",
    boxShadow: "0 18px 34px rgba(15, 23, 42, 0.08)",
  },
  aiAssistantProductCardSkeleton: {
    pointerEvents: "none",
    backgroundColor: "#f8fafc",
  },
  aiAssistantProductImageWrap: {
    width: "100%",
    height: 190,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  aiAssistantProductImageSkeleton: {
    background:
      "linear-gradient(135deg, rgba(226, 232, 240, 0.95) 0%, rgba(248, 250, 252, 1) 100%)",
  },
  aiAssistantProductImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    backgroundColor: "#ffffff",
  },
  aiAssistantProductImagePlaceholder: {
    color: "#64748b",
    fontSize: 28,
    fontWeight: 900,
  },
  aiAssistantProductInfo: {
    display: "grid",
    gap: 6,
  },
  aiAssistantProductName: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.3,
  },
  aiAssistantProductDescription: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 600,
  },
  aiAssistantProductPrice: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 900,
  },
  aiAssistantComposer: {
    display: "grid",
    gap: 12,
    paddingTop: 4,
  },
  aiAssistantInputWrap: {
    display: "grid",
    gap: 8,
  },
  aiAssistantInputLabel: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
  },
  aiAssistantTextarea: {
    width: "100%",
    minHeight: 96,
    borderRadius: 16,
    border: "1px solid #cbd5e1",
    padding: "14px 16px",
    resize: "vertical",
    fontSize: 15,
    lineHeight: 1.5,
    color: "#0f172a",
    backgroundColor: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
  },
  aiAssistantSuggestionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  aiAssistantSuggestionButton: {
    appearance: "none",
    border: "1px solid #dbe3ee",
    borderRadius: 999,
    minHeight: 38,
    padding: "0 14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  aiAssistantLiveButtonActive: {
    borderColor: "#93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.16) inset",
  },
  aiAssistantFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    paddingTop: 2,
  },
  aiAssistantFooterText: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 600,
    flex: "1 1 240px",
  },
  aiAssistantFooterActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  aiAssistantSendButton: {
    minWidth: 132,
  },
  waiterHelpModalCard: {
    width: "100%",
    maxWidth: 640,
    maxHeight: "88vh",
    overflow: "auto",
    backgroundColor: "#ffffff",
    borderRadius: 18,
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: 22,
  },
  waiterOptionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
  },
  waiterOptionChip: {
    appearance: "none",
    border: "1px solid #dbe3ee",
    borderRadius: 14,
    minHeight: 48,
    padding: "10px 12px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.2,
    cursor: "pointer",
    textAlign: "left",
  },
  waiterOptionChipActive: {
    border: "1px solid #93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    boxShadow: "inset 0 0 0 1px rgba(59, 130, 246, 0.08)",
  },
  waiterTextareaWrap: {
    display: "grid",
    gap: 8,
  },
  waiterTextareaLabel: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
  },
  waiterTextarea: {
    width: "100%",
    minHeight: 112,
    borderRadius: 14,
    border: "1px solid #cbd5e1",
    padding: "12px 14px",
    resize: "vertical",
    fontSize: 14,
    lineHeight: 1.5,
    color: "#0f172a",
    backgroundColor: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
  },
  waiterActionBox: {
    display: "grid",
    gap: 12,
    padding: 16,
    borderRadius: 16,
    border: "1px solid #dbe3ee",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  },
  waiterActionCopy: {
    display: "grid",
    gap: 6,
  },
  waiterActionTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 800,
  },
  waiterActionText: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
  },
  waiterActionButton: {
    width: "100%",
    minHeight: 46,
  },
  cartModalCard: {
    width: "100%",
    maxWidth: 860,
    maxHeight: "88vh",
    overflow: "auto",
    backgroundColor: "#ffffff",
    borderRadius: 18,
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: 22,
  },
  cartHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  cartTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.05,
  },
  cartHeadingContent: {
    flex: "1 1 0",
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  cartCloseButton: {
    width: 44,
    height: 44,
    minWidth: 44,
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cartSubtitle: {
    margin: "8px 0 0",
    color: "#64748b",
    lineHeight: 1.5,
  },
  cartList: {
    display: "grid",
    gap: 12,
  },
  reviewOnlyNotice: {
    padding: "12px 14px",
    borderRadius: 14,
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  cartItemCard: {
    borderRadius: 16,
    border: "1px solid #dbe3ee",
    backgroundColor: "#ffffff",
    padding: 14,
  },
  cartItemMain: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  cartItemImage: {
    width: 96,
    height: 96,
    objectFit: "cover",
    borderRadius: 12,
    display: "block",
    flex: "0 0 auto",
    cursor: "pointer",
  },
  cartItemImagePlaceholder: {
    width: 96,
    height: 96,
    borderRadius: 12,
    backgroundColor: "#e2e8f0",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    cursor: "pointer",
  },
  cartItemInfo: {
    flex: 1,
    minWidth: 220,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  cartItemHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  cartItemName: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.2,
  },
  cartItemDetails: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 10,
  },
  cartItemDetailChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 26,
    padding: "0 10px",
    borderRadius: 999,
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 700,
  },
  cartItemBaseLabel: {
    display: "inline-flex",
    marginTop: 10,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 600,
  },
  cartItemSubtotal: {
    fontSize: 18,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  },
  cartItemFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  cartItemUnitPrice: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  cartItemActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  removeItemButton: {
    appearance: "none",
    border: "1px solid #fecaca",
    borderRadius: 999,
    minHeight: 36,
    padding: "0 12px",
    backgroundColor: "#fff5f5",
    color: "#b91c1c",
    fontWeight: 700,
    cursor: "pointer",
  },
  cartFooter: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    paddingTop: 6,
  },
  cartModalFooter: {
    position: "sticky",
    bottom: 0,
    zIndex: 3,
    margin: "0 -22px -22px",
    padding: "14px 22px 18px",
    borderTop: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 -16px 32px rgba(15, 23, 42, 0.08)",
  },
  cartTotalsBox: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  cartPricingSummary: {
    display: "grid",
    gap: 6,
    marginTop: 8,
    padding: "10px 12px",
    borderRadius: 14,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  cartPricingRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    color: "#475569",
    fontSize: 13,
  },
  cartAdjustmentList: {
    display: "grid",
    gap: 6,
    marginTop: 2,
  },
  cartAdjustmentItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    color: "#334155",
    fontSize: 12,
    lineHeight: 1.4,
  },
  cartTotalsLabel: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  cartTotalsValue: {
    fontSize: 24,
    lineHeight: 1.05,
  },
  cartFooterActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
    flex: "1 1 auto",
  },
  cartPayButton: {
    minWidth: 156,
    minHeight: 50,
    padding: "0 30px",
    borderRadius: 14,
    fontSize: 18,
    boxShadow: "0 14px 28px rgba(15, 23, 42, 0.14)",
  },
  submittedOrderPayButton: {
    minWidth: 148,
    minHeight: 46,
    padding: "0 22px",
    boxShadow: "0 10px 22px rgba(15, 23, 42, 0.12)",
  },
  submittedOrderCancelButton: {
    borderColor: "#f04438",
    color: "#b42318",
    backgroundColor: "#ffffff",
  },
  paymentOnlyFooterActions: {
    flex: "1 1 100%",
    justifyContent: "center",
  },
  checkoutBox: {
    display: "grid",
    gap: 10,
    flex: "1 1 280px",
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid #dbe3ee",
    backgroundColor: "#fbfdff",
  },
  checkoutBoxHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  checkoutLabel: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
    textTransform: "uppercase",
  },
  checkoutHint: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 600,
  },
  checkoutNameBox: {
    display: "grid",
    gap: 10,
    padding: "16px 18px",
    borderRadius: 18,
    border: "2px solid #bfdbfe",
    backgroundColor: "#f8fbff",
    boxShadow: "0 12px 28px rgba(29, 78, 216, 0.08)",
  },
  checkoutNameBoxAttention: {
    borderColor: "#60a5fa",
    backgroundColor: "#eff6ff",
  },
  checkoutNameLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    color: "#0f172a",
    fontSize: 17,
    lineHeight: 1.2,
    fontWeight: 900,
  },
  checkoutNameRequired: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 26,
    padding: "0 10px",
    borderRadius: 999,
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  checkoutNameInput: {
    width: "100%",
    minHeight: 64,
    borderRadius: 16,
    border: "2px solid #93c5fd",
    padding: "0 18px",
    fontSize: 20,
    fontWeight: 800,
    color: "#0f172a",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
    cursor: "pointer",
    outline: "none",
  },
  checkoutNameInputError: {
    borderColor: "#f97316",
    boxShadow: "0 0 0 4px rgba(249, 115, 22, 0.14)",
  },
  checkoutNameHint: {
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.45,
    fontWeight: 800,
  },
  kioskNameWarningOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1090,
    backgroundColor: "rgba(15, 23, 42, 0.52)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  kioskNameWarningDialog: {
    width: "100%",
    maxWidth: 560,
    display: "grid",
    gap: 22,
    padding: 28,
    borderRadius: 24,
    border: "1px solid #fed7aa",
    backgroundColor: "#ffffff",
    boxShadow: "0 30px 80px rgba(15, 23, 42, 0.32)",
  },
  kioskNameWarningHeader: {
    display: "grid",
    gap: 10,
  },
  kioskNameWarningEyebrow: {
    color: "#c2410c",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  kioskNameWarningTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 30,
    lineHeight: 1.1,
  },
  kioskNameWarningText: {
    margin: 0,
    color: "#334155",
    fontSize: 17,
    lineHeight: 1.55,
    fontWeight: 700,
  },
  kioskNameWarningButton: {
    appearance: "none",
    border: "1px solid transparent",
    borderRadius: 16,
    minHeight: 56,
    padding: "0 22px",
    fontSize: 17,
    fontWeight: 900,
    cursor: "pointer",
  },
  kioskKeyboardOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1060,
    backgroundColor: "rgba(15, 23, 42, 0.34)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  kioskInactivityOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1080,
    backgroundColor: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  kioskInactivityDialog: {
    width: "100%",
    maxWidth: 560,
    display: "grid",
    gap: 22,
    padding: 26,
    borderRadius: 24,
    border: "1px solid rgba(203, 213, 225, 0.92)",
    backgroundColor: "#ffffff",
    boxShadow: "0 28px 70px rgba(15, 23, 42, 0.3)",
  },
  kioskInactivityHeader: {
    display: "grid",
    gap: 8,
  },
  kioskInactivityEyebrow: {
    color: "#f97316",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  kioskInactivityTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 28,
    lineHeight: 1.1,
  },
  kioskInactivityText: {
    margin: 0,
    color: "#475569",
    fontSize: 16,
    lineHeight: 1.6,
    fontWeight: 600,
  },
  kioskInactivityActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },
  kioskInactivitySecondaryButton: {
    appearance: "none",
    border: "1px solid #fca5a5",
    borderRadius: 16,
    minHeight: 52,
    padding: "0 18px",
    backgroundColor: "#fff1f2",
    color: "#b91c1c",
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
  },
  kioskInactivityPrimaryButton: {
    appearance: "none",
    border: "1px solid transparent",
    borderRadius: 16,
    minHeight: 52,
    padding: "0 18px",
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
  },
  kioskKeyboardDialog: {
    width: "100%",
    maxWidth: 860,
    display: "grid",
    gap: 18,
    padding: 22,
    borderRadius: 22,
    border: "1px solid rgba(203, 213, 225, 0.92)",
    backgroundColor: "#ffffff",
    boxShadow: "0 28px 70px rgba(15, 23, 42, 0.28)",
  },
  kioskKeyboardDialogHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  kioskKeyboard: {
    display: "grid",
    gap: 12,
    marginTop: 12,
    padding: 16,
    borderRadius: 18,
    border: "1px solid #dbe3ee",
    backgroundColor: "#f8fafc",
  },
  kioskKeyboardHeader: {
    display: "grid",
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
  kioskKeyboardTitle: {
    color: "#334155",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  kioskKeyboardValue: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 700,
    minHeight: 22,
  },
  kioskKeyboardCloseButton: {
    appearance: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    minHeight: 40,
    padding: "0 14px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    flex: "0 0 auto",
  },
  kioskKeyboardRows: {
    display: "grid",
    gap: 10,
  },
  kioskKeyboardRow: {
    display: "grid",
    gridTemplateColumns: "repeat(10, minmax(0, 1fr))",
    gap: 8,
  },
  kioskKeyboardKey: {
    appearance: "none",
    border: "1px solid #cbd5e1",
    borderRadius: 14,
    minHeight: 52,
    padding: "0 10px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    textAlign: "center",
  },
  kioskKeyboardKeyWide: {
    gridColumn: "span 6",
  },
  kioskKeyboardKeyCommand: {
    backgroundColor: "#e2e8f0",
    color: "#334155",
  },
  kioskQrPaymentPanel: {
    display: "grid",
    gap: 12,
    flex: "1 1 320px",
    padding: "14px 16px",
    borderRadius: 16,
    border: "2px solid #bfdbfe",
    backgroundColor: "#f8fbff",
    boxShadow: "0 14px 30px rgba(30, 64, 175, 0.1)",
  },
  paymentOnlyQrPanel: {
    flex: "1 1 100%",
    maxWidth: 720,
    margin: "0 auto",
  },
  kioskQrPaymentHeader: {
    display: "grid",
    gap: 6,
  },
  kioskQrPaymentTitle: {
    color: "#0f172a",
    fontSize: 18,
    lineHeight: 1.15,
    fontWeight: 900,
  },
  kioskQrPaymentAlert: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: "12px 14px",
    borderRadius: 14,
    border: "2px solid #f97316",
    backgroundColor: "#ffedd5",
    boxShadow: "0 12px 26px rgba(249, 115, 22, 0.18)",
  },
  kioskQrPaymentAlertTitle: {
    color: "#9a3412",
    fontSize: 22,
    lineHeight: 1.05,
    fontWeight: 950,
  },
  kioskQrPaymentAlertText: {
    color: "#b91c1c",
    fontSize: 14,
    lineHeight: 1.15,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  kioskQrPaymentText: {
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.4,
    fontWeight: 700,
  },
  kioskQrPaymentBody: {
    display: "flex",
    gap: 16,
    alignItems: "center",
    flexWrap: "wrap",
  },
  kioskQrCanvasWrap: {
    display: "grid",
    placeItems: "center",
    width: 226,
    minHeight: 226,
    padding: 12,
    borderRadius: 16,
    border: "2px solid #dbeafe",
    backgroundColor: "#ffffff",
    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.07)",
  },
  kioskQrLinkCard: {
    display: "grid",
    gap: 8,
    minWidth: 220,
    padding: "18px 16px",
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.04)",
  },
  kioskQrNativeImage: {
    width: 198,
    height: 198,
    objectFit: "contain",
    display: "block",
  },
  kioskQrPaymentMeta: {
    display: "grid",
    gap: 7,
    minWidth: 180,
    flex: "1 1 180px",
  },
  checkoutLinkDebugBox: {
    marginTop: 8,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#475569",
    fontSize: 12,
    lineHeight: 1.5,
    wordBreak: "break-all",
  },
  kioskQrPaymentAmount: {
    color: "#0f172a",
    fontSize: 28,
    lineHeight: 1,
    fontWeight: 900,
  },
  kioskQrPaymentInstruction: {
    display: "block",
    maxWidth: 360,
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.25,
    fontWeight: 900,
  },
  kioskQrPaymentActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  kioskQrPaymentFallback: {
    display: "grid",
    gap: 12,
  },
  checkoutMethodRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  checkoutMethodButton: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    minHeight: 38,
    padding: "0 14px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 800,
    cursor: "pointer",
  },
  menuWalletChooser: {
    display: "grid",
    gap: 12,
    marginTop: 14,
  },
  menuWalletChooserHeader: {
    display: "grid",
    gap: 4,
  },
  menuWalletChooserTitle: {
    color: "#0f172a",
    fontSize: 15,
  },
  menuWalletChooserHint: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
  },
  menuWalletOptionList: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  menuWalletOptionButton: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    minHeight: 44,
    padding: "0 14px",
    borderRadius: 999,
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 800,
    cursor: "pointer",
  },
  menuWalletOptionButtonDisabled: {
    opacity: 0.58,
    cursor: "not-allowed",
  },
  menuWalletOptionContent: {
    display: "grid",
    gap: 2,
    textAlign: "left",
  },
  menuWalletOptionMeta: {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
  },
  menuWalletOptionDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    flexShrink: 0,
  },
};

export default MenuComensalQR;





